<template>
  <div v-if="isDataLoaded" class="individual-page-container">
    <!-- Sidebar-->
    <div class="individual-page-sidebar bak-white-1" :class="isSidebarOpen ? 'open' : 'closed'">
      <ExpandCollapse name="sidebar" :is-open-initially="true" :is-vertical-center="false" open-state="left"
        closed-state="right-zero" :show-double-chevron="true"
        @toggle-expand-collapse="toggleSidebar(); onResizeSidebar()" :show-circle="true" />
      <div :class="!isSidebarOpen ? 'hidden' : ''">
        <div class="project-name">
          <div><strong>{{ projectName }}</strong></div>
          <div class="menu-container">
            <button @click="toggleMenu" class="menu-button">⋮</button>
            <div v-if="isMenuVisible" class="menu-content">
              <ul>
                <li @click="editProjectDetails">Edit Project Details</li>
                <li @click="openEditImagesModal">Edit Project Images</li>
              </ul>
            </div>
          </div>
        </div>
        <!-- Sidebar Menu -->
        <div>
          <div class="sidebar-menu">
            <div v-for="item in sidebarMenuItems" :key="item.id"
              :class="{ 'sidebar-menu-selected': item.id === sidebarSelectedItemId }"
              @click="sidebarSelectItem(item.id)">
              {{ item.text }}
            </div>
          </div>
        </div>
        <div ref="propertyImageContainer" v-if="sidebarSelectedItemId == 1">
          <div class="project-img-carousel">
            <!-- Carousel Container -->
            <div class="carousel-container">
              <div class="carousel-wrapper">
                <!-- Navigation Arrows -->
                <button 
                  v-if="displayImages.length > 1"
                  @click="previousImage" 
                  class="carousel-arrow carousel-arrow-left"
                  :disabled="currentImageIndex === 0"
                >
                  ‹
                </button>
                
                <!-- Main Image Display -->
                <div class="main-image-container" @click="openImageLightbox">
                  <div class="image-background">
                    <img 
                      v-if="hasLoaded && displayImages.length > 0" 
                      :src="displayImages[currentImageIndex]?.url" 
                      :alt="displayImages[currentImageIndex]?.name || projectName + ' - Building View'"
                      class="main-image"
                    >
                    <div 
                      v-else
                      class="no-image-placeholder"
                      @click="openEditImagesModal"
                    >
                      <span class="placeholder-icon">📷</span>
                      <p>Click to add project images</p>
                    </div>
                  </div>
                </div>
                
                <!-- Navigation Arrows -->
                <button 
                  v-if="displayImages.length > 1"
                  @click="nextImage" 
                  class="carousel-arrow carousel-arrow-right"
                  :disabled="currentImageIndex === displayImages.length - 1"
                >
                  ›
                </button>
              </div>
              
              <!-- Image Counter - Only show if multiple images -->
              <div v-if="displayImages.length > 1" class="image-counter">
                {{ currentImageIndex + 1 }} / {{ displayImages.length }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="sidebarSelectedItemId == 2">
          <div class="map-container">
            <div v-if="!isProjectLocationValid">Project has invalid location values.</div>
            <div id="individualPageMap"></div>
          </div>
        </div>
        <div class="project-details-wrapper">
            <div class="project-details">
              <div class="project-img-summary">
                <div class="project-img-summary-item">
                  <label>Project Address</label>
                  <span>{{ fullAddress }}</span>
                </div>
                <div class="project-img-summary-item">
                  <label>City</label><span>{{ getDisplayValue(projectInformation?.city) }}</span>
                </div>
                <div class="project-img-summary-item">
                  <label>State</label><span>{{ getDisplayValue(projectInformation?.state) }}</span>
                </div>
                <div class="project-img-summary-item">
                  <label>Zip Code</label><span>{{ getDisplayValue(projectInformation?.zipcode) }}</span>
                </div>
                <div class="project-img-summary-item construction-type-container">
                  <label>Construction Type:</label><span>{{ projectInformation?.project_construction_types_s ? projectInformation.project_construction_types_s.join('; ') : 'N/A' }}</span>
                </div>

                <div class="project-img-summary-item">
                  <label>Former Use Type:</label><span>{{ getFormerUseType() }}</span>
                </div>
              </div>
              <!-- Building Details -->
              <div class="building-details-container">
                <div class="accordion-panel-header">
                  <label for="buildingDetails"><strong>Building Details</strong></label>
                  <ExpandCollapse name="buildingDetails" :is-open-initially="true" :is-vertical-center="false"
                    open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"
                    :chevron-fill="'#4A5FD5'" @toggle-expand-collapse="expandCollapseController('buildingDetails');" />
                </div>
                <div v-if="isBuildingDetailsOpen">
                  <div>
                    <table class="building-details-table">
                      <tr>
                        <th class="left-align">Use Type(s)</th>
                        <th>GSF</th>
                        <th>Units</th>
                      </tr>
                      <template v-for="(row, index) in useTypeTableData" :key="index">
                        <tr>
                          <td class="left-align use-types" :class="row.building_use_type_id_s == 'Total' ? 'total' : ''">
                            {{ row.codeable_id_s ? numberFormat(row.codeable_id_s, 0) : 'N/A'}}
                          </td>
                          <td>{{ row.total_gsf ? numberFormat(row.total_gsf, 0) : 'N/A' }}</td>
                          <td>{{ row.num_units ? numberFormat(row.num_units, 0) : (row.num_spaces ? numberFormat(row.num_spaces, 0) : 'N/A') }}</td>
                        </tr>
                        <!-- Affordable Component Row (if exists) -->
                        <tr v-if="row.affordable_comp_gsf || row.affordable_comp_num_units" class="affordable-component-row">
                          <td class="left-align use-types" style="padding-left: 30px; font-style: italic; color: #666;">
                            ↳ Affordable Component
                          </td>
                          <td>{{ row.affordable_comp_gsf ? numberFormat(row.affordable_comp_gsf, 0) : 'N/A' }}</td>
                          <td>{{ row.affordable_comp_num_units ? numberFormat(row.affordable_comp_num_units, 0) : 'N/A' }}</td>
                        </tr>
                      </template>
                      <tr>
                        <td class="left-align use-types">BOH/Other</td>
                        <td>{{ bohOtherGSF() }} </td>
                        <td></td>
                      </tr>
                      <tr class="totals">
                        <td class="left-align">Total</td>
                        <td>{{ numberFormat(totalGsf()) }}</td>
                        <td>{{ totalUnits() }}</td>
                        <td></td>
                      </tr>
                    </table>
                  </div>
                  <div class="building-details-summary">
                    <div class="building-details-summary-item">
                      <label>Building Height Classification:</label><span> {{ classifyBuilding(projectInformation?.total_height) }} </span>
                    </div>
                    <div class="building-details-summary-item">
                      <label>Buildings Height (Feet)</label><span>{{ projectInformation?.total_height }}</span>
                    </div>
                    <div class="building-details-summary-item">
                      <label>Building Height (Stories)</label><span>{{ projectInformation?.num_floors }}</span>
                    </div>
                  </div>
                  <div class="building-details-summary">
                    <div class="building-details-summary-item amenities-container">
                      <label>Amenities:</label><span> {{ projectInformation?.amenities_s ? projectInformation.amenities_s.join('; ') : 'N/A' }} </span>
                    </div>
                  </div>
                  <div class="building-details-summary">
                    <div class="building-details-summary-item">
                      <label>Affordable Component:</label><span> {{ totalAffordableComponent() ? totalAffordableComponent() : 'N/A' }}</span>
                    </div>
                    <div class="building-details-summary-item">
                      <label>Historic/Landmarks:</label><span>N/A</span>
                    </div>
                    <div class="building-details-summary-item">
                      <label>Sustainability:</label><span>{{ projectInformation?.sustainability_type_id_s ? projectInformation.sustainability_type_id_s : 'N/A' }}</span>
                    </div>
                    <div class="building-details-summary-item">
                      <label>Brand/Flag:</label><span>{{ getAllBrandNames() }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Construction Details -->
              <div class="construction-details-container">
                <div class="accordion-panel-header">
                  <label for="buildingDetails"><strong>Construction Details</strong></label>
                  <ExpandCollapse name="buildingDetails" :is-open-initially="true" :is-vertical-center="false"
                    open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"
                    :chevron-fill="'#4A5FD5'"
                    @toggle-expand-collapse="expandCollapseController('constructionDetails');" />
                </div>
                <div v-if="isConstructionDetailsOpen">
                  <div class="construction-details-summary">
                    <div class="construction-details-summary-item construction-details-summary-item-header">
                      <label>Construction Contract:</label><span>{{  getProjectConstructionContract() }}</span>
                    </div>
                    <div class="construction-details-summary-item">
                      <label>Year Construction Started</label><span>{{ projectInformation?.actual_start_date ? new Date(projectInformation.actual_start_date).getFullYear() : 'N/A' }}</span>
                    </div>
                    <div class="construction-details-summary-item">
                      <label>Year Construction Completed</label><span> {{ projectInformation?.actual_end_date ? new Date(projectInformation.actual_end_date).getFullYear() : 'N/A' }}</span>
                    </div>
                    <div class="construction-details-summary-item">
                      <label>Construction Duration</label><span>{{ projectInformation?.project_length_months ? parseFloat(projectInformation.project_length_months).toFixed(0) : 'N/A' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>

    </div>
    <!-- Content -->
    <div class="individal-page-content" :class="!largeSidebar ? 'large' : 'small'">
      <div class="breadcrumbs">
        <span @click="navigateToPortfolio" class="breadcrumb-link">Portfolio</span> | 
        <strong>{{ projectName }}</strong> |
        <span v-for="menuItem in filteredMenuItems()" :key="menuItem.id">
          {{ menuItem.name }}
        </span>
      </div>
    
      <div class="individual-page-title">Property Summary: {{ projectName }} </div>
      <div class="individual-page-sub-menu">
        <div v-for="item in subMenuItems" :key="item.id"
          :class="{ 'individual-page-sub-menu-selected': item.id === subMenuSelectedItemId }"
          @click="subMenuSelectItem(item.id)">
          {{ item.name }}
        </div>
      </div>

      <!-- Budget Summary Content -->
      <div v-if="subMenuSelectedItemId == 1">
        <div>
          <div class="budget-summary-header-container">
            <div class="budget-summary-header-title">Budget Summary</div>
            <!-- ✅ CORRECT: View By controls next to heading -->
            <div class="global-budget-controls">
              <div class="global-control-group">
                <label>View By:</label>
                <select v-model="globalViewMode" @change="updateAllChartsAndGrid" class="global-view-select">
                  <option value="total">View by Total</option>
                  <option value="per_gsf">View per GSF</option>
                  <option value="per_unit">View per Unit</option>
                </select>
              </div>
            </div>
          </div>
          <div class="budget-summary-content">
            <!-- Total Project Costs Chart -->
            <div class="tile costs-to-date-chart">
              <div class="chart-main-title">Total Project Costs</div>
              
              <!-- Summary Cards - Match Mockup Layout -->
              <div class="project-costs-summary-cards">
                <div class="summary-card">
                  <div class="card-label">Original Budget</div>
                  <div class="card-value">{{ kMillionsWithDecimalsFormat(cdTotalOriginalBudget) }}</div>
                </div>
                <div class="summary-card">
                  <div class="card-label">Current Budget</div>
                  <div class="card-value">{{ kMillionsWithDecimalsFormat(cdTotalCurrentBudget) }}</div>
                </div>
                <div class="summary-card">
                  <div class="card-label">Variance</div>
                  <div class="card-value" :class="{ 'positive-variance': (cdTotalCurrentBudget || 0) - (cdTotalOriginalBudget || 0) >= 0, 'negative-variance': (cdTotalCurrentBudget || 0) - (cdTotalOriginalBudget || 0) < 0 }">
                    {{ ((cdTotalCurrentBudget || 0) - (cdTotalOriginalBudget || 0) >= 0 ? '+' : '') }}{{ (((cdTotalCurrentBudget || 0) - (cdTotalOriginalBudget || 0)) / (cdTotalOriginalBudget || 1) * 100).toFixed(0) }}%
                  </div>
                </div>
              </div>
              <!-- Chart filter removed - using global draw filter -->
              <div class="cost-curve-chart-container total-project-costs-chart" style="position: relative;">
                <div class="chart-wrapper">
                  <AgChartsVue :options="costsToDateAgChartOptions" />
                </div>
                <!-- Custom Labels - Match Mockup: Left side for completed, Right side for planned/current -->
                <div class="total-costs-labels-left">
                  <div class="label-item label-completed" :style="{ top: `${getLabelPosition('completed')}%` }">
                    <span class="label-text">Complete to Date</span>
                    <span class="label-value">{{ kMillionsWithDecimalsFormat(cdTotalBilledToDate) }}</span>
                    <span class="label-percentage">{{ ((cdTotalBilledToDate || 0) / (cdTotalCurrentBudget || 1) * 100).toFixed(0) }}%</span>
                  </div>
                </div>
                <div class="total-costs-labels-right">
                  <div class="label-item label-planned" :style="{ top: `${getLabelPosition('planned')}%` }">
                    <span class="label-text">Planned Budget</span>
                    <span class="label-value">{{ kMillionsWithDecimalsFormat(cdTotalOriginalBudget) }}</span>
                  </div>
                  <div v-if="(cdTotalCurrentBudget || 0) > (cdTotalOriginalBudget || 0)" class="label-item label-current" :style="{ top: `${getLabelPosition('current')}%` }">
                    <span class="label-text">Current Budget</span>
                    <span class="label-value">{{ kMillionsWithDecimalsFormat(cdTotalCurrentBudget) }}</span>
                    <span class="label-percentage">100%</span>
                  </div>
                </div>
              </div>
              <div class="cost-curve-legend">
                <div class="cost-curve-legend-item planned-budget">
                  <span class="legend-color" style="background-color: #93c5fd;"></span>
                  <span>Planned Budget</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(cdTotalOriginalBudget) }}</span>
                </div>
                <div class="cost-curve-legend-item current-budget">
                  <span class="legend-color" style="background-color: #bfdbfe;"></span>
                  <span>Current Budget</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(cdTotalCurrentBudget) }}</span>
                </div>
                <div class="cost-curve-legend-item completed-to-date">
                  <span class="legend-color" style="background-color: #2563eb;"></span>
                  <span>Complete to Date</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(cdTotalBilledToDate) }} ({{ ((cdTotalBilledToDate || 0) / (cdTotalCurrentBudget || 1) * 100).toFixed(0) }}%)</span>
                </div>
              </div>
            </div>
            <!-- Cost Curve Chart -->
            <div class="tile cost-curve-chart">
              <div class="chart-main-title">Cost Curve</div>
              <div class="chart-subtitle">
                {{ costCurveContractor ? `${costCurveContractor} - View Billing by Category` : 'View Billing by Category' }}
              </div>
              <div class="cost-curve-controls">
                <div class="cost-curve-filter">
                  <select v-model="costCurveCategory" @change="() => { 
                    invalidateChartCache();
                    updateCostCurveData(); 
                  }">
                    <option value="all">All Categories</option>
                    <option value="hard_costs">Hard Costs</option>
                    <option value="soft_costs">Soft Costs</option>
                    <option value="acquisition">Acquisition</option>
                    <option value="financing">Financing & Misc.</option>
                    <option value="contingencies">Contingencies</option>
                  </select>
                </div>
                <!-- ✅ HIDDEN: Contractor dropdown removed as requested -->
                <!-- 
                <div class="contractor-filter">
                  <div class="contractor-autocomplete">
                    <div class="input-wrapper">
                      <input 
                        type="text" 
                        v-model="costCurveContractorSearch"
                        :placeholder="costCurveContractor ? costCurveContractor : 'Search contractors...'"
                        @input="handleCostCurveContractorSearch"
                        @focus="handleCostCurveContractorFocus"
                        @blur="handleCostCurveContractorBlur"
                        class="contractor-search-input compact"
                        :class="{ 'has-selection': costCurveContractor }"
                      />
                      <button 
                        type="button"
                        class="dropdown-toggle"
                        @mousedown="toggleCostCurveContractorDropdown"
                      >
                        ▼
                      </button>
                    </div>
                    
                    <div v-if="costCurveContractorDropdown && costCurveFilteredContractors" class="contractor-dropdown compact">
                      <div 
                        class="contractor-option"
                        @mousedown="selectCostCurveContractor('')"
                        :class="{ 'selected': !costCurveContractor }"
                      >
                        <div class="contractor-name"><em>Show All Contractors</em></div>
                      </div>
                      <div v-if="costCurveFilteredContractors.length === 0 && costCurveContractorSearch" class="no-results">
                        No matching contractors found
                      </div>
                      <div 
                        v-for="contractor in costCurveFilteredContractors" 
                        :key="contractor"
                        class="contractor-option"
                        @mousedown="selectCostCurveContractor(contractor)"
                        :class="{ 'selected': costCurveContractor === contractor }"
                      >
                        <div class="contractor-name">{{ contractor }}</div>
                      </div>
                    </div>
                    
                    <div v-if="costCurveContractor" class="selected-contractor-display compact">
                      <span class="selected-contractor-name">{{ costCurveContractor }}</span>
                      <button @click="clearCostCurveContractorSelection" class="clear-selection" type="button">×</button>
                    </div>
                  </div>
                </div>
                -->
              </div>
              <div class="cost-curve-charts-split">
                <div class="cost-curve-chart-container main">
                <AgChartsVue 
                  v-if="costCurveChartReady && costCurveAgChartOptions" 
                  :options="costCurveAgChartOptions" 
                  style="width: 100%;"
                />
                <div v-else class="chart-loading">
                  <p>Loading chart data...</p>
                </div>
                </div>
                
                <!-- Prefunded at Closing chart removed - now shows as tooltip on Draw 1 hover -->
                
                <!-- Interactive hint for prefunded information positioned near Draw 1 label -->
                <div v-if="prefundedCircleData && prefundedCircleData.length > 0" class="prefunded-hint">
                  <div 
                    class="hint-text" 
                    @mouseenter="showPrefundedTooltip = true"
                    @mouseleave="showPrefundedTooltip = false"
                  >
                    💡 Hover here for prefunded details
                  </div>
                  
                  <!-- Custom tooltip for prefunded information -->
                  <div v-if="showPrefundedTooltip" class="prefunded-tooltip">
                    <div class="tooltip-title">⚠️ PREFUNDED AT CLOSING</div>
                    <div class="tooltip-subtitle">(Excluded from Draw 1)</div>
                    <div class="tooltip-content">
                      <div class="tooltip-total">
                        Total: {{ kMillionsWithDecimalsFormat(prefundedCircleData.reduce((sum, item) => sum + item.amount, 0)) }}
                      </div>
                      <div class="tooltip-breakdown">
                        <div class="breakdown-title">Breakdown:</div>
                        <div v-for="item in prefundedCircleData" :key="item.category" class="breakdown-item">
                          <span v-if="item.amount > 0">• {{ item.category }}: {{ kMillionsWithDecimalsFormat(item.amount) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- ✅ HIDDEN: Cost curve legend with category totals removed per client request -->
              <!-- This was skewing the chart scale and is not needed for draw-by-draw view -->
              <!--
              <div class="cost-curve-legend">
                <div class="cost-curve-legend-item hard-costs">
                  <span class="legend-color hard-costs-color"></span>
                  <span>Hard Costs</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(costCurveTotals.hardCosts) }}</span>
                </div>
                <div class="cost-curve-legend-item soft-costs">
                  <span class="legend-color soft-costs-color"></span>
                  <span>Soft Costs</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(costCurveTotals.softCosts) }}</span>
                </div>
                <div class="cost-curve-legend-item acquisition">
                  <span class="legend-color acquisition-color"></span>
                  <span>Acquisition</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(costCurveTotals.acquisition) }}</span>
                </div>
                <div class="cost-curve-legend-item financing">
                  <span class="legend-color financing-color"></span>
                  <span>Financing & Misc.</span>
                  <span class="legend-value">{{ kMillionsWithDecimalsFormat(costCurveTotals.financing) }}</span>
                </div>
                <div class="cost-curve-legend-item total">
                  <span class="legend-color total-color"></span>
                  <span><strong>Total</strong></span>
                  <span class="legend-value"><strong>{{ kMillionsWithDecimalsFormat(costCurveTotals.total) }}</strong></span>
                </div>
              </div>
              -->
            </div>
            <!-- Budget Variance Summary / DiDi Insights Panel -->
            <div class="tile budget-variance-chart" :class="{ 'didi-insights-card': [1, 2, 3, 5, 7].includes(btSubMenuSelectedItemId) }">
              <div class="chart-main-title">
                <span v-if="[1, 2, 3, 5, 7].includes(btSubMenuSelectedItemId)" class="didi-insights-title">DiDiAi Insights:</span>
                <span v-else>Budget Variance Summary</span>
              </div>
              <!-- No dropdown - automatically shows enhanced view based on selected tab -->
              
              <!-- Enhanced Variance Display - TEXT ONLY (like Draw 12 mockup) -->
              <div class="variance-display-container">
                <!-- G703 Payment Applications Section -->
                <div v-if="btSubMenuSelectedItemId === 2" class="variance-content">
                  <div class="variance-section">
                    <h4 class="section-title">Payment Application #{{ g703ApplicationNumber || 'N/A' }}</h4>
                    <!-- Major Budget Movements -->
                    <h5 class="subsection-title">Major Budget Movements</h5>
                    <div class="variance-items" v-if="g703TopBudgetMovements.length > 0 || g703StoredMaterials > 0">
                      <div class="variance-item" v-for="item in g703TopBudgetMovements" :key="item.description">
                        <span class="item-label">{{ item.description }}</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(item.variance) }}</span>
                        <span class="item-percent" :class="{
                          'variance-good': (item.variance / Math.abs(item.originalBudget || 1)) * 100 < 0,
                          'variance-warning': (item.variance / Math.abs(item.originalBudget || 1)) * 100 >= 0 && (item.variance / Math.abs(item.originalBudget || 1)) * 100 < 3,
                          'variance-bad': (item.variance / Math.abs(item.originalBudget || 1)) * 100 >= 3
                        }">{{ item.variance > 0 ? '+' : '' }}{{ ((item.variance / Math.abs(item.originalBudget || 1)) * 100).toFixed(0) }}%</span>
                      </div>
                      <!-- Stored Materials as line item -->
                      <div class="variance-item" v-if="g703StoredMaterials > 0">
                        <span class="item-label">Stored Materials Requested</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(g703StoredMaterials) }}</span>
                        <span class="item-percent"></span>
                      </div>
                    </div>
                    <div v-else class="variance-items">
                      <div class="variance-item">
                        <span class="item-label" style="font-style: italic; color: #999;">No significant budget movements found</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- G702 BVS Section -->
                <div v-else-if="btSubMenuSelectedItemId === 3" class="variance-content">
                  <div class="variance-section">
                    <h4 class="section-title">Contractor Payment Summary</h4>
                    <!-- Total Amount Requested -->
                    <h5 class="subsection-title">Total Amount Requested</h5>
                    <div class="variance-items" v-if="g702TotalAmountRequested.length > 0">
                      <div class="variance-item" v-for="gc in g702TotalAmountRequested" :key="gc.contractor + gc.appNumber">
                        <span class="item-label">{{ gc.contractor }} #{{ gc.appNumber }}</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(gc.amount) }}</span>
                        <span class="item-percent"></span>
                      </div>
                    </div>
                    <div v-else class="variance-items">
                      <div class="variance-item">
                        <span class="item-label" style="font-style: italic; color: #999;">No GC payment applications found</span>
                      </div>
                    </div>
                    
                    <!-- Net Change by Change Orders -->
                    <h5 class="subsection-title" style="margin-top: 20px;">Net Change by CO</h5>
                    <div class="variance-items" v-if="g702ChangeOrders.length > 0">
                      <div class="variance-item" v-for="co in g702ChangeOrders" :key="co.contractor + '_co_' + co.appNumber">
                        <span class="item-label">CO#{{ co.coNumber }} {{ co.contractor }} #{{ co.appNumber }}</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(co.amount) }}</span>
                        <span class="item-percent"></span>
                      </div>
                    </div>
                    <div v-else class="variance-items">
                      <div class="variance-item">
                        <span class="item-label" style="font-style: italic; color: #999;">No change orders found</span>
                      </div>
                    </div>
                    
                    <!-- Outstanding Items -->
                    <h5 class="subsection-title" style="margin-top: 20px;">Outstanding Items</h5>
                    <div class="variance-items">
                      <div style="margin-bottom: 15px;">
                        <div style="font-weight: 600; margin-bottom: 8px; font-size: 14px;">Subcontractor pay apps</div>
                        <div v-if="missingPayApps.length > 0" style="margin-left: 20px;">
                          <div v-for="sub in missingPayApps" :key="sub.contractor" style="margin-bottom: 4px; font-size: 13px;">
                            <span style="margin-right: 8px; color: #f44336;">○</span>{{ sub.contractor }}
                          </div>
                        </div>
                        <div v-else class="variance-item" style="margin-left: 20px;">
                          <span class="item-label" style="font-style: italic; color: #4caf50;">✓ All received</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 🆕 Lien Waiver Tab: Missing LW Summary -->
                <div v-else-if="btSubMenuSelectedItemId === 5" class="variance-content">
                  <div class="variance-section">
                    <h4 class="section-title">Lien Waiver Status - Draw #{{ globalDrawFilter === 'all' ? 'All' : globalDrawFilter }}</h4>
                    
                    <!-- LW Summary Stats -->
                    <h5 class="subsection-title">Summary</h5>
                    <div class="variance-items">
                      <div class="variance-item">
                        <span class="item-label">Total Contractors</span>
                        <span class="item-amount">{{ lwRowData.length }}</span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">LWs Received</span>
                        <span class="item-amount" style="color: #4caf50;">{{ lwRowData.length - g702MissingLienWaivers.length }}</span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">LWs Missing</span>
                        <span class="item-amount" style="color: #f44336;">{{ g702MissingLienWaivers.length }}</span>
                      </div>
                    </div>
                    
                    <!-- Missing Lien Waivers List -->
                    <h5 class="subsection-title" style="margin-top: 20px;">Missing Lien Waivers</h5>
                    <div class="variance-items">
                      <div v-if="g702MissingLienWaivers.length > 0">
                        <div v-for="sub in g702MissingLienWaivers" :key="sub.contractor + '_lw_missing'" style="margin-bottom: 6px; font-size: 13px;">
                          <span style="margin-right: 8px; color: #f44336;">○</span>
                          <span>{{ sub.contractor }}</span>
                        </div>
                      </div>
                      <div v-else class="variance-item">
                        <span class="item-label" style="font-style: italic; color: #4caf50;">✓ All Lien Waivers received</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 🆕 Retainage Tab: Retainage Insights -->
                <div v-else-if="btSubMenuSelectedItemId === 7" class="variance-content">
                  <div class="variance-section">
                    <h4 class="section-title">Retainage Insights - Draw #{{ globalDrawFilter === 'all' ? 'All' : globalDrawFilter }}</h4>
                    
                    <h5 class="subsection-title">Summary</h5>
                    <div class="variance-items">
                      <div class="variance-item">
                        <span class="item-label">Retainage Held This Period</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(retainageThisPeriodComputed) }}</span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">Overall Retainage %</span>
                        <span class="item-percent">{{ overallRetainagePctComputed.toFixed(1) }}%</span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">Total Contractors</span>
                        <span class="item-amount">{{ retainageRowData.length }}</span>
                      </div>
                    </div>
                    
                    <h5 class="subsection-title" style="margin-top: 20px;">Partial Retainage Releases This Period</h5>
                    <div class="variance-items" v-if="partialRetainageReleases.length > 0">
                      <div class="variance-item" v-for="release in partialRetainageReleases" :key="release.contractor + '_partial'">
                        <span class="item-label">{{ release.contractor }}</span>
                        <span class="item-amount" style="color: #4caf50;">{{ kMillionsWithDecimalsFormat(release.amount) }}</span>
                        <span class="item-percent">{{ release.retainagePct.toFixed(1) }}%</span>
                      </div>
                    </div>
                    <div v-else class="variance-items">
                      <div class="variance-item">
                        <span class="item-label" style="font-style: italic; color: #999;">No partial releases this period</span>
                      </div>
                    </div>

                    <h5 class="subsection-title" style="margin-top: 20px;">Final Retainage Releases This Period</h5>
                    <div class="variance-items" v-if="finalRetainageReleases.length > 0">
                      <div class="variance-item" v-for="release in finalRetainageReleases" :key="release.contractor + '_final'">
                        <span class="item-label">
                          {{ release.contractor }}
                          <span v-if="release.hasFinalLW" style="color: #4caf50; font-weight: 600; margin-left: 8px;">(Final LW Recvd)</span>
                          <span v-else style="color: #f44336; font-weight: 600; margin-left: 8px;">(NO Final LW)</span>
                        </span>
                        <span class="item-amount" style="color: #4caf50;">{{ kMillionsWithDecimalsFormat(release.amount) }}</span>
                        <span class="item-percent">{{ release.retainagePct.toFixed(1) }}%</span>
                      </div>
                    </div>
                    <div v-else class="variance-items">
                      <div class="variance-item">
                        <span class="item-label" style="font-style: italic; color: #999;">No final releases this period</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Draw Details: Major Budget Movements + Contingencies -->
                <div v-else-if="btSubMenuSelectedItemId === 1" class="variance-content">
                  <!-- Draw Header -->
                  <div class="variance-section">
                    <h4 class="section-title">Current Change Orders & Budget Increases</h4>
                    
                    <!-- Major Budget Movements -->
                    <h5 class="subsection-title">Major Budget Movements</h5>
                    <div class="variance-items">
                      <div class="variance-item" v-for="item in topBudgetMovements" :key="item.description">
                        <span class="item-label" :class="{ 'new-item': item.isNew }">{{ item.description }}</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(item.variance) }}</span>
                        <span class="item-percent" :class="{
                          'variance-good': (item.variance / Math.abs(item.originalBudget || 1)) * 100 < 0,
                          'variance-warning': (item.variance / Math.abs(item.originalBudget || 1)) * 100 >= 0 && (item.variance / Math.abs(item.originalBudget || 1)) * 100 < 3,
                          'variance-bad': (item.variance / Math.abs(item.originalBudget || 1)) * 100 >= 3
                        }">{{ item.variance > 0 ? '+' : '' }}{{ ((item.variance / Math.abs(item.originalBudget || 1)) * 100).toFixed(0) }}%</span>
                      </div>
                    </div>
                    
                    <!-- Hard Cost Contingency -->
                    <h5 class="subsection-title">Hard Cost Contingency</h5>
                    <div class="variance-items" v-if="hardCostContingency">
                      <div class="variance-item">
                        <span class="item-label">Usage This Period</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(hardCostContingency.usage || 0) }}</span>
                        <span class="item-percent"></span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">% utilized</span>
                        <span class="item-amount">{{ hardCostContingency.originalBudget > 0 ? (((hardCostContingency.originalBudget - hardCostContingency.balanceOfContingency) / hardCostContingency.originalBudget) * 100).toFixed(0) : '0' }}%</span>
                        <span class="item-percent"></span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">Balance on Remaining Cost</span>
                        <span class="item-amount">{{ (hardCostContingency.remaining || 0).toFixed(1) }}%</span>
                        <span class="item-percent"></span>
                      </div>
                    </div>
                    
                    <!-- Soft Cost Contingency -->
                    <h5 class="subsection-title">Soft Cost Contingency</h5>
                    <div class="variance-items" v-if="softCostContingency">
                      <div class="variance-item">
                        <span class="item-label">Usage This Period</span>
                        <span class="item-amount">{{ kMillionsWithDecimalsFormat(softCostContingency.usage || 0) }}</span>
                        <span class="item-percent"></span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">% utilized</span>
                        <span class="item-amount">{{ softCostContingency.originalBudget > 0 ? (((softCostContingency.originalBudget - softCostContingency.balanceOfContingency) / softCostContingency.originalBudget) * 100).toFixed(0) : '0' }}%</span>
                        <span class="item-percent"></span>
                      </div>
                      <div class="variance-item">
                        <span class="item-label">Balance on Remaining Cost</span>
                        <span class="item-amount">{{ (softCostContingency.remaining || 0).toFixed(1) }}%</span>
                        <span class="item-percent"></span>
                      </div>
                    </div>
                    
                    <!-- Development Fee % Utilized -->
                    <div class="variance-items" v-if="developmentFeeUtilized !== null" style="margin-top: 20px;">
                      <div class="variance-item">
                        <span class="item-label">Development Fee % Utilized</span>
                        <span class="item-amount" :class="developmentFeeColorClass">{{ developmentFeeUtilized.toFixed(1) }}%</span>
                        <span class="item-percent"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Hidden AG Grid to create the charts -->
            <div style="display: none;">
              <ag-grid
                :row-height="30"
                :row-data="leafParentData"
                :columnDefs="columnDefs"
                :treeData="false"
                :key="`${leafParentDataCtr}${viewModeDivisor}`"
                @setChartData="setChartData"
                :pinnedBottomRowData="pinnedBottomRowData"
              />
            </div>
          </div>
        </div>
        <!-- Table Grid -->
        <div>
          <div class="budget-tracking-header-container">
            <div class="budget-tracking-header-title">Budget Tracking</div>
            <!-- TODO: Remove this for now as it is not functional yet -->
            <!--  <div class="budget-tracking-subtitle">View in Original Format</div>
            <div class="toggle-container" :class="{ 'original-format': isOriginalFormat }" @click="toggleActive">
              <div class="circle" :class="{ 'move-right': isOriginalFormat }"></div>
            </div>
            <div class="budget-tracking-subtitle">View in DDA Categories</div> -->
          </div>
          <div class="budget-tracking-sub-menu">
            <div v-for="item in btSubMenuItems" :key="item.id"
              :class="{ 'budget-tracking-sub-menu-selected': item.id === btSubMenuSelectedItemId }"
              @click="btSubMenuSelectItem(item.id)">
              {{ item.name }}
            </div>
            <div class="view-by-draw-number-container">
              <div class="left-side-controls">
                <!-- Left side now empty for more space -->
              </div>
              
              <div class="right-side-controls">
                <div class="draw-selection-controls">
                  <div class="global-draw-filter">
                    <label>See project at:</label>
                    <!-- 🎯 DRAGGABLE WHEEL: Replace dropdown with draggable slider -->
                    <div class="draw-wheel-container">
                      <button 
                        class="draw-wheel-btn draw-wheel-prev" 
                        @click="changeDraw(1)"
                        :disabled="!canGoToNextDraw"
                        title="Previous Draw">
                        ◀
                      </button>
                      
                      <!-- 🎯 DRAGGABLE SLIDER: Allow drag to jump to any draw -->
                      <div class="draw-slider-container">
                        <div class="draw-wheel-display">
                          <span class="draw-current">Draw {{ globalDrawFilter }}</span>
                          <span class="draw-total">of {{ Math.max(...(drawNumberList || [0])) }}</span>
                        </div>
                        <div class="draw-slider-track" ref="sliderTrack" @mousedown="startDrag" @touchstart="startDrag">
                          <div 
                            class="draw-slider-thumb" 
                            :style="{ left: sliderPosition + '%' }"
                            @mousedown.stop="startDrag"
                            @touchstart.stop="startDrag">
                          </div>
                          <div class="draw-slider-progress" :style="{ width: sliderPosition + '%' }"></div>
                        </div>
                      </div>
                      
                      <button 
                        class="draw-wheel-btn draw-wheel-next" 
                        @click="changeDraw(-1)"
                        :disabled="!canGoToPreviousDraw"
                        title="Next Draw">
                        ▶
                      </button>
                    </div>
                  </div>
                </div>
                <div class="upload-draw-button-container">
                  <button 
                    class="upload-new-draw-button" 
                    @click="openUploadModal"
                    title="Upload Documents"
                  >
                    Upload Documents
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Export and View Documents Buttons Below Tabs and Dropdown -->
          <div class="export-and-docs-buttons" style="display: flex; justify-content: space-between; align-items: center;">
            
            <!-- LEFT SIDE BUTTONS -->
            <div style="display: flex; gap: 10px;">
              <!-- DDA TERMS COLUMN TOGGLE (ONLY FOR DRAW DETAILS) - HIDDEN FOR NOW -->
              <!-- <div v-if="btSubMenuSelectedItemId == 1 && drawDetailRowData && drawDetailRowData.length > 0" class="dda-toggle-container">
                <button 
                  class="dda-toggle-btn"
                  :class="{ 'active': showDDATermsColumn }"
                  @click="toggleDDATermsColumn"
                  :title="showDDATermsColumn ? 'Hide DDA Terms Column' : 'Show DDA Terms Column'"
                >
                  {{ showDDATermsColumn ? 'Hide' : 'Show' }} DDA Terms
                </button>
              </div> -->
              
              <!-- CATEGORY COLUMN TOGGLE (ONLY FOR DRAW DETAILS) -->
              <div v-if="btSubMenuSelectedItemId == 1 && drawDetailRowData && drawDetailRowData.length > 0" class="dda-toggle-container">
                <button 
                  class="dda-toggle-btn"
                  :class="{ 'active': showCategoryColumn }"
                  @click="toggleCategoryColumn"
                  :title="showCategoryColumn ? 'Hide Category Column' : 'Show Category Column'"
                >
                  {{ showCategoryColumn ? 'Hide' : 'Show' }} Category
                </button>
              </div>
              
              <!-- SOURCE DOCUMENT BUTTONS -->
              <div v-if="btSubMenuSelectedItemId == 1 && drawNumber && drawDetailSourceDocuments.length > 0" class="view-documents-below-tabs">
                <button 
                  class="doc-link-btn"
                  @click="openDrawDocumentViewer(selectedId, drawNumber, 'draw_detail')"
                  :title="`View all documents for Draw ${drawNumber}`"
                >
                    Source Doc
                </button>
              </div>
              
              <!-- CALCULATE TOTALS BUTTON - HIDDEN FOR NOW -->
              <div v-if="btSubMenuSelectedItemId == 1 && drawDetailRowData && drawDetailRowData.length > 0" class="view-documents-below-tabs">
                <!-- <button 
                  class="doc-link-btn calculate-totals-btn"
                  @click="computeCategoryTotals"
                  title="Use if there are not Total Rows for every category. Will use category mapping on grid."
                >
                    Calculate Totals
                </button> -->
                <button 
                  class="doc-link-btn draw-insights-btn"
                  @click="openDrawInsightsModal"
                  :disabled="isLoadingInsights"
                  title="Get AI-powered insights about this draw package"
                  style="margin-left: 10px;"
                >
                  <span v-if="isLoadingInsights">Analyzing...</span>
                  <span v-else>Draw Insights</span>
                </button>
                <button 
                  class="doc-link-btn spd-report-btn"
                  @click="generateSPDReport"
                  :disabled="isGeneratingSPDReport"
                  title="Generate professional SPD third-party review report"
                  style="margin-left: 10px;"
                >
                  <span v-if="isGeneratingSPDReport">Generating Report...</span>
                  <span v-else>Generate SPD Report</span>
                </button>
                
                <!-- CONTINGENCY CALCULATION SETTING -->
                <label 
                  class="contingency-checkbox-label"
                  style="margin-left: 15px; font-size: 13px; display: inline-flex; align-items: center; cursor: pointer;"
                  title="Check this if contingencies are already excluded from HC/SC budget (uses different calculation formula)"
                >
                  <input 
                    type="checkbox" 
                    v-model="contingenciesExcludedFromBudget"
                    @change="updateContingencySetting"
                    style="margin-right: 6px; cursor: pointer;"
                  />
                  <span style="white-space: nowrap;">Contingencies not in HC/SC budget</span>
                </label>
                
                <button 
                  class="doc-link-btn"
                  @click="autoMatchAllDDATerms"
                  :disabled="isAutoMatchingDDA"
                  title="Re-match all DDA terms for this draw"
                  style="margin-left: 10px;"
                >
                  <span v-if="isAutoMatchingDDA">Matching...</span>
                  <span v-else>Auto-Match DDA Terms</span>
                </button>
              </div>
              
              <!-- G703 TAB BUTTONS - Show always when G703 tab is active and has data -->
              <div v-if="btSubMenuSelectedItemId == 2 && g703DrawNumber && g703RowData && g703RowData.length > 0" class="view-documents-below-tabs">
                <button 
                  class="doc-link-btn"
                  @click="autoMatchAllDDATerms"
                  :disabled="isAutoMatchingDDA"
                  title="Re-match all DDA terms for G703 line items"
                >
                  <span v-if="isAutoMatchingDDA">Matching...</span>
                  <span v-else>Auto-Match DDA Terms</span>
                </button>
                <button 
                  class="doc-link-btn"
                  @click="openColumnRemapModal"
                  title="Remap G703 columns to different DocuPipe source fields"
                  style="margin-left: 10px;"
                >
                    🔧 Remap Columns
                </button>
                <!-- Source Doc button - only show if there are source documents -->
                <button 
                  v-if="g703SourceDocuments.length > 0"
                  class="doc-link-btn"
                  @click="openDrawDocumentViewer(selectedId, g703DrawNumber, 'g703')"
                  :title="`View all documents for Draw ${g703DrawNumber}`"
                  style="margin-left: 10px;"
                >
                    Source Doc
                </button>
              </div>
            </div>
            
            <!-- FAR RIGHT SIDE - EXPORT BUDGET BUTTON -->
            <div v-if="btSubMenuSelectedItemId == 1 && drawDetailRowData && drawDetailRowData.length > 0" class="export-button-container-far-right">
              <button 
                class="export-budget-btn"
                @click="exportCategorizedBudget('draw_details')"
                :title="`Export Draw ${drawNumber} Budget in Categorized Format`"
              >
                Export Budget
              </button>
            </div>
          <div v-if="btSubMenuSelectedItemId == 3 && g702DrawNumber && g702SourceDocuments.length > 0" class="view-documents-below-tabs">
            <button 
              class="doc-link-btn"
              @click="openDrawDocumentViewer(selectedId, g702DrawNumber, 'g702')"
              :title="`View all documents for Draw ${g702DrawNumber}`"
            >
                Source Doc
            </button>
          </div>

          <!-- 🔥 Companies tab now shows ONLY the LATEST data per contractor - no filter needed -->
          
          <!-- Show message when no G702 data exists yet -->
          <div v-if="btSubMenuSelectedItemId == 3 && g702DrawNumberList.length === 0" class="no-g702-message" style="padding: 15px; text-align: center; color: #718096; font-size: 14px;">
            No G702 applications found. Upload G702/G703 documents to see contractor payment data.
          </div>
            
          </div>
          
          <div v-if="btSubMenuSelectedItemId == 1">
            
            <div class="grid-container budget-tracking-grid" id="drawDetailGrid">
              <ag-grid
                  :row-height="26"
                  :row-data="drawDetailRowData"
                  :columnDefs="drawDetailColumnDefs"
                  :rowClassRules="drawTotalRowClassRules"
                  :treeData="false"
                  :key="`${drawDetailDataCtr}${viewModeDivisor}`"
                  :getRowId="params => params.data.id"
                  @grid-ready="onDrawDetailGridReady"
                  :getContextMenuItems="getContextMenuItems"
                  :enableRangeSelection="true"
                />
            </div>
          </div>
          <div v-if="btSubMenuSelectedItemId == 2">
            <div class="grid-container" id="g703Grid">
              <ag-grid
                  :row-height="30"
                  :row-data="g703RowData"
                  :columnDefs="g703DetailColumnDefs"
                  :treeData="false"
                  :getRowId="params => String(params.data.id) || `fallback_${params.node.rowIndex}`"
                  :key="`${g703DataCtr}${viewModeDivisor}`"
                  @grid-ready="onG703GridReady"
                />
            </div>
            
            <!-- Column Remap Modal -->
            <div v-if="showColumnRemapModal" class="modal-overlay" @click.self="closeColumnRemapModal">
              <div class="column-remap-modal">
                <div class="modal-header">
                  <h3>🔧 Remap G703 Columns</h3>
                  <button class="modal-close-btn" @click="closeColumnRemapModal">×</button>
                </div>
                <div class="modal-body">
                  <p class="modal-description">
                    Map grid columns to different DocuPipe source fields. This allows you to correct column mappings when the transformer assigns data to the wrong fields.
                  </p>
                  <div class="column-mapping-list">
                    <div v-for="mapping in columnMappings" :key="mapping.gridColumn" class="mapping-row">
                      <div class="grid-column-name">{{ mapping.displayName }}</div>
                      <div class="arrow">→</div>
                      <select v-model="mapping.sourceField" class="source-field-select">
                        <option value="">-- Select Source Field --</option>
                        <option v-for="field in availableSourceFields" :key="field.value" :value="field.value">
                          {{ field.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button class="modal-cancel-btn" @click="closeColumnRemapModal">Cancel</button>
                  <button class="modal-apply-btn" @click="applyColumnRemappings">Apply Mappings</button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="btSubMenuSelectedItemId == 3">
            <div class="grid-container" id="g702Grid">
              <ag-grid
                  :row-height="30"
                  :row-data="companiesTabRowData"
                  :columnDefs="g702DetailColumnDefs"
                  :treeData="false"
                  :getRowId="params => String(params.data.id) || `fallback_${params.node.rowIndex}`"
                  :key="`${g702DataCtr}${viewModeDivisor}`"
                  @grid-ready="onG702GridReady"
                />
            </div>
          </div>
          
          <!-- 🆕 LIEN WAIVER TAB -->
          <div v-if="btSubMenuSelectedItemId == 5">
            <div class="grid-container" id="lwGrid">
              <ag-grid
                  :row-height="30"
                  :row-data="lwRowData"
                  :columnDefs="lwDetailColumnDefs"
                  :treeData="false"
                  :getRowId="params => String(params.data.id) || `lw_${params.node.rowIndex}`"
                  :key="`lw_${lwDataCtr}${viewModeDivisor}`"
                  @grid-ready="onLWGridReady"
                />
            </div>
          </div>
          
          <!-- 🆕 RETAINAGE TAB -->
          <div v-if="btSubMenuSelectedItemId == 7">
            <div class="retainage-tab-container">
              <!-- Header Row -->
              <div class="retainage-header-row">
                <div class="retainage-title-left">Current Payment Applications</div>
                <div class="retainage-title-right">Retainage this Period</div>
              </div>
              
              <!-- Grid -->
              <div class="grid-container" id="retainageGrid">
                <ag-grid
                    :row-height="30"
                    :row-data="retainageRowData"
                    :columnDefs="retainageDetailColumnDefs"
                    :treeData="false"
                    :getRowId="params => String(params.data.id) || `ret_${params.node.rowIndex}`"
                    :key="`ret_${retainageDataCtr}${viewModeDivisor}`"
                    @grid-ready="onRetainageGridReady"
                  />
              </div>
            </div>
          </div>
          
          <!-- 📄 INVOICE TAB -->
          <div v-if="btSubMenuSelectedItemId == 8">
            <div class="grid-container" id="invoiceGrid">
              <ag-grid
                  :row-height="30"
                  :row-data="invoiceRowData"
                  :columnDefs="invoiceColumnDefs"
                  :pinnedBottomRowData="invoiceTotalRow"
                  :treeData="false"
                  :getRowId="params => String(params.data.id) || `fallback_${params.node.rowIndex}`"
                  :key="`${invoiceDataCtr}${viewModeDivisor}`"
                  @grid-ready="onInvoiceGridReady"
                />
            </div>
          </div>
        </div>
      </div>

      <!-- Schedule Summary Content -->
      <div v-if="subMenuSelectedItemId == 2">
        <ProjectScheduleDetails />
      </div>

      <div v-if="subMenuSelectedItemId == 3">
        <ProjectCompanyDetails />
      </div>

      <div v-if="subMenuSelectedItemId == 4" class="map-embed-container">
        <iframe
          :src="zolaLink"
          title="NYC ZOLA Map"
          style="width: 100%; height: 80vh; border: 0;"
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
          allowfullscreen>
        </iframe>
      </div>
    </div>
  </div>

  <!-- DDA Term Edit Modal -->
  <div v-if="ddaTermModalData.isOpen" class="modal-backdrop" @click="closeDDATermModal">
    <div class="contractor-modal dda-term-modal" @click.stop>
      <div class="modal-header">
        <h3>Edit DDA Term</h3>
        <button class="close-button" @click="closeDDATermModal">×</button>
      </div>
      
      <div class="modal-body">
        <div v-if="ddaTermModalData.selectedDDATerm && ddaTermModalData.selectedDDATerm.method" class="auto-match-info">
          <div class="match-badge" :class="ddaTermModalData.selectedDDATerm.method === 'ML' ? 'ml-match' : 'keyword-match'">
            {{ ddaTermModalData.selectedDDATerm.method === 'ML' ? '🤖 AI Matched' : '🔍 Keyword Matched' }}
            <span class="confidence">{{ Math.round((ddaTermModalData.selectedDDATerm.confidence || 0) * 100) }}%</span>
          </div>
        </div>
        
        <div class="form-section">
          <label class="form-label">Search DDA Terms</label>
          <div class="contractor-search-container">
            <input 
              type="text" 
              v-model="ddaTermModalData.searchTerm"
              @input="searchDDATermsFromList(ddaTermModalData.searchTerm)"
              @focus="handleDDATermInputFocus"
              @blur="handleDDATermInputBlur"
              placeholder="Type to search DDA terms..."
              class="form-input contractor-search-input"
            />
            
            <div v-if="ddaTermModalData.showDropdown && ddaTermModalData.searchResults.length > 0" class="contractor-dropdown">
              <div 
                v-for="ddaTerm in ddaTermModalData.searchResults" 
                :key="ddaTerm.id"
                @click="selectDDATerm(ddaTerm)"
                class="contractor-option"
                :class="{ selected: ddaTermModalData.selectedDDATerm?.id === ddaTerm.id }"
              >
                <div class="contractor-name">{{ ddaTerm.term }}</div>
                <div class="contractor-address" v-if="ddaTerm.description">{{ ddaTerm.description }}</div>
                <div class="contractor-address" v-if="ddaTerm.category">Category: {{ ddaTerm.category }}</div>
              </div>
            </div>
            
            <div v-if="ddaTermModalData.showDropdown && ddaTermModalData.searchResults.length === 0 && ddaTermModalData.searchTerm" class="no-results">
              No matching DDA terms found for "{{ ddaTermModalData.searchTerm }}"
            </div>
          </div>
        </div>

        <div v-if="ddaTermModalData.selectedDDATerm" class="selected-contractor-section">
          <h4>Selected DDA Term:</h4>
          <div class="selected-contractor-card">
            <div class="contractor-name">{{ ddaTermModalData.selectedDDATerm.term }}</div>
            <div class="contractor-address" v-if="ddaTermModalData.selectedDDATerm.description">
              {{ ddaTermModalData.selectedDDATerm.description }}
            </div>
            <div class="contractor-address" v-if="ddaTermModalData.selectedDDATerm.category">
              Category: {{ ddaTermModalData.selectedDDATerm.category }}
            </div>
            <button @click="clearDDATermSelection" class="clear-selection-btn">Clear Selection</button>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-secondary" @click="closeDDATermModal">Cancel</button>
        <button 
          class="btn-primary" 
          @click="saveDDATermAssignment"
          :disabled="!ddaTermModalData.selectedDDATerm"
        >
          Save DDA Term
        </button>
      </div>
    </div>
  </div>
  <!-- Category Override Modal -->
  <div v-if="categoryModalData.isOpen" class="modal-backdrop" @click="categoryModalData.isOpen = false">
    <div class="contractor-modal dda-term-modal" @click.stop>
      <div class="modal-header">
        <h3>Override Category</h3>
        <button class="close-button" @click="categoryModalData.isOpen = false">×</button>
      </div>
      
      <div class="modal-body">
        <div class="form-section">
          <label class="form-label">Current: {{ categoryModalData.currentCategory }}</label>
          <p style="margin-bottom: 15px; color: #666;">Select a category to override the automatic categorization:</p>
          
          <div class="category-options">
            <button 
              v-for="category in ['Hard Costs', 'Soft Costs', 'Acquisition', 'Financing', 'Contingencies']" 
              :key="category"
              @click="saveCategoryOverride(category)"
              class="category-option-btn"
              :class="{ selected: categoryModalData.currentCategory === category }"
            >
              {{ category }}
            </button>
          </div>
          
          <button 
            @click="saveCategoryOverride(null)"
            class="category-option-btn clear-btn"
            style="margin-top: 10px; width: 100%;"
          >
            Clear Override (Use Auto Category)
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- NEW: Redesigned Upload Modal -->
  <UploadDrawModalRedesign
    :is-visible="showNewUploadModal"
    :project-id="selectedId"
    :existing-draws="existingDraws"
    :suggested-draw-number="suggestedDrawNumber"
    @close="closeNewUploadModal"
    @uploadQueue="handleUploadQueue"
  />

  <!-- OLD: Original Upload Draw Modal (keeping for backward compatibility) -->
  <div v-if="showUploadDrawModal" class="modal-backdrop" @click="closeUploadModal">
    <div class="upload-draw-modal" @click.stop :key="uploadModalKey">
      <div class="modal-header">
        <h3>Upload Documents</h3>
        <button class="close-button" @click="closeUploadModal">×</button>
      </div>
      
      <div class="modal-body">
        <!-- Step 1: File Upload -->
        <div v-if="!uploadedDocumentId && !showOCRProgress && !showExtractionResults">
          <!-- Template Selection -->
          <div class="form-section">
            <label class="form-label">Select Template Type</label>
            <select v-model="selectedTemplate" class="form-select">
              <option value="">Choose template type...</option>
              <option 
                v-for="template in availableTemplates" 
                :key="template.id" 
                :value="template.value"
              >
                {{ template.name }}
              </option>
            </select>
          </div>

          <!-- Draw Number Selection -->
          <div class="form-section">
            <label class="form-label">Draw Number</label>
            <div class="draw-input-wrapper">
              <select 
                v-model="selectedDrawNumber" 
                class="form-select draw-number-input"
                @change="handleDrawInput"
                :disabled="useCustomDrawNumber"
              >
                <option value="" disabled>Select existing draw or create new</option>
                <!-- Existing draws -->
                <optgroup v-if="existingDraws.length > 0" label="📋 Existing Draws">
                  <option v-for="draw in existingDraws" :key="draw.id" :value="draw.draw_number">
                    Draw {{ draw.draw_number }} {{ draw.period_through ? `(${formatDate(draw.period_through)})` : '' }}
                  </option>
                </optgroup>
                <!-- New draw option -->
                <optgroup label="➕ Create New Draw">
                  <option :value="suggestedDrawNumber">
                    Draw {{ suggestedDrawNumber }} (New)
                  </option>
                </optgroup>
              </select>
              
              <!-- Custom Draw Number Toggle -->
              <div class="custom-draw-toggle">
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    v-model="useCustomDrawNumber"
                    @change="toggleCustomDrawNumber"
                  />
                  <span class="checkbox-text">Enter custom draw number</span>
                </label>
              </div>
              
              <!-- Custom Draw Number Input -->
              <input 
                v-if="useCustomDrawNumber"
                type="text"
                v-model="selectedDrawNumber"
                class="form-control custom-draw-input"
                placeholder="Enter draw number (e.g., 22, APP-5)"
                @input="handleCustomDrawInput"
              />
              
              <small v-if="selectedDrawNumber && existingDraws.some(d => d.draw_number == selectedDrawNumber)" class="draw-hint existing-draw">
                ✅ Adding document to existing Draw {{ selectedDrawNumber }}
              </small>
              <small v-else-if="selectedDrawNumber && !existingDraws.some(d => d.draw_number == selectedDrawNumber)" class="draw-hint new-draw">
                ➕ Creating new Draw {{ selectedDrawNumber }}
              </small>
            </div>
          </div>

          <div class="upload-section">
            <div class="upload-instructions">
              <p>Upload a draw tracker document (Excel/PDF) to create a new draw template</p>
              <small>Supported formats: .xlsx, .xls, .pdf</small>
            </div>
            
            <!-- Append to current draw (Excel only) -->
            <div class="form-section" v-if="isExcelSelected && isExistingSelectedDraw">
              <label class="checkbox-label">
                <input type="checkbox" v-model="appendToCurrent" />
                <span class="checkbox-text">Append rows to current draw instead of overwrite</span>
              </label>
            </div>
            
            <div class="file-upload-area" 
                 @dragover.prevent 
                 @drop.prevent="handleFileDrop"
                 :class="{ 'drag-over': isDragOver }"
                 @dragenter.prevent="isDragOver = true"
                 @dragleave.prevent="isDragOver = false">
              <div v-if="!selectedFile">
                <div class="upload-placeholder">
                  <span class="upload-icon-large">📄</span>
                  <p>Drag and drop files here or <button class="browse-button" @click="triggerFileInput">browse</button></p>
                </div>
              </div>
              <div v-else class="file-selected">
                <div class="file-info">
                  <span class="file-icon">📄</span>
                  <span class="file-name">{{ selectedFile.name }}</span>
                  <button class="remove-file" @click="removeFile">×</button>
                </div>
              </div>
              <input 
                type="file" 
                ref="fileInput" 
                @change="handleFileSelect" 
                accept=".xlsx,.xls,.pdf" 
                style="display: none"
              />
            </div>
            
            <!-- Upload Progress -->
            <div v-if="isUploading" class="progress-section">
              <div class="progress-bar-container">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                </div>
                <span class="progress-text">{{ uploadProgress }}%</span>
              </div>
              <p class="progress-status">{{ uploadStatus }}</p>
            </div>
          </div>
          
          <div class="modal-actions">
            <button class="cancel-button" @click="closeUploadModal">Cancel</button>
            <button 
              class="upload-button" 
              @click="uploadDrawDocument" 
              :disabled="!selectedFile || !selectedTemplate || !selectedDrawNumber || isUploading"
              v-if="!uploadedDocumentId"
            >
              {{ isUploading ? 'Uploading...' : 'Upload Document' }}
            </button>
            
            <!-- Show these buttons when processing is complete -->
            <div v-if="uploadedDocumentId && !isUploading" class="result-actions">
              <button class="view-results-button" @click="viewDocumentResults">
                📄 View Source & Extraction
              </button>
              <button class="document-list-button" @click="viewDocumentList">
                📋 View All Documents
              </button>
              <button class="close-success-button" @click="closeUploadModal">
                ✓ Done
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: OCR Processing -->
        <div v-if="showOCRProgress && !showExtractionResults" class="ocr-processing-section">
          <div class="processing-header">
            <span class="processing-icon">🔍</span>
            <h4>Processing Document with OCR</h4>
          </div>
          
          <div class="progress-section">
            <div class="progress-bar-container">
              <div class="progress-bar ocr-progress">
                <div class="progress-fill ocr-fill" :style="{ width: ocrProgress + '%' }"></div>
              </div>
              <span class="progress-text">{{ ocrProgress }}%</span>
            </div>
            <p class="progress-status">{{ ocrStatus }}</p>
          </div>
          
          <div class="ocr-steps">
            <div class="step" :class="{ active: ocrProgress >= 20, completed: ocrProgress > 20 }">
              <span class="step-number">1</span>
              <span class="step-text">Document Analysis</span>
            </div>
            <div class="step" :class="{ active: ocrProgress >= 40, completed: ocrProgress > 40 }">
              <span class="step-number">2</span>
              <span class="step-text">Text Extraction</span>
            </div>
            <div class="step" :class="{ active: ocrProgress >= 60, completed: ocrProgress > 60 }">
              <span class="step-number">3</span>
              <span class="step-text">Table Detection</span>
            </div>
            <div class="step" :class="{ active: ocrProgress >= 80, completed: ocrProgress > 80 }">
              <span class="step-number">4</span>
              <span class="step-text">Category Mapping</span>
            </div>
            <div class="step" :class="{ active: ocrProgress >= 100, completed: ocrProgress === 100 }">
              <span class="step-number">5</span>
              <span class="step-text">Complete</span>
            </div>
          </div>
        </div>

        <!-- Step 3: Extraction Results & Error Correction -->
        <div v-if="showExtractionResults" class="extraction-results-section">
          <div class="results-header">
            <span class="results-icon">✨</span>
            <h4>OCR Extraction Complete</h4>
            <p>Review and correct the extracted data below</p>
          </div>
          
          <div class="source-document-preview">
            <h5>Source Document</h5>
            <div class="document-viewer">
              <!-- This would show the document with BBox overlays -->
              <img v-if="extractionResults?.document_preview" :src="extractionResults.document_preview" alt="Source document" />
              <div v-else class="placeholder-document">
                <span>📄</span>
                <p>Document preview will appear here</p>
              </div>
            </div>
          </div>
          
          <div class="extraction-summary">
            <h5>Extracted Values</h5>
            <div class="extracted-fields">
              <div v-for="field in extractionResults?.extracted_fields || []" :key="field.id" class="field-item">
                <label>{{ field.label }}</label>
                <input 
                  v-model="field.value" 
                  type="text" 
                  class="field-input"
                  :class="{ 'low-confidence': field.confidence < 0.8 }"
                />
                <span class="confidence-score" :class="{ 'low': field.confidence < 0.8 }">
                  {{ Math.round(field.confidence * 100) }}%
                </span>
              </div>
            </div>
          </div>
          
          <div class="modal-actions">
            <button class="cancel-button" @click="closeUploadModal">Cancel</button>
            <button class="corrections-button" @click="saveCorrections">
              Save Corrections & Continue
            </button>
            <button class="create-template-button" @click="createDrawTemplateFromDocument">
              Create Template
            </button>
          </div>
        </div>
      </div>
      
      <!-- Documents link at bottom of modal -->
      <div class="modal-footer-link">
        <router-link :to="`/projects/${selectedId}/documents`" class="documents-link" @click="closeUploadModal">
          📄 Looking for uploaded documents? View all project documents →
        </router-link>
      </div>
    </div>
  </div>

  <!-- G703 Template Configuration Modal -->
  <G703TemplateModal 
    :visible="showG703TemplateModal"
    :project-id="selectedId"
    :project-name="projectName"
    :document-id="g703TemplateId"
    :sample-file-name="g703SampleFileName"
    @close="closeG703TemplateModal"
    @template-saved="handleG703TemplateSaved"
  />

  <!-- Contractor Edit Modal -->
  <div v-if="contractorModalData.isOpen" class="modal-backdrop" @click="closeContractorModal">
    <div class="contractor-modal" @click.stop>
      <div class="modal-header">
        <h3>Edit Contractor</h3>
        <button class="close-button" @click="closeContractorModal">×</button>
      </div>
      
      <div class="modal-body">
        <div class="contractor-form">
          <div class="form-section">
            <label class="form-label">
              Contractor for: <strong>{{ contractorModalData.rowData?.description || 'Selected Item' }}</strong>
            </label>
            <div class="current-value" v-if="contractorModalData.currentValue">
              Current: {{ contractorModalData.currentValue }}
            </div>
          </div>
          
          <div class="form-section">
            <label class="form-label">Search Contractors</label>
            <div class="contractor-autocomplete">
              <input 
                type="text" 
                v-model="contractorModalData.searchTerm"
                :placeholder="contractorModalData.selectedContractor ? contractorModalData.selectedContractor.name : 'Type to search contractors...'"
                @input="searchContractors($event.target.value)"
                @focus="handleContractorInputFocus"
                @blur="handleContractorInputBlur"
                class="contractor-search-input"
                :class="{ 'has-selection': contractorModalData.selectedContractor }"
              />
              
              <div v-if="contractorModalData.showDropdown && contractorModalData.searchResults" class="contractor-dropdown">
                <div v-if="contractorModalData.searchResults.length === 0" class="no-results">
                  No matching contractors found
                </div>
                <div 
                  v-for="contractor in contractorModalData.searchResults.slice(0, 10)" 
                  :key="contractor.id"
                  class="contractor-option"
                  @mousedown="selectContractor(contractor)"
                  :class="{ 'selected': contractorModalData.selectedContractor?.id === contractor.id }"
                >
                  <div class="contractor-name">{{ contractor.name }}</div>
                  <div v-if="contractor.address_line_1" class="contractor-address">{{ contractor.address_line_1 }}</div>
                </div>
              </div>
              
              <div v-if="contractorModalData.selectedContractor" class="selected-contractor-display">
                <span class="selected-contractor-name">{{ contractorModalData.selectedContractor.name }}</span>
                <button @click="clearContractorSelection" class="clear-selection" type="button">×</button>
              </div>
            </div>
          </div>
          
          <div class="form-hint">
            <small>Type to search existing contractors, or enter a new name to create one.</small>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-secondary" @click="closeContractorModal">Cancel</button>
        <button class="btn-primary" @click="saveContractorSelection">Save Contractor</button>
      </div>
    </div>
  </div>

  <!-- Edit Project Modal -->
  <div v-if="showEditProjectModal" class="modal-backdrop" @click="closeEditProjectModal">
    <div class="edit-project-modal" @click.stop>
      <div class="modal-header">
        <h3>Edit Project Details</h3>
        <button class="close-button" @click="closeEditProjectModal">×</button>
      </div>
      
      <div class="modal-body">
        <div class="form-row">
          <div class="form-group">
            <label>Project Name</label>
            <input v-model="editProjectData.name" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label>Entity Name</label>
            <input v-model="editProjectData.entity_name" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label>Construction Status</label>
            <select v-model="editProjectData.status" class="form-input">
              <option value="">Select Status</option>
              <option value="A">Active</option>
              <option value="C">Complete</option>
            </select>
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label>Address Line 1</label>
            <input v-model="editProjectData.address_line_1" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label>Address Line 2</label>
            <input v-model="editProjectData.address_line_2" type="text" class="form-input" />
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label>City</label>
            <input v-model="editProjectData.city" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label>State</label>
            <input v-model="editProjectData.state" type="text" class="form-input" />
          </div>
          <div class="form-group">
            <label>Zip Code</label>
            <input v-model="editProjectData.zipcode" type="text" class="form-input" />
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label>Number of Units</label>
            <input v-model="editProjectData.total_num_units" type="number" class="form-input" />
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group full-width">
            <label>Notes</label>
            <textarea v-model="editProjectData.notes" class="form-textarea" rows="3"></textarea>
          </div>
        </div>
        
        <!-- Building Specifications Section -->
        <div class="form-section">
          <h4 class="section-title">Building Specifications</h4>
          <div class="form-row">
            <div class="form-group">
              <label>Number of Floors</label>
              <input v-model="editProjectData.num_floors" type="number" class="form-input" />
            </div>
            <div class="form-group">
              <label>Total Height (ft)</label>
              <input v-model="editProjectData.total_height" type="number" class="form-input" />
            </div>
          </div>
        </div>
        
        <!-- Project Dates Section -->
        <div class="form-section">
          <h4 class="section-title">Project Dates</h4>
          <div class="form-row">
            <div class="form-group">
              <label>Planned Start Date</label>
              <input v-model="editProjectData.planned_start_date" type="date" class="form-input" @change="calculateProjectLength" />
            </div>
            <div class="form-group">
              <label>Planned End Date</label>
              <input v-model="editProjectData.planned_end_date" type="date" class="form-input" @change="calculateProjectLength" />
            </div>
          </div>
          
          <div class="form-row" v-if="computedProjectLengthMonths">
            <div class="info-display" style="padding: 12px; background: #f5f5f5; border-radius: 4px; margin-top: 8px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-weight: 600; color: #555;">Project Length (computed from dates):</span>
                <span style="font-size: 16px; font-weight: 700; color: #1e3a8a;">{{ computedProjectLengthMonths }} months</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Construction Details Section -->
        <div class="form-section">
          <h4 class="section-title">Construction Details</h4>
          <div class="form-row">
            <div class="form-group">
              <label>Construction Contract Type</label>
              <select v-model="editProjectData.construction_contract_type_id" class="form-input">
                <option value="">Select Contract Type</option>
                <option 
                  v-for="contractType in editModalDropdownData.contractTypes" 
                  :key="contractType.id" 
                  :value="contractType.id"
                >
                  {{ contractType.code }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Sustainability Type</label>
              <select v-model="editProjectData.sustainability_type_id" class="form-input">
                <option value="">Select Sustainability Type</option>
                <option 
                  v-for="sustainabilityType in editModalDropdownData.sustainabilityTypes" 
                  :key="sustainabilityType.id" 
                  :value="sustainabilityType.id"
                >
                  {{ sustainabilityType.code }}
                </option>
              </select>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>Finish Level</label>
              <select v-model="editProjectData.finish_level_id" class="form-input">
                <option value="">Select Finish Level</option>
                <option 
                  v-for="finishLevel in editModalDropdownData.finishLevels" 
                  :key="finishLevel.id" 
                  :value="finishLevel.id"
                >
                  {{ finishLevel.code }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Historic/Landmarks Component</label>
              <select v-model="editProjectData.is_historic_has_landmark" class="form-input">
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div class="form-group">
              <label>Modular Build</label>
              <select v-model="editProjectData.is_modular" class="form-input">
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group full-width">
              <label>Construction Types</label>
              <div class="checkbox-grid">
                <label 
                  v-for="constructionType in editModalDropdownData.constructionTypes" 
                  :key="constructionType.id"
                  class="checkbox-label checkbox-inline"
                >
                  <input 
                    v-model="editProjectData.construction_types" 
                    :value="constructionType.code" 
                    type="checkbox" 
                    class="form-checkbox" 
                  />
                  {{ constructionType.code }}
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Amenities Section -->
        <div class="form-section">
          <h4 class="section-title">Amenities</h4>
          <div class="form-row">
            <div class="form-group full-width">
              <label>Select Amenities</label>
              <div class="checkbox-grid">
                <label 
                  v-for="amenity in editModalDropdownData.amenityTypes" 
                  :key="amenity.id"
                  class="checkbox-label checkbox-inline"
                >
                  <input 
                    v-model="editProjectData.amenities" 
                    :value="amenity.code" 
                    type="checkbox" 
                    class="form-checkbox" 
                  />
                  {{ amenity.code }}
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Project Characteristics Section -->
        <div class="form-section">
          <h4 class="section-title">Project Characteristics</h4>
          <div class="form-row">
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input v-model="editProjectData.is_historic_has_landmark" type="checkbox" class="form-checkbox" />
                Historic/Landmark Property
              </label>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input v-model="editProjectData.is_modular" type="checkbox" class="form-checkbox" />
                Modular Construction
              </label>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input v-model="editProjectData.is_released" type="checkbox" class="form-checkbox" />
                Released Project
              </label>
            </div>
          </div>
        </div>
        
        <!-- Building Use Types Section - Hierarchical -->
        <div class="form-section">
          <h4 class="section-title">Building Use Types & Square Footage</h4>
          <div class="form-row">
            <div class="form-group full-width">
              <table class="project-detail-table">
                <tr class="project-detail-table-headers">
                  <td class="project-detail-table-header-desc">
                    <div>New Use Category Type<span>(Select All That Apply)</span></div>
                  </td>
                  <td class="project-detail-table-sub-header square-feet-per-use-input"></td>
                  <td class="project-detail-table-sub-header num-of-units-input"></td>
                </tr>
                
                <!-- Main Parent Categories -->
                <template v-for="newUseCategory in updatedUseCategories" :key="newUseCategory.id">
                  <tr>
                    <td class="new-use-category-checkbox-container category-checkbox-header">
                      <label class="custom-checkbox">
                        <span class="custom-checkbox-label">{{ newUseCategory.code }}</span>
                        <input 
                          type="checkbox" 
                          style="display: none;" 
                          :id="newUseCategory.id" 
                          :value="newUseCategory.id"
                          v-model="hierarchicalBuildingUseData.parentCategories[newUseCategory.id]" 
                          @click="toggleCheck(newUseCategory)"
                        />
                        <span class="checkmark"></span>
                      </label>
                      <div v-if="newUseCategory.isChecked && newUseCategory.subCategories && newUseCategory.subCategories.length > 0">
                        (Select All That Apply)
                      </div>
                    </td>
                    <td>
                      <div class="square-feet-per-use-header" v-if="newUseCategory.isChecked && newUseCategory.subCategories && newUseCategory.subCategories.length > 0">
                        Gross Square Feet
                      </div>
                    </td>
                    <td>
                      <div class="num-of-units-header" v-if="newUseCategory.isChecked && newUseCategory.subCategories && newUseCategory.subCategories.length > 0">
                        # Units
                      </div>
                    </td>
                  </tr>
                  
                  <!-- Sub-Categories (when parent is checked) -->
                  <template v-if="newUseCategory.isChecked">
                    <template v-for="subCategory in newUseCategory.subCategories" :key="subCategory.id">
                      <tr>
                        <td class="sub-category-indent">
                          <label class="custom-checkbox">
                            <span class="custom-checkbox-label">{{ subCategory.code }}</span>
                            <input 
                              type="checkbox" 
                              style="display: none;" 
                              :id="subCategory.id" 
                              :value="subCategory.id"
                              v-model="hierarchicalBuildingUseData.subCategories[subCategory.id]" 
                              @click="toggleCheck(subCategory)"
                            />
                            <span class="checkmark"></span>
                          </label>
                        </td>
                        <td v-if="subCategory.isChecked">
                          <div class="checkbox-text-input">
                            <input 
                              type="text"
                              @input="hierarchicalBuildingUseData.subCategoriesGSF[subCategory.id] = $event.target.value.replace(/\D/g, '')"
                              v-model="hierarchicalBuildingUseData.subCategoriesGSF[subCategory.id]"
                              placeholder="ex. 5000" 
                            />
                          </div>
                        </td>
                        <td v-if="subCategory.isChecked">
                          <div class="checkbox-text-input">
                            <input 
                              type="text"
                              @input="hierarchicalBuildingUseData.subCategoriesUnits[subCategory.id] = $event.target.value.replace(/\D/g, '')"
                              v-model="hierarchicalBuildingUseData.subCategoriesUnits[subCategory.id]"
                              placeholder="ex. 10" 
                            />
                          </div>
                        </td>
                      </tr>
                      
                      <!-- Special: Affordable Component for Rental Apartment and Condominium -->
                      <template v-if="(subCategory.isChecked && subCategory.code == BUILDING_USE_TYPES_RESIDENTIAL.RENTAL_APARTMENT) || (subCategory.isChecked && subCategory.code == BUILDING_USE_TYPES_RESIDENTIAL.CONDOMINIUM)">
                        <tr>
                          <td class="brand-type-container">Affordable Component</td>
                          <td>
                            <div class="checkbox-text-input">
                              <input 
                                type="text"
                                @input="hierarchicalBuildingUseData.affordableGSF[subCategory.id] = $event.target.value.replace(/\D/g, '')"
                                v-model="hierarchicalBuildingUseData.affordableGSF[subCategory.id]"
                                placeholder="ex. 5000" 
                              />
                            </div>
                          </td>
                          <td>
                            <div class="checkbox-text-input">
                              <input 
                                type="text"
                                @input="hierarchicalBuildingUseData.affordableUnits[subCategory.id] = $event.target.value.replace(/\D/g, '')"
                                v-model="hierarchicalBuildingUseData.affordableUnits[subCategory.id]"
                                placeholder="ex. 10" 
                              />
                            </div>
                          </td>
                        </tr>
                      </template>
                      
                      <!-- Special: Brand Name for Hospitality and Co-Living -->
                      <template v-if="(subCategory.isChecked && newUseCategory.code == ASSET_CATEGORY_TYPES.HOSPITALITY) || (subCategory.isChecked && subCategory.code == BUILDING_USE_TYPES_RESIDENTIAL.CO_LIVING)">
                        <tr>
                          <td class="brand-type-container">Brand Name/Flag</td>
                          <td colspan="2">
                            <div class="checkbox-text-input">
                              <input 
                                type="text"
                                v-model="hierarchicalBuildingUseData.brandTypes[subCategory.id]"
                                placeholder="ex. Four Seasons Residences" 
                              />
                            </div>
                          </td>
                        </tr>
                      </template>
                      
                      <!-- Special: Branded Residences for Condominium -->
                      <template v-if="(subCategory.isChecked && subCategory.code == BUILDING_USE_TYPES_RESIDENTIAL.CONDOMINIUM)">
                        <tr>
                          <td class="brand-type-container">Branded Residences</td>
                          <td colspan="2">
                            <div class="checkbox-text-input">
                              <input 
                                type="text"
                                v-model="hierarchicalBuildingUseData.brandTypes['branded_' + subCategory.id]"
                                placeholder="ex. Four Seasons Residences" 
                              />
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </template>
                </template>
                
                <!-- Totals Row -->
                <tr v-if="totalBuildingGSF > 0 || totalBuildingUnits > 0" class="totals-row">
                  <td><strong>Subtotals:</strong></td>
                  <td><strong>{{ formatNumber(totalBuildingGSF) }} GSF</strong></td>
                  <td><strong>{{ formatNumber(totalBuildingUnits) }} Units</strong></td>
                </tr>
                
                <!-- GSF Comparison/Warning -->
                <!-- Building Total GSF comparison removed - GSF is now calculated from Building Use selections -->
              </table>
            </div>
          </div>
        </div>
        
        <!-- Parking Types Section - Hierarchical -->
        <div class="form-section">
          <h4 class="section-title">Parking Types</h4>
          <div class="form-row">
            <div class="form-group full-width">
              <table class="project-detail-table">
                <tr class="project-detail-table-headers">
                  <td class="project-detail-table-header-desc">
                    <div>Parking Type<span>(Select All That Apply)</span></div>
                  </td>
                  <td class="project-detail-table-sub-header square-feet-per-use-input">Gross Square Feet</td>
                  <td class="project-detail-table-sub-header num-of-units-input"># Spaces</td>
                </tr>
                
                <tr v-for="parkingType in updatedParkingTypes" :key="parkingType.id">
                  <td class="parking-type-checkbox-container">
                    <label class="custom-checkbox">
                      <span class="custom-checkbox-label">{{ parkingType.code }}</span>
                      <input 
                        type="checkbox" 
                        style="display: none;" 
                        :id="parkingType.id" 
                        :value="parkingType.id"
                        v-model="hierarchicalParkingData.parkingTypes[parkingType.id]" 
                        @click="toggleCheck(parkingType, true)"
                      />
                      <span class="checkmark"></span>
                    </label>
                  </td>
                  <td>
                    <div v-if="parkingType.isChecked && parkingType.code != PARKING_TYPES.AUTOMATED" class="checkbox-text-input">
                      <input 
                        type="text"
                        @input="hierarchicalParkingData.parkingGSF[parkingType.id] = $event.target.value.replace(/\D/g, '')"
                        v-model="hierarchicalParkingData.parkingGSF[parkingType.id]"
                        placeholder="ex. 5000" 
                      />
                    </div>
                  </td>
                  <td>
                    <div v-if="parkingType.isChecked && parkingType.code != PARKING_TYPES.AUTOMATED" class="checkbox-text-input">
                      <input 
                        type="text"
                        @input="hierarchicalParkingData.parkingSpaces[parkingType.id] = $event.target.value.replace(/\D/g, '')"
                        v-model="hierarchicalParkingData.parkingSpaces[parkingType.id]"
                        placeholder="ex. 10" 
                      />
                    </div>
                  </td>
                </tr>
                
                <!-- Parking Totals Row -->
                <tr v-if="totalParkingGSF > 0 || totalParkingSpaces > 0" class="totals-row">
                  <td><strong>Subtotals:</strong></td>
                  <td><strong>{{ formatNumber(totalParkingGSF) }} GSF</strong></td>
                  <td><strong>{{ formatNumber(totalParkingSpaces) }} Spaces</strong></td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-secondary" @click="closeEditProjectModal">Cancel</button>
        <button class="btn-primary" @click="saveProjectDetails">Save Changes</button>
      </div>
    </div>
  </div>

  <!-- Image Lightbox Modal -->
  <div v-if="showImageLightbox" class="lightbox-backdrop" @click="closeImageLightbox">
    <div class="lightbox-container" @click.stop>
      <button class="lightbox-close" @click="closeImageLightbox">×</button>
      
      <!-- Lightbox Navigation -->
      <button 
        v-if="displayImages.length > 1"
        @click="previousImage" 
        class="lightbox-arrow lightbox-arrow-left"
        :disabled="currentImageIndex === 0"
      >
        ‹
      </button>
      
      <!-- Lightbox Image -->
      <div class="lightbox-image-container">
        <img 
          :src="displayImages[currentImageIndex]?.url" 
          :alt="displayImages[currentImageIndex]?.name"
          class="lightbox-image"
        >
      </div>
      
      <!-- Lightbox Navigation -->
      <button 
        v-if="displayImages.length > 1"
        @click="nextImage" 
        class="lightbox-arrow lightbox-arrow-right"
        :disabled="currentImageIndex === displayImages.length - 1"
      >
        ›
      </button>
      
      <!-- Lightbox Info -->
      <div v-if="displayImages.length > 1" class="lightbox-info">
        <div class="lightbox-counter">
          {{ currentImageIndex + 1 }} of {{ displayImages.length }}
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Project Images Modal -->
  <div v-if="showEditImagesModal" class="modal-backdrop" @click="closeEditImagesModal">
    <div class="edit-images-modal" @click.stop>
      <div class="modal-header">
        <h3>Manage Project Images</h3>
        <button class="close-button" @click="closeEditImagesModal">×</button>
      </div>
      
      <div class="modal-body">
        <!-- Upload Section -->
        <div class="upload-section">
          <label class="upload-button">
            <input 
              ref="imageUploadRef"
              type="file" 
              multiple 
              accept="image/*" 
              @change="handleImageUpload"
              style="display: none;"
            />
            📷 Add Images
          </label>
          <p class="upload-hint">Click to select multiple images for this project</p>
        </div>
        
        <!-- New Images Preview -->
        <div v-if="newImageFiles.length > 0" class="images-section">
          <h4>New Images to Upload</h4>
          <div class="images-grid">
            <div 
              v-for="image in newImageFiles" 
              :key="image.id" 
              class="image-item"
              :class="{ 'primary': image.isPrimary }"
            >
              <div class="image-preview">
                <img :src="image.url" :alt="image.name" />
                <div class="image-overlay">
                  <button 
                    @click="setPrimaryImage(image.id, true)"
                    class="btn-primary-image"
                    :class="{ 'active': image.isPrimary }"
                  >
                    {{ image.isPrimary ? '★' : '☆' }} Primary
                  </button>
                  <button @click="removeImage(image.id)" class="btn-remove">×</button>
                </div>
              </div>
              <div class="image-info">
                <input 
                  v-model="image.name" 
                  type="text" 
                  class="image-name-input"
                  placeholder="Image description"
                />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Existing Images -->
        <div v-if="projectImages.length > 0" class="images-section">
          <h4>Current Project Images</h4>
          <div class="images-grid">
            <div 
              v-for="image in projectImages" 
              :key="image.id" 
              class="image-item"
              :class="{ 'primary': image.isPrimary }"
            >
              <div class="image-preview">
                <img :src="image.url" :alt="image.name" />
                <div class="image-overlay">
                  <button 
                    @click="setPrimaryImage(image.id, false)"
                    class="btn-primary-image"
                    :class="{ 'active': image.isPrimary }"
                  >
                    {{ image.isPrimary ? '★' : '☆' }} Primary
                  </button>
                  <button @click="removeExistingImage(image.id)" class="btn-remove">×</button>
                </div>
              </div>
              <div class="image-info">
                <input 
                  v-model="image.name" 
                  type="text" 
                  class="image-name-input"
                  placeholder="Image description"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="projectImages.length === 0 && newImageFiles.length === 0" class="no-images">
          <p>No images uploaded yet. Click "Add Images" to get started.</p>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-secondary" @click="closeEditImagesModal">Cancel</button>
        <button class="btn-primary" @click="saveProjectImages">Save Images</button>
      </div>
    </div>
  </div>

  <!-- Document Viewer Modal -->
  <DocumentViewerModal
    :visible="documentViewerVisible"
    :documents="documentViewerDocuments"
    :title="documentViewerTitle"
    :initial-index="documentViewerInitialIndex"
    @close="documentViewerVisible = false"
  />

  <!-- 📊 SPD REPORT MODAL -->
  <div v-if="showSPDReportModal" class="modal-backdrop" @click="closeSPDReportModal">
    <div class="contractor-modal spd-report-modal" @click.stop>
      <div class="modal-header">
        <h3>SPD Report - Draw #{{ spdReportData?.report_metadata?.draw_number }}</h3>
        <button class="close-button" @click="closeSPDReportModal">×</button>
      </div>
      
      <div class="modal-body">
        <div v-if="spdReportData" class="report-content">
          <!-- Project Info -->
          <div class="report-section">
            <div class="project-info">
              <div class="info-row">
                <strong>Project:</strong> {{ spdReportData.report_metadata.project_name }}
              </div>
              <div class="info-row">
                <strong>Address:</strong> {{ spdReportData.report_metadata.project_address }}
              </div>
              <div class="info-row">
                <strong>Draw Number:</strong> {{ spdReportData.report_metadata.draw_number }}
              </div>
              <div class="info-row">
                <strong>Period Through:</strong> {{ spdReportData.report_metadata.period_through }}
              </div>
              <div class="info-row">
                <strong>Report Date:</strong> {{ spdReportData.report_metadata.report_date }}
              </div>
            </div>
          </div>

          <!-- AI-Generated Narrative -->
          <div class="report-section">
            <h3>Executive Summary</h3>
            <div class="narrative-content" v-html="formatNarrative(spdReportData.narrative)"></div>
          </div>

          <!-- Budget Summary Table -->
          <div class="report-section">
            <h3>Budget Summary</h3>
            <div class="budget-summary">
              <div class="summary-card">
                <div class="card-label">Grand Total Budget</div>
                <div class="card-value">${{ (spdReportData.budget_table.totals.grand_total.current_budget || 0).toLocaleString() }}</div>
                <div class="card-subtext">{{ spdReportData.budget_table.totals.grand_total.percent_complete || 0 }}% Complete</div>
              </div>
              <div class="summary-card">
                <div class="card-label">Hard Costs</div>
                <div class="card-value">${{ (spdReportData.budget_table.totals.hard_costs.current_budget || 0).toLocaleString() }}</div>
                <div class="card-subtext">{{ spdReportData.budget_table.totals.hard_costs.percent_complete || 0 }}% Complete</div>
              </div>
              <div class="summary-card">
                <div class="card-label">Soft Costs</div>
                <div class="card-value">${{ (spdReportData.budget_table.totals.soft_costs.current_budget || 0).toLocaleString() }}</div>
                <div class="card-subtext">{{ spdReportData.budget_table.totals.soft_costs.percent_complete || 0 }}% Complete</div>
              </div>
            </div>
          </div>

          <!-- Payment Application Summary -->
          <div class="report-section">
            <h3>Payment Application Summary</h3>
            <div class="payment-summary">
              <div class="summary-row">
                <span class="label">Contractor:</span>
                <span class="value">{{ spdReportData.payment_application.g702.contractor_name || 'N/A' }}</span>
              </div>
              <div class="summary-row">
                <span class="label">Current Payment Due:</span>
                <span class="value">${{ (spdReportData.payment_application.g702.current_payment_due || 0).toLocaleString() }}</span>
              </div>
              <div class="summary-row">
                <span class="label">Total Completed & Stored:</span>
                <span class="value">${{ (spdReportData.payment_application.g702.total_completed_stored || 0).toLocaleString() }}</span>
              </div>
              <div class="summary-row">
                <span class="label">Total Retainage:</span>
                <span class="value">${{ (spdReportData.payment_application.g702.total_retainage || 0).toLocaleString() }}</span>
              </div>
              <div class="summary-row">
                <span class="label">Percent Complete:</span>
                <span class="value">{{ spdReportData.payment_application.g702.percent_complete || 0 }}%</span>
              </div>
            </div>
          </div>

          <!-- Compliance Summary -->
          <div class="report-section">
            <h3>Compliance Summary</h3>
            <div class="compliance-summary">
              <div class="compliance-item">
                <span class="icon">📝</span>
                <span class="text">{{ spdReportData.payment_application.lien_waivers.count }} Lien Waivers</span>
              </div>
              <div class="compliance-item">
                <span class="icon">🧾</span>
                <span class="text">{{ spdReportData.payment_application.invoices.count }} Invoices (${{ (spdReportData.payment_application.invoices.total_amount || 0).toLocaleString() }})</span>
              </div>
              <div class="compliance-item">
                <span class="icon">📄</span>
                <span class="text">{{ spdReportData.payment_application.g703_summary.total_line_items || 0 }} G703 Line Items</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="secondary-btn" @click="closeSPDReportModal">Close</button>
        <button class="primary-btn" @click="copySPDReportToClipboard">📋 Copy to Clipboard</button>
        <button class="primary-btn" @click="exportSPDReportToWord" style="margin-left: 10px;">📄 Export to Word</button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint no-undef: 0 */
import { computed, onMounted, onBeforeUnmount, reactive, ref, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from "@/stores/auth_store";
import { useErrorStore } from "@/stores/error_store";
import { useDropdownStore } from "@/stores/dropdown_store";
import { useDidaiStore } from "@/stores/didiai_store";
import { cleanupHierarchyNames } from "@/data/data";
import { axiosQuery, currencyFormat, kMillionsFormat, kMillionsWithDecimalsFormat, numberFormat, percentFormat, standardizeLabelsLength, median, getImageUrl } from "@/assets/js/utils";
import { generateSPDReportDocx } from "@/utils/spdReportDocxGenerator";
import { datadogRum } from '@datadog/browser-rum';

// 🔥 HELPER: Custom currency formatter that shows "-" for placeholder rows
function formatCurrencyForPlaceholderRows(params) {
  if (!params || !params.data) return '';
  
  // Check if this is a placeholder row by description
  const description = params.data.description || '';
  if (description.match(/^\(Row \d+\)$/)) {
    return '-'; // Show dash for placeholder rows
  }
  
  // Normal currency formatting for real rows
  const value = params.value || 0;
  return currencyFormat(value, 0);
}

// 💰 HELPER: Format currency without unnecessary decimals (e.g., $1,234 instead of $1,234.00)
function formatCompactCurrency(value) {
  if (value === 0) return '$0';
  if (!value) return '$0';
  
  const num = parseFloat(value);
  // If it's a whole number, don't show decimals
  if (num % 1 === 0) {
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  // Otherwise show 2 decimals
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

import AgGrid from "../../components/AgGrid.vue";
import ExpandCollapse from "../helpers/ExpandCollapse.vue";
import DocumentViewerModal from "../modals/DocumentViewerModal.vue";
import { Bar, Doughnut, Line } from "vue-chartjs";
import {
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  CategoryScale,
  LinearScale,
  Colors,
  Filler,
  Legend,
  Title,
  Tooltip,
  Chart as ChartJS,
} from "chart.js";
import { doughnutMiddleTextPlugin, standardDoughnutChartJson, standardStackedBarChartJson } from "@/assets/js/chart_config";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AgChartsVue } from "ag-charts-vue3";
import ProjectScheduleDetails from './ProjectScheduleDetails.vue';
import ProjectCompanyDetails from './ProjectCompanyDetails.vue';
import G703TemplateModal from '../G703TemplateModal.vue';
import UploadDrawModalRedesign from '../UploadDrawModalRedesign.vue';
import templateQueueManager from '../../utils/TemplateQueueManager';
import { 
  CODES, 
  CODEABLE_TYPES, 
  BUILDING_CLASIFICATION, 
  ASSET_CATEGORY_TYPES,
  BUILDING_USE_TYPES_RESIDENTIAL,
  PARKING_TYPES 
} from "@/types/Constants";
import { loadGoogleMapsAPI } from '@/main.js';
import { VIEW_MODES_TGU } from "@/types/Constants";
import axios from 'axios';

// General Settings
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
  doughnutMiddleTextPlugin(),
  ChartDataLabels
);

// TODO: Related to the toggle view in original format
// let isOriginalFormat = ref(true);

// function toggleActive() {
//   isOriginalFormat.value = !isOriginalFormat.value
// }

// Menu Settings
const isMenuVisible = ref(false);
const sidebarSelectedItemId = ref(1);
const subMenuSelectedItemId = ref(1);
const btSubMenuSelectedItemId = ref(1);

// DDA terms for chart categorization
const ddaTerms = ref([]);

// Load DDA terms for chart categorization
async function loadDDATermsForCharts() {
  try {
    console.log('📥 Loading DDA terms for chart categorization...');
    const response = await axios.get(`${apiBase}dda_terms`, { headers: authToken.value });
    
    if (response.data && Array.isArray(response.data)) {
      ddaTerms.value = response.data.map(term => ({
        id: term.id,
        term: term.term || term.name,
        description: term.description || '',
        category: term.category || '',
        dda_term_category_id: term.dda_term_category_id,
        type: 'dda_term'
      }));
      console.log('✅ DDA terms loaded for charts:', ddaTerms.value.length);
    } else {
      console.warn('⚠️ API returned non-array DDA terms data:', response.data);
    }
  } catch (error) {
    console.error('❌ Error loading DDA terms for charts:', error);
  }
}

const sidebarMenuItems = [
  { id: 1, text: 'Images' },
  { id: 2, text: 'Map' }
];

const subMenuItems = [
  { id: 1, name: 'Budget' },
  { id: 2, name: 'Schedule' },
  { id: 3, name: 'Companies' },
  { id: 4, name: 'Zoning' }
];
const btSubMenuItems = [
  { id: 1, name: 'Draw Detail' },
  { id: 3, name: 'G702' }, // 🔄 SWAPPED: G702 now before G703
  { id: 2, name: 'G703' }, // 🔄 SWAPPED: G703 now after G702
  { id: 5, name: 'Lien Waiver' }, // 🆕 NEW: Separate LW tracking tab
  { id: 7, name: 'Retainage' }, // 🆕 NEW: Retainage tracking tab
  // { id: 8, name: 'Invoices' }, // 🔄 HIDDEN: Invoices tab hidden per user request
];

// Use Type Table Data
let useTypeTableData = ref([])

// Grid API References
const drawDetailGridApi = ref(null);
const g703GridApi = ref(null);
const g702GridApi = ref(null);
const g702LienWaivers = ref([]); // 🆕 Store Lien Waiver data as flat array for name+amount matching

// 📄 INVOICE GRID API & DATA
const invoiceGridApi = ref(null);
const invoiceRowData = ref([]);
const invoiceDataCtr = ref(0);

// 🆕 LIEN WAIVER GRID API & DATA
const lwGridApi = ref(null);
const lwRowData = ref([]);
const lwDataCtr = ref(0);

// 🆕 RETAINAGE GRID API & DATA
const retainageGridApi = ref(null);
const retainageRowData = ref([]);
const retainageDataCtr = ref(0);


// 🆕 HELPER: Find matching LW for a G702 row (matches by contractor+draw, then by amount if multiple)
// Handles 2-page LW where page 1 has totalPaymentsReceivedToDate, page 2 has progressPayment
function findMatchingLW(contractorId, drawNum, g702Amount, contractorName) {
  if (!drawNum) return null;
  
  // 🔥 NEW: Backend now sends flat array, filter by draw number
  const allLwsForDraw = Array.isArray(g702LienWaivers.value) 
    ? g702LienWaivers.value.filter(lw => lw.draw_number === drawNum)
    : [];
  
  if (!allLwsForDraw || allLwsForDraw.length === 0) {
    console.log(`❌ NO LWs for draw ${drawNum}, contractor: ${contractorName}`);
    return null;
  }
  
  // Normalize names for matching
  const normalizeName = (name) => {
    if (!name) return '';
    return name.toLowerCase()
      .replace(/\b(llc|inc|corp|corporation|company|ltd|limited)\b\.?/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  const normalizedG702Name = normalizeName(contractorName);
  
  // 🔍 Find ALL LWs that match this contractor by name
  let lws = allLwsForDraw.filter(lw => {
    const lwName = lw.contractor_name || '';
    const normalizedLwName = normalizeName(lwName);
    
    // Skip if either name is empty
    if (!normalizedG702Name || !normalizedLwName) return false;
    
    // Check if names match (fuzzy with multiple strategies)
    // Strategy 1: Full substring match
    if (normalizedG702Name.includes(normalizedLwName) || normalizedLwName.includes(normalizedG702Name)) {
      return true;
    }
    
    // Strategy 2: First word match (at least 3 chars)
    const g702Words = normalizedG702Name.split(' ').filter(w => w.length >= 3);
    const lwWords = normalizedLwName.split(' ').filter(w => w.length >= 3);
    
    if (g702Words.length > 0 && lwWords.length > 0 && g702Words[0] === lwWords[0]) {
      return true;
    }
    
    // Strategy 3: Common significant words (at least 2 matching words of 4+ chars)
    const commonWords = g702Words.filter(word => word.length >= 4 && lwWords.includes(word));
    if (commonWords.length >= 2) {
      return true;
    }
    
    // Strategy 4: Single very distinctive word match (6+ chars, not common words)
    const distinctiveWords = g702Words.filter(word => 
      word.length >= 6 && 
      !['construction', 'contractors', 'services', 'systems', 'industries', 'products', 'builders', 'building', 'general', 'commercial', 'residential'].includes(word)
    );
    const matchingDistinctive = distinctiveWords.filter(word => lwWords.includes(word));
    if (matchingDistinctive.length > 0) {
      return true;
    }
    
    return false;
  });
  
  if (!lws || lws.length === 0) {
    console.log(`❌ NO NAME MATCH for draw ${drawNum}, G702: "${contractorName}" (normalized: "${normalizedG702Name}"), Available LWs:`, allLwsForDraw.map(lw => lw.contractor_name));
    return null;
  }
  
  // If only one LW, use it
  if (lws.length === 1) return lws[0];
  
  // 🆕 2-PAGE LW LOGIC: Group by subcontract_po_number (Application/Requisition number)
  // Page 1 (Financial): Has amounts, NO subcontractor name
  // Page 2 (Signature): Has subcontractor name, might have $0 amounts
  
  // Try to find matching pages by subcontract_po_number
  const lwsByPoNumber = {};
  lws.forEach(lw => {
    const poNum = lw.subcontract_po_number;
    if (poNum) {
      if (!lwsByPoNumber[poNum]) lwsByPoNumber[poNum] = [];
      lwsByPoNumber[poNum].push(lw);
    }
  });
  
  // Check if we have 2-page LWs grouped by PO number
  for (const [poNum, pages] of Object.entries(lwsByPoNumber)) {
    if (pages.length === 2) {
      // Find which page has the name and which has the amounts
      const pageWithName = pages.find(p => p.extracted_subcontractor_supplier && p.extracted_subcontractor_supplier.trim().length > 0);
      const pageWithAmounts = pages.find(p => parseFloat(p.total_payments_received_to_date || 0) > 0);
      
      if (pageWithName && pageWithAmounts) {
        console.log(`🔀 2-PAGE LW MERGE by PO#${poNum}: Name="${pageWithName.extracted_subcontractor_supplier}", Unconditional=$${pageWithAmounts.total_payments_received_to_date}, Conditional=$${pageWithName.progress_payment || 0}`);
        
        // Merge both pages
        return {
          ...pageWithName,
          // Take financial data from the amounts page
          total_payments_received_to_date: pageWithAmounts.total_payments_received_to_date || pageWithName.total_payments_received_to_date,
          amount_paid_to_date: pageWithAmounts.amount_paid_to_date || pageWithName.amount_paid_to_date,
          // Take current billing from whichever page has it
          progress_payment: pageWithName.progress_payment || pageWithAmounts.progress_payment,
          current_payment_amount: pageWithName.current_payment_amount || pageWithAmounts.current_payment_amount,
          // Merge signature/notary status
          is_signed: pageWithName.is_signed || pageWithAmounts.is_signed,
          is_notarized: pageWithName.is_notarized || pageWithAmounts.is_notarized,
          // Keep the name from the signature page
          contractor_name: pageWithName.contractor_name || pageWithName.extracted_subcontractor_supplier
        };
      }
    }
  }
  
  // FALLBACK: Old 2-page logic based on financial amounts only
  const lwsWithConditional = lws.filter(lw => {
    const hasConditional = parseFloat(lw.progress_payment || lw.current_payment_amount || 0) > 0;
    return hasConditional;
  });
  
  const lwsWithUnconditional = lws.filter(lw => {
    const hasUnconditional = parseFloat(lw.total_payments_received_to_date || lw.amount_paid_to_date || 0) > 0;
    const hasNoConditional = parseFloat(lw.progress_payment || lw.current_payment_amount || 0) === 0;
    return hasUnconditional && hasNoConditional;
  });
  
  if (lwsWithConditional.length > 0 && lwsWithUnconditional.length > 0) {
    const pageWithConditional = lwsWithConditional[0];
    const pageWithUnconditional = lwsWithUnconditional[0];
    
    console.log(`🔀 2-PAGE LW MERGE (fallback): contractor=${contractorName}, draw=${drawNum}, conditional=$${pageWithConditional.progress_payment}, unconditional=$${pageWithUnconditional.total_payments_received_to_date}`);
    
    return {
      ...pageWithConditional,
      progress_payment: pageWithConditional.progress_payment || pageWithConditional.current_payment_amount,
      current_payment_amount: pageWithConditional.current_payment_amount || pageWithConditional.progress_payment,
      total_payments_received_to_date: pageWithUnconditional.total_payments_received_to_date || pageWithUnconditional.amount_paid_to_date,
      amount_paid_to_date: pageWithUnconditional.amount_paid_to_date || pageWithUnconditional.total_payments_received_to_date,
      is_signed: pageWithConditional.is_signed || pageWithUnconditional.is_signed,
      is_notarized: pageWithConditional.is_notarized || pageWithUnconditional.is_notarized
    };
  }
  
  // If multiple LWs but not a 2-page scenario, find the one that matches this G702 application by amount
  const matchingLw = lws.find(lw => {
    const lwAmt = parseFloat(lw.current_payment_amount || lw.progress_payment || 0);
    const g702Amt = parseFloat(g702Amount || 0);
    return Math.abs(lwAmt - g702Amt) < 1; // Allow $1 rounding difference
  });
  
  // Return matching LW or first one as fallback
  return matchingLw || lws[0];
}

// 🏷️ DDA Terms column visibility toggle
const showDDATermsColumn = ref(true);

// 🏷️ Category column visibility toggle
const showCategoryColumn = ref(false); // Show by default

// Contractor Modal Data
const contractorModalData = ref({
  isOpen: false,
  rowData: null,
  fieldName: '',
  currentValue: '',
  searchTerm: '',
  searchResults: [],
  showDropdown: false,
  selectedContractor: null
});

// Edit Project Modal Data
const showEditProjectModal = ref(false);
const editProjectData = ref({
  // Basic Information
  name: '',
  entity_name: '',
  status: '',
  
  // Address Information
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zipcode: '',
  latitude: '',
  longitude: '',
  
  // Building Specifications
  num_floors: '',
  total_height: '',
  initial_gsf: '',
  total_gsf: '',
  total_num_units: '',
  subtotal_building_gsf: '',
  subtotal_num_units: '',
  subtotal_parking_gsf: '',
  subtotal_num_spaces: '',
  
  // Project Dates
  planned_start_date: '',
  planned_end_date: '',
  actual_start_date: '',
  actual_end_date: '',
  project_length_months: '',
  
  // Financial Information
  total_original_budget: '',
  
  // Project Characteristics
  is_historic_has_landmark: false,
  is_modular: false,
  is_released: false,
  
  // Foreign Key References
  finish_level_id: '',
  sustainability_type_id: '',
  asset_category_id: '',
  contractor_id: '',
  construction_contract_type_id: '',
  
  // Enhanced Fields - Multi-select and complex types
  construction_types: [],
  amenities: [],
  building_use_types: [],
  building_use_gsf: {}, // Track GSF for each building use type { use_type_id: gsf_value }
  parking_types: [],
  brand_types: [],
  
  // Notes
  notes: ''
});

// Edit Project Images Modal Data
const showEditImagesModal = ref(false);
const projectImages = ref([]);
const newImageFiles = ref([]);
const imageUploadRef = ref(null);
const imagesToDelete = ref([]); // Track images marked for deletion
// No more default image tracking - images are just images

// 🤖 Draw Insights & DDA Auto-Match Data
const isLoadingInsights = ref(false);
const isAutoMatchingDDA = ref(false);
const ddaTermCategories = ref([]);

// 📊 SPD Report Data
const isGeneratingSPDReport = ref(false);
const showSPDReportModal = ref(false);
const spdReportData = ref(null);
const spdReportError = ref('');

// Contingency calculation setting
const contingenciesExcludedFromBudget = ref(false);

// Image Carousel & Lightbox Data
const showImageLightbox = ref(false);
const currentImageIndex = ref(0);
const displayImages = computed(() => {
  if (projectImages.value.length === 0) {
    return [{ id: 'default', url: getImageUrl(projectInformation.value), name: 'Main Building View', isPrimary: true }];
  }
  
  // Sort images to show primary image first
  const sortedImages = [...projectImages.value].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });
  
  return sortedImages;
});

// Building Use GSF Calculation
const buildingUseTotalGSF = ref(0);

function onBuildingUseTypeChange() {
  console.log('🏗️ Building use type changed:', editProjectData.value.building_use_types);
  console.log('🏗️ GSF data:', editProjectData.value.building_use_gsf);
  console.log('🏗️ Edit modal data types:', editModalDropdownData.value.buildingUseTypes);
  // Force reactivity update
  nextTick(() => {
    calculateTotalBuildingUseGSF();
  });
}

function calculateTotalBuildingUseGSF() {
  let total = 0;
  if (editProjectData.value.building_use_gsf) {
    Object.values(editProjectData.value.building_use_gsf).forEach(gsf => {
      const gsfValue = parseInt(gsf) || 0;
      total += gsfValue;
    });
  }
  buildingUseTotalGSF.value = total;
  console.log('📊 Total Building Use GSF:', total);
}

function formatNumber(value) {
  if (!value && value !== 0) return '0';
  return parseFloat(value).toLocaleString('en-US');
}

// Edit Modal Dropdown Data (no auth needed)
const editModalDropdownData = ref({
  constructionTypes: [],
  contractTypes: [],
  sustainabilityTypes: [],
  amenityTypes: [],
  assetCategories: [],
  finishLevels: [],
  buildingUseTypes: [],
  parkingTypes: []
});

// Hierarchical Building Use Types Data Structure
const hierarchicalBuildingUseData = ref({
  parentCategories: {},      // Parent category selections (e.g., Residential: true)
  subCategories: {},          // Sub-category selections (e.g., Rental Apartment: true)
  subCategoriesGSF: {},       // GSF per sub-category
  subCategoriesUnits: {},     // Units per sub-category
  affordableGSF: {},          // Affordable Component GSF (for Rental/Condo)
  affordableUnits: {},        // Affordable Component Units (for Rental/Condo)
  brandTypes: {}              // Brand selections (for Hospitality/Co-Living/Condo)
});

// Parking Types Data Structure
const hierarchicalParkingData = ref({
  parkingTypes: {},           // Parking type selections
  parkingGSF: {},             // GSF per parking type
  parkingSpaces: {}           // Number of spaces per parking type
});

// Computed property for hierarchical building use categories
const updatedUseCategories = computed(() => {
  const categories = editModalDropdownData.value.assetCategories || [];
  const allBuildingUseTypes = editModalDropdownData.value.buildingUseTypes || [];
  
  // Map of asset category codes to their building use type codes
  // ✅ VALIDATED AGAINST DATABASE - All 51 building use types from codes table (2025-10-06)
  const categoryMappings = {
    'Residential': ['Rental Apartment', 'Condominium', 'Co-Living', 'Student Housing', 'Senior Housing', 'Single Family Residence', 'Manufactured Home', 'Modular Home', 'Micro Housing'],
    'Commercial': ['Office', 'Co-Working', 'Health Care / Medical Office', 'Life Science', 'Creative Office', 'Community Facility', 'Film Studio', 'Sound Stage'],
    'Industrial': ['Warehouse', 'Self-Storage', 'Cold Storage', 'Data Center', 'Manufacturing'],
    'Hospitality': ['Upscale / Luxury Hotel', 'Economy / Midscale Hotel', 'Extended Stay Hotel', 'Micro Hotel', 'Boutique Hotel', 'Resort', 'Timeshare', 'Golf Course', 'Gaming', "Member's Club"],
    'Retail': ['Integrated Ground Retail', 'Mall', 'Strip Center', 'Market Place', 'Restaurant', 'Theater', 'Event Space', 'Art Gallery'],
    'Civic & Cultural': ['Education', 'Hospital', 'Government', 'Religious', 'Stadium', 'Museum', 'Cultural Center', 'Convention Center', 'Music Venue', 'Public Park', 'Concert Hall']
  };
  
  return categories.map(category => {
    const categoryObj = reactive({ 
      ...category, 
      isChecked: hierarchicalBuildingUseData.value.parentCategories[category.id] || false
    });
    
    // Get the list of building use type codes for this category
    const categoryCodesList = categoryMappings[category.code] || [];
    
    // Filter building use types that belong to this category
    categoryObj.subCategories = allBuildingUseTypes
      .filter(useType => categoryCodesList.includes(useType.code))
      .map(subCat => reactive({
        ...subCat,
        isChecked: hierarchicalBuildingUseData.value.subCategories[subCat.id] || false
      }));
    
    return categoryObj;
  });
});

// Computed property for parking types with isChecked state
const updatedParkingTypes = computed(() => {
  const parkingTypes = editModalDropdownData.value.parkingTypes || [];
  
  return parkingTypes.map(parkingType => reactive({
    ...parkingType,
    isChecked: hierarchicalParkingData.value.parkingTypes[parkingType.id] || false
  }));
});

// Toggle check function for hierarchical checkboxes
function toggleCheck(item, isParkingType = false) {
  // CRITICAL FIX: Determine if this is a parking type, parent category, or building use type
  const allParkingTypes = editModalDropdownData.value.parkingTypes || [];
  const isParking = isParkingType || allParkingTypes.some(p => p.id === item.id);
  
  // Check if this is a PARENT CATEGORY (has subCategories array)
  const isParentCategory = item.subCategories && Array.isArray(item.subCategories);
  
  if (isParking) {
    // PARKING TYPE - toggle in hierarchicalParkingData (source of truth)
    const currentState = hierarchicalParkingData.value.parkingTypes[item.id] || false;
    const newState = !currentState;
    hierarchicalParkingData.value.parkingTypes[item.id] = newState;
    item.isChecked = newState; // Update UI
    
    // If unchecking, also clear the parking GSF and spaces data
    if (!newState) {
      delete hierarchicalParkingData.value.parkingGSF[item.id];
      delete hierarchicalParkingData.value.parkingSpaces[item.id];
    }
    
    return newState;
  } else if (isParentCategory) {
    // PARENT CATEGORY - toggle parent and ALL children
    const currentState = hierarchicalBuildingUseData.value.parentCategories[item.id] || false;
    const newState = !currentState;
    
    // Update parent category state
    hierarchicalBuildingUseData.value.parentCategories[item.id] = newState;
    item.isChecked = newState;
    
    // If unchecking parent, UNCHECK and CLEAR ALL child sub-categories
    if (!newState && item.subCategories) {
      console.log(`🗑️ Unchecking parent category ${item.code}, clearing ${item.subCategories.length} sub-categories`);
      item.subCategories.forEach(subCat => {
        // Uncheck the sub-category
        hierarchicalBuildingUseData.value.subCategories[subCat.id] = false;
        subCat.isChecked = false;
        
        // Delete all associated data
        delete hierarchicalBuildingUseData.value.subCategoriesGSF[subCat.id];
        delete hierarchicalBuildingUseData.value.subCategoriesUnits[subCat.id];
        delete hierarchicalBuildingUseData.value.affordableGSF[subCat.id];
        delete hierarchicalBuildingUseData.value.affordableUnits[subCat.id];
        delete hierarchicalBuildingUseData.value.brandTypes[subCat.id];
        delete hierarchicalBuildingUseData.value.brandTypes['branded_' + subCat.id];
        
        console.log(`  🗑️ Cleared sub-category: ${subCat.code} (ID: ${subCat.id})`);
      });
    }
    
    return newState;
  } else {
    // BUILDING USE TYPE (SUB-CATEGORY) - toggle in hierarchicalBuildingUseData (source of truth)
    const currentState = hierarchicalBuildingUseData.value.subCategories[item.id] || false;
    const newState = !currentState;
    
    // If unchecking, DELETE the GSF and Units data FIRST before toggling
    if (!newState) {
      delete hierarchicalBuildingUseData.value.subCategoriesGSF[item.id];
      delete hierarchicalBuildingUseData.value.subCategoriesUnits[item.id];
      delete hierarchicalBuildingUseData.value.affordableGSF[item.id];
      delete hierarchicalBuildingUseData.value.affordableUnits[item.id];
      delete hierarchicalBuildingUseData.value.brandTypes[item.id];
      delete hierarchicalBuildingUseData.value.brandTypes['branded_' + item.id];
      console.log(`🗑️ Cleared all data for building use ${item.id}`);
    }
    
    // NOW update the checkbox state
    hierarchicalBuildingUseData.value.subCategories[item.id] = newState;
    item.isChecked = newState; // Update UI
    
    console.log(`✅ Building use ${item.id} toggled to ${newState}. GSF: ${hierarchicalBuildingUseData.value.subCategoriesGSF[item.id] || 0}`);
    
    return newState;
  }
}

// Calculate total building GSF - ONLY for checked sub-categories
const totalBuildingGSF = computed(() => {
  if (!hierarchicalBuildingUseData.value?.subCategories) return 0;
  
  let total = 0;
  
  // Only sum GSF for checked sub-categories
  Object.keys(hierarchicalBuildingUseData.value.subCategories).forEach(subCatId => {
    if (hierarchicalBuildingUseData.value.subCategories[subCatId]) {
      // Sub-category is checked, include its GSF
      const gsf = parseFloat(hierarchicalBuildingUseData.value.subCategoriesGSF?.[subCatId]) || 0;
      total += gsf;
      
      // Also include affordable GSF if exists for this sub-category
      const affordableGsf = parseFloat(hierarchicalBuildingUseData.value.affordableGSF?.[subCatId]) || 0;
      total += affordableGsf;
    }
  });
  
  return total;
});

// Computed property for project length in months from planned dates
const computedProjectLengthMonths = computed(() => {
  if (!editProjectData.value.planned_start_date || !editProjectData.value.planned_end_date) {
    return null;
  }
  
  const startDate = new Date(editProjectData.value.planned_start_date);
  const endDate = new Date(editProjectData.value.planned_end_date);
  
  if (isNaN(startDate) || isNaN(endDate)) {
    return null;
  }
  
  // Calculate difference in months
  const diffTime = endDate - startDate;
  const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.44); // Average month length
  
  return diffMonths > 0 ? diffMonths.toFixed(1) : null;
});

function calculateProjectLength() {
  // This function triggers reactivity - the computed property handles the calculation
  // We could also update editProjectData.project_length_months here if needed for saving
  if (computedProjectLengthMonths.value) {
    editProjectData.value.project_length_months = parseFloat(computedProjectLengthMonths.value);
  }
}

// Calculate total building units - ONLY for checked sub-categories
const totalBuildingUnits = computed(() => {
  if (!hierarchicalBuildingUseData.value?.subCategories) return 0;
  
  let total = 0;
  
  // Only sum Units for checked sub-categories
  Object.keys(hierarchicalBuildingUseData.value.subCategories).forEach(subCatId => {
    if (hierarchicalBuildingUseData.value.subCategories[subCatId]) {
      // Sub-category is checked, include its units
      const units = parseFloat(hierarchicalBuildingUseData.value.subCategoriesUnits?.[subCatId]) || 0;
      total += units;
      
      // Also include affordable units if exists for this sub-category
      const affordableUnits = parseFloat(hierarchicalBuildingUseData.value.affordableUnits?.[subCatId]) || 0;
      total += affordableUnits;
    }
  });
  
  return total;
});

// Calculate total parking GSF - ONLY for checked parking types
const totalParkingGSF = computed(() => {
  if (!hierarchicalParkingData.value?.parkingTypes) return 0;
  
  let total = 0;
  
  Object.keys(hierarchicalParkingData.value.parkingTypes).forEach(parkingId => {
    if (hierarchicalParkingData.value.parkingTypes[parkingId]) {
      const gsf = parseFloat(hierarchicalParkingData.value.parkingGSF?.[parkingId]) || 0;
      total += gsf;
    }
  });
  
  return total;
});

// Calculate total parking spaces - ONLY for checked parking types
const totalParkingSpaces = computed(() => {
  if (!hierarchicalParkingData.value?.parkingTypes) return 0;
  
  let total = 0;
  
  Object.keys(hierarchicalParkingData.value.parkingTypes).forEach(parkingId => {
    if (hierarchicalParkingData.value.parkingTypes[parkingId]) {
      const spaces = parseFloat(hierarchicalParkingData.value.parkingSpaces?.[parkingId]) || 0;
      total += spaces;
    }
  });
  
  return total;
});

// Image Carousel Functions
function previousImage() {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  }
}

function nextImage() {
  if (currentImageIndex.value < displayImages.value.length - 1) {
    currentImageIndex.value++;
  }
}

function openImageLightbox() {
  showImageLightbox.value = true;
}

function closeImageLightbox() {
  showImageLightbox.value = false;
}

// Image Management Functions
function openEditImagesModal() {
  isMenuVisible.value = false;
  // Reset deletion tracking
  imagesToDelete.value = [];
  // Load existing project images
  loadProjectImages();
  showEditImagesModal.value = true;
}

function closeEditImagesModal() {
  showEditImagesModal.value = false;
  newImageFiles.value = [];
  imagesToDelete.value = [];
}

async function loadProjectImages() {
  try {
    
    // Fetch all documents for this project
    const response = await axios.get(`${apiBase}projects/${selectedId.value}/documents`, {
      headers: authToken.value
    });
    
    let projectImageDocs = [];
    
    if (response.data && Array.isArray(response.data)) {
      // First, try to find documents with document_type = 'project_image'
      projectImageDocs = response.data.filter(doc => 
        doc.document_type === 'project_image'
      );
      
      // If no explicit project_image documents found, look for image files by extension
      // BUT exclude G703 OCR generated images and other document types
      if (projectImageDocs.length === 0) {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.avif', '.svg'];
        projectImageDocs = response.data.filter(doc => {
          const filepath = (doc.filepath || '').toLowerCase();
          const description = (doc.description || '').toLowerCase();
          
          // Must be an image file
          const isImage = imageExtensions.some(ext => filepath.endsWith(ext));
          
          // Exclude G703 OCR generated images and other document processing artifacts
          const isG703Image = filepath.includes('g703') || 
                             filepath.includes('page_') || 
                             description.includes('g703') ||
                             doc.document_type === 'g703' ||
                             doc.document_type === 'g702' ||
                             doc.document_type === 'draw_document';
          
          return isImage && !isG703Image;
        });
      }
    }
    
    // Convert document records to image objects
    const uploadedImages = projectImageDocs.map((doc) => ({
      id: doc.id,
      url: `${apiBase}documents/${doc.id}/file`, // Use the actual file serving endpoint
      name: doc.description || extractFilename(doc.filepath),
      isPrimary: doc.is_primary === true || doc.is_primary === 'true'
    }));
    
    // Ensure at least one image is marked as primary if we have uploaded images
    // Only set first as primary if NONE are marked primary in the database
    if (uploadedImages.length > 0 && !uploadedImages.some(img => img.isPrimary)) {
      uploadedImages[0].isPrimary = true;
      console.log('🖼️ No primary image found in database, setting first image as primary');
    }
    
    console.log('🖼️ Loaded images with is_primary flags:', uploadedImages.map(img => ({id: img.id, name: img.name, isPrimary: img.isPrimary})));
    
    // Just use uploaded images - no default/stock images
    projectImages.value = uploadedImages;
    console.log(`🏠 Loaded ${uploadedImages.length} uploaded images`);
    if (uploadedImages.length > 0) {
      console.log('🖼️ Primary images:', uploadedImages.filter(img => img.isPrimary).map(img => img.name));
    }
    
  } catch (error) {
    // Error loading images - just show empty
    projectImages.value = [];
    console.log('🏠 No images (error loading from database)');
  }
}

// Helper function to extract filename from filepath
function extractFilename(filepath) {
  if (!filepath) return 'Unknown Image';
  const parts = filepath.split('/');
  return parts[parts.length - 1];
}

function handleImageUpload(event) {
  const files = Array.from(event.target.files);
  files.forEach(file => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImageFiles.value.push({
          id: Date.now() + Math.random(),
          file: file,
          url: e.target.result,
          name: file.name,
          isPrimary: false
        });
      };
      reader.readAsDataURL(file);
    }
  });
  // Reset input
  event.target.value = '';
}

function removeImage(imageId) {
  newImageFiles.value = newImageFiles.value.filter(img => img.id !== imageId);
}

function removeExistingImage(imageId) {
  // Add to deletion list if it's a real database image
  if (imageId !== 'default') {
    imagesToDelete.value.push(imageId);
  }
  // Remove from UI immediately
  projectImages.value = projectImages.value.filter(img => img.id !== imageId);
  console.log(`🗑️ Image removed: ${imageId}`);
}

function setPrimaryImage(imageId, isNew = false) {
  if (isNew) {
    // Clear primary flag from all new images
    newImageFiles.value.forEach(img => img.isPrimary = false);
    // Set the selected image as primary
    const selectedImage = newImageFiles.value.find(img => img.id === imageId);
    if (selectedImage) {
      selectedImage.isPrimary = true;
    }
    // Also clear primary from existing images since new image will be primary
    projectImages.value.forEach(img => img.isPrimary = false);
  } else {
    // Clear primary flag from all existing images
    projectImages.value.forEach(img => img.isPrimary = false);
    // Set the selected image as primary
    const selectedImage = projectImages.value.find(img => img.id === imageId);
    if (selectedImage) {
      selectedImage.isPrimary = true;
    }
    // Also clear primary from new images since existing image will be primary
    newImageFiles.value.forEach(img => img.isPrimary = false);
  }
  console.log(`🖼️ Set primary image: ${imageId} (isNew: ${isNew})`);
}

// No more default image database tracking - keep it simple

async function saveProjectImages() {
  try {
    let deleteErrors = [];
    let updateErrors = [];
    let uploadErrors = [];
    
    // Delete marked images first
    if (imagesToDelete.value.length > 0) {
      console.log(`🗑️ Deleting ${imagesToDelete.value.length} images...`);
      for (const imageId of imagesToDelete.value) {
        try {
          const deleteResponse = await axios.delete(`${apiBase}documents/${imageId}`, {
            headers: authToken.value
          });
          console.log(`✅ Successfully deleted image ID: ${imageId}`, deleteResponse.status);
        } catch (deleteError) {
          console.error(`❌ Failed to delete image ID: ${imageId}`, deleteError.response?.status, deleteError.response?.data);
          deleteErrors.push({ id: imageId, error: deleteError.response?.data?.message || deleteError.message });
        }
      }
    }
    
    // Update existing images metadata (names, primary status)
    const existingImages = projectImages.value.filter(img => 
      img.id !== 'default' && !imagesToDelete.value.includes(img.id)
    );
    
    // If we're deleting the primary image, ensure at least one remaining image is primary
    const hasPrimaryInExisting = existingImages.some(img => img.isPrimary);
    if (existingImages.length > 0 && !hasPrimaryInExisting) {
      console.log('⚠️ No primary image in remaining images, setting first as primary');
      existingImages[0].isPrimary = true;
    }
    
    console.log(`📝 Updating ${existingImages.length} existing images...`);
    for (const image of existingImages) {
      try {
        const updateResponse = await axios.patch(`${apiBase}documents/${image.id}`, {
          document: {
            description: image.name,
            is_primary: image.isPrimary
          }
        }, {
          headers: authToken.value
        });
        console.log(`✅ Updated image ID: ${image.id}, is_primary: ${image.isPrimary}`, updateResponse.status);
      } catch (updateError) {
        console.error(`❌ Failed to update image ID: ${image.id}`, updateError.response?.status, updateError.response?.data);
        updateErrors.push({ id: image.id, error: updateError.response?.data?.message || updateError.message });
      }
    }
    
    // Upload new images
    if (newImageFiles.value.length > 0) {
      console.log(`📤 Uploading ${newImageFiles.value.length} new images...`);
      for (const imageData of newImageFiles.value) {
        const formData = new FormData();
        formData.append('document[file]', imageData.file);
        formData.append('document[project_id]', selectedId.value);
        formData.append('document[document_type]', 'project_image');
        formData.append('document[description]', imageData.name);
        formData.append('document[is_primary]', imageData.isPrimary);
        
        try {
          const response = await axios.post(`${apiBase}documents/`, formData, {
            headers: {
              ...authToken.value,
              'Content-Type': 'multipart/form-data'
            }
          });
          console.log(`✅ Uploaded image: ${imageData.name}, ID: ${response.data?.id}, is_primary: ${imageData.isPrimary}`);
        } catch (uploadError) {
          console.error(`❌ Failed to upload image: ${imageData.name}`, uploadError.response?.status, uploadError.response?.data);
          uploadErrors.push({ name: imageData.name, error: uploadError.response?.data?.message || uploadError.message });
        }
      }
    }
    
    // Clear state BEFORE reloading
    const deletedCount = imagesToDelete.value.length;
    imagesToDelete.value = [];
    newImageFiles.value = [];
    
    // Wait a moment for database to commit changes
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Reload project images to reflect all changes
    await loadProjectImages();
    
    closeEditImagesModal();
    
    // Show summary of operations
    if (deleteErrors.length === 0 && updateErrors.length === 0 && uploadErrors.length === 0) {
      console.log(`✅ All operations completed successfully (${deletedCount} deleted, ${existingImages.length} updated, ${newImageFiles.value.length} uploaded)`);
    } else {
      let errorMessage = 'Some operations failed:\n';
      if (deleteErrors.length > 0) errorMessage += `\nDelete errors: ${deleteErrors.map(e => `ID ${e.id}: ${e.error}`).join(', ')}`;
      if (updateErrors.length > 0) errorMessage += `\nUpdate errors: ${updateErrors.map(e => `ID ${e.id}: ${e.error}`).join(', ')}`;
      if (uploadErrors.length > 0) errorMessage += `\nUpload errors: ${uploadErrors.map(e => `${e.name}: ${e.error}`).join(', ')}`;
      console.error('❌ Operation errors:', errorMessage);
      alert(errorMessage);
    }
  } catch (error) {
    console.error('❌ Critical error updating project images:', error);
    alert('Error updating project images: ' + (error.response?.data?.message || error.message));
  }
}

// Map Settings
const isGoogleMapsMounted = ref(false);
const isProjectLocationValid = ref(false);
let map;
let circleCenterLocation = {};

function geocodeProjectAddress() {
  if (typeof google === 'undefined') {
    isProjectLocationValid.value = false;
    return;
  }

  // Build address string from project information
  const addressParts = [
    projectInformation.value.address_line_1,
    projectInformation.value.city,
    projectInformation.value.state,
    projectInformation.value.zipcode
  ].filter(part => part && part.trim() !== '');

  if (addressParts.length === 0) {
    isProjectLocationValid.value = false;
    return;
  }

  const fullAddress = addressParts.join(', ');

  const geocoder = new google.maps.Geocoder();
  geocoder.geocode({ address: fullAddress }, (results, status) => {
    if (status === 'OK' && results[0]) {
      const location = results[0].geometry.location;
      const lat = location.lat();
      const lng = location.lng();
      
      // Update project coordinates temporarily (for this session)
      projectInformation.value.latitude = lat;
      projectInformation.value.longitude = lng;
      
      // Now load the map with the geocoded coordinates
      loadGoogleMapsWithCoordinates(lat, lng);
    } else {
      isProjectLocationValid.value = false;
    }
  });
}

function loadGoogleMapsWithCoordinates(lat, lng) {
  isProjectLocationValid.value = true;
  
  // See https://developers.google.com/maps/documentation/javascript/advanced-markers/graphic-markers
  const parser = new DOMParser()
  const pinSvgString =
    '<svg xmlns="http://www.w3.org/2000/svg" width="5.6444445mm" height="9.847393mm" viewBox="0 0 20 34.892337" version="1.1" >\n' +
    '  <g transform="translate(-814.59595,-274.38623)">\n' +
    '    <g transform="matrix(1.1855854,0,0,1.1855854,-151.17715,-57.3976)">\n' +
    '      <path d="m 817.11249,282.97118 c -1.25816,1.34277 -2.04623,3.29881 -2.01563,5.13867 0.0639,3.84476 1.79693,5.3002 4.56836,10.59179 0.99832,2.32851 2.04027,4.79237 3.03125,8.87305 0.13772,0.60193 0.27203,1.16104 0.33416,1.20948 0.0621,0.0485 0.19644,-0.51262 0.33416,-1.11455 0.99098,-4.08068 2.03293,-6.54258 3.03125,-8.87109 2.77143,-5.29159 4.50444,-6.74704 4.56836,-10.5918 0.0306,-1.83986 -0.75942,-3.79785 -2.01758,-5.14062 -1.43724,-1.53389 -3.60504,-2.66908 -5.91619,-2.71655 -2.31115,-0.0475 -4.4809,1.08773 -5.91814,2.62162 z" style="display:inline;opacity:1;fill:#4A5FD5;fill-opacity:1;stroke:#3b73a8;stroke-width:1;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1"/>\n' +
    '      <circle r="3.0355" cy="288.25278" cx="823.03064" style="display:inline;opacity:1;fill:#fff;fill-opacity:1;stroke-width:0"/>\n' +
    '    </g>\n' +
    '  </g>\n' +
    '</svg>\n';
  circleCenterLocation = { lat: lat, lng: lng };
  const mapOptions = {
    center: circleCenterLocation,
    zoom: 14,
    mapId: 'individualPageMap',
    mapTypeId: 'terrain'
  };

  map = new google.maps.Map(document.getElementById('individualPageMap'), mapOptions);
  // Create the marker
  const marker = new google.maps.marker.AdvancedMarkerElement({
    position: circleCenterLocation,
    map: map,
    content: parser.parseFromString(
      pinSvgString,
      "image/svg+xml",
    ).documentElement
  });

  // TODO Dynamically require the image and get its URL
  const imageUrl = getImageUrl(projectInformation.value);

  const infoWindow = new google.maps.InfoWindow({
    content: `<div style="background: #3b73a8; color: #fff; font-size: 12px; display: flex; flex-direction: column; align-items: center; height: 100%; padding: 5px; width: 180px">
              <div><img style="width:100px" src="${imageUrl}"></div>
              <div style="padding-left: 5px;">
                <div style="white-space:nowrap;"><strong>${projectInformation.value.address_line_1}</strong></div>
                <div style="display: flex; align-items: center;">
                  <label style="width: 100%;">Construction Type:</label>
                  <div style="white-space:nowrap;">${projectInformation.value.project_construction_types_s ? projectInformation.value.project_construction_types_s[0] : '-'}</div>
                </div>
                <div style="display: flex; align-items: center;">
                  <label style="width: 100%;">Total GSF:</label>
                  <div style="white-space:nowrap;">${projectInformation.value.total_gsf ? numberFormat(projectInformation.value.total_gsf, 0) : '-'}</div>
                </div>
                <div style="display: flex; align-items: center;">
                  <label style="width: 100%;">Unit Count:</label>
                  <div style="white-space:nowrap;">${projectInformation.value.num_units ? numberFormat(projectInformation.value.num_units, 0) : '-'}</div>
                </div>
              </div>
            </div>`,
  });

  marker.addListener('click', () => {
    infoWindow.open({
      anchor: marker,
      map,
      shouldFocus: true,
    });
  });
  isGoogleMapsMounted.value = true;
}

function loadGoogleMaps() {
  if (typeof google !== 'undefined') {
    // Check if latitude and longitude are valid
    const latitude = parseFloat(projectInformation.value.latitude);
    const longitude = parseFloat(projectInformation.value.longitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      // Try to geocode the address
      geocodeProjectAddress();
      return;
    }

    // Use the coordinates to load the map
    loadGoogleMapsWithCoordinates(latitude, longitude);
  } else {
    errorStore.setError('Google Map Error', 'Unknown error opening Google Maps', 'error');
  }
}

// Doughnut Chart Settings
// General Variables
let totalRevisedBudget = computed(() => {
  return lcTotalRevisedBudget.value + hcTotalRevisedBudget.value + scTotalRevisedBudget.value +
    miscFinancingTotalRevisedBudget.value + contingenciesTotalRevisedBudget.value
})
let doughnutViewMode = ref('total') // this toggles between total view and per GSF
let doughnutSummaryValue = computed(() => {
  return kMillionsWithDecimalsFormat(totalRevisedBudget.value)
})
let doughnutLabel = computed(() => {
  if (doughnutViewMode.value === 'total') {
    return 'Total Project Costs'
  } else if (doughnutViewMode.value === 'gsf') {
     return 'Avg. $/GSF'
  } else if (doughnutViewMode.value === 'unit'){
    return 'Avg. $/Unit'
  } else {
    return 'Total Project Costs'
  }
})

let doughnutChartJsonConfig = standardDoughnutChartJson({
  doughnutTitle: "Actual Costs",
})
let doughnutChartPercentage = ref([]);
let doughnutChartData = ref([])
const doughnutData = computed(() => {
  return {
    labels: ["Acquisition Costs", "Hard Costs", "Soft Cost ", "Finance/Misc Costs", "Contingencies Costs"],
    datasets: [
      {
        backgroundColor: [
          "#52596D", // $darkGray1
          "#4A5FD5", // $brightBlue1
          "#4A8FD0", // $mediumBlue1
          "#8A92A9", // $mediumGray1
          "#d3d3d3", // assigning to light gray for now
        ],
        data: doughnutChartData.value
      },
    ],
  };
});

const doughnutOptions = computed(() => {
  let json = doughnutChartJsonConfig
  json.cutout = 70
  json.plugins.legend.display = false
  json.plugins.doughnutMiddleTextPlugin.middleTextLabel = doughnutLabel.value;
  json.plugins.doughnutMiddleTextPlugin.middleTextValue = doughnutSummaryValue.value;
  json.plugins.doughnutMiddleTextPlugin.fontSize = 14;
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      let actual = 0;
      if (context.label?.includes("Acquisition")) {
        actual = currencyFormat(lcTotalRevisedBudget.value, 0)
      } else if (context.label?.includes("Hard")) {
        actual = currencyFormat(hcTotalRevisedBudget.value, 0)
      } else if (context.label?.includes("Soft")) {
        actual = currencyFormat(scTotalRevisedBudget.value, 0)
      } else if (context.label?.includes("Misc")) {
        actual = currencyFormat(miscFinancingTotalRevisedBudget.value, 0)
      } else if (context.label?.includes("Contingencies")) {
        actual = currencyFormat(contingenciesTotalRevisedBudget.value, 0)
      }
      return actual;
    },
    footer: function (context) {
      let variancePerc = 0;
      let varianceVal = 0;
      if (context[0].label?.includes("Acquisition")) {
        varianceVal = kMillionsFormat(lcTotalVariance.value);
        variancePerc = percentFormat(lcVariancePercent.value);
      } else if (context[0].label?.includes("Hard")) {
        varianceVal = kMillionsFormat(hcTotalVariance.value);
        variancePerc = percentFormat(hcVariancePercent.value);
      } else if (context[0].label?.includes("Soft")) {
        varianceVal = kMillionsFormat(scTotalVariance.value);
        variancePerc = percentFormat(scVariancePercent.value);
      } else if (context[0].label?.includes("Misc")) {
        varianceVal = kMillionsFormat(miscFinancingTotalVariance.value);
        variancePerc = percentFormat(miscFinancingVariancePercent.value);
      } else if (context[0].label?.includes("Contingencies")) {
        varianceVal = kMillionsFormat(contingenciesTotalVariance.value);
        variancePerc = percentFormat(contingenciesVariancePercent.value);
      }
      return `Variance: ${varianceVal} (${variancePerc})`
    },
  }
  json.responsive = true;
  return json
});

let cdTotalBilledToDate = ref();
let cdTotalCurrentBudget = ref();
let cdTotalOriginalBudget = ref();

// Costs to Date Stacked Bar Chart
const costsToDateStackedBarData = computed(() => {
  return {
    labels: ["Total"],
    datasets: [
      {
        label: "Billed to Date",
        backgroundColor: "#4A5FD5", // $brightBlue1
        // cdTotalBilledToDate is always the larger number here
        data: [cdTotalBilledToDate.value],
      },
      {
        label: "Current Budget",
        backgroundColor: "#4A90E2", // It's not in our colors scss but it's called Biloba Flower
        data: [cdTotalCurrentBudget.value - cdTotalBilledToDate.value],
      }
    ],
  };
});

let costsToDateJsonConfig = standardStackedBarChartJson({
  barPercentage: 1,
  xTicksFontSize: 12,
  xTicksFont: 'Inter-Bold',
  yMaxTicksLimit: 10,
})

const costsToDateStackedBarOptions = computed(() => {
  let json = costsToDateJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      return context.dataset.label + ' : ' + kMillionsWithDecimalsFormat(context.dataset.data[0])
    }
  }
  json.barThickness = 50

  return json
})

// Hard Costs Stacked Bar Chart
/**
 * Stacked Bar Chart Settings
 */

// Hard Costs Summary
const hardCostsSummaryStackedBarData = computed(() => {
  // Do not show a negative variance on the chart as the chart looks really funky
  // adjusted sov - original sov (delta value)
  let changeInBudget = hcTotalVariance.value
  if (changeInBudget < 0) {
    changeInBudget = 0
  }
  return {
    labels: ["Total"],
    datasets: [
      {
        label: "Original Budget",
        backgroundColor: "#4A5FD5", // $brightBlue1
        data: [hcTotalOriginalBudget.value], // original sov
      },
      {
        label: "Budget Variance",
        backgroundColor: "#84CADE", // $lightBlue1, css var doesn't work here
        data: [changeInBudget],
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let hardSummaryJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.8,
  xTicksFontSize: 12,
  xTicksFont: 'Inter-Bold',
  yMaxTicksLimit: 10,
})
const hardCostsSummaryStackedBarOptions = computed(() => {
  let json = hardSummaryJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      let multilineTooltip = [label + ":"];
      multilineTooltip.push(currencyFormat(value, 0));
      return multilineTooltip;
    },
    footer: function () {
      return (
        "Variance: " +
        percentFormat(
          (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
          hcTotalOriginalBudget.value
        )
      )
    }
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        return `${Math.round(parseFloat((
          (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
          hcTotalOriginalBudget.value
        )).toFixed(0) * 100)}%`
      } else {
        return ''
      }
    }
  }
  return json
})


const labelMaxLength = 15;
let hcStackedBarLabels = ref();

const hardCostsStackedBarData = computed(() => {
  return {
    labels: standardizeLabelsLength(hcStackedBarLabels.value),
    datasets: [
      {
        label: "Budget",
        backgroundColor: "#4A5FD5", // $brightBlue1
        data: hcOriginalBudgetArr.value
      },
      {
        label: "Variance",
        backgroundColor: "#84CADE", // $lightBlue1
        data: hcBudgetVarianceArr.value
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let hardStackedBarJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.8,
  xTicksFontSize: 12,
  xTicksFont: 'Inter',
  labelForcedLength: labelMaxLength,
  yMinTicksLimit: 7,
  yMaxTicksLimit: 10,
  yStepSize: 1000000
})
const hardCostsStackedBarOptions = computed(() => {
  let json = hardStackedBarJsonConfig;
  json.plugins.tooltip = {
    callbacks: {
      title: function (context) {
        return hcStackedBarLabels.value[context[0].dataIndex];
      },
      label: function (context) {
        const label = context.dataset.label;
        const value = context.parsed.y;
        return label + ": " + currencyFormat(value, 0);
      },
      footer: function (context) {
        const index = context[0].dataIndex
        const variance = (hcRevisedBudgetArr.value[index] - hcOriginalBudgetArr.value[index]) / hcOriginalBudgetArr.value[index]
        const varianceText = context[0].datasetIndex === 0 ? 'Variance: ' : ''
        return varianceText + percentFormat(variance)
      },
    },
  };
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12,
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      if (ctx.datasetIndex === 1) {
        const index = ctx.dataIndex
        const varianceNumerator = (hcRevisedBudgetArr.value[index] - hcOriginalBudgetArr.value[index])
        if (varianceNumerator !== 0) {
          const percentage = (val * 100) / hcOriginalBudgetArr.value[index]
          const roundedPercentage = Math.round(percentage * 100) / 100;
          return `${roundedPercentage.toFixed(0)}%`;
        } else {
          return '';
        }
      } else {
        return '';
      }
    },
  };
  return json;
});

// Soft Costs
// Soft Costs Summary
const softCostsSummaryStackedBarData = computed(() => {
  // Do not show a negative variance on the chart as the chart looks really funky
  // adjusted sov - original sov (delta value)
  let changeInBudget = scTotalVariance.value
  if (changeInBudget < 0) {
    changeInBudget = 0
  }
  
  return {
    labels: ["Total"],
    datasets: [
      {
        label: "Original Budget",
        backgroundColor: "#4A8FD0", // $mediumBlue1
        data: [scTotalOriginalBudget.value], // original sov
      },
      {
        label: "Budget Variance",
        backgroundColor: "#84CADE", // $lightBlue1
        data: [changeInBudget],
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let softSummaryJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.8,
  xTicksFontSize: 12,
  xTicksFont: 'Inter-Bold',
  yMaxTicksLimit: 10,
})
const softCostsSummaryStackedBarOptions = computed(() => {
  let json = softSummaryJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      let multilineTooltip = [label + ":"];
      multilineTooltip.push(currencyFormat(value, 0));
      return multilineTooltip;
    },
    footer: function () {
      return (
        "Variance: " +
        percentFormat(scVariancePercent.value
        )
      );
    },
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        return `${Math.round(parseFloat(scVariancePercent.value).toFixed(0) * 100)}%`
      } else {
        return ''
      }
    }
  }
  return json
})
let scStackedBarLabels = ref([]);
const softCostsStackedBarData = computed(() => {
  return {
    labels: standardizeLabelsLength(scStackedBarLabels.value),
    datasets: [
      {
        label: "Budget",
        backgroundColor: "#4A8FD0", // $mediumBlue1
        data: scOriginalBudgetArr.value
      },
      {
        label: "Variance",
        backgroundColor: "#84C9DE", // NOTE : color variable doesn't work here
        data: scBudgetVarianceArr.value
      },
    ],
  };
});
// Keep unique for hard/soft as we edit it without cloning
// (default cloning will mess up callbacks)
let softStackedBarJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.8,
  xTicksFontSize: 12,
  xTicksFont: 'Inter',
  labelForcedLength: labelMaxLength,
  yMinTicksLimit: 7,
  yMaxTicksLimit: 10,
  yStepSize: 1000000
})
const softCostsStackedBarOptions = computed(() => {
  let json = softStackedBarJsonConfig
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    title: function (context) {
      // return the full length version of the labels
      return scStackedBarLabels.value[context[0].dataIndex]
    },
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.y;
      return label + ": " + currencyFormat(value, 0);
    },
    footer: function (context) {
      const index = context[0].dataIndex
      let variance = (scRevisedBudgetArr.value[index] - scOriginalBudgetArr.value[index]) / scOriginalBudgetArr.value[index]
      const varianceText = context[0].datasetIndex === 0 ? 'Variance: ' : ''
      return varianceText + percentFormat(variance)
    },
  }
  json.plugins.datalabels = {
    color: 'black',
    font: {
      size: 12
    },
    anchor: 'end',
    align: 'top',
    formatter: function (val, ctx) {
      // Get only values for Budget Variance
      if (ctx.datasetIndex === 1) {
        const totalDatasetSum = ctx.chart.data.datasets[1].data.reduce((a, b) => (a + b), 0);
        if (totalDatasetSum != 0) {
          const percentage = val * 100 / totalDatasetSum
          const roundedPercentage = Math.round(percentage * 100) / 100
          return `${roundedPercentage.toFixed(0)}%`
        } else {
          return ''
        }
      } else {
        return ''
      }
    }
  }
  return json
});

// Functions
function toggleMenu() {
  isMenuVisible.value = !isMenuVisible.value;
}

function sidebarSelectItem(id) {
  sidebarSelectedItemId.value = id;
  if (sidebarSelectedItemId.value === 1) {
    fetchImage()
  } else if (sidebarSelectedItemId.value === 2) {
    setTimeout(() => {
      loadGoogleMapsAPI.then(() => {
        loadGoogleMaps()
      }).catch(error => {
          errorStore.setError('Error loading Google Maps API:', error);
      });
    }, 250);
  }
}

function subMenuSelectItem(id) {
  subMenuSelectedItemId.value = id;
}

function btSubMenuSelectItem(id) {
  btSubMenuSelectedItemId.value = id;

  if(btSubMenuSelectedItemId.value == 1) {
    setDrawDetail();
    fetchDrawNumber();
  }
  else if(btSubMenuSelectedItemId.value == 2){
    setG703DrawNumber();  // This already calls fetchG703DrawDetailGridData() inside
    fetchG703DrawNumber();
    // 🔥 REMOVED DUPLICATE: fetchG703DrawDetailGridData() - already called by setG703DrawNumber()
  }
  else if (btSubMenuSelectedItemId.value == 3) {
    // 🔥 SYNC: Use the same draw as Draw Details tab (globalDrawFilter or drawNumber)
    g702DrawNumber.value = globalDrawFilter.value || drawNumber.value || (drawNumberList.value?.[0] || 0);
    fetchG702DrawNumber();
    // Always fetch when switching to this tab
    fetchG702DrawDetailGridData();
    // If first fetch returns empty, try once more shortly after
    setTimeout(() => {
      if (!g702RowData.value || g702RowData.value.length === 0) {
        fetchG702DrawDetailGridData();
      }
    }, 120);
  }
  else if (btSubMenuSelectedItemId.value == 5) {
    // 🆕 LIEN WAIVER TAB: Always fetch fresh G702 data for current draw
    console.log('🔥 Lien Waiver tab selected, fetching G702 data for LW tracking...');
    g702DrawNumber.value = globalDrawFilter.value || drawNumber.value || (drawNumberList.value?.[0] || 0);
    fetchG702DrawDetailGridData(); // This will populate lwRowData via refreshLWData()
  }
  else if (btSubMenuSelectedItemId.value == 7) {
    // 🆕 RETAINAGE TAB: Fetch current + previous draw ONLY (2 draws, not all - fast!)
    console.log('🔥 Retainage tab selected, fetching current + previous draw for retainage comparison...');
    g702DrawNumber.value = 'retainage'; // Special signal to fetch current + previous draw
    fetchG702DrawDetailGridData(); // This will populate retainageRowData via refreshRetainageData()
  }
  else if (btSubMenuSelectedItemId.value == 8) {
    // 📄 INVOICES TAB: Fetch invoice data directly (like G702 does)
    console.log('🎯 Invoices tab selected, fetching invoice data...');
    fetchInvoiceData();
  }
}

// Watch statements moved to after variable declarations to avoid undefined errors

function filteredMenuItems() {
  return subMenuItems.filter(item => item.id === subMenuSelectedItemId.value);
}

// Toggle Values
let isBuildingDetailsOpen = ref(true);
let isConstructionDetailsOpen = ref(true);

function expandCollapseController(element) {
  switch (element) {
    case 'buildingDetails':
      isBuildingDetailsOpen.value = !isBuildingDetailsOpen.value
      break;
    case 'constructionDetails':
      isConstructionDetailsOpen.value = !isConstructionDetailsOpen.value
      break;
    default:
      break;
  }
}

// Ag-Grid Settings

// Helper function for parent row styling in budget columns
function getBudgetCellStyle(params) {
  if (!params || !params.data) return {};
  if (params.data.is_parent) {
    return { 
      fontWeight: 'bold',
      backgroundColor: '#f8f9fa',
      borderTop: '2px solid #dee2e6',
      borderBottom: '1px solid #dee2e6'
    };
  }
  const d = params.data;
  const isTotal = d.is_total_row === true || (d.dda_term && (d.dda_term.is_total === true || d.dda_term.is_total_row === true));
  if (!isTotal) return {};

  const categoryColors = {
    hardCosts: '#3B5CCC',
    softCosts: '#7DAAF8',
    acquisition: '#52596D',
    financing: '#9AA1AF',
    contingencies: '#d3d3d3'
  };
  let backgroundColor = '#f0f0f0';
  const catId = d.dda_term && d.dda_term.dda_term_category_id;
  if (catId === 197) backgroundColor = categoryColors.hardCosts + '20';
  else if (catId === 198) backgroundColor = categoryColors.softCosts + '20';
  else if (catId === 203) backgroundColor = categoryColors.acquisition + '20';
  else if (catId === 204) backgroundColor = categoryColors.financing + '20';
  else if (catId === 210) backgroundColor = categoryColors.contingencies + '20';
  else if (catId === 209) {
    const name = (d.dda_term.term || '').toLowerCase();
    if (name.includes('hard cost')) backgroundColor = categoryColors.hardCosts + '20';
    else if (name.includes('soft cost')) backgroundColor = categoryColors.softCosts + '20';
    else if (name.includes('acquisition')) backgroundColor = categoryColors.acquisition + '20';
    else if (name.includes('financing')) backgroundColor = categoryColors.financing + '20';
    else if (name.includes('contingenc')) backgroundColor = categoryColors.contingencies + '20';
  }
  return {
    fontWeight: 'bold',
    backgroundColor,
    borderTop: '2px solid #666',
    borderBottom: '2px solid #666',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    height: '100%'
  };
}

// Need to ask what endpoint should I be getting these values to indicate the fields as well
// NOTE: if adding more sumAggregate or other aggregate functions, please adjust leafParentData accordingly
const sumAggregateCols = ['original_budget', 'total_adjustments', 'current_budget', 'total_spent', 'total_completed_to_date']
const autoGroupColumnDef = {
  headerName: 'Description of Work',
  minWidth: 400,
  pinned: 'left', // freeze/pin itemHierarchy to the left so it can be seen when scrolling (esp. for Schedule page)
  cellRendererParams: {
    suppressCount: true,
  },
  // NOTE: Removed default alphabetical sorting to preserve Excel source order
  // sortingOrder: ['asc'],
  // sort: 'asc',
  // NOTE: Removed custom comparator to preserve source_row_position sorting
  // comparator: (valueA, valueB) => {
  //   if (valueA === valueB) return 0;
  //   if (!valueA) return 1;
  //   if (!valueB) return -1;

  //   const headers = ['ACQUISITION COSTS', 'HARD COSTS', 'SOFT COSTS', 'FINANCING COSTS', 'CONTINGENCIES', 'GENERAL']
  //   if (headers.includes(valueA) && headers.includes(valueB)) {
  //     return (headers.indexOf(valueA) > headers.indexOf(valueB)) ? 1 : -1;
  //   }
  //   return (valueA > valueB) ? 1 : -1;
  // },
  cellStyle: function(params) {
    // FIXED: Remove hierarchy styling since all items are now flat (hierarchy_level = 1)
      return { 
      fontWeight: 'normal',
      color: '#333',
      backgroundColor: '#fff'
    };
  },
  valueGetter: params => {
    if (params.node.rowPinned === 'bottom') {
      return 'Total';
    }
    // For tree data, show the actual description content
    if (params.data && (params.data.scope_detail || params.data.description)) {
      return params.data.scope_detail || params.data.description;
    }
    // For group nodes, use the key (from itemHierarchy)
    return params.node.key || 'Work Item';
  }
}
const columnDefs = [
  {
    headerName: 'Scopes of Work',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: 'Company',
        field: 'company',
        cellStyle: { color: '#333', backgroundColor: '#fff' },
        headerClass: 'header-custom-lilac',
        sortable: true,
        filter: true,
        editable: false,
        resizable: true,
        width: 200,
        valueGetter: params => {
          // Extract company name from the row data
          return params.data?.contractor_name || params.data?.company || 'N/A';
        }
      }
    ],
  },
  {
    headerName: 'Budget Tracking',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Original Budget /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Original Budget /Unit";
          return "Original Budget";
        }),
        field: "original_budget",
        headerClass: 'header-custom-lilac',
        aggFunc: sumAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        cellRenderer: (params) => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const divisor = viewModeDivisor.value || 1;
          const value = (params.value || 0) / divisor;
          
          if(viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL) {
            return currencyFormat(value, 2)
          }
          else {
            return currencyFormat(value, 0)
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Prior Adjustments /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Prior Adjustments /Unit";
          return "Prior Adjustments";
        }),
        field: "total_adjustments",
        headerClass: 'header-custom-lilac',
        aggFunc: sumAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        cellRenderer: (params) => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const divisor = viewModeDivisor.value || 1;
          const value = (params.value || 0) / divisor;
          
          if(viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL) {
            return currencyFormat(value, 2)
          }
          else {
            return currencyFormat(value, 0)
          }
        },
        type: 'rightAligned'
      },
    {
      headerName: computed(() => {
        if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Prior Adjustments /GSF";
        if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Prior Adjustments /Unit";
        return "Prior Adjustments";
      }),
      field: "previous_adjustments",
      headerClass: 'header-custom-lilac',
      aggFunc: 'sum',
      editable: false,
      valueParser: "Number(newValue)",
      cellStyle: getBudgetCellStyle,
      cellRenderer: (params) => {
        if (!params.data) return '';
        const divisor = viewModeDivisor.value || 1;
        const value = (params.value || 0) / divisor;
        const formattedValue = currencyFormat(value, 0);
        return `<div class="budget-value">${formattedValue}</div>`;
      },
      type: 'rightAligned'
    },
    {
      headerName: computed(() => {
        if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Current Adjustments /GSF";
        if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Current Adjustments /Unit";
        return "Current Adjustments";
      }),
      field: "current_adjustments",
      headerClass: 'header-custom-lilac',
      aggFunc: 'sum',
      editable: false,
      valueParser: "Number(newValue)",
      cellStyle: getBudgetCellStyle,
      cellRenderer: (params) => {
        if (!params.data) return '';
        const divisor = viewModeDivisor.value || 1;
        const value = (params.value || 0) / divisor;
        const formattedValue = currencyFormat(value, 0);
        return `<div class="budget-value">${formattedValue}</div>`;
        },
        type: 'rightAligned'
      },
      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Current Budget /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Current Budget /Unit";
          return "Current Budget";
        }),
        field: "current_budget",
        headerClass: 'header-custom-lilac',
        aggFunc: sumAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        cellRenderer: (params) => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const divisor = viewModeDivisor.value || 1;
          const value = (params.value || 0) / divisor;
          
          if(viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL) {
            return currencyFormat(value, 2)
          }
          else {
            return currencyFormat(value, 0)
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: "Variance",
        field: "variance",
        headerClass: 'header-custom-lightblue',
        aggFunc: varianceAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        valueFormatter: params => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          return `${percentFormat(params.value)}`;
        },
        type: 'rightAligned'
      },
    ],
  },
  {
    headerName: 'Expenditure Tracking',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Prior Billing /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Prior Billing /Unit";
          return "Prior Billing";
        }),
        field: "previously_funded",
        aggFunc: sumAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        cellRenderer: (params) => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const divisor = viewModeDivisor.value || 1;
          const value = (params.value || 0) / divisor;
          
          if(viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL) {
            return currencyFormat(value, 2)
          }
          else {
            return currencyFormat(value, 0)
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Current Billing /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Current Billing /Unit";
          return "Current Billing";
        }),
        field: "current_request",
        aggFunc: sumAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        cellRenderer: (params) => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const divisor = viewModeDivisor.value || 1;
          const value = (params.value || 0) / divisor;
          
          if(viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL) {
            return currencyFormat(value, 2)
          }
          else {
            return currencyFormat(value, 0)
          }
        },
        type: 'rightAligned'
      },

      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Total Billing to Date /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Total Billing to Date /Unit";
          return "Total Billing to Date";
        }),
        field: "total_completed_to_date",
        aggFunc: sumAggregate,
        editable: true,
        valueParser: "Number(newValue)",
        cellRenderer: (params) => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const divisor = viewModeDivisor.value || 1;
          const value = (params.value || 0) / divisor;
          
          if(viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL) {
            return currencyFormat(value, 2)
          }
          else {
            return currencyFormat(value, 0)
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: "% Complete",
        field: "percent_complete",
        width: 90,
        valueFormatter: formatPercentWholeNumber,
        aggFunc: simplePercentCompleteAggregate,
        editable: false,
        valueParser: "Number(newValue)",
        valueFormatter: params => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          return `${percentFormat(params.value)}`;
        },
        type: 'rightAligned'
      },
    ],
  },
];

// 💰 FORMATTING FUNCTIONS FOR GRIDS
const formatCurrencyWholeNumber = (params) => {
  if (params.value == null || params.value === '' || isNaN(params.value)) return '';
  return '$' + Math.round(params.value).toLocaleString('en-US');
};

const formatPercentWholeNumber = (params) => {
  if (params.value == null || params.value === '' || isNaN(params.value)) return '';
  const pct = params.value * 100; // Assuming value is 0-1
  return Math.round(pct) + '%';
};

const drawDetailColumnDefs = [
  {
    headerName: 'Scopes of Work',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: 'Description of Work',
        field: 'description',
        cellStyle: function(params) {
          // FIXED: No hierarchy styling - all items display flat with vertical centering
          if (!params || !params.data) return {};
          return { 
            fontWeight: '500',
            color: '#333',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            height: '100%'
          };
        },
        headerClass: 'header-custom-lilac',
        sortable: true,
        filter: true,
        editable: false,
        resizable: true,
        width: 300,
        cellRenderer: (params) => {
          if (!params || !params.data) return '';
          
          const description = params.data.description || '';
          
          // 🔥 HIDE ROW PLACEHOLDERS: Show blank for "(Row XXX)" placeholders
          let displayDescription = description.match(/^\(Row \d+\)$/) ? '' : description;
          
          // 🔥 FIX: Show "-" for Unknown rows
          if (description === 'Unknown') {
            displayDescription = '-';
          }
          
          return `
            <div class="description-cell" style="padding: 8px;">
              <span class="description-value">${displayDescription}</span>
            </div>
          `;
        }
      },
      {
        headerName: 'DDA Term',
        field: 'dda_term',
        sortable: true,
        filter: true,
        width: 250,
        hide: false,
        valueGetter: params => {
          // 🔥 PLACEHOLDER-AWARE DDA TERM FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          // 🔥 FIX: Show "-" for Unknown rows (no DDA term)
          if (description === 'Unknown') {
            return '-';
          }
          // Get DDA term name from relationship or show blank
          return params.data?.dda_term?.term || params.data?.dda_term_name || '';
        },
        cellStyle: params => {
          // 🔥 DON'T HIGHLIGHT PLACEHOLDER ROWS
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return null; // No special styling for placeholder rows
          }
          // Highlight unassigned DDA terms for real rows only
          if (!params.data?.dda_term && !params.data?.dda_term_name) {
            return { backgroundColor: '#fff3cd', color: '#856404' }; // Light yellow background
          }
          return null;
        },
        cellRenderer: (params) => {
          if (!params || !params.data) return 'N/A';
          
          const ddaTerm = params.data?.dda_term?.term || params.data?.dda_term_name || '';
          
          return `
            <div class="dda-term-edit-cell" data-field="dda_term_id" data-row-id="${params.data.id || params.node.id}">
              <span class="dda-term-value">${ddaTerm}</span>
              <span class="dda-term-edit-overlay" style="opacity: 0; visibility: hidden;">
                <button class="dda-term-edit-btn" title="Click to edit DDA term" style="display: none;">✏️</button>
              </span>
            </div>
          `;
        },
        onCellClicked: (params) => {
          if (params.data) {
            handleEditDDATerm(params.data, 'dda_term_id', params.data?.dda_term?.term || params.data?.dda_term_name);
          }
        }
      },
      {
        headerName: 'Category',
        field: 'category',
        sortable: true,
        filter: true,
        width: 150,
        hide: !showCategoryColumn.value,
        valueGetter: params => {
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) return '-';
          
          // Check for override first
          if (params.data?.category_override) {
            return params.data.category_override;
          }
          
          // Map from dda_term_category_id
          const categoryId = params.data?.dda_term?.dda_term_category_id || params.data?.dda_term_category_id;
          const categoryMap = {
            197: 'Hard Costs',
            198: 'Soft Costs',
            203: 'Acquisition',
            204: 'Financing',
            210: 'Contingencies'
          };
          return categoryMap[categoryId] || 'Uncategorized';
        },
        cellStyle: params => {
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) return null;
          
          // Highlight if category is Uncategorized
          const category = params.value;
          if (category === 'Uncategorized') {
            return { backgroundColor: '#fff3cd', color: '#856404' };
          }
          // Highlight if overridden
          if (params.data?.category_override) {
            return { backgroundColor: '#e3f2fd', color: '#1976d2' };
          }
          return null;
        },
        cellRenderer: (params) => {
          if (!params || !params.data) return 'N/A';
          
          const category = params.value || 'Uncategorized';
          const isOverride = !!params.data?.category_override;
          
          return `
            <div class="category-edit-cell" data-field="category" data-row-id="${params.data.id || params.node.id}">
              <span class="category-value">${category}${isOverride ? ' ⚙️' : ''}</span>
              <span class="category-edit-overlay" style="opacity: 0; visibility: hidden;">
                <button class="category-edit-btn" title="Click to override category" style="display: none;">✏️</button>
              </span>
            </div>
          `;
        },
        onCellClicked: (params) => {
          if (params.data) {
            handleEditCategory(params.data, params.value);
          }
        }
      }
    ],
  },
  {
    headerName: 'Budget Tracking',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: "Original Budget",
        field: "original_budget",
        headerClass: 'header-custom-lilac',
        aggFunc: 'sum',
        editable: false,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
            return '-'; // Show dash for placeholder/Unknown rows
          }
          
          const formattedValue = viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL 
            ? currencyFormat(params.value, 0) 
            : currencyFormat(params.value, 0);
          
          return `
            <div class="hover-edit-cell" data-field="original_budget" data-row-id="${params.data.id || params.node.id}">
              <span class="budget-value">${formattedValue}</span>
              <span class="edit-overlay" style="opacity: 0; visibility: hidden;">
                <button class="edit-btn" title="Click to edit" style="display: none;">✏️</button>
              </span>
            </div>
          `;
        },
        onCellClicked: (params) => {
          if (params.data) {
            handleEditBudgetValue(params.data, 'original_budget', params.value);
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: "Prior Adjustments",
        field: "previous_adjustments",
        headerClass: 'header-custom-lilac',
        aggFunc: 'sum',
        editable: false,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
            return '-'; // Show dash for placeholder/Unknown rows
          }
          const formattedValue = currencyFormat(params.value || 0, 0);
          return `<div class="budget-value">${formattedValue}</div>`;
        },
        type: 'rightAligned'
      },
      {
        headerName: "Current Adjustments",
        field: "current_adjustments",
        headerClass: 'header-custom-lilac',
        aggFunc: 'sum',
        editable: false,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
            return '-'; // Show dash for placeholder/Unknown rows
          }
          const formattedValue = currencyFormat(params.value || 0, 0);
          return `<div class="budget-value">${formattedValue}</div>`;
        },
        type: 'rightAligned'
      },
      {
        headerName: "Current Budget",
        field: "current_budget",
        headerClass: 'header-custom-lilac',
        aggFunc: 'sum',
        editable: false,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
            return '-'; // Show dash for placeholder/Unknown rows
          }
          
          const formattedValue = viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL 
            ? currencyFormat(params.value, 0) 
            : currencyFormat(params.value, 0);
          
          return `
            <div class="hover-edit-cell" data-field="current_budget" data-row-id="${params.data.id || params.node.id}">
              <span class="budget-value">${formattedValue}</span>
              <span class="edit-overlay" style="opacity: 0; visibility: hidden;">
                <button class="edit-btn" title="Click to edit" style="display: none;">✏️</button>
              </span>
            </div>
          `;
        },
        onCellClicked: (params) => {
          if (params.data) {
            handleEditBudgetValue(params.data, 'current_budget', params.value);
          }
        },
        type: 'rightAligned'
      },

      {
        headerName: "Variance",
        field: "variance",
        headerClass: 'header-custom-lightblue',
        aggFunc: 'avg',
        editable: false,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        valueFormatter: params => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          return `${percentFormat(params.value)}`;
        },
        type: 'rightAligned'
      },
    ],
  },
  {
    headerName: 'Expenditure Tracking',
    headerClass: 'center-align-header',
    children: [

      {
        headerName: 'Prior Billing',
        field: "previously_funded",
        aggFunc: 'sum',
        editable: true,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const formattedValue = viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL 
            ? currencyFormat(params.value, 0) 
            : currencyFormat(params.value, 0);
          
          return `
            <div class="hover-edit-cell" data-field="previously_funded" data-row-id="${params.data.id || params.node.id}">
              <span class="budget-value">${formattedValue}</span>
              <span class="edit-overlay" style="opacity: 0; visibility: hidden;">
                <button class="edit-btn" title="Click to edit" style="display: none;">✏️</button>
              </span>
            </div>
          `;
        },
        onCellClicked: (params) => {
          if (params.data) {
            handleEditBudgetValue(params.data, 'previously_funded', params.value);
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: 'Current Billing',
        field: "current_request",
        aggFunc: 'sum',
        editable: true,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        valueGetter: (params) => {
          if (!params || !params.data) return 0;
          return parseFloat(params.data.current_request || 0);
        },
        cellRenderer: (params) => {
          if (!params.data) return '';
          
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const currentBilling = parseFloat(params.data.current_request || 0);
          const formattedValue = viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL 
            ? currencyFormat(currentBilling, 2) 
            : currencyFormat(currentBilling, 0);
          return `
            <div class="budget-value">${formattedValue}</div>
          `;
        },
        type: 'rightAligned'
      },
      {
        headerName: "Total Billing to Date",
        field: "total_completed_to_date",
        aggFunc: 'sum',
        editable: true,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          const formattedValue = viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL 
            ? currencyFormat(params.value, 0) 
            : currencyFormat(params.value, 0);
          
          return `
            <div class="hover-edit-cell" data-field="total_completed_to_date" data-row-id="${params.data.id || params.node.id}">
              <span class="budget-value">${formattedValue}</span>
              <span class="edit-overlay" style="opacity: 0; visibility: hidden;">
                <button class="edit-btn" title="Click to edit" style="display: none;">✏️</button>
              </span>
            </div>
          `;
        },
        onCellClicked: (params) => {
          if (params.data) {
            handleEditBudgetValue(params.data, 'total_completed_to_date', params.value);
          }
        },
        type: 'rightAligned'
      },
      {
        headerName: computed(() => {
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) return "Remaining Budget /GSF";
          if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) return "Remaining Budget /Unit";
          return "Remaining Budget";
        }),
        field: "remaining_budget",
        headerClass: 'header-custom-lilac',
        aggFunc: 'sum',
        editable: true,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        cellRenderer: (params) => {
          if (!params.data) return '';
          
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          
          // Use the direct remaining_budget field from backend
          const remainingBudget = parseFloat(params.data.remaining_budget || 0);
          const divisor = viewModeDivisor.value || 1;
          const value = remainingBudget / divisor;
          
          const formattedValue = viewByTGU.value == VIEW_MODES_TGU.VIEW_BY_TOTAL 
            ? currencyFormat(value, 2) 
            : currencyFormat(value, 0);
          
          return `
            <div class="budget-value">${formattedValue}</div>
          `;
        },
        type: 'rightAligned'
      },
      {
        headerName: "% Complete",
        field: "percent_complete",
        width: 90,
        aggFunc: 'avg',
        editable: false,
        valueParser: "Number(newValue)",
        cellStyle: getBudgetCellStyle,
        valueFormatter: params => {
          // 🔥 USE PLACEHOLDER-AWARE FORMATTER
          const description = params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/)) {
            return '-'; // Show dash for placeholder rows
          }
          return `${percentFormat(params.value)}`;
        },
        type: 'rightAligned'
      },
    ],
  },

];

// Ensure total-row styling applies to every column by merging with existing cellStyle
function applyTotalRowCellStyleToAllColumns() {
  const attach = (col) => {
    if (!col) return;
    if (col.children && Array.isArray(col.children)) {
      col.children.forEach(attach);
      return;
    }
    const existing = col.cellStyle;
    col.cellStyle = (params) => {
      const base = typeof existing === 'function' ? (existing(params) || {}) : (existing || {});
      const total = getBudgetCellStyle(params) || {};
      return { ...base, ...total };
    };
  };
  drawDetailColumnDefs.forEach(attach);
}

applyTotalRowCellStyleToAllColumns();

// Whole-row coloring for total rows using chart palette (DDA-driven)
function hasTotalIndicator(row) {
  if (!row) return false;
  const desc = (row.description || row.scope_detail || '').toLowerCase();
  const totalTypeStr = (row.total_type || '').toLowerCase();
  const ddaTermName = (row.dda_term_name || (row.dda_term && row.dda_term.term) || '').toLowerCase();
  const hasFlag = row.is_total_row === true || (row.dda_term && (row.dda_term.is_total === true || row.dda_term.is_total_row === true));
  
  // CRITICAL FIX: Also check dda_term_name for "total" 
  return hasFlag || desc.includes('total') || totalTypeStr.includes('total') || ddaTermName === 'total';
}
const drawTotalRowClassRules = {
  'row-total-any': params => hasTotalIndicator(params.data || {}),
  'row-total-hard': params => {
    const d = params.data || {};
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 197;
  },
  'row-total-soft': params => {
    const d = params.data || {};
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 198;
  },
  'row-total-acq': params => {
    const d = params.data || {};
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 203;
  },
  'row-total-financing': params => {
    const d = params.data || {};
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 204;
  },
  'row-total-contingency': params => {
    const d = params.data || {};
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 210;
  },
  // Totals category 209: infer by term name for color selection
  'row-total-totals-hard': params => {
    const d = params.data || {};
    const name = (d.dda_term && d.dda_term.term) ? d.dda_term.term : '';
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 209 && /hard cost/i.test(name);
  },
  'row-total-totals-soft': params => {
    const d = params.data || {};
    const name = (d.dda_term && d.dda_term.term) ? d.dda_term.term : '';
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 209 && /soft cost/i.test(name);
  },
  'row-total-totals-financing': params => {
    const d = params.data || {};
    const name = (d.dda_term && d.dda_term.term) ? d.dda_term.term : '';
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 209 && /financing/i.test(name);
  },
  'row-total-totals-acq': params => {
    const d = params.data || {};
    const name = (d.dda_term && d.dda_term.term) ? d.dda_term.term : '';
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 209 && /acquisition/i.test(name);
  },
  'row-total-totals-contingency': params => {
    const d = params.data || {};
    const name = (d.dda_term && d.dda_term.term) ? d.dda_term.term : '';
    return hasTotalIndicator(d) && d.dda_term && d.dda_term.dda_term_category_id === 209 && /contingenc/i.test(name);
  },
};

const g703DetailColumnDefs = [
  {
    headerName: 'Item No.',
    field: 'trade_code',
    sortable: true,
    filter: true,
    width: 80,
    pinned: 'left',
    valueGetter: params => {
      // 🔥 FIX: Show "-" for blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return '-';
      }
      return params.data?.trade_code || 'N/A';
    }
  },
  {
    headerName: 'Description of Work',
    field: 'g703_description',
    sortable: true,
    filter: true,
    width: 300,
    pinned: 'left',
    editable: false, // Not editable like G702 tab
    valueGetter: params => {
      // 🔥 FIX: Use description field (not g703_description) from API response
      if (params.data?.description) {
        return params.data.description;
      }
      // Fallback for records without description - show empty, don't fake it
      return '';
    },
    valueSetter: params => {
      // Update the description field
      if (params.newValue !== params.oldValue) {
        params.data.description = params.newValue;
        // Call API to update G703 description
        updateG703Description(params.data.id, params.newValue);
        return true;
      }
      return false;
    },
    cellStyle: { 
      cursor: 'text',
      backgroundColor: '#fffbeb' // Light yellow to indicate editable
    }
  },
  {
    headerName: 'Contractor',
    field: 'contractor_names',
    sortable: true,
    filter: true,
    width: 200,
    pinned: 'left',
    valueGetter: params => {
      // 🔥 FIX: Show "-" for blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return '-';
      }
      // This will be populated by G702 contractor matching logic
      return params.data?.contractor_names || params.data?.contractor_name || 'Not Assigned';
    },
    cellStyle: params => {
      // 🔥 FIX: Don't highlight blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return null;
      }
      // Highlight unassigned contractors
      if (!params.data?.contractor_names && !params.data?.contractor_name) {
        return { backgroundColor: '#fff3cd', color: '#856404' }; // Light yellow background
      }
      return null;
    },
    cellRenderer: (params) => {
      if (!params || !params.data) return 'N/A';
      
      // 🔥 FIX: Show "-" for blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return '-';
      }
      
      const contractorName = params.data?.contractor_names || params.data?.contractor_name || 'Not Assigned';
      
      return `
        <div class="contractor-edit-cell" data-field="contractor_names" data-row-id="${params.data.id || params.node.id}">
          <span class="contractor-value">${contractorName}</span>
          <span class="contractor-edit-overlay" style="opacity: 0; visibility: hidden;">
            <button class="contractor-edit-btn" title="Click to edit contractor" style="display: none;">✏️</button>
          </span>
        </div>
      `;
    },
          onCellClicked: (params) => {
        if (params.data) {
          handleEditContractor(params.data, 'contractor_names', params.data.contractor_names || params.data.contractor_name);
        }
    }
  },
  { 
    headerName: 'Application #', 
    field: 'application_number', 
    width: 130,
    pinned: 'left',
    hide: true, // 🔥 HIDDEN per user request
    valueGetter: params => {
      // 🔥 FIX: Show "-" for blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return '-';
      }
      return params.data?.application_number || params.data?.draw_number || 'N/A';
    },
    cellStyle: { backgroundColor: '#f8f9fa', fontWeight: '500' }
  },
  { 
    headerName: 'Period To', 
    field: 'period_through', 
    width: 130,
    pinned: 'left',
    hide: true, // 🔥 HIDDEN per user request
    valueGetter: params => {
      // 🔥 FIX: Show "-" for blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown' || description === '' || description.match(/^\(Row \d+\)$/)) {
        return '-';
      }
      return params.data?.period_through || '-';
    },
    valueFormatter: params => {
      const v = params.value;
      if (!v || v === '-' || v === '') return '-'; // Return "-" for empty/blank values
      try {
        const d = new Date(v);
        if (!isNaN(d.getTime())) return d.toLocaleDateString();
        return '-'; // Return "-" if date is invalid
      } catch { return '-'; }
    },
    cellStyle: { backgroundColor: '#f8f9fa', fontWeight: '500' }
  },
  {
    headerName: 'DDA Term',
    field: 'dda_term',
    sortable: true,
    filter: true,
    width: 250,
    pinned: 'left',
    hide: false,
        valueGetter: params => {
          // 🔥 PLACEHOLDER-AWARE DDA TERM FORMATTER
          const description = params.data?.scope_detail || params.data?.description || '';
          if (description.match(/^\(Row \d+\)$/) || description === 'Unknown' || description === '-') {
            return '-'; // Show dash for placeholder/Unknown/blank rows
          }
          // Get DDA term name from relationship or show blank
          return params.data?.dda_term_name || params.data?.dda_term?.term || '';
        },
    cellStyle: params => {
      // 🔥 FIX: Don't highlight "-" rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return null; // No highlighting for blank rows
      }
      // Highlight unassigned DDA terms for real rows only
      if (!params.data?.dda_term && !params.data?.dda_term_name) {
        return { backgroundColor: '#fff3cd', color: '#856404' }; // Light yellow background
      }
      return null;
    },
    cellRenderer: (params) => {
      if (!params || !params.data) return 'N/A';
      
      // 🔥 FIX: Show "-" for blank rows
      const description = params.data?.scope_detail || params.data?.description || '';
      if (description === '-' || description === 'Unknown') {
        return '-';
      }
      
      const ddaTerm = params.data?.dda_term?.term || params.data?.dda_term_name || '';
      
      return `
        <div class="dda-term-edit-cell" data-field="dda_term_id" data-row-id="${params.data.id || params.node.id}">
          <span class="dda-term-value">${ddaTerm}</span>
          <span class="dda-term-edit-overlay" style="opacity: 0; visibility: hidden;">
            <button class="dda-term-edit-btn" title="Click to edit DDA term" style="display: none;">✏️</button>
          </span>
        </div>
      `;
    },
    onCellClicked: (params) => {
      if (params.data) {
        handleEditDDATerm(params.data, 'dda_term_id', params.data?.dda_term?.term || params.data?.dda_term_name);
      }
    }
  },
  {
    headerName: 'Original Contract Value',
    field: 'original_budget',
    type: 'rightAligned',
    width: 120,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.original_budget || 0);
    }
  },
  {
    headerName: 'Change orders',
    field: 'change_orders',
    type: 'rightAligned',
    width: 100,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      // Calculate change orders as current_budget - original_budget
      const current = parseFloat(params.data?.current_budget || 0);
      const original = parseFloat(params.data?.original_budget || 0);
      return current - original;
    }
  },
  {
    headerName: 'Scheduled Value',
    field: 'current_budget',
    type: 'rightAligned',
    width: 110,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.current_budget || 0);
    }
  },
  {
    headerName: 'Work Completed Prev. Appl.',
    field: 'previously_funded',
    type: 'rightAligned',
    width: 130,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.previously_funded || 0);
    }
  },
  {
    headerName: 'This Period',
    field: 'current_request',
    type: 'rightAligned',
    width: 100,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.current_request || 0);
    }
  },
  {
    headerName: 'Stored Materials',
    field: 'materials_presently_stored',
    type: 'rightAligned',
    width: 110,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.materials_presently_stored || 0);
    }
  },
  {
    headerName: 'Total Completed and Stored',
    field: 'total_completed_to_date',
    type: 'rightAligned',
    width: 140,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.total_completed_to_date || 0);
    }
  },
  {
    headerName: '% Complete',
    field: 'total_completed_to_date_pct',
    type: 'rightAligned',
    width: 80,
    cellRenderer: params => {
      // 🔥 FIX: Show "-" for Unknown rows
      const description = params.data?.description || '';
      if (description === 'Unknown') {
        return '-';
      }
      const value = parseFloat(params.value || 0);
      return Math.round(value) + '%';
    },
    valueGetter: params => {
      return parseFloat(params.data?.total_completed_to_date_pct || 0);
    }
  },
  {
    headerName: 'Balance to Finish',
    field: 'balance_to_complete',
    type: 'rightAligned',
    width: 120,
    cellRenderer: params => {
      // 🔥 USE PLACEHOLDER-AWARE FORMATTER
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-'; // Show dash for placeholder/Unknown rows
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      return parseFloat(params.data?.balance_to_complete || 0);
    }
  },
  {
    headerName: 'Retainage $',
    field: 'current_retainage_held',
    type: 'rightAligned',
    width: 120,
    cellRenderer: params => {
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-';
      }
      return currencyFormat(params.value || 0, 0);
    },
    valueGetter: params => {
      // Read actual retainage value from DB
      return parseFloat(params.data?.current_retainage_held || params.data?.retainage || 0);
    }
  },
  {
    headerName: 'Retainage %',
    field: 'retainage_percent',
    type: 'rightAligned',
    width: 90,
    cellRenderer: params => {
      const description = params.data?.description || '';
      if (description.match(/^\(Row \d+\)$/) || description === 'Unknown') {
        return '-';
      }
      const value = parseFloat(params.value || 0);
      return Math.round(value) + '%';
    },
    valueGetter: params => {
      // Calculate: Retainage $ / Total Completed to Date * 100
      const retainage = parseFloat(params.data?.current_retainage_held || params.data?.retainage || 0);
      const totalCompleted = parseFloat(params.data?.total_completed_and_stored_to_date || params.data?.total_completed_to_date || 0);
      if (totalCompleted === 0) return 0;
      return (retainage / totalCompleted) * 100;
    }
  }
];


let leafParentData = ref([]);
let leafParentDataCtr = ref(0)
let pinnedBottomRowData = ref([])

function calculateGrandTotal() {
  // Check if leafRowData is empty or not defined
  if (!leafRowData.value || leafRowData.value.length === 0) return;

  // Initialize the grand total object
  const grandTotal = initializeGrandTotal();

  // Calculate sums and collect values for variance and percent complete
  const { varianceValues, percentCompleteValues } = calculateSums(grandTotal);

  // Calculate the median for variance and percent complete
  const medianVariance = median(varianceValues);
  const medianPercentComplete = median(percentCompleteValues);

  // Update the grand total with median values
  grandTotal["variance"] = medianVariance;
  grandTotal["percent_complete"] = medianPercentComplete;

  // TODO: remove this once we know the values that  causing the median not to get it properly
  grandTotal["variance"] = null;
  grandTotal["percent_complete"] = null;

  // Set the grand total row data
  pinnedBottomRowData = [grandTotal];
}

function initializeGrandTotal() {
  // Initialize an empty object to hold the grand total values
  const grandTotal = {};

  // Iterate over each column definition in the columnDefs array
  columnDefs.forEach((colDef) => {
    // Check if the column definition has children (sub-columns)
    if (colDef.children) {
      // Iterate over each child column definition
      colDef.children.forEach((child) => {
        // Check if the child column definition has a field property
        if (child.field) {
          // Initialize the grand total for this field to null
          // This sets up a placeholder for the total value of this field
          grandTotal[child.field] = null;
        }
      });
    }
  });

  // Return the initialized grand total object
  return grandTotal;
}

function calculateSums(grandTotal) {
  const varianceValues = [];
  const percentCompleteValues = [];

  // Iterate over each row in leafRowData
  leafRowData.value.forEach((row) => {
    columnDefs.forEach((colDef) => {
      if (colDef.children) {
        colDef.children.forEach((child) => {
          if (child.field && !isNaN(parseFloat(row[child.field]))) {
            // Sum the values for each field
            grandTotal[child.field] += parseFloat(row[child.field]);
            // Collect values for variance and percent complete
            if (child.field === "variance") {
              varianceValues.push(parseFloat(row[child.field]));
            }
            if (child.field === "percent_complete") {
              percentCompleteValues.push(parseFloat(row[child.field]));
            }
          }
        });
      }
    });
  });

  return { varianceValues, percentCompleteValues };
}

// API Calls
const apiBase =
  process.env.VUE_APP_ENV_API_BASE || "/api/v1/";
const authStore = useAuthStore();
const errorStore = useErrorStore();
const dropdownStore = useDropdownStore();
const didaiStore = useDidaiStore();
let authToken = ref({});

let isNoDataFetch = ref(false);
let isGridDataLoaded = ref(false);

let branchGridData = {}
let leafParentNodesToData = {}

let isDataLoaded = computed(() => {
  return isGridDataLoaded.value || isDrawDetailGridDataLoaded.value || isG703GridDataLoaded.value
})

// Checking for project information
let projectInformation = ref({})

async function fetchProjectInformation() {
  return new Promise((resolve) => {
    axiosQuery(
      `${apiBase}projects/${selectedId.value}`,
      authToken,
      (data) => {
        projectInformation.value = data
        useTypeTableData.value = data.project_building_uses
        resolve(projectInformation.value.id)
      },
      errorStore,
      'Error fetching project_id' + selectedId.value
    );
  })
}

const projectName = computed(() => {
  return projectInformation.value?.name || 'Loading...';
});

const fullAddress = computed(() => {
  if (!projectInformation.value) return 'Loading...';
  const address1 = projectInformation.value.address_line_1 || '';
  const address2 = projectInformation.value.address_line_2 || '';
  const fullAddress = `${address1} ${address2}`.trim();
  return fullAddress || 'Address not available';
});

const primaryProjectImage = computed(() => {
  // Find the primary uploaded image first
  const uploadedPrimary = projectImages.value.find(img => img.isPrimary);
  if (uploadedPrimary) {
    return uploadedPrimary.url;
  }
  
  // Fall back to first uploaded image
  const firstUploaded = projectImages.value[0];
  if (firstUploaded) {
    return firstUploaded.url;
  }
  
  // No images at all - return null
  return null;
});

function getDisplayValue(projectInfo) {
  return projectInfo ? projectInfo : 'N/A';
}

function totalGsf() {
  if (!useTypeTableData.value || useTypeTableData.value.length === 0) {
    return projectInformation.value?.total_gsf ? projectInformation.value.total_gsf : 0;
  }
  // Sum all GSF from building use types, parking types, AND affordable components
  return useTypeTableData.value.reduce((acc, row) => {
    return acc + (row.total_gsf || 0) + (row.affordable_comp_gsf || 0);
  }, 0);
}

function totalUnits() {
  if (!useTypeTableData.value || useTypeTableData.value.length === 0) {
    // Fall back to project's total_num_units if no building use data exists
    return projectInformation.value?.total_num_units || 0;
  }
  // Sum ONLY num_units (building types) and affordable component units
  // EXCLUDE num_spaces (parking types) from the grand total
  return useTypeTableData.value.reduce((acc, row) => {
    return acc + (row.num_units || 0) + (row.affordable_comp_num_units || 0);
  }, 0);
}

function subTotalBuildingGSF() {
  if (!useTypeTableData.value) return 0;
  return useTypeTableData.value
    .filter(row => row.codeable_type === CODEABLE_TYPES.BUILDING_USE_TYPE)
    .reduce((acc, row) => acc + (row.total_gsf || 0) + (row.affordable_comp_gsf || 0), 0);
}

function subTotalParkingGSF() {
  if (!useTypeTableData.value) return 0;
  return useTypeTableData.value
    .filter(row => row.codeable_type === CODEABLE_TYPES.PARKING_TYPE)
    .reduce((acc, row) => acc + (row.total_gsf || 0) + (row.affordable_comp_gsf || 0), 0);
}

function bohOtherGSF() { 
  if (!useTypeTableData.value) return 'N/A';
  const computedbohOtherGsf = totalGsf() - subTotalBuildingGSF() - subTotalParkingGSF();
  return computedbohOtherGsf > 0 ? numberFormat(computedbohOtherGsf) : 'N/A';
}

function totalAffordableComponent() {
  if (!useTypeTableData.value) return 0;
  return useTypeTableData.value.reduce((acc, row) => acc + (row.affordable_comp_gsf || 0), 0);
}

function getProjectConstructionContract() {
  const projConstructionTypes = projectInformation.value?.project_construction_types_full || [];
  // Find 'ContractType' from construction types objecs
  const contractTypeObject = projConstructionTypes.find(type => type?.codeable_type === CODES.CONTRACT_TYPE);
  return contractTypeObject ? contractTypeObject.codeable_id_s : 'N/A';
}

function getFormerUseType() {
  const projConstructionTypes = projectInformation.value?.project_construction_types_full || [];
  // Find 'ConstructionType' from construction types objecs
  const constructionTypeObject = projConstructionTypes.find(type => type?.codeable_type === CODES.CONSTRUCTION_TYPE);
  return constructionTypeObject?.former_asset_category_id_s ? constructionTypeObject.former_asset_category_id_s : 'N/A';
}

function getAllBrandNames() {
  if (!useTypeTableData.value) return 'N/A';
  
  const brandNames = useTypeTableData.value
    .filter(row => row.brand_type_id_s !== null)
    .map(row => row.brand_type_id_s);

  return brandNames.length > 0 ? brandNames.join('; ') : 'N/A';
}

function classifyBuilding(height) {
  if (height >= 0 && height <= 70) {
    return BUILDING_CLASIFICATION.LOW_RISE;
  } else if (height >= 71 && height <= 280) {
    return BUILDING_CLASIFICATION.MID_RISE;
  } else if (height >= 281 && height <= 983) {
    return BUILDING_CLASIFICATION.HIGH_RISE;
  } else if (height >= 984 && height <= 1967) {
    return BUILDING_CLASIFICATION.SUPER_TALL;
  } else if (height >= 1968) {
    return BUILDING_CLASIFICATION.MEGA_TALL;
  } else {
    return 'N/A';
  }
}

const leafRowData = ref([]);

// fetch GridData first - this is for the hidden ag-grid data use to build the chart data
function fetchGridData() {
  if (selectedId.value) {
    // 🔥 FIX: Use the same working endpoint as cost curve instead of broken MVW logic
    axiosQuery(
      `${apiBase}projects/fetch_project_draw_details?project_ids=${selectedId.value}`,
      authToken,
      (data) => {
        // 🔥 MINIMAL FIX: Handle new API format
        let processData = data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          if (data.draw_details && Array.isArray(data.draw_details)) {
            processData = data.draw_details;
          } else {
            processData = [];
          }
        }
        
        if (processData.length === 0) {
          isNoDataFetch.value = true
        } else {
          branchGridData = {}
          leafParentNodesToData = {}
          let leafParentDataTemp = []
          processData?.forEach((row) => {
            // Map DrawDetail data to grid format (with proper DDA term mapping)
            const aRow = {
              dda_term_id: row.dda_term_id || null,
              dda_term: row.dda_term || null,
              dda_term_name: row.dda_term_name || null,
              hiddenHierarchy: [row.description || 'Unknown'], // Simple flat hierarchy for now
              itemHierarchy: [row.description || 'Unknown'],
              scope_detail: row.description,
              description: row.description || 'Unknown',
              contractor_name: row.contractor_name || row.company,
              company: row.contractor_name || row.company,
              original_budget: (row.original_budget || 0) / viewModeDivisor.value,
              total_adjustments:
                (parseFloat(row.current_budget || 0) -
                parseFloat(row.original_budget || 0)) / viewModeDivisor.value,
              current_budget: (row.current_budget || 0) / viewModeDivisor.value,
              variance: 
                row.original_budget > 0 ? 
                (((row.current_budget || 0) - (row.original_budget || 0)) / row.original_budget) : 0,
              draw_summary: row.current_request || 0,
              total_spent: (row.current_request || 0) / viewModeDivisor.value,
              percent_complete: row.current_budget > 0 ? ((row.current_request || 0) / row.current_budget) : 0,
              previously_funded: 0, // DrawDetails don't track this separately
              total_completed_to_date: (row.current_request || 0) / viewModeDivisor.value,
            }
            // Add this row everywhere in the hierarchy
            aRow.hiddenHierarchy.forEach((term, idx) => {
              branchGridData[term] ||= []
              branchGridData[term].push(aRow)
              // Add leaf parent data -- ie the node just above a leaf node (2nd to the bottom)
              if (aRow.hiddenHierarchy.length - 2 === idx) {
                leafParentNodesToData[aRow.hiddenHierarchy[idx]] ||= []
                leafParentNodesToData[aRow.hiddenHierarchy[idx]].push(aRow)
              }
            })

            leafRowData.value.push(aRow);
          })

          // Do the summation and other aggregate math on the leaf parent nodes as AG Grid will not call this automatically
          Object.keys(leafParentNodesToData).forEach(leafParentTerm => {
            // Remove the leaf node as we don't show these.
            // Ensure we clone the objects so we don't destroy the hierarchy for other queries
            let aRow = { ...leafParentNodesToData[leafParentTerm][0] }
            aRow.hiddenHierarchy = [...aRow.hiddenHierarchy]
            aRow.hiddenHierarchy.pop()
            aRow.itemHierarchy = [...aRow.itemHierarchy]
            aRow.itemHierarchy.pop()
            // Calculate subtotals at the non-leaf node level
            sumAggregateCols.forEach(colId => {
              aRow[colId] = sumAggregate({
                rowNode: { key: leafParentTerm },
                column: { colId: colId }
              })
            })
            aRow.variance = varianceAggregate({
              rowNode: { key: leafParentTerm },
              column: { colId: 'variance' }
            })
            aRow.percent_complete = simplePercentCompleteAggregate({
              rowNode: { key: leafParentTerm },
              column: { colId: 'percent_complete' }
            })

            leafParentDataTemp.push(aRow)
          })
          leafParentData.value = leafParentDataTemp
          leafParentDataCtr.value++
          
          // 🔥 FORCE: Always update chart from API data
          console.log(`🎯 FORCE: Updating chart with ${processData ? processData.length : 0} API rows for draw ${drawNumber.value}`);
          buildChartDataFromDDATermTotals(processData || []);
          
          isNoDataFetch.value = false
          calculateGrandTotal();
        }
      },
      errorStore
    );
  } else {
    isNoDataFetch.value = true
  }
  isGridDataLoaded.value = true
}

let hcOriginalBudgetArr = ref([]);
let hcRevisedBudgetArr = ref([]);
let hcBudgetVarianceArr = ref([]);
let hcBudgetIncPercArr = ref([]);
let scOriginalBudgetArr = ref([]);
let scRevisedBudgetArr = ref([]);
let scBudgetVarianceArr = ref([]);
let scBudgetIncPercArr = ref([]);
let hcTotalOriginalBudget = ref(0);
let hcTotalRevisedBudget = ref(0);
let scTotalOriginalBudget = ref(0);
let scTotalRevisedBudget = ref(0);
let miscFinancingTotalOriginalBudget = ref(0);
let miscFinancingTotalRevisedBudget = ref(0);
let lcTotalOriginalBudget = ref(0);
let lcTotalRevisedBudget = ref(0);
let contingenciesTotalOriginalBudget = ref(0);
let contingenciesTotalRevisedBudget = ref(0);
let lcTotalVariance = ref(0);
let hcTotalVariance = ref(0);
let scTotalVariance = ref(0);
let miscFinancingTotalVariance = ref(0);
let contingenciesTotalVariance = ref(0);
let lcVariancePercent = ref(0);
let hcVariancePercent = ref(0);
let scVariancePercent = ref(0);
let miscFinancingVariancePercent = ref(0);
let contingenciesVariancePercent = ref(0);

// 🔥 NEW: Build chart data from DDA term total rows
function buildChartDataFromDDATermTotals(drawData) {
  // Get the selected draw number
  const selectedDrawNum = parseInt(drawNumber.value) || 0;
  
  // Get all rows for this draw
  const allRowsThisDraw = drawData.filter(row => {
    const rowDrawNum = parseInt(row.draw_number) || 0;
    return rowDrawNum === selectedDrawNum;
  });
  
  const totalRowsThisDraw = allRowsThisDraw.filter(row => row.is_total_row === true);
  
  // Find the row that has been SET as the Project Total DDA term for this draw
  const projectTotalRow = drawData.find(row => {
    if (!row.is_total_row) return false;
    
    // Check if this row is for the selected draw
    const rowDrawNum = parseInt(row.draw_number) || 0;
    if (rowDrawNum !== selectedDrawNum) return false;
    
    // Check if this row has been assigned the Project Total DDA term
    const termName = (row.dda_term?.term || row.dda_term_name || '').toLowerCase();
    return termName.includes('project total');
  });
  
  if (projectTotalRow) {
    // 🎯 APPLY VIEW MODE DIVISOR (for per GSF / per Unit views)
    const divisor = viewModeDivisor.value || 1;
    cdTotalCurrentBudget.value = parseFloat(projectTotalRow.current_budget || 0) / divisor;
    cdTotalOriginalBudget.value = parseFloat(projectTotalRow.original_budget || 0) / divisor;
    cdTotalBilledToDate.value = parseFloat(projectTotalRow.total_completed_to_date || 0) / divisor;
  } else {
    cdTotalCurrentBudget.value = 0;
    cdTotalOriginalBudget.value = 0;
    cdTotalBilledToDate.value = 0;
  }
}

function setChartData(event) {
  let chartData = event;

  // Group data by DDA term categories
  let acquisitionGroupData = null;
  let hcGroupData = null; 
  let scGroupData = null;
  let financingGroupData = null;
  let contingenciesGroupData = null;

  // Find data by DDA term category or compatible keys
  chartData.forEach(row => {
    const key = row.key || '';
    if (key.includes('ACQUISITION') || key.includes('Acquisition')) {
      acquisitionGroupData = row;
    } else if (key.includes('HARD') || key.includes('Hard')) {
      hcGroupData = row;
    } else if (key.includes('SOFT') || key.includes('Soft')) {
      scGroupData = row;
    } else if (key.includes('FINANCING') || key.includes('Financing')) {
      financingGroupData = row;
    } else if (key.includes('CONTINGENCIES') || key.includes('Contingencies') || key.includes('Owner Contingencies')) {
      contingenciesGroupData = row;
    }
  });

  // TODO : Will the aggregated data ever be NaN / Null
  lcTotalRevisedBudget.value = acquisitionGroupData ? acquisitionGroupData['aggData']['current_budget'] : 0
  hcTotalRevisedBudget.value = hcGroupData ? hcGroupData['aggData']['current_budget'] : 0
  scTotalRevisedBudget.value = scGroupData ? scGroupData['aggData']['current_budget'] : 0
  miscFinancingTotalRevisedBudget.value = financingGroupData ? financingGroupData['aggData']['current_budget'] : 0
  contingenciesTotalRevisedBudget.value = contingenciesGroupData ? contingenciesGroupData['aggData']['current_budget'] : 0

  lcTotalOriginalBudget.value = acquisitionGroupData ? acquisitionGroupData['aggData']['original_budget'] : 0
  hcTotalOriginalBudget.value = hcGroupData ? hcGroupData['aggData']['original_budget'] : 0
  scTotalOriginalBudget.value = scGroupData ? scGroupData['aggData']['original_budget'] : 0
  miscFinancingTotalOriginalBudget.value = financingGroupData ? financingGroupData['aggData']['original_budget'] : 0
  contingenciesTotalOriginalBudget.value = contingenciesGroupData ? contingenciesGroupData['aggData']['original_budget'] : 0

  lcTotalVariance.value = acquisitionGroupData ? acquisitionGroupData['aggData']['total_adjustment'] : 0
  hcTotalVariance.value = hcGroupData ? hcGroupData['aggData']['total_adjustments'] : 0
  scTotalVariance.value = scGroupData ? scGroupData['aggData']['total_adjustments'] : 0
  miscFinancingTotalVariance.value = financingGroupData ? financingGroupData['aggData']['total_adjustments'] : 0
  contingenciesTotalVariance.value = contingenciesGroupData ? contingenciesGroupData['aggData']['total_adjustments'] : 0

  lcVariancePercent.value = acquisitionGroupData ? acquisitionGroupData['aggData']['variance'] : 0
  hcVariancePercent.value = hcGroupData ? hcGroupData['aggData']['variance'] : 0
  scVariancePercent.value = scGroupData ? scGroupData['aggData']['variance'] : 0
  miscFinancingVariancePercent.value = financingGroupData ? financingGroupData['aggData']['variance'] : 0
  contingenciesVariancePercent.value = contingenciesGroupData ? contingenciesGroupData['aggData']['variance'] : 0

  // Calculate per view mode
  // Default : View by total
  hcAvgOriginalCostPerGSF.value = hcTotalOriginalBudget.value / ( totalGsf() || 1)
  hcAvgRevisedCostPerGSF.value = hcTotalRevisedBudget.value / (totalGsf() || 1)
  scAvgOriginalCostPerGSF.value = scTotalOriginalBudget.value / (totalGsf() || 1)
  scAvgRevisedCostPerGSF.value = scTotalRevisedBudget.value / (totalGsf() || 1)

  if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) {
    hcAvgOriginalCostPerGSF.value = hcTotalOriginalBudget.value
    hcAvgRevisedCostPerGSF.value = hcTotalRevisedBudget.value
    scAvgOriginalCostPerGSF.value = scTotalOriginalBudget.value 
    scAvgRevisedCostPerGSF.value = scTotalRevisedBudget.value
  }
  else if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) {
    hcAvgOriginalCostPerGSF.value = (hcTotalOriginalBudget.value * totalUnits()) / ( totalGsf() || 1)
    hcAvgRevisedCostPerGSF.value = (hcTotalRevisedBudget.value * totalUnits()) / ( totalGsf() || 1)
    scAvgOriginalCostPerGSF.value = (scTotalOriginalBudget.value * totalUnits()) / ( totalGsf() || 1)
    scAvgRevisedCostPerGSF.value = (scTotalRevisedBudget.value * totalUnits()) / ( totalGsf() || 1)
  }

  // 🔥 CRITICAL FIX: Use the actual chart data instead of old category totals
  const acquisitionAmount = acquisitionGroupData ? acquisitionGroupData.aggData.current_budget : 0;
  const hardCostsAmount = hcGroupData ? hcGroupData.aggData.current_budget : 0; 
  const softCostsAmount = scGroupData ? scGroupData.aggData.current_budget : 0;
  const financingAmount = financingGroupData ? financingGroupData.aggData.current_budget : 0;
  const contingenciesAmount = contingenciesGroupData ? contingenciesGroupData.aggData.current_budget : 0;
  
  console.log('🎯 DOUGHNUT CHART AMOUNTS:', {
    acquisition: acquisitionAmount,
    hardCosts: hardCostsAmount, 
    softCosts: softCostsAmount,
    financing: financingAmount,
    contingencies: contingenciesAmount
  });

  doughnutChartData.value = [
    acquisitionAmount,
    hardCostsAmount,
    softCostsAmount,
    financingAmount,
    contingenciesAmount
  ]

  const doughnutChartDataArray = [
    acquisitionAmount,
    hardCostsAmount,
    softCostsAmount,
    financingAmount,
    contingenciesAmount
  ]

  // 🔥 DISABLED: Project Total values now set by buildChartDataFromDDATermTotals() 
  // This old logic was overriding the correct values from buildChartDataFromDDATermTotals
  console.log('🔥 BAR CHART USING PROJECT TOTAL: {plannedBudget: ' + cdTotalOriginalBudget.value + ', currentBudget: ' + cdTotalCurrentBudget.value + ', completedToDate: ' + cdTotalBilledToDate.value + '}');

  // Loop through the dougnutChartData
  const totalSumOfDoughnut = doughnutChartDataArray.reduce((a, b) => { return a + b; }).toFixed(0)
  doughnutChartPercentage.value = []
  doughnutChartDataArray.forEach(val => {
    const percentage = val * 100 / totalSumOfDoughnut;
    const roundedPercentage = Math.round(percentage * 100) / 100
    doughnutChartPercentage.value.push(`${roundedPercentage.toFixed(0)}%`);
  });

  console.log("💰 Budget calculated:", {
    cdTotalCurrentBudget: cdTotalCurrentBudget.value,
    lcTotal: lcTotalRevisedBudget.value,
    hcTotal: hcTotalRevisedBudget.value,
    scTotal: scTotalRevisedBudget.value,
    rows: drawDetailRowData.value?.length || 0
  });
  // Update chart configurations with real data
  updateCostCurveData();
  updateCostsToDateData();
  updateBudgetVarianceData();

  // Create a copy of the leaf children
  const hcGroupDataLeafChildren = hcGroupData?.allLeafChildren ?? [];
  const scGroupDataLeafChildren = scGroupData?.allLeafChildren ?? [];

  // Sort the values base on total-adjustment to get the top offenders in decreasing order
  const sortedHCGroupData = hcGroupDataLeafChildren.sort((a, b) => b.data.total_adjustments - a.data.total_adjustments);
  const sortedSCGroupData = scGroupDataLeafChildren.sort((a, b) => b.data.total_adjustments - a.data.total_adjustments);

  // Get the top 5 but remove the ones if they include a key that should not be there
  const top5DataHC = [];
  const top5DataSC = [];

  // Loop through the sorted array for hard cost
  for (const obj of sortedHCGroupData) {
    // Check if the key property contains the string "Trade" *and* ensure it is a leaf node/almost leaf node
    if (obj.allLeafChildren?.length < 2 && !obj.key.toLowerCase().includes("trade")) {
      // If not, push the object to the top5DataHC array
      top5DataHC.push(obj);
    }

    // If top5DataHC array has 5 elements, break the loop
    if (top5DataHC.length === 5) {
      break;
    }
  }

  // Loop through the sorted array for soft cost
  for (const obj of sortedSCGroupData) {
    // Check if the key property contains the string "Carrying" and "Land Lease" *and* ensure it is a leaf node/almost leaf node
    if (obj.allLeafChildren?.length < 2 && !obj.key.toLowerCase().includes("carrying") && !obj.key.toLowerCase().includes("land lease")) {
      // If not, push the object to the top5DataSC array
      top5DataSC.push(obj);
    }

    // If top5DataSC array has 5 elements, break the loop
    if (top5DataSC.length === 5) {
      break;
    }
  }

  // HCTopOffenders
  // Build the chart data
  hcStackedBarLabels.value = [];
  hcOriginalBudgetArr.value = [];
  hcRevisedBudgetArr.value = [];
  hcBudgetVarianceArr.value = [];
  hcBudgetIncPercArr.value = [];
  top5DataHC.forEach((row) => {
    const variance = row.data.current_budget - row.data.original_budget;
    if (variance > 0) { // Exclude negative and zero values
      hcStackedBarLabels.value.push(row.key);
      hcOriginalBudgetArr.value.push(row.data.original_budget);
      hcRevisedBudgetArr.value.push(row.data.current_budget);
      hcBudgetVarianceArr.value.push(variance);
      hcBudgetIncPercArr.value.push(row.data.total_adjustments);
    }
  });
  
  // SCTopOffenders
  // Build the chart data
  scStackedBarLabels.value = [];
  scOriginalBudgetArr.value = [];
  scRevisedBudgetArr.value = [];
  scBudgetVarianceArr.value = [];
  scBudgetIncPercArr.value = [];
  top5DataSC.forEach((row) => {
    const variance = row.data.current_budget - row.data.original_budget;
    if (variance > 0) { // Exclude negative and zero values
      scStackedBarLabels.value.push(row.key);
      scOriginalBudgetArr.value.push(row.data.original_budget);
      scRevisedBudgetArr.value.push(row.data.current_budget);
      scBudgetVarianceArr.value.push(variance);
      scBudgetIncPercArr.value.push(row.data.total_adjustments);
    }
  });
}
// Save the sum aggregates so they can be used to calculate variance
let sumAggregates = {}
function sumAggregate(params) {
  // Go through the full sum of each child item, then divide by # of projects
  let otherTotal = 0.0;
  (branchGridData[params.rowNode.key] || []).forEach(value => otherTotal += parseFloat(value[params.column.colId] || 0.0))
  // As opposed to the Budget Page, we do *not* divide by the number of projects in the compSet, because this is a single item

  sumAggregates[params.column.colId] ||= {}
  sumAggregates[params.column.colId][params.rowNode.key] = otherTotal

  return otherTotal
}

// Uses the sum aggregates so they can be used to calculate variance
// NOTE: this only works because the sum aggregate columns are calculated first!
function varianceAggregate(params) {
  const originalBudget = sumAggregates['original_budget'][params.rowNode.key]
  const currentBudget = sumAggregates['current_budget'][params.rowNode.key]

  return ((currentBudget || 0.0) - (originalBudget || 0.0)) / (parseFloat(originalBudget) || 1.0)
}

// Uses the sum aggregates so they can be used to calculate % complete
// NOTE: this only works because the sum aggregate columns are calculated first!
function pctCompleteAggregate(params) {
  const currentBudget = sumAggregates['current_budget'][params.rowNode.key]
  const totalCompletedToDate = sumAggregates['total_completed_to_date'][params.rowNode.key]

  return (totalCompletedToDate || 0.0) / (parseFloat(currentBudget) || 1.0)
}

// Simple percent complete calculation: current budget / original budget
function simplePercentCompleteAggregate(params) {
  const originalBudget = sumAggregates['original_budget'][params.rowNode.key]
  const currentBudget = sumAggregates['current_budget'][params.rowNode.key]

  return (currentBudget || 0.0) / (parseFloat(originalBudget) || 1.0)
}

let isSidebarOpen = ref(true)
function toggleSidebar() {
  isSidebarOpen.value = !isSidebarOpen.value
}

let largeSidebar = ref(false)
function onResizeSidebar() {
  largeSidebar.value = !largeSidebar.value
}

const selectedId = ref(null);
const route = useRoute();
const router = useRouter();

// 🛡️ COMPONENT LIFECYCLE: Track if component is still mounted to prevent async operations after destroy
let isComponentMounted = true;
onBeforeUnmount(() => {
  isComponentMounted = false;
  console.log('🛡️ ProjectSummary: Component unmounting, stopping async operations');
});

// Navigation function for breadcrumb
function navigateToPortfolio() {
  router.push('/portfolio');
}

const propertyImageContainer = ref()
const hasPropertyImage = ref(null)
async function fetchImage() {
  return new Promise((resolve) => {
    axiosQuery(
        `${apiBase}projects/${selectedId.value}/images?use_thumbnail=false`,
        authToken,
        (data) => {
          if (data && !data.error_message) {
            const img = document.createElement('img')
            projectInformation.value.image_url = data.url;
            // NOTE that png and webp work with this spec as well
            img.src = "" + data.url + ""
            img.style = "width: 66%; height: auto;"
            // Safety check: ensure container and children exist
            if (propertyImageContainer.value && propertyImageContainer.value.children && propertyImageContainer.value.children[0]) {
              propertyImageContainer.value.children[0].appendChild(img)
            }
            hasPropertyImage.value = true
            resolve(selectedId.value)
          } else {
            hasPropertyImage.value = false
            resolve(selectedId.value)
          }
          resolve(selectedId.value)
        },
        errorStore,
        'Error fetching project_id image' + selectedId.value
    );
  })
}

async function loadDropdownData() {
  console.log('📋 Loading dropdown data for edit modal...');
  
  try {
    // Load construction types directly (no auth)
    const constructionTypesResponse = await axios.get(`${apiBase}codes?type=ConstructionType`);
    editModalDropdownData.value.constructionTypes = constructionTypesResponse.data || [];
    
    // Load contract types
    const contractTypesResponse = await axios.get(`${apiBase}codes?type=ContractType`);
    editModalDropdownData.value.contractTypes = contractTypesResponse.data || [];
    
    // Load sustainability types
    const sustainabilityTypesResponse = await axios.get(`${apiBase}codes?type=SustainabilityType`);
    editModalDropdownData.value.sustainabilityTypes = sustainabilityTypesResponse.data || [];
    
    // Load amenity types
    const amenityTypesResponse = await axios.get(`${apiBase}codes?type=AmenityType`);
    editModalDropdownData.value.amenityTypes = amenityTypesResponse.data || [];
    
    // Load asset categories
    const assetCategoriesResponse = await axios.get(`${apiBase}codes?type=AssetCategory`);
    editModalDropdownData.value.assetCategories = assetCategoriesResponse.data || [];
    
    // Load finish levels
    const finishLevelsResponse = await axios.get(`${apiBase}codes?type=FinishLevel`);
    editModalDropdownData.value.finishLevels = finishLevelsResponse.data || [];
    
    // Load building use types
    const buildingUseTypesResponse = await axios.get(`${apiBase}codes?type=BuildingUseType`);
    editModalDropdownData.value.buildingUseTypes = buildingUseTypesResponse.data || [];
    
    // Load parking types
    const parkingTypesResponse = await axios.get(`${apiBase}codes?type=ParkingType`);
    editModalDropdownData.value.parkingTypes = parkingTypesResponse.data || [];
    
    console.log('✅ Dropdown data loaded:', {
      constructionTypes: editModalDropdownData.value.constructionTypes.length,
      contractTypes: editModalDropdownData.value.contractTypes.length,
      sustainabilityTypes: editModalDropdownData.value.sustainabilityTypes.length,
      amenityTypes: editModalDropdownData.value.amenityTypes.length,
      assetCategories: editModalDropdownData.value.assetCategories.length,
      finishLevels: editModalDropdownData.value.finishLevels.length,
      buildingUseTypes: editModalDropdownData.value.buildingUseTypes.length,
      parkingTypes: editModalDropdownData.value.parkingTypes.length
    });
  } catch (error) {
    console.error('❌ Error loading dropdown data:', error);
  }
}

const hasLoaded = ref(false)
// Progressive loading states for better UX
const loadingPhase = ref('Initializing...')
const isLoadingPhase1 = ref(true)  // Essential data
const isLoadingPhase2 = ref(true)  // Grid data  
const isLoadingPhase3 = ref(true)  // Tab data
const isLoadingPhase4 = ref(true)  // Heavy data
const isLoadingPhase5 = ref(true)  // Charts
// NEW: Function to check and continue queue processing
function checkAndContinueQueue() {
  if (templateQueueManager.loadFromLocalStorage()) {
    // CRITICAL FIX: Get current project ID from route
    const currentProjectId = selectedId.value || route.params.id;
    
    // CRITICAL FIX: If queue doesn't have projectId, set it now
    if (!templateQueueManager.projectId && currentProjectId) {
      console.log('🔧 Queue missing projectId, setting it now:', currentProjectId);
      templateQueueManager.projectId = currentProjectId;
      templateQueueManager.saveToLocalStorage();
    }
    
    const queueState = templateQueueManager.getState();
    console.log('📦 Queue detected:', queueState);
    console.log('🔍 Project ID comparison:', { queueProjectId: templateQueueManager.projectId, selectedId: selectedId.value, currentProjectId });
    
    if (queueState.hasNext && currentProjectId) {
      console.log('🔄 Continuing queue processing...');
      isProcessingQueue.value = true;
      // Small delay to let UI render
      setTimeout(() => {
        processNextInQueue();
      }, 500);
      return true; // Queue is being processed
    } else if (queueState.isComplete) {
      console.log('🎉 Queue completed, showing notification');
      showToastNotification('Draw is Processing', 'success', 3000);
      templateQueueManager.clearLocalStorage();
      return false;
    } else {
      console.warn('⚠️ Queue not continued:', { hasNext: queueState.hasNext, hasProjectId: !!currentProjectId });
      return false;
    }
  }
  return false;
}

// Watch for project information changes to sync the contingency checkbox
watch(() => projectInformation.value, (newProject) => {
  if (newProject && newProject.contingencies_excluded_from_budget !== undefined) {
    contingenciesExcludedFromBudget.value = newProject.contingencies_excluded_from_budget || false;
    console.log(`📊 Synced contingency setting from project: ${contingenciesExcludedFromBudget.value}`);
  }
});

onMounted(async () => {
  console.log('🚀 ProjectSummary Phase 1: Progressive Loading Started');
  const startTime = performance.now();
  
  authToken.value = authStore.getAuthTokenHeader;
  selectedId.value = route.params.selectedId;
  ddaTermCategories.value = dropdownStore.ddaTermCategories || [];

  // NEW: Check if we need to continue processing queue
  const queueIsActive = checkAndContinueQueue();
  
  // If queue is active, skip normal loading
  if (queueIsActive) {
    console.log('⏭️ Skipping normal page load - queue is active');
    return;
  }

  // Check if we should auto-open upload modal (from notification)
  if (route.query.openUpload === 'true') {
    console.log('📋 Auto-opening upload modal from notification');
    // Set draw number if provided
    if (route.query.drawNumber) {
      globalDrawFilter.value = parseInt(route.query.drawNumber);
    }
    // Wait for page to load then open modal
    setTimeout(() => {
      openUploadModal();
    }, 1000);
  }

  // 🎯 PHASE 1: Load essential data first (show UI immediately)
  console.log('📊 Phase 1: Loading essential project data...');
  loadingPhase.value = 'Loading project information...';
  
  const essentialPromises = [
    fetchProjectInformation(),
    fetchImage(),
    fetchDrawNumber(),
    loadProjectImages() // 📷 Load carousel images
  ];
  
  try {
    await Promise.all(essentialPromises);
    console.log(`✅ Phase 1 complete: ${Math.round(performance.now() - startTime)}ms`);
    
    // Show basic UI immediately - user can see project info while rest loads
    isLoadingPhase1.value = false;
    hasLoaded.value = true;
    loadingPhase.value = 'Loading grid data...';
    
  } catch (error) {
    console.error('❌ Phase 1 failed:', error);
    isLoadingPhase1.value = false;
    hasLoaded.value = true; // Show UI anyway
    loadingPhase.value = 'Loading with errors...';
  }

  // 🎯 PHASE 2: Load primary grid data (parallel with other non-dependent calls)
  console.log('📋 Phase 2: Loading grid and draw data...');
  const phase2StartTime = performance.now();
  
  const gridPromises = [
    fetchGridData(), // Hidden Grid
    fetchDrawDetailGridData(), // Draw Details tab (default/most common)
    // 🚀 REMOVED: fetchG703DrawNumber() - will load when G703 tab is clicked (lazy loading)
    fetchAvailableTemplates(),
    loadProjectImages()
  ];
  
  // Start these in parallel but don't block UI
  Promise.all(gridPromises).then(() => {
    console.log(`✅ Phase 2 complete: ${Math.round(performance.now() - phase2StartTime)}ms`);
    isLoadingPhase2.value = false;
    loadingPhase.value = 'Loading tab data...';
  }).catch(error => {
    console.error('❌ Phase 2 failed:', error);
    isLoadingPhase2.value = false;
  });

  // 🎯 PHASE 3: Load G702 application numbers (needed for dropdown)
  console.log('📑 Phase 3: Loading G702 application numbers...');
  const phase3StartTime = performance.now();
  
  // Load G702 app numbers (uses cache if available - see fetchG702DrawNumber)
  setG702DrawNumber();
  fetchG702DrawNumber().then(() => {
    console.log(`✅ Phase 3 complete: ${Math.round(performance.now() - phase3StartTime)}ms`);
    isLoadingPhase3.value = false;
    loadingPhase.value = 'Loading dropdown data...';
  }).catch(error => {
    console.error('❌ Phase 3 failed:', error);
    isLoadingPhase3.value = false;
  });

  // 🎯 PHASE 4: Load heavy chart data in background (most expensive operations)
  console.log('📈 Phase 4: Loading chart data in background...');
  const phase4StartTime = performance.now();
  
  // Load contractors for editing functionality (non-blocking)
  dropdownStore.getGeneralContractors();

  // Load all the heavy data in background
  const heavyPromises = [
    loadDropdownData(),
    loadDDATermsForCharts()
  ];
  
  Promise.all(heavyPromises).then(async () => {
    console.log(`✅ Phase 4 data loading complete: ${Math.round(performance.now() - phase4StartTime)}ms`);
    isLoadingPhase4.value = false;
    loadingPhase.value = 'Processing charts...';
    
    // 🎯 PHASE 5: Process charts (most CPU-intensive)
    console.log('🧮 Phase 5: Processing cost curve charts...');
    const chartStartTime = performance.now();
    
    try {
  // Initialize cost curve chart data (independent of grid)
  await updateCostCurveData();
  
  // Now that cost curve data is loaded, calculate costs to date
  await updateCostsToDateData();

      // Force one recompute after next tick to ensure initial render matches post-filter behavior
      nextTick(() => {
        costCurveChartReady.value = true;
        // Touch computed to trigger refresh
        void buildAgChartData();
        
        isLoadingPhase5.value = false;
        loadingPhase.value = 'Complete';
        
        console.log(`✅ Phase 5 charts complete: ${Math.round(performance.now() - chartStartTime)}ms`);
        console.log(`🏁 Total ProjectSummary load time: ${Math.round(performance.now() - startTime)}ms`);
      });
      
    } catch (error) {
      console.error('❌ Phase 5 chart processing failed:', error);
      isLoadingPhase5.value = false;
      loadingPhase.value = 'Chart loading failed';
    }
    
  }).catch(error => {
    console.error('❌ Phase 4 failed:', error);
    isLoadingPhase4.value = false;
    loadingPhase.value = 'Data loading failed';
  });
});

let hcSummaryAvgOriginal = computed(() => {
  return hcAvgOriginalCostPerGSF.value
})
let hcSummaryAvgRevised = computed(() => {
  return hcAvgRevisedCostPerGSF.value
})
let scSummaryAvgOriginal = computed(() => {
  return scAvgOriginalCostPerGSF.value
})
let scSummaryAvgRevised = computed(() => {
  return scAvgRevisedCostPerGSF.value
})
let hcAvgOriginalCostPerGSF = ref(0)
let hcAvgRevisedCostPerGSF = ref(0)
let scAvgOriginalCostPerGSF = ref(0)
let scAvgRevisedCostPerGSF = ref(0)

const viewByTGU = ref(VIEW_MODES_TGU.VIEW_BY_TOTAL);
const viewModeDivisor = ref(1);
function setViewMode() {
  if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) {
    viewModeDivisor.value = totalGsf();
    doughnutViewMode.value = 'gsf'
  } else if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) {
    viewModeDivisor.value = totalUnits() !== 0 ? totalUnits() : 1;
    doughnutViewMode.value = 'unit'
  } else {
    viewModeDivisor.value = 1;
    doughnutViewMode.value = 'total'
  }
  fetchGridData();
  // Only fetch data for the currently active tab
  if (btSubMenuSelectedItemId.value === 1) {
    fetchDrawDetailGridData();
  } else if (btSubMenuSelectedItemId.value === 2) {
    fetchG703DrawDetailGridData();
  }
}

// Draw Detail settings
let isDrawDetailGridDataLoaded = ref(false)
let isG703GridDataLoaded = ref(false)
let isG702GridDataLoaded = ref(false)

let drawNumber = ref(0);
let g703DrawNumber = ref(0);
let g702DrawNumber = ref(0);
let drawNumberList = ref([]);
let g703DrawNumberList = ref([]);
let g702DrawNumberList = ref([]);
let g703SourceDocuments = ref([]);
let g702SourceDocuments = ref([]);
let drawDetailSourceDocuments = ref([]);

// Document Viewer Modal state
let documentViewerVisible = ref(false);
let documentViewerDocuments = ref([]);
let documentViewerTitle = ref('');
let documentViewerInitialIndex = ref(0);

// --- G702 draw helpers ---
function setG702DrawNumber() {
  try {
    const latest = (drawNumberList.value && drawNumberList.value.length > 0) ? drawNumberList.value[0] : 0;
    g702DrawNumber.value = globalDrawFilter?.value || drawNumber?.value || latest || 0;
  } catch (e) {
    g702DrawNumber.value = 0;
  }
}

// 🚀 CACHE for G702 application numbers (prevents refetching on every page load)
const g702AppNumbersCache = ref({});

async function fetchG702DrawNumber() {
  // 🚀 OPTIMIZATION: Check cache first (saves 116 seconds on page load!)
  const projectId = selectedId.value;
  const cacheKey = `g702_app_numbers_${projectId}`;
  
  if (g702AppNumbersCache.value[cacheKey]) {
    console.log(`✅ CACHE HIT: Using cached G702 application numbers for project ${projectId}`);
    const cached = g702AppNumbersCache.value[cacheKey];
    g702DrawNumberList.value = cached.applicationNumbers;
    if (!g702DrawNumber.value || g702DrawNumber.value === 0) {
      g702DrawNumber.value = cached.applicationNumbers[0] || 0;
    }
    return; // ⚡ Exit early - no API call needed!
  }
  
  console.log(`🔄 CACHE MISS: Fetching G702 application numbers for project ${projectId}...`);
  
  // 🔥 FIX: Fetch unique application numbers from G702 records, not draw numbers
  await axiosQuery(
    `${apiBase}projects/fetch_project_g703?project_ids=${selectedId.value}`,
    authToken,
    (data) => {
      // 🆕 Extract unique application numbers from BOTH GC and Sub G702s
      const subG702s = data.subcontractor_g702s || [];
      const gcG702s = data.g702_contractors || [];
      const allG702s = [...gcG702s, ...subG702s];
      const applicationNumbers = [...new Set(
        allG702s.map(g702 => g702.application_number).filter(num => num != null)
      )].sort((a, b) => b - a); // Sort descending (newest first)
      
      console.log(`🔥 Fetched G702 application numbers (${applicationNumbers.length} total):`, applicationNumbers.slice(0, 10), '...');
      g702DrawNumberList.value = applicationNumbers;
      
      // 🚀 CACHE the results for this project
      g702AppNumbersCache.value[cacheKey] = {
        applicationNumbers,
        fetchedAt: Date.now()
      };
      console.log(`✅ CACHED G702 application numbers for project ${projectId}`);
      
      if (!g702DrawNumber.value || g702DrawNumber.value === 0) {
        // Default to newest application number
        g702DrawNumber.value = applicationNumbers[0] || 0;
      }
    },
    errorStore
  );
}

function fetchDrawNumber() {
  axiosQuery(
    `${apiBase}draws/for_project/${selectedId.value}`,
    authToken,
    (data) => {
      // Handle the new API response format that returns {draws: [...], next_suggested_draw: "...", can_create_new: true}
      const draws = data.draws || data; // Fallback to data if draws property doesn't exist
      
      // Extract draw numbers from the draws data
      const drawNumbers = draws.map(draw => draw.draw_number).sort((a, b) => {
        // Sort numerically if possible, otherwise alphabetically
        const aNum = parseInt(a.toString().match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.toString().match(/\d+/)?.[0] || '0');
        return bNum - aNum; // Descending order (newest first)
      });
      
      drawNumberList.value = drawNumbers;
      if(drawNumbers.length === 0) {
        drawNumber.value = 0;
        globalDrawFilter.value = null;
        costsToDateDraw.value = null;
        budgetVarianceDraw.value = null;
      } else {
        // 🔥 FIX: Only set to newest draw if no draw is currently selected
        // This preserves the user's draw selection when switching tabs
        if (!globalDrawFilter.value || !drawNumbers.includes(globalDrawFilter.value)) {
          drawNumber.value = drawNumbers[0]; // Use the newest draw by default
          globalDrawFilter.value = drawNumbers[0]; // Set global filter to newest draw
          costsToDateDraw.value = drawNumbers[0]; // Set chart controls to newest draw
          budgetVarianceDraw.value = drawNumbers[0];
        } else {
          // Keep the currently selected draw
          drawNumber.value = globalDrawFilter.value;
          costsToDateDraw.value = globalDrawFilter.value;
          budgetVarianceDraw.value = globalDrawFilter.value;
        }
        // Load grid data for the selected draw
        setDrawDetail();
      }
    },
    errorStore
    );
}

function fetchG703DrawNumber() {
  // 🔥 FIX: Use APPLICATION NUMBERS from G703 data, not draw numbers from draws table
  axiosQuery(
    `${apiBase}projects/fetch_project_g703_draw_numbers?project_ids=${selectedId.value}`,
    authToken,
    (data) => {
      // Data is an array of application numbers that exist in G703 data
      const applicationNumbers = Array.isArray(data) ? data : [];
      
      // Sort in descending order (newest first)
      const sortedAppNumbers = applicationNumbers.sort((a, b) => {
        const aNum = parseInt(a.toString().match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.toString().match(/\d+/)?.[0] || '0');
        return bNum - aNum;
      });
      
      console.log(`📋 G703 Application Numbers:`, sortedAppNumbers);
      
      g703DrawNumberList.value = sortedAppNumbers;
      if(sortedAppNumbers.length === 0) {
        g703DrawNumber.value = 0;
      } else {
        g703DrawNumber.value = sortedAppNumbers[0]; // Use the newest application number by default
        console.log(`🎯 G703 Default Application Number:`, g703DrawNumber.value);
        // Fetch source documents for the default application
        fetchG703SourceDocuments();
      }
    },
    errorStore
    );
}

async function setDrawDetail() {
  // 🔥 CRITICAL FIX: Don't refetch grid data when changing draw filter
  // The grid should always show ALL data, not filtered data
  // Only the visual display should be filtered, not the underlying data
  
  // 🚀 FIX: Await the fetch so drawDetailRowData is populated before chart rebuild
  await fetchDrawDetailGridData() // Fixed - now properly filters by draw
  fetchDrawDetailSourceDocuments()
  
  // 🎯 DISABLED: Chart data is set from fresh API response in fetchDrawDetailGridData
  // This was overriding the correct chart values with stale grid data
  console.log('🎯 Chart update skipped - using fresh API data instead of stale grid data');
  
  // Update cost curve chart with new draw selection
  if (costCurveData.value && costCurveData.value.length > 0) {
    buildAgChartData();
  }
}

async function fetchDrawDetailSourceDocuments() {
  if (!drawNumber.value || !selectedId.value) {
    drawDetailSourceDocuments.value = [];
    return;
  }
  
  try {
    // Try multiple approaches to find documents
    const responses = await Promise.all([
      // Approach 1: Try with draw_number parameter
      axios.get(`${apiBase}documents?project_id=${selectedId.value}&draw_number=${drawNumber.value}up_to_draw=up_to_draw=${drawNumber.value}`, { headers: authToken.value }).catch(() => null),
      // Approach 2: Get all project documents and filter
      axios.get(`${apiBase}documents?project_id=${selectedId.value}`, { headers: authToken.value }).catch(() => null)
    ]);
    
    let allDocs = [];
    responses.forEach(response => {
      if (response && response.data) {
        allDocs = allDocs.concat(Array.isArray(response.data) ? response.data : []);
      }
    });
    
    if (allDocs.length > 0) {
      // Remove duplicates by ID
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );
      
      // Filter for relevant documents (be more inclusive)
      drawDetailSourceDocuments.value = uniqueDocs.filter(doc => {
        const filename = (doc.filename || '').toLowerCase();
        const drawNum = drawNumber.value.toString();
        
        // Check if document is related to this draw
        const hasDrawNumber = filename.includes(drawNum) || doc.draw_number == drawNum;
        
        // Include various document types but exclude G703
        const isRelevant = filename.includes('draw') || 
                          filename.includes('application') || 
                          filename.includes('payment') ||
                          filename.includes('app') ||
                          filename.includes('.pdf') ||
                          filename.includes('.xlsx');
        
        const isNotG703 = !filename.includes('g703');
        
        return (hasDrawNumber || isRelevant) && isNotG703;
      });
      

    } else {
      drawDetailSourceDocuments.value = [];
    }
  } catch (error) {
    console.error('Error fetching Draw Detail source documents:', error);
    drawDetailSourceDocuments.value = [];
  }
}

function setG703DrawNumber() {
  fetchG703DrawDetailGridData()
  fetchG703SourceDocuments()
}

async function fetchG703SourceDocuments() {
  if (!g703DrawNumber.value || !selectedId.value) {
    g703SourceDocuments.value = [];
    return;
  }
  
  try {
    // Try multiple approaches to find documents
    const responses = await Promise.all([
      // Approach 1: Try with draw_number parameter
      axios.get(`${apiBase}documents?project_id=${selectedId.value}&draw_number=${g703DrawNumber.value}`, { headers: authToken.value }).catch(() => null),
      // Approach 2: Get all project documents and filter
      axios.get(`${apiBase}documents?project_id=${selectedId.value}`, { headers: authToken.value }).catch(() => null)
    ]);
    
    let allDocs = [];
    responses.forEach(response => {
      if (response && response.data) {
        allDocs = allDocs.concat(Array.isArray(response.data) ? response.data : []);
      }
    });
    
    if (allDocs.length > 0) {
      // Remove duplicates by ID
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );
      
      // 🔥 SIMPLE: Show ALL PDFs for this project and draw number
      const drawNum = g703DrawNumber.value.toString();
      
      g703SourceDocuments.value = uniqueDocs.filter(doc => {
        const filename = (doc.filename || '').toLowerCase();
        const isPdf = filename.endsWith('.pdf');
        
        // Match by draw_number field in database OR draw number in filename
        const matchesDrawNumber = doc.draw_number == drawNum || 
                                 filename.includes(`draw ${drawNum}`) ||
                                 filename.includes(`draw${drawNum}`) ||
                                 filename.includes(`#${drawNum}`) ||
                                 filename.includes(`app ${drawNum}`) ||
                                 filename.includes(`app${drawNum}`);
        
        return isPdf && matchesDrawNumber;
      });
      
      console.log(`🔍 G703 Source Docs: Found ${g703SourceDocuments.value.length} PDFs for draw ${drawNum}:`, 
                  g703SourceDocuments.value.map(d => d.filename));
      

    } else {
      g703SourceDocuments.value = [];
    }
  } catch (error) {
    console.error('Error fetching G703 source documents:', error);
    g703SourceDocuments.value = [];
  }
}

function getDocumentDisplayName(doc) {
  if (!doc.filename) return `Document ${doc.id}`;
  
  // Clean up the filename for display
  let displayName = doc.filename;
  
  // Remove timestamp prefixes like "1746812216872-"
  displayName = displayName.replace(/^\d+-/, '');
  
  // Truncate if too long
  if (displayName.length > 40) {
    displayName = displayName.substring(0, 37) + '...';
  }
  
  return displayName;
}

function getDocumentType(doc) {
  if (!doc.filename) return '';
  
  const filename = doc.filename.toLowerCase();
  if (filename.includes('g703')) return 'G703';
  if (filename.includes('application')) return 'Application';
  if (filename.includes('draw')) return 'Draw';
  if (filename.includes('invoice')) return 'Invoice';
  return 'Document';
}

let drawDetailRowData = ref([]);
let drawDetailDataCtr = ref(0);
// ➕ Add Project Total button state
const hasProjectTotal = ref(false);
const isAddingProjectTotal = ref(false);
let g703RowData = ref([]);
let g703DataCtr = ref(0);
let g703TemplateInfo = ref([]); // 🔗 NEW: Template information for G703 documents
let g703ApplicationNumber = ref(null); // 📋 Application number from G703 data
let g703PeriodThrough = ref(null); // 📅 Period through date from G703 data
let g703Reallocations = ref(0); // 💰 Reallocations and Increases

// 🔧 Column Remap Modal State
const showColumnRemapModal = ref(false);
const columnMappings = ref([
  { gridColumn: 'original_budget', displayName: 'Original Contract Value', sourceField: 'originalContractValue' },
  { gridColumn: 'current_budget', displayName: 'Scheduled Value (Current)', sourceField: 'contractValue' },
  { gridColumn: 'previously_funded', displayName: 'Work Completed Prev. Appl.', sourceField: 'workCompletedPrevious' },
  { gridColumn: 'current_request', displayName: 'This Period', sourceField: 'workCompletedThisPeriod' },
  { gridColumn: 'materials_presently_stored', displayName: 'Stored Materials', sourceField: 'materialsPresently' },
  { gridColumn: 'total_completed_to_date', displayName: 'Total Completed and Stored', sourceField: 'totalCompletedAndStored' },
  { gridColumn: 'balance_to_finish', displayName: 'Balance to Finish', sourceField: 'balanceToFinish' },
  { gridColumn: 'retainage', displayName: 'Retainage', sourceField: 'retainage' },
]);

// Available DocuPipe source fields
const availableSourceFields = ref([
  { value: 'originalContractValue', label: 'Original Contract Value' },
  { value: 'scheduledValue', label: 'Scheduled Value' },
  { value: 'contractValue', label: 'Contract Value (Current)' },
  { value: 'adjustedBudget', label: 'Adjusted Budget' },
  { value: 'changeOrders', label: 'Change Orders' },
  { value: 'scopeChanges', label: 'Scope Changes' },
  { value: 'budgetTransfer', label: 'Budget Transfer' },
  { value: 'awardContracts', label: 'Award Contracts' },
  { value: 'workCompletedPrevious', label: 'Work Completed Previous' },
  { value: 'workCompletedThisPeriod', label: 'Work Completed This Period' },
  { value: 'materialsPresently', label: 'Materials Presently Stored' },
  { value: 'totalCompletedAndStored', label: 'Total Completed and Stored' },
  { value: 'balanceToFinish', label: 'Balance to Finish' },
  { value: 'percentComplete', label: 'Percent Complete' },
  { value: 'retainage', label: 'Retainage' },
]);
let g703ReallocationsPct = ref(0); // 📊 Reallocations percentage
let g703NetChange = ref(0); // 💰 Net Contract Change by Change Orders
let g703NetChangePct = ref(0); // 📊 Net Change percentage
let g703ContractSum = ref(0); // 💰 Contract Sum to Date
let g703CurrentRequest = ref(0); // 💰 Current Request

// 🆕 G703 Top Budget Movements (top 5 largest variances from original budget)
// Calculate current_budget - original_budget variance for each line item
const g703TopBudgetMovements = computed(() => {
  if (!g703RowData.value || g703RowData.value.length === 0) {
    return [];
  }

  // 🔥 CRITICAL FIX: Don't filter by draw here - the API endpoint already filters by draw!
  // The frontend was filtering for draw 21, but API returned draw 81, causing 0 results.
  // Since API call includes &draw_number=X, g703RowData should already contain only the correct draw.
  const currentDrawNum = parseInt(globalDrawFilter.value) || parseInt(selectedDrawNumber.value);
  
  console.log(`🔍 g703TopBudgetMovements: Calculating variances (API pre-filtered for draw ${currentDrawNum})`);
  
  // Calculate variance (current - original) for each row (no draw filter needed)
  const movements = [];
  let totalRows = 0;
  let debugSample = null;
  
  g703RowData.value.forEach(row => {
    // 🔥 REMOVED: if (drawNum !== currentDrawNum) return;
    // API already filtered by draw, so all rows in g703RowData are for the correct draw
    
    totalRows++;
    if (!debugSample) {
      debugSample = {
        fields: Object.keys(row),
        sample: {
          description: row.description,
          original_budget: row.original_budget,
          current_budget: row.current_budget,
          scheduled_value: row.scheduled_value,
          adjusted_scheduled_value: row.adjusted_scheduled_value
        }
      };
    }
    
    const originalBudget = parseFloat(row.original_budget) || parseFloat(row.scheduled_value) || 0;
    const currentBudget = parseFloat(row.current_budget) || parseFloat(row.adjusted_scheduled_value) || 0;
    const variance = currentBudget - originalBudget;
    
    // 🔍 DEBUG: Log first row with non-zero values
    if (totalRows === 1 && (originalBudget > 0 || currentBudget > 0)) {
      console.log(`🔍 G703 First Row Debug:`, {
        description: row.description,
        originalBudget,
        currentBudget,
        variance,
        rawRow: { original_budget: row.original_budget, current_budget: row.current_budget }
      });
    }
    
    // Skip totals and subtotals
    const descLower = (row.description || '').toLowerCase();
    if (descLower.includes('grand total') || descLower.includes('subtotal') || descLower.includes('total ')) {
      return;
    }
    
    // Skip blank descriptions
    if (!row.description || row.description.trim() === '') {
      return;
    }
    
    // Only show significant movements (> $10,000 variance)
    if (Math.abs(variance) > 10000) {
      movements.push({
        description: row.description || row.contractor_names || 'Unknown',
        variance: variance,
        originalBudget: originalBudget,
        currentBudget: currentBudget
      });
    }
  });
  
  console.log(`📊 G703: Found ${movements.length} items with budget variance > $10K (out of ${totalRows} rows for draw ${currentDrawNum})`);
  if (debugSample) {
    console.log(`🔍 G703 Data Structure:`, debugSample);
  }
  if (movements.length > 0) {
    console.log(`📊 Top 5 G703 Budget Movements:`, movements.slice(0, 5).map(m => `${m.description}: $${(m.variance / 1000000).toFixed(1)}M`));
  } else if (totalRows > 0) {
    console.warn(`⚠️ G703: ${totalRows} rows found but NO variances > $10K. Check if fields are populated.`);
  }
  
  // Sort by absolute value of variance and take top 5
  return movements
    .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
    .slice(0, 5);
});

// 🆕 G703 Stored Materials Requested
const g703StoredMaterials = computed(() => {
  if (!g703RowData.value || g703RowData.value.length === 0) {
    return 0;
  }

  const currentDrawNum = parseInt(globalDrawFilter.value) || selectedDrawNumber.value;
  
  const total = g703RowData.value
    .filter(row => parseInt(row.draw_number) === currentDrawNum)
    .reduce((sum, row) => {
      return sum + (parseFloat(row.materials_presently_stored) || 0);
    }, 0);
  
  return total;
});

// 🆕 RETAINAGE INSIGHTS COMPUTED PROPERTIES
const retainageThisPeriodComputed = computed(() => {
  const currentDrawNum = parseInt(globalDrawFilter.value) || parseInt(selectedDrawNumber.value);
  const currentDrawRetainage = retainageRowData.value
    .filter(row => parseInt(row.draw_number) === currentDrawNum)
    .reduce((sum, row) => sum + (parseFloat(row.retainage) || 0), 0); // 🔥 FIX: Use 'retainage' not 'retainage_current'
  return currentDrawRetainage;
});

const overallRetainagePctComputed = computed(() => {
  const currentDrawNum = parseInt(globalDrawFilter.value) || parseInt(selectedDrawNumber.value);
  const currentDrawRows = retainageRowData.value.filter(row => parseInt(row.draw_number) === currentDrawNum);
  const totalCurrentRetainage = currentDrawRows.reduce((sum, row) => sum + (parseFloat(row.retainage) || 0), 0); // 🔥 FIX: Use 'retainage'
  const totalCompletedWork = currentDrawRows.reduce((sum, row) => sum + (parseFloat(row.total_completed_and_stored_to_date) || 0), 0); // 🔥 FIX: Full field name
  return totalCompletedWork > 0 ? (totalCurrentRetainage / totalCompletedWork) * 100 : 0;
});

const partialRetainageReleases = computed(() => {
  const currentDrawNum = parseInt(globalDrawFilter.value) || parseInt(selectedDrawNumber.value);
  const previousDrawNum = currentDrawNum - 1;
  if (previousDrawNum < 1) return [];

  const releases = [];
  retainageRowData.value.forEach(currentDrawRow => {
    if (parseInt(currentDrawRow.draw_number) === currentDrawNum) {
      const previousDrawRow = g702RowData.value.find(
        row => parseInt(row.draw_number) === previousDrawNum && row.contractor_id === currentDrawRow.contractor_id
      );

      if (previousDrawRow) {
        const previousRetainage = parseFloat(previousDrawRow.retainage) || 0; // 🔥 FIX: Use 'retainage'
        const currentRetainage = parseFloat(currentDrawRow.retainage) || 0; // 🔥 FIX: Use 'retainage'
        const releasedAmount = previousRetainage - currentRetainage;

        if (releasedAmount > 100 && currentRetainage > 0) { // Partial release = reduced but not to zero
          const totalCompleted = parseFloat(currentDrawRow.total_completed_and_stored_to_date) || 0; // 🔥 FIX: Full field name
          releases.push({
            contractor: currentDrawRow.contractor_name,
            amount: releasedAmount,
            currentRetainage: currentRetainage,
            previousRetainage: previousRetainage,
            retainagePct: totalCompleted > 0 ? (currentRetainage / totalCompleted) * 100 : 0
          });
        }
      }
    }
  });
  return releases.sort((a, b) => b.amount - a.amount);
});

const finalRetainageReleases = computed(() => {
  const currentDrawNum = parseInt(globalDrawFilter.value) || parseInt(selectedDrawNumber.value);
  const previousDrawNum = currentDrawNum - 1;
  if (previousDrawNum < 1) return [];

  const releases = [];
  retainageRowData.value.forEach(currentDrawRow => {
    if (parseInt(currentDrawRow.draw_number) === currentDrawNum) {
      const previousDrawRow = g702RowData.value.find(
        row => parseInt(row.draw_number) === previousDrawNum && row.contractor_id === currentDrawRow.contractor_id
      );

      if (previousDrawRow) {
        const previousRetainage = parseFloat(previousDrawRow.retainage) || 0; // 🔥 FIX: Use 'retainage'
        const currentRetainage = parseFloat(currentDrawRow.retainage) || 0; // 🔥 FIX: Use 'retainage'
        const releasedAmount = previousRetainage - currentRetainage;

        // Final release = had retainage before, now at/near zero
        if (previousRetainage > 100 && currentRetainage <= 500) {
          // 🔥 CHECK FOR FINAL LW: Unconditional amount should equal current budget
          const currentBudget = parseFloat(currentDrawRow.current_budget) || 0;
          const matchedLW = findMatchingLW(
            currentDrawRow.contractor_id, 
            currentDrawNum, 
            currentDrawRow.current_payment_due || 0, 
            currentDrawRow.contractor_name
          );
          
          let hasFinalLW = false;
          if (matchedLW) {
            const unconditionalAmount = parseFloat(matchedLW.total_payments_received_to_date) || 0;
            // Check if unconditional amount is close to current budget (within 1%)
            hasFinalLW = Math.abs(unconditionalAmount - currentBudget) < (currentBudget * 0.01);
          }
          
          releases.push({
            contractor: currentDrawRow.contractor_name,
            contractor_id: currentDrawRow.contractor_id,
            amount: previousRetainage, // Show full amount released
            retainagePct: 0,
            hasFinalLW: hasFinalLW
          });
        }
      }
    }
  });
  return releases.sort((a, b) => a.amount - b.amount);
});

// 🆕 G703 Retainage Releases (contractors where retainage decreased)
const g703RetainageReleases = computed(() => {
  if (!g703RowData.value || g703RowData.value.length === 0) {
    return [];
  }

  const currentDrawNum = parseInt(globalDrawFilter.value) || selectedDrawNumber.value;
  const previousDrawNum = currentDrawNum - 1;
  
  if (previousDrawNum < 1) {
    return []; // No previous draw to compare
  }

  // Group by contractor + description for matching
  const currentDrawMap = new Map();
  const previousDrawMap = new Map();
  
  g703RowData.value.forEach(row => {
    const drawNum = parseInt(row.draw_number);
    const key = `${row.contractor_names}||${row.description}`;
    const retainage = parseFloat(row.current_retainage_held) || 0;
    
    if (drawNum === currentDrawNum) {
      currentDrawMap.set(key, {
        contractor: row.contractor_names,
        description: row.description,
        retainage: retainage
      });
    } else if (drawNum === previousDrawNum) {
      previousDrawMap.set(key, {
        contractor: row.contractor_names,
        description: row.description,
        retainage: retainage
      });
    }
  });

  // Find releases (where retainage decreased)
  const releases = [];
  currentDrawMap.forEach((current, key) => {
    const previous = previousDrawMap.get(key);
    if (previous) {
      const releaseAmount = previous.retainage - current.retainage;
      if (releaseAmount > 10) { // Significant release
        const retainagePct = current.retainage > 0 && (parseFloat(g703RowData.value.find(r => `${r.contractor_names}||${r.description}` === key && parseInt(r.draw_number) === currentDrawNum)?.total_completed_to_date) || 1) > 0
          ? (current.retainage / parseFloat(g703RowData.value.find(r => `${r.contractor_names}||${r.description}` === key && parseInt(r.draw_number) === currentDrawNum)?.total_completed_to_date)) * 100
          : 0;
        
        releases.push({
          contractor: current.contractor,
          description: current.description,
          releaseAmount: releaseAmount,
          currentRetainage: current.retainage,
          retainagePct: retainagePct
        });
      }
    }
  });

  // Sort by release amount descending
  return releases.sort((a, b) => b.releaseAmount - a.releaseAmount);
});

// 🆕 G702 BVS (Budget Variance Summary) Computed Properties
// 1. Total Amount Requested (GC G702s for current draw)
const g702TotalAmountRequested = computed(() => {
  if (!g702RowData.value || g702RowData.value.length === 0) {
    return [];
  }

  const currentDrawNum = parseInt(g702DrawNumber.value) || parseInt(globalDrawFilter.value) || selectedDrawNumber.value;
  
  // Find all GC G702 records for this draw
  const gcRecords = g702RowData.value.filter(row => {
    const drawNum = parseInt(row.draw_number);
    const isGC = (row.contractor_type === 'GeneralContractor' || 
                  row.contractor_name?.toLowerCase().includes('gilbane'));
    return drawNum === currentDrawNum && isGC;
  });

  return gcRecords.map(gc => ({
    contractor: gc.contractor_name,
    appNumber: gc.application_number,
    amount: parseFloat(gc.current_payment_due) || parseFloat(gc.total_earned_less_retainage) || 0
  }));
});

// 2. Net Change by Change Orders
const g702ChangeOrders = computed(() => {
  if (!g702RowData.value || g702RowData.value.length === 0) {
    return [];
  }

  const currentDrawNum = parseInt(g702DrawNumber.value) || parseInt(globalDrawFilter.value) || selectedDrawNumber.value;
  
  // Get GC G702 records for this draw that have change orders
  const gcRecords = g702RowData.value.filter(row => {
    const drawNum = parseInt(row.draw_number);
    const isGC = (row.contractor_type === 'GeneralContractor' || 
                  row.contractor_name?.toLowerCase().includes('gilbane'));
    // 🔥 FIX: Show change orders if non-zero (positive OR negative)
    const hasChangeOrders = parseFloat(row.change_orders || row.net_change_by_change_order) !== 0;
    return drawNum === currentDrawNum && isGC && hasChangeOrders;
  });

  return gcRecords.map((gc, index) => ({
    contractor: gc.contractor_name,
    appNumber: gc.application_number,
    coNumber: index + 1, // CO numbering
    amount: parseFloat(gc.change_orders || gc.net_change_by_change_order) || 0
  }));
});

// 🔧 Helper: Normalize contractor name for fuzzy matching
function normalizeContractorName(name) {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/[,\.]+/g, '') // Remove commas and periods
    .replace(/\s+(inc|llc|corp|co|company|ltd|limited|corporation)\.?$/i, '') // Remove suffixes
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
}

// 🔧 Helper: Check if two contractor names match (fuzzy)
function contractorNamesMatch(name1, name2) {
  const n1 = normalizeContractorName(name1);
  const n2 = normalizeContractorName(name2);
  if (!n1 || !n2) return false;
  // Exact match after normalization
  if (n1 === n2) return true;
  // One contains the other (handles "enterprise architectural" vs "enterprise architectural sales")
  if (n1.includes(n2) || n2.includes(n1)) return true;
  // First 10 chars match (handles truncated names)
  if (n1.substring(0, 10) === n2.substring(0, 10) && n1.length > 5) return true;
  return false;
}

// 3. Missing Pay Apps (contractors from G703 who don't have SubcontractorG702 for this draw)
const missingPayApps = computed(() => {
  const currentDrawNum = parseInt(g702DrawNumber.value) || parseInt(globalDrawFilter.value) || selectedDrawNumber.value;
  
  // Get unique contractors from G703 who have payments this draw
  const g703Contractors = [];
  if (g703RowData.value && g703RowData.value.length > 0) {
    g703RowData.value.forEach(row => {
      const drawNum = parseInt(row.draw_number);
      const hasPayment = parseFloat(row.current_request) > 0;
      const contractorName = row.contractor_names;
      if (drawNum === currentDrawNum && hasPayment && contractorName) {
        g703Contractors.push(contractorName);
      }
    });
  }
  
  // Get contractors from G702 data for this draw (these have submitted pay apps)
  const g702ContractorNames = [];
  if (g702RowData.value && g702RowData.value.length > 0) {
    g702RowData.value.forEach(row => {
      const drawNum = parseInt(row.draw_number) || parseInt(row.application_number);
      const contractorName = row.contractor_name || row.contractor_names;
      if (drawNum === currentDrawNum && contractorName) {
        g702ContractorNames.push(contractorName);
      }
    });
  }
  
  // Missing = in G703 but NOT in G702 (using fuzzy matching)
  const missing = [];
  const seen = new Set();
  g703Contractors.forEach(g703Name => {
    const normalized = normalizeContractorName(g703Name);
    if (seen.has(normalized)) return; // Skip duplicates
    seen.add(normalized);
    
    // Check if ANY G702 contractor matches this G703 contractor
    const hasMatch = g702ContractorNames.some(g702Name => contractorNamesMatch(g703Name, g702Name));
    if (!hasMatch) {
      missing.push({ contractor: g703Name });
    }
  });
  
  return missing;
});

// 4. Missing Lien Waivers (contractors with G702 but no lien waiver submitted)
const g702MissingLienWaivers = computed(() => {
  const currentDrawNum = parseInt(g702DrawNumber.value) || parseInt(globalDrawFilter.value) || selectedDrawNumber.value;
  
  if (!g702RowData.value || g702RowData.value.length === 0) {
    return [];
  }
  
  const lwData = g702LienWaivers.value || {};
  console.log(`🔍 Checking LW status for draw ${currentDrawNum}, LW keys available: ${Object.keys(lwData).length}`);
  
  // Check each G702 record for lien waiver status
  // 🔥 FIX: Each draw can have multiple contractors (app numbers vary per contractor)
  // G702 records show which contractors submitted payment apps for this draw
  const missingLW = [];
  const seenContractors = new Set(); // Dedupe by contractor name
  
  g702RowData.value.forEach(row => {
    const drawNum = parseInt(row.draw_number) || parseInt(row.application_number);
    const contractorName = row.contractor_name || row.contractor_names;
    const contractorId = row.contractor_id;
    
    // Filter to current draw AND require contractor info
    if (drawNum === currentDrawNum && contractorName && !seenContractors.has(contractorName)) {
      seenContractors.add(contractorName);
      
      // 🔥 USE SAME MATCHING LOGIC AS GRID: Match by $$ amount using findMatchingLW
      const matchedLW = findMatchingLW(contractorId, drawNum, row.current_payment_due || 0, contractorName);
      
      // Check if contractor has any executed LW records
      let hasExecutedLW = false;
      if (matchedLW) {
        // Check multiple possible field names for signed/notarized status
        const hasWaiverNumber = !!matchedLW.waiver_number;
        const isNotarized = matchedLW.is_notarized === true || matchedLW.isNotarized === true || matchedLW.notarized === true;
        const isSigned = matchedLW.is_signed === true || matchedLW.isSigned === true || matchedLW.signed === true;
        // If LW exists at all for this contractor, count it (even without explicit flags)
        hasExecutedLW = hasWaiverNumber || isNotarized || isSigned || matchedLW.id;
      }
      
      // 🔥 CRITICAL FIX: If no LW data exists at all OR contractor has no executed LW → missing
      if (!hasExecutedLW) {
        console.log(`❌ Missing LW: ${contractorName} (draw=${drawNum}, amount=$${row.current_payment_due || 0}, matched=${!!matchedLW})`);
        missingLW.push({ contractor: contractorName, contractor_id: contractorId });
      }
    }
  });
  
  console.log(`📋 Total missing LWs: ${missingLW.length} for draw ${currentDrawNum}`);
  return missingLW;
});

let g702RowData = ref([]);
let g702DataCtr = ref(0);

// 🔥 COMPANIES TAB ONLY: Deduplicated G702 data (removes GC duplicates from OCR variations)
const companiesTabRowData = computed(() => {
  if (!g702RowData.value || g702RowData.value.length === 0) return [];
  
  // Helper: Aggressive normalization to match contractor name variations
  const normalizeForMatch = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/\s*\(subcontractor\)/gi, '')       // Remove (Subcontractor) anywhere
      .replace(/\s*\(generalcontractor\)/gi, '')   // Remove (GeneralContractor) anywhere
      .replace(/\s*-\s*new york/gi, '')            // Remove location suffixes anywhere
      .replace(/\s*-\s*ny\b/gi, '')                // Remove -NY
      .replace(/\b(llc|inc|corp|co|ltd)\.?\b/gi, '') // Remove entity types ANYWHERE
      .replace(/[^a-z0-9]/g, '')                   // Keep only alphanumeric
      .substring(0, 10);                           // Use first 10 chars for matching
  };
  
  // Helper: Simple Levenshtein distance for short strings
  const levenshtein = (s1, s2) => {
    if (s1 === s2) return 0;
    if (s1.length === 0) return s2.length;
    if (s2.length === 0) return s1.length;
    
    const matrix = [];
    for (let i = 0; i <= s2.length; i++) matrix[i] = [i];
    for (let j = 0; j <= s1.length; j++) matrix[0][j] = j;
    
    for (let i = 1; i <= s2.length; i++) {
      for (let j = 1; j <= s1.length; j++) {
        const cost = s1[j-1] === s2[i-1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i-1][j] + 1,      // deletion
          matrix[i][j-1] + 1,      // insertion
          matrix[i-1][j-1] + cost  // substitution
        );
      }
    }
    return matrix[s2.length][s1.length];
  };
  
  // Helper: Check if two names are similar (handles typos like Vertcon vs Verteon)
  const namesAreSimilar = (name1, name2) => {
    const n1 = normalizeForMatch(name1);
    const n2 = normalizeForMatch(name2);
    
    if (n1 === n2) return true;
    if (n1.length < 4 || n2.length < 4) return false;
    
    // Check if one contains the other
    if (n1.includes(n2) || n2.includes(n1)) return true;
    
    // If length difference > 3, likely different companies
    if (Math.abs(n1.length - n2.length) > 3) return false;
    
    // Use proper Levenshtein distance - allow up to 2 edits for similar names
    const distance = levenshtein(n1, n2);
    const maxLen = Math.max(n1.length, n2.length);
    
    // Debug: log comparisons that are close but not matching
    if (distance <= 3) {
      console.log(`🔍 FUZZY MATCH: "${n1}" vs "${n2}" = distance ${distance}, maxLen ${maxLen}, match: ${distance <= 2 || (distance / maxLen) <= 0.2}`);
    }
    
    // Allow 2 edits for longer names, or ~20% difference
    return distance <= 2 || (distance / maxLen) <= 0.2;
  };
  
  // Group contractors by fuzzy name matching
  const grouped = new Map();
  
  g702RowData.value.forEach(item => {
    let foundGroup = null;
    
    // Check if this contractor matches any existing group
    for (const [groupKey, groupItems] of grouped.entries()) {
      if (namesAreSimilar(item.contractor_name, groupKey)) {
        foundGroup = groupKey;
        break;
      }
    }
    
    if (foundGroup) {
      grouped.get(foundGroup).push(item);
    } else {
      grouped.set(item.contractor_name || '', [item]);
    }
  });
  
  console.log(`🔥 COMPANIES TAB: Grouped ${g702RowData.value.length} entries into ${grouped.size} unique contractors`);
  
  // For each group, keep only the LATEST DRAW (like subs do)
  const result = [];
  for (const [groupName, groupItems] of grouped.entries()) {
    if (groupItems.length > 1) {
      // Sort by draw_number descending, keep the newest
      const latest = groupItems.sort((a, b) => {
        const drawA = parseInt(a.draw_number || 0);
        const drawB = parseInt(b.draw_number || 0);
        return drawB - drawA; // Newest first
      })[0];
      console.log(`🔥 COMPANIES TAB: "${groupName.substring(0, 30)}..." - kept latest draw ${latest.draw_number} (from ${groupItems.length} entries)`);
      result.push(latest);
    } else {
      result.push(groupItems[0]);
    }
  }
  
  return result;
});
let isInvoiceFetching = ref(false); // 📄 Guard flag to prevent multiple simultaneous fetches
// --- G702 Grid: column definitions (parallel to G703, tailored to G702 fields) ---
const g702DetailColumnDefs = [
  { headerName: 'Contractor', field: 'contractor_name', pinned: 'left', width: 220,
    valueGetter: p => p.data?.contractor_name || p.data?.contractor_names || 'Unknown'
  },
  { headerName: 'Application #', field: 'application_number', width: 130,
    valueGetter: p => p.data?.application_number || p.data?.applicationno || p.data?.applicationNo || p.data?.application || p.data?.app_number || p.data?.app_no || null },
  { headerName: 'Period To', field: 'period_through', width: 130,
    valueGetter: p => p.data?.period_through || p.data?.period_to || p.data?.application_date || p.data?.period_end || null,
    valueFormatter: p => {
      const v = p.value;
      if (!v) return '';
      try {
        const d = new Date(v);
        if (!isNaN(d.getTime())) return d.toLocaleDateString();
        return v;
      } catch { return v; }
    }
  },
  {
    headerName: 'DDA Term',
    field: 'dda_term',
    sortable: true,
    filter: true,
    width: 250,
    pinned: 'left',
    hide: true, // Hidden for G702 grid
    valueGetter: params => {
      // Get DDA term name from relationship or show blank
      return params.data?.dda_term_name || params.data?.dda_term?.term || '';
    },
    cellStyle: params => {
      // Highlight unassigned DDA terms
      if (!params.data?.dda_term && !params.data?.dda_term_name) {
        return { backgroundColor: '#fff3cd', color: '#856404' }; // Light yellow background
      }
      return null;
    },
    cellRenderer: (params) => {
      if (!params || !params.data) return 'N/A';
      
      const ddaTerm = params.data?.dda_term?.term || params.data?.dda_term_name || '';
      
      return `
        <div class="dda-term-edit-cell" data-field="dda_term_id" data-row-id="${params.data.id || params.node.id}">
          <span class="dda-term-value">${ddaTerm}</span>
          <span class="dda-term-edit-overlay" style="opacity: 0; visibility: hidden;">
            <button class="dda-term-edit-btn" title="Click to edit DDA term" style="display: none;">✏️</button>
          </span>
        </div>
      `;
    },
    onCellClicked: (params) => {
      if (params.data) {
        handleEditDDATerm(params.data, 'dda_term_id', params.data?.dda_term?.term || params.data?.dda_term_name);
      }
    }
  },
  {
    headerName: 'Scope',
    field: 'g703_description',
    width: 250,
    valueGetter: params => {
      // 🔥 FIX: ONLY use g703_description from matched G703 records
      // NEVER use scope_detail as it contains contractor addresses from DocuPipe
      if (params.data?.g703_description) {
        return params.data.g703_description;
      }
      // Fallback for records without description - show empty, don't fake it
      return '';
    }
  },
  { headerName: 'Original Contract', field: 'original_contract_sum', type: 'rightAligned', width: 140,
    valueGetter: p => parseFloat(p.data?.original_contract_sum || p.data?.original_contract_amount || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Net Change (COs)', field: 'net_change_by_change_order', type: 'rightAligned', width: 140,
    valueGetter: p => parseFloat(p.data?.net_change_by_change_order || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Contract Sum To Date', field: 'contract_sum_to_date', type: 'rightAligned', width: 160,
    valueGetter: p => parseFloat(p.data?.contract_sum_to_date || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Total Completed/Stored', field: 'total_completed_and_stored_to_date', type: 'rightAligned', width: 180,
    valueGetter: p => parseFloat(p.data?.total_completed_and_stored_to_date || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Retainage', field: 'retainage', type: 'rightAligned', width: 120,
    valueGetter: p => parseFloat(p.data?.retainage || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Total Earned < Retain.', field: 'total_earned_less_retainage', type: 'rightAligned', width: 170,
    valueGetter: p => parseFloat(p.data?.total_earned_less_retainage || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Less Previous Certs', field: 'less_previous_certificates_for_payment', type: 'rightAligned', width: 170,
    valueGetter: p => parseFloat(p.data?.less_previous_certificates_for_payment || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  { headerName: 'Current Payment Due', field: 'current_payment_due', type: 'rightAligned', width: 160,
    valueGetter: p => parseFloat(p.data?.current_payment_due || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
  
  // 🆕 LW AMOUNT COLUMN: Shows Lien Waiver amount and highlights variance (HIDDEN - moved to LW tab)
  { headerName: 'LW Amount', field: 'lw_amount', type: 'rightAligned', width: 140, hide: true,
    valueGetter: p => {
      // Find matching Lien Waiver by contractor + draw, then match by amount
      const contractorId = p.data?.contractor_id;
      const drawNum = p.data?.draw_number;
      const g702Amount = parseFloat(p.data?.current_payment_due || 0);
      if (!contractorId || !drawNum) return null;
      
      const lwKey = `${contractorId}_${drawNum}`;
      const lws = g702LienWaivers.value[lwKey];
      if (!lws || lws.length === 0) return null;
      
      // If only one LW, use it
      if (lws.length === 1) {
        return parseFloat(lws[0].current_payment_amount || 0);
      }
      
      // If multiple LWs, find the one that matches this G702 application by amount
      const matchingLw = lws.find(lw => {
        const lwAmt = parseFloat(lw.current_payment_amount || 0);
        return Math.abs(lwAmt - g702Amount) < 1; // Allow $1 rounding difference
      });
      
      if (matchingLw) {
        return parseFloat(matchingLw.current_payment_amount || 0);
      }
      
      // If no exact match, return first LW amount (fallback)
      return parseFloat(lws[0].current_payment_amount || 0);
    },
    cellRenderer: p => {
      if (!p.value) return '-'; // No LW found
      
      // Compare LW amount to G702 Current Payment Due
      const lwAmount = parseFloat(p.value || 0);
      const g702Amount = parseFloat(p.data?.current_payment_due || 0);
      const variance = Math.abs(lwAmount - g702Amount);
      const isMatch = variance < 1; // Allow $1 rounding difference
      
      // 🟢 Green if match, 🔴 Red if variance
      const color = isMatch ? '#4caf50' : '#f44336';
      const fontWeight = isMatch ? 'normal' : 'bold';
      
      return `<span style="color: ${color}; font-weight: ${fontWeight};">${currencyFormat(lwAmount, 0)}</span>`;
    }
  },
  
  // 🆕 SIGNED COLUMN: Shows if Lien Waiver is signed (HIDDEN - moved to LW tab)
  { headerName: 'Signed', field: 'lw_signed', width: 90, hide: true, cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      
      // Check if LW is signed - check backend root-level fields first, then nested
      if (lw.is_signed === true || lw.isSigned === true) return true;
      if (lw.signed === true || lw.docuSigned === true || lw.isDocuSigned === true) return true;
      if (lw.signatoryInfo?.isDocuSigned === true) return true;
      if (lw.documentInfo?.docuSignEnvelopeId) return true;
      // Backend doesn't always set signed flag, assume true if has waiver_number
      if (lw.waiver_number) return true;
      return false;
    },
    cellRenderer: p => {
      if (p.value === null) return '-';
      if (p.value === true) {
        return '<span style="color: #4caf50; font-size: 18px; font-weight: bold;">✓</span>';
      }
      return '<span style="color: #f44336; font-size: 18px; font-weight: bold;">✗</span>';
    }
  },
  
  // 🆕 NOTARIZED COLUMN: Shows if Lien Waiver is notarized (HIDDEN - moved to LW tab)
  { headerName: 'Notarized', field: 'lw_notarized', width: 100, hide: true, cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      
      // Check if LW is notarized - check backend root-level fields first (is_notarized), then nested
      if (lw.is_notarized === true) return true;
      if (lw.notarized === true || lw.isNotarized === true) return true;
      if (lw.notarizationInfo?.isNotarized === true) return true;
      return false;
    },
    cellRenderer: p => {
      if (p.value === null) return '-';
      if (p.value === true) {
        return '<span style="color: #4caf50; font-size: 18px; font-weight: bold;">✓</span>';
      }
      return '<span style="color: #f44336; font-size: 18px; font-weight: bold;">✗</span>';
    }
  },
  
  // 🆕 FINAL/CONDITIONAL COLUMN: Shows if Lien Waiver is Final or Conditional (HIDDEN on G702 tab - shown on LW tab)
  { headerName: 'Type', field: 'lw_type', width: 110, hide: true, cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      
      // Check backend root-level fields first (is_final, is_conditional)
      if (lw.is_final === true) return 'Final';
      if (lw.is_conditional === true) return 'Conditional';
      
      // Check other root level variations
      if (lw.isFinal === true) return 'Final';
      if (lw.isConditional === true) return 'Conditional';
      
      // Check nested documentInfo
      if (lw.documentInfo?.isFinal === true) return 'Final';
      if (lw.documentInfo?.isConditional === true) return 'Conditional';
      
      // Check waiver_type string field from backend
      if (lw.waiver_type && typeof lw.waiver_type === 'string') {
        if (lw.waiver_type.toLowerCase().includes('final')) return 'Final';
        if (lw.waiver_type.toLowerCase().includes('conditional')) return 'Conditional';
      }
      
      return null;
    },
    cellRenderer: p => {
      if (!p.value) return '-';
      const color = p.value === 'Final' ? '#4caf50' : '#ff9800';
      const bgColor = p.value === 'Final' ? '#e8f5e9' : '#fff3e0';
      return `<span style="color: ${color}; background-color: ${bgColor}; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px;">${p.value}</span>`;
    }
  },
  
  // 🆕 SIGNED COLUMN: Shows if G702 is signed
  { headerName: 'Signed', field: 'is_signed', width: 100, hide: false, cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      return p.data?.is_signed === true;
    },
    cellRenderer: p => {
      if (p.value === true) {
        const signedBy = p.data?.signed_by || '';
        const signatureDate = p.data?.signature_date || '';
        const tooltip = signedBy || signatureDate ? `Signed by: ${signedBy}${signatureDate ? ' on ' + signatureDate : ''}` : 'Signed';
        return `<span title="${tooltip}" style="color: #4caf50; background-color: #e8f5e9; padding: 3px 8px; border-radius: 12px; font-weight: bold; font-size: 11px;">✓ Signed</span>`;
      }
      return '<span style="color: #9e9e9e;">—</span>';
    }
  },
  
  // 🆕 NOTARIZED COLUMN: Shows if G702 is notarized
  { headerName: 'Notarized', field: 'is_notarized', width: 110, hide: false, cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      return p.data?.is_notarized === true;
    },
    cellRenderer: p => {
      if (p.value === true) {
        const notaryName = p.data?.notary_name || '';
        const notaryState = p.data?.notary_state || '';
        const tooltip = notaryName || notaryState ? `Notary: ${notaryName}${notaryState ? ', ' + notaryState : ''}` : 'Notarized';
        return `<span title="${tooltip}" style="color: #2196f3; background-color: #e3f2fd; padding: 3px 8px; border-radius: 12px; font-weight: bold; font-size: 11px;">✓ Notarized</span>`;
      }
      return '<span style="color: #9e9e9e;">—</span>';
    }
  },
  
  { headerName: 'Balance to Finish', field: 'balance_to_finish_including_retainage', type: 'rightAligned', width: 200,
    valueGetter: p => parseFloat(p.data?.balance_to_finish_including_retainage || 0), cellRenderer: p => {
    // 🔥 USE PLACEHOLDER-AWARE FORMATTER
    const description = p.data?.description || '';
    if (description.match(/^\(Row \d+\)$/)) {
      return '-'; // Show dash for placeholder rows
    }
    return currencyFormat(p.value || 0, 0);
  } },
];

// 🆕 LIEN WAIVER GRID COLUMN DEFINITIONS
const lwDetailColumnDefs = [
  { 
    headerName: 'Description of Work', 
    field: 'description', 
    pinned: 'left',
    width: 250,
    headerClass: 'header-scopes-of-work',
    valueGetter: p => p.data?.g703_description || p.data?.scope_detail || p.data?.description || ''
  },
  { 
    headerName: 'Company', 
    field: 'contractor_name', 
    width: 220,
    headerClass: 'header-scopes-of-work',
    valueGetter: p => p.data?.contractor_name || p.data?.contractor_names || 'Unknown'
  },
  { 
    headerName: 'Payment Application #', 
    field: 'application_number', 
    width: 150,
    headerClass: 'header-expenditure',
    cellStyle: { textAlign: 'center' },
    valueGetter: p => p.data?.application_number || p.data?.draw_number || '-'
  },
  { 
    headerName: 'Current Billing', 
    field: 'current_payment_due', 
    width: 140,
    type: 'numericColumn',
    headerClass: 'header-expenditure',
    valueGetter: p => parseFloat(p.data?.current_payment_due || 0),
    cellRenderer: p => currencyFormat(p.value || 0, 0)
  },
  { 
    headerName: 'Lien Waiver Provided (Y/N)', 
    field: 'lw_provided', 
    width: 180,
    headerClass: 'header-lw-tracking',
    cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      return lw ? true : false;
    },
    cellRenderer: p => {
      if (p.value === true) {
        return '<span style="color: #4caf50; font-weight: bold;">Yes</span>';
      }
      return '<span style="color: #d32f2f; font-weight: bold;">No</span>';
    }
  },
  { 
    headerName: 'Unconditional Amount Waived', 
    field: 'lw_unconditional_amount', 
    width: 190,
    type: 'numericColumn',
    headerClass: 'header-lw-tracking',
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      
      // Unconditional = Prior billing amount (totalPaymentsReceivedToDate / amount_paid_to_date)
      // Check flattened DB fields first, then raw DocuPipe data
      const totalPaid = parseFloat(
        lw.total_payments_received_to_date || 
        lw.amount_paid_to_date || 
        lw.financialSummary?.totalPaymentsReceivedToDate || 
        lw.financialSummary?.amountPaidToDate || 
        0
      );
      return totalPaid || null;
    },
    cellRenderer: p => {
      if (p.value === null || p.value === undefined) {
        return '<span style="color: #999;">—</span>';
      }
      return currencyFormat(p.value, 0);
    }
  },
  { 
    headerName: 'Conditional Amount Waived', 
    field: 'lw_conditional_amount', 
    width: 180,
    type: 'numericColumn',
    headerClass: 'header-lw-tracking',
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      
      // Conditional = Current billing amount (progressPayment / current_payment_amount)
      // Check flattened DB fields first, then raw DocuPipe data
      const progressPayment = parseFloat(
        lw.progress_payment || 
        lw.current_payment_amount || 
        lw.financialSummary?.progressPayment || 
        lw.financialSummary?.currentPaymentAmount || 
        0
      );
      return progressPayment || null;
    },
    cellRenderer: p => {
      if (p.value === null || p.value === undefined) {
        return '<span style="color: #999;">—</span>';
      }
      return currencyFormat(p.value, 0);
    }
  },
  { 
    headerName: 'Variance', 
    field: 'lw_variance', 
    width: 120,
    type: 'numericColumn',
    headerClass: 'header-lw-tracking',
    valueGetter: p => {
      const billing = parseFloat(p.data?.current_payment_due || 0);
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, billing, p.data?.contractor_name);
      if (!lw) return null;
      const lwAmount = parseFloat(lw.current_payment_amount || 0);
      return billing - lwAmount;
    },
    cellRenderer: p => {
      if (p.value === null || p.value === undefined) {
        return '<span style="color: #d32f2f; font-weight: bold;">MISSING</span>';
      }
      const variance = p.value || 0;
      if (Math.abs(variance) < 1) {
        return '<span style="color: #4caf50;">$0</span>';
      }
      const color = variance > 0 ? '#d32f2f' : '#ff9800';
      return `<span style="color: ${color}; font-weight: bold;">${currencyFormat(variance, 0)}</span>`;
    }
  },
  { 
    headerName: 'Lien Waiver Is Signed?', 
    field: 'lw_signed', 
    width: 150,
    headerClass: 'header-lw-tracking',
    cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      if (lw.is_signed === true || lw.isSigned === true || lw.signed === true) return true;
      if (lw.waiver_number) return true;
      return false;
    },
    cellRenderer: p => {
      if (p.value === null) return '<span style="color: #d32f2f; font-weight: bold;">MISSING</span>';
      return p.value ? 'Yes' : 'No';
    }
  },
  { 
    headerName: 'Lien Waiver Is Notarized?', 
    field: 'lw_notarized', 
    width: 170,
    headerClass: 'header-lw-tracking',
    cellStyle: { textAlign: 'center' },
    valueGetter: p => {
      const lw = findMatchingLW(p.data?.contractor_id, p.data?.draw_number, p.data?.current_payment_due, p.data?.contractor_name);
      if (!lw) return null;
      if (lw.is_notarized === true || lw.notarized === true || lw.isNotarized === true) return true;
      if (lw.notarizationInfo?.isNotarized === true) return true;
      return false;
    },
    cellRenderer: p => {
      if (p.value === null) return '<span style="color: #d32f2f; font-weight: bold;">MISSING</span>';
      return p.value ? 'Yes' : '<span style="color: #ff9800;">NO</span>';
    }
  }
];

// 🆕 RETAINAGE GRID COLUMN DEFINITIONS
const retainageDetailColumnDefs = [
  // Scopes of Work Group
  { 
    headerName: 'Description of Work', 
    field: 'description', 
    pinned: 'left',
    width: 250,
    headerClass: 'header-scopes-of-work',
    valueGetter: p => p.data?.g703_description || p.data?.scope_detail || p.data?.description || ''
  },
  { 
    headerName: 'Company', 
    field: 'contractor_name', 
    width: 200,
    headerClass: 'header-scopes-of-work',
    valueGetter: p => p.data?.contractor_name || p.data?.contractor_names || 'Unknown'
  },
  // Expenditure Tracking Group
  { 
    headerName: 'Current Billing', 
    field: 'current_payment_due', 
    width: 130,
    type: 'numericColumn',
    headerClass: 'header-expenditure',
    valueGetter: p => parseFloat(p.data?.current_payment_due || 0),
    cellRenderer: p => p.value ? currencyFormat(p.value, 0) : ''
  },
  { 
    headerName: 'Total Billing to Date', 
    field: 'total_completed_and_stored_to_date', 
    width: 150,
    type: 'numericColumn',
    headerClass: 'header-expenditure',
    valueGetter: p => parseFloat(p.data?.total_completed_and_stored_to_date || 0),
    cellRenderer: p => p.value ? currencyFormat(p.value, 0) : ''
  },
  { 
    headerName: 'Complete %', 
    field: 'percent_complete', 
    width: 100,
    type: 'numericColumn',
    headerClass: 'header-expenditure',
    valueGetter: p => {
      const total = parseFloat(p.data?.contract_sum_to_date || p.data?.original_contract_sum || 0);
      const completed = parseFloat(p.data?.total_completed_and_stored_to_date || 0);
      if (total === 0) return 0;
      return (completed / total * 100);
    },
    cellRenderer: p => p.value ? `${p.value.toFixed(1)}%` : ''
  },
  // Retainage Tracking Group
  { 
    headerName: 'Retainage Previous Period', 
    field: 'retainage_previous', 
    width: 170,
    type: 'numericColumn',
    headerClass: 'header-retainage',
    valueGetter: p => {
      // 🔥 FIX: Look up this contractor's retainage in PREVIOUS draw
      const currentDrawNum = parseInt(globalDrawFilter.value);
      const previousDrawNum = currentDrawNum - 1;
      
      if (previousDrawNum < 1) return 0; // No previous draw
      
      const contractorName = (p.data?.contractor_name || p.data?.description || '').toLowerCase().trim();
      if (!contractorName) return 0;
      
      // Find this contractor in previous draw
      const previousDrawRow = (g702RowData.value || []).find(row => {
        const rowDrawNum = parseInt(row.draw_number);
        const rowContractor = (row.contractor_name || row.description || '').toLowerCase().trim();
        return rowDrawNum === previousDrawNum && rowContractor === contractorName;
      });
      
      return parseFloat(previousDrawRow?.retainage || 0);
    },
    cellRenderer: p => p.value ? currencyFormat(p.value, 0) : ''
  },
  { 
    headerName: 'Current Increase/Decrease', 
    field: 'current_retainage_held', 
    width: 170,
    type: 'numericColumn',
    headerClass: 'header-retainage',
    valueGetter: p => {
      // 🔥 FIX: Calculate DELTA from previous draw
      const currentDrawNum = parseInt(globalDrawFilter.value);
      const previousDrawNum = currentDrawNum - 1;
      
      if (previousDrawNum < 1) {
        // For draw 1, the "increase" is the total retainage
        return parseFloat(p.data?.retainage || 0);
      }
      
      const currentRetainage = parseFloat(p.data?.retainage || 0);
      const contractorName = (p.data?.contractor_name || p.data?.description || '').toLowerCase().trim();
      if (!contractorName) return 0;
      
      // Find this contractor in previous draw
      const previousDrawRow = (g702RowData.value || []).find(row => {
        const rowDrawNum = parseInt(row.draw_number);
        const rowContractor = (row.contractor_name || row.description || '').toLowerCase().trim();
        return rowDrawNum === previousDrawNum && rowContractor === contractorName;
      });
      
      const previousRetainage = parseFloat(previousDrawRow?.retainage || 0);
      return currentRetainage - previousRetainage; // Delta
    },
    cellRenderer: p => {
      if (!p.value) return '';
      const color = p.value < 0 ? '#d32f2f' : '#333';
      const formatted = p.value < 0 ? `(${currencyFormat(Math.abs(p.value), 0)})` : currencyFormat(p.value, 0);
      return `<span style="color: ${color};">${formatted}</span>`;
    }
  },
  { 
    headerName: 'Total Retainage Held ($)', 
    field: 'retainage', 
    width: 160,
    type: 'numericColumn',
    headerClass: 'header-retainage',
    valueGetter: p => parseFloat(p.data?.retainage || 0),
    cellRenderer: p => p.value ? currencyFormat(p.value, 0) : ''
  },
  { 
    headerName: '% Held', 
    field: 'retainage_percent', 
    width: 80,
    type: 'numericColumn',
    headerClass: 'header-retainage',
    valueGetter: p => {
      const completed = parseFloat(p.data?.total_completed_and_stored_to_date || 0);
      const retainage = parseFloat(p.data?.retainage || 0);
      if (completed === 0) return 0;
      return (retainage / completed * 100);
    },
    cellRenderer: p => {
      if (!p.value) return '';
      const color = p.value > 10 ? '#d32f2f' : '#333';
      return `<span style="color: ${color}; font-weight: ${p.value > 10 ? 'bold' : 'normal'};">${p.value.toFixed(1)}%</span>`;
    }
  }
];

// 📄 INVOICE GRID COLUMN DEFINITIONS
const invoiceColumnDefs = [
  { 
    headerName: 'Vendor', 
    field: 'vendor', 
    width: 250,
    cellStyle: p => p.node.rowPinned ? { textAlign: 'right', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f0f0f0' } : { textAlign: 'left', fontWeight: '500' }
  },
  { 
    headerName: 'Invoice #', 
    field: 'invoice_number', 
    width: 150,
    cellStyle: p => p.node.rowPinned ? { backgroundColor: '#f0f0f0' } : { textAlign: 'left' }
  },
  { 
    headerName: 'Date', 
    field: 'invoice_date', 
    width: 120,
    valueFormatter: p => {
      if (p.node.rowPinned) return '';
      if (!p.value) return '';
      try {
        const date = new Date(p.value);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      } catch {
        return p.value;
      }
    },
    cellStyle: p => p.node.rowPinned ? { backgroundColor: '#f0f0f0' } : {}
  },
  { 
    headerName: 'Amount', 
    field: 'total_amount', 
    width: 150,
    type: 'numericColumn',
    valueFormatter: p => currencyFormat(p.value || 0),
    cellStyle: p => p.node.rowPinned ? { textAlign: 'right', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f0f0f0', borderTop: '2px solid #333' } : { textAlign: 'right', fontWeight: '500' }
  },
  { 
    headerName: 'Job #', 
    field: 'job_number', 
    width: 150,
    cellStyle: p => p.node.rowPinned ? { backgroundColor: '#f0f0f0' } : { textAlign: 'left' }
  }
];

// 📄 INVOICE TOTAL ROW (computed)
const invoiceTotalRow = computed(() => {
  const total = invoiceRowData.value.reduce((sum, invoice) => {
    return sum + (parseFloat(invoice.total_amount) || 0);
  }, 0);
  
  return [{
    vendor: 'TOTAL',
    invoice_number: '',
    invoice_date: '',
    total_amount: total,
    job_number: ''
  }];
});

function onG702GridReady() {
  console.log('G702 Grid API ready');
  fetchG702DrawDetailGridData();
  isG702GridDataLoaded.value = true;
}

// 📄 INVOICE GRID READY HANDLER
function onInvoiceGridReady(params) {
  invoiceGridApi.value = params.api;
  console.log('Invoice Grid API ready');
  // Data will be fetched by btSubMenuSelectItem -> updateAllChartsAndGrid
}

// 🆕 LW Grid Ready
function onLWGridReady(params) {
  lwGridApi.value = params.api;
  console.log('🔥 Lien Waiver Grid API ready');
  // Data is populated from G702 data
}

// 🆕 Retainage Grid Ready
function onRetainageGridReady(params) {
  retainageGridApi.value = params.api;
  console.log('🔥 Retainage Grid API ready');
  // Data is populated from G702 data
}

// 🆕 HELPER: Refresh Lien Waiver data from G702 data
function refreshLWData() {
  if (btSubMenuSelectedItemId.value !== 5) return; // Only refresh if LW tab is active
  
  // 🔥 FIX: Deduplicate by contractor_name + draw_number + application_number + amount
  // This handles database duplicates and OCR variations
  const rawData = g702RowData.value || [];
  const seen = new Map();
  
  rawData.forEach(row => {
    // Create unique key using normalized contractor name + draw + app# + amount
    const normalizedName = (row.contractor_name || row.contractor_names || '').toLowerCase().trim();
    const appNum = row.application_number || '';
    const amount = Math.round(parseFloat(row.current_payment_due || 0) * 100); // Round to cents
    const key = `${normalizedName}_${row.draw_number}_${appNum}_${amount}`;
    
    if (!seen.has(key)) {
      seen.set(key, row);
    }
    // Skip duplicates - first one wins
  });
  
  lwRowData.value = Array.from(seen.values());
  lwDataCtr.value++;
  console.log(`🔄 LW Data refreshed: ${lwRowData.value.length} rows (deduplicated from ${rawData.length}, draw ${g702DrawNumber.value || globalDrawFilter.value})`);
}

// 🆕 HELPER: Refresh Retainage data from G702 data
function refreshRetainageData() {
  if (btSubMenuSelectedItemId.value !== 7) return; // Only refresh if Retainage tab is active
  
  // 🔥 FIX: Filter to show ONLY current draw in grid, but g702RowData contains ALL draws for comparison
  const currentDraw = globalDrawFilter.value || drawNumber.value;
  if (currentDraw && currentDraw > 0) {
    // Show only current draw rows in the grid
    retainageRowData.value = (g702RowData.value || []).filter(row => {
      const rowDraw = parseInt(row.draw_number || 0);
      return rowDraw === parseInt(currentDraw);
    });
    console.log(`🔄 Retainage Data refreshed: ${retainageRowData.value.length} rows for draw ${currentDraw} (${g702RowData.value?.length} total rows available for comparison)`);
  } else {
    // If no draw filter, show all (shouldn't happen on Retainage tab)
    retainageRowData.value = [...(g702RowData.value || [])];
    console.log(`🔄 Retainage Data refreshed: ${retainageRowData.value.length} rows (all draws)`);
  }
  retainageDataCtr.value++;
}

function fetchG702DrawDetailGridData() {
  if (!selectedId.value) return;
  const norm = v => { const m = v?.toString().match(/\d+/); return m ? parseInt(m[0]) : null; };
  
  // 🔥 SPECIAL CASE: If g702DrawNumber === 'retainage', fetch current + previous draw ONLY
  let drawParam = '';
  let targetNorm = null;
  let fetchMultipleDraws = false;
  
  if (g702DrawNumber?.value === 'retainage') {
    // Retainage tab: Fetch current + previous draw (2 draws, not all - FAST!)
    const currentDraw = norm(globalDrawFilter?.value) || norm(drawNumber?.value);
    const previousDraw = currentDraw > 1 ? currentDraw - 1 : currentDraw;
    
    if (currentDraw && currentDraw > 0) {
      // Fetch both draws in one API call using comma-separated values
      // 🔥 NO g702_only - we need G703 matching to get scope descriptions!
      drawParam = `&draw_numbers=${previousDraw},${currentDraw}`;
      targetNorm = 'RETAINAGE';
      fetchMultipleDraws = true;
      console.log(`🔥 G702 FETCH: Fetching draws ${previousDraw} + ${currentDraw} for retainage with G703 scope matching`);
    } else {
      drawParam = '';
      targetNorm = null;
    }
  } else {
    // Normal tabs: Prefer explicit g702DrawNumber, then global, then main drawNumber
    targetNorm = norm(g702DrawNumber?.value) || norm(globalDrawFilter?.value) || norm(drawNumber?.value);
    // Only send draw filter when we actually have a valid draw number
    drawParam = (targetNorm && targetNorm > 0) ? `&draw_number=${targetNorm}` : '';
  }
  
  const endpoint = `${apiBase}projects/fetch_project_g703?project_ids=${selectedId.value}${drawParam}`;
  console.log(`🔥 G702 FETCH: Calling API endpoint: ${endpoint} (effectiveDraw=${targetNorm})`);
  
  // Use standard auth token (no extended timeout needed for just 2 draws)
  const requestConfig = authToken.value;
  
  axiosQuery(
    endpoint,
    requestConfig,
    (data) => {
      const subs = data?.subcontractor_g702s || [];
      const gcs = data?.g702_contractors || [];  // 🆕 INCLUDE GC G702s
      console.log(`🔥 G702 DATA: Found ${gcs.length} GC G702s + ${subs.length} Sub G702s (database records only)`);
      
      // 🆕 Store Lien Waiver data for LW Tie-Out column
      const lws = data?.lien_waivers || [];
      g702LienWaivers.value = Array.isArray(lws) ? lws : [];
      console.log(`🔥 LW DATA: Found ${g702LienWaivers.value.length} Lien Waivers (flat array for name+amount matching)`);
      
      const target = targetNorm;
      const normDraw = (s) => norm(s.draw_number);
      const beforeLen = g702RowData.value?.length || 0;
      
      // 🆕 COMBINE GC and Sub G702s - Filter by draw number
      const allG702s = [...gcs, ...subs];
      // 🔥 FIX: When target === 'RETAINAGE', keep ALL returned draws (current + previous)
      const combinedData = allG702s.filter(s => target === 'RETAINAGE' || target == null || normDraw(s) === target);
      
      // Sort and organize contractors intelligently
      const sortedData = combinedData.sort((a, b) => {
        // First sort by contractor name (case insensitive)
        const nameA = (a.contractor_name || '').toLowerCase();
        const nameB = (b.contractor_name || '').toLowerCase();
        if (nameA !== nameB) {
          return nameA.localeCompare(nameB);
        }
        
        // Then sort by date (newest first)
        const dateA = new Date(a.period_through || a.period_to || '1900-01-01');
        const dateB = new Date(b.period_through || b.period_to || '1900-01-01');
        return dateB - dateA;
      });
      
      console.log(`🔥 G702 SORTED: ${sortedData.length} contractors sorted by name, then date`);
      g702RowData.value = sortedData;
      const afterLen = g702RowData.value?.length || 0;
      if (beforeLen === 0 && afterLen > 0) {
        // Nudge grid to render once when transitioning from empty → populated
        g702DataCtr.value++;
      }
      
      // 🆕 REFRESH LW & RETAINAGE DATA when G702 data changes
      refreshLWData();
      refreshRetainageData();
      // One-time late retry if draws initialized after first call
      if ((!g702RowData.value || g702RowData.value.length === 0) && target) {
        setTimeout(() => {
          const retryTarget = norm(g702DrawNumber?.value) || norm(globalDrawFilter?.value) || norm(drawNumber?.value);
          const retryParam = (retryTarget && retryTarget > 0) ? `&draw_number=${retryTarget}` : '';
          if (retryParam !== drawParam) {
            axiosQuery(`${apiBase}projects/fetch_project_g703?project_ids=${selectedId.value}${retryParam}`,
              authToken,
              (d2) => {
                const subs2 = d2?.subcontractor_g702s || [];
                const gcs2 = d2?.g702_contractors || [];  // 🆕 INCLUDE GC G702s in retry
                const allG702s2 = [...gcs2, ...subs2];
                g702RowData.value = allG702s2.filter(s => norm(s.draw_number) === retryTarget);
              },
              errorStore
            );
          }
        }, 250);
      }
    },
    errorStore
  );
}

// 📄 FETCH INVOICE DATA
async function fetchInvoiceData() {
  // 🛡️ GUARD: Prevent multiple simultaneous fetches
  if (isInvoiceFetching.value) {
    console.log('⏸️ Invoice fetch already in progress, skipping...');
    return;
  }
  
  if (!selectedId.value) {
    console.warn('No project selected');
    return;
  }
  
  const drawNum = globalDrawFilter.value || drawNumber.value;
  if (!drawNum) {
    console.warn('No draw number selected');
    invoiceRowData.value = [];
    return;
  }
  
  isInvoiceFetching.value = true;
  console.log(`📄 Fetching invoices for project ${selectedId.value}, draw ${drawNum}...`);
  
  try {
    const beforeLen = invoiceRowData.value?.length || 0;
    
    const response = await axios.get(
      `${apiBase}projects/${selectedId.value}/fetch_project_invoices`,
      {
        params: { draw_number: drawNum },
        headers: authToken.value
      }
    );
    
    if (response.data.success) {
      invoiceRowData.value = response.data.invoices || [];
      console.log(`✅ Loaded ${invoiceRowData.value.length} invoices`);
      
      // 🎯 CRITICAL: Only increment counter when transitioning from empty → populated
      // This prevents flashing on subsequent fetches (like G702 does)
      const afterLen = invoiceRowData.value?.length || 0;
      if (beforeLen === 0 && afterLen > 0) {
        invoiceDataCtr.value++;
        console.log(`🔄 Invoice grid counter incremented to ${invoiceDataCtr.value} (empty → populated)`);
      }
      
      // Log summary stats
      if (response.data.summary) {
        console.log(`📊 Invoice Summary: Total: ${response.data.summary.total_count}, Amount: $${response.data.summary.total_amount}, Vendors: ${response.data.summary.vendors_count}`);
      }
    } else {
      console.error('Failed to fetch invoices:', response.data.error);
      invoiceRowData.value = [];
    }
  } catch (error) {
    console.error('Error fetching invoices:', error);
    invoiceRowData.value = [];
  } finally {
    isInvoiceFetching.value = false;
  }
}

// Tree data path functions for AG-Grid hierarchy
const getDrawDetailDataPath = (data) => {
  return data.itemHierarchy || ['General'];
};

const getG703DataPath = (data) => {
  return data.itemHierarchy || ['General'];
};

const getG702DataPath = (data) => {
  return data.itemHierarchy || ['General'];
};
function fetchDrawDetailGridData(forCostCurve = false) {
  if (selectedId.value) {
    // 🔥 CRITICAL FIX: Grid should show only selected draw, charts need all data
    let endpoint;
    if (forCostCurve) {
      // Cost curve always needs all data - no filtering
      endpoint = `${apiBase}projects/fetch_project_draw_details?project_ids=${selectedId.value}`;
    } else {
      // Grid should show only the selected draw to avoid duplicates and show correct Current Billing
      endpoint = `${apiBase}projects/fetch_project_draw_details?project_ids=${selectedId.value}&draw_number=${drawNumber.value}`;
    }
    
    console.log(`🔄 Fetching draw details with${forCostCurve ? 'out' : ''} draw filter: ${endpoint}`);
    
    axiosQuery(
      endpoint,
      authToken,
      async (data) => {
        // 🛡️ GUARD: Don't update if component was unmounted during async operation
        if (!isComponentMounted) {
          console.log('🛡️ fetchDrawDetailGridData: Component unmounted, skipping update');
          return;
        }
        
        // Initialize with empty array to prevent null reference errors
        drawDetailRowData.value = [];
        
        // 🔥 MINIMAL FIX: Handle new API format but keep September 10th logic
        let processData = data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          if (data.draw_details && Array.isArray(data.draw_details)) {
            processData = data.draw_details;
            console.log(`🔍 Using draw_details: ${processData.length} rows`);
          } else {
            processData = [];
          }
        }
        
        if (!processData || processData.length === 0) {
          isNoDataFetch.value = true;
        } else {
          // For cost curve, we want to process all draws
          // For grid view, backend filtering is already applied
          // 🔥 CRITICAL FIX: Single processing path to prevent duplicates
          if (forCostCurve) {
            
            // Don't consolidate for cost curve - keep each draw separate
            processData?.forEach((row, index) => {
              const description = row.scope_detail || row.description || 'General Work';
              const contractorName = row.contractor_name || 'Unknown';
              
              const aRow = {
                id: `cost_curve_${row.id}_${index}`, // 🔥 ENSURE UNIQUE ID for cost curve
                draw_number: row.draw_number || '',
                scope_detail: description,
                description: description,
                contractor_name: contractorName,
                company: contractorName,
                main_category: row.main_category || 'General',
                sub_category_1: row.sub_category_1 || '',
                sub_category_2: row.sub_category_2 || '',
                sub_category_3: row.sub_category_3 || '',
                hierarchy_level: row.hierarchy_level || 1,
                original_budget: (row.original_budget || 0) / viewModeDivisor.value,
                current_budget: (row.current_budget || 0) / viewModeDivisor.value,
                // 🎯 FIXED: If no spent_to_date column mapped, calculate from current_budget - remaining_budget
                total_completed_to_date: ((row.current_budget || 0) - (row.balance_to_complete || 0)) / viewModeDivisor.value,
                previously_funded: (row.previously_funded || 0) / viewModeDivisor.value,
                current_request: (row.current_request || 0) / viewModeDivisor.value,
                variance: (row.original_budget && row.original_budget > 0) ? ((row.current_budget - row.original_budget) / row.original_budget) : 0,
                // FIXED BILLING LOGIC: Use simple budget data calculations
                // Total Billing to Date = current_budget - balance_to_complete
                total_billing_to_date: ((row.current_budget || 0) - (row.balance_to_complete || 0)) / viewModeDivisor.value,
                // FIXED: Calculate % complete as (Total Billing to Date / Current Budget)
                percent_complete: (row.current_budget && row.current_budget > 0) ? (((row.current_budget || 0) - (row.balance_to_complete || 0)) / row.current_budget) : 0,
                is_parent: false,
                is_indented: false,
                document_id: row.document_id,
                document_filepath: row.document_filepath
              };
              
              // 🔥 FIX: Replace "Unknown" with "-" for rows with all $0 values
              if ((aRow.description === 'Unknown' || aRow.description === '') && 
                  aRow.original_budget === 0 && 
                  aRow.current_budget === 0 && 
                  aRow.total_billing_to_date === 0) {
                aRow.description = '-';
              }
              
              // 🔥 NEW: Exclude computed totals from grid (they're only for charts)
              if (!row.is_computed_total) {
                drawDetailRowData.value.push(aRow);
              }
            });
          } else {
            // CONSOLIDATE GRID DATA: Group by description and keep latest draw values
            
            // Sort data by source_row_position for Excel only - let AG Grid handle G703 sorting
            const sortDrawDetailData = (data) => {
              return data.sort((a, b) => {
                // Only apply custom sorting for Excel data with source_row_position
                if (a.source_row_position != null && b.source_row_position != null) {
                  return a.source_row_position - b.source_row_position;
                }
                
                // If only one has source_row_position, prioritize it
                if (a.source_row_position != null && b.source_row_position == null) return -1;
                if (a.source_row_position == null && b.source_row_position != null) return 1;
                
                // For G703 data or data without source_row_position, use minimal sorting
                // Let AG Grid handle the actual column sorting when user clicks headers
                const aDescription = (a.scope_detail || a.description || '').toLowerCase();
                const bDescription = (b.scope_detail || b.description || '').toLowerCase();
                return aDescription.localeCompare(bDescription);
              });
            };
            
            // Apply sorting before processing
            processData = sortDrawDetailData(processData);
            
            // 🔥 NO CONSOLIDATION: Show ALL rows exactly as they are in Excel
            console.log(`🔥 Processing ALL ${processData.length} rows WITHOUT consolidation - keeping headers, blanks, everything`);
          
            // Process each row as-is, preserving original Excel structure
            processData?.forEach((row, index) => {
              // FIXED: Only use real contractor data from backend - NO fake extraction from descriptions
              let contractorName = 'N/A';
              
              // Use actual contractor_name from backend if it exists and is valid
              if (row.contractor_name && row.contractor_name !== 'Unknown' && row.contractor_name.trim() !== '') {
                contractorName = row.contractor_name;
              }
              // NOTE: row.company field doesn't exist in DrawDetail table - removed that check
              
              // Create itemHierarchy array for tree structure based on hierarchy_level from backend
              const hierarchyPath = [];
              
              // FIXED: Respect hierarchy_level from backend for flat vs hierarchical items
              if (row.hierarchy_level === 1) {
                // Flat items: Only use the description (no nesting)
                hierarchyPath.push(row.scope_detail || row.description || 'General Work');
              } else {
                // Hierarchical items: Build multi-level hierarchy
              const mainCat = row.main_category || 'General';
              hierarchyPath.push(mainCat);
              
              // Add subcategories if they exist
              if (row.sub_category_1 && row.sub_category_1.trim() !== '') {
                hierarchyPath.push(row.sub_category_1);
                if (row.sub_category_2 && row.sub_category_2.trim() !== '') {
                  hierarchyPath.push(row.sub_category_2);
                  if (row.sub_category_3 && row.sub_category_3.trim() !== '') {
                    hierarchyPath.push(row.sub_category_3);
                  }
                }
                } else if (row.hierarchy_level > 1) {
                  // Multi-level but no subcategories - add description as leaf
                hierarchyPath.push(row.scope_detail || row.description || 'General Work');
                }
              }
              
              // 🎯 SIMPLIFIED: Current billing is just the direct current_request field from spreadsheet
              // No computation needed - it's an extracted column!
              
              // 🔥 FIX: Check if this is a blank/Unknown row with all zeros BEFORE creating aRow
              const isBlankRow = (!row.description || row.description === '' || row.description === 'Unknown') &&
                                 (!row.scope_detail || row.scope_detail === '') &&
                                 (row.original_budget || 0) === 0 &&
                                 (row.current_budget || 0) === 0 &&
                                 (row.balance_to_complete || 0) === 0 &&
                                 (row.current_request || 0) === 0;
              
              // Create row exactly as it comes from Excel - no consolidation
              const aRow = {
                id: row.id ? `grid_${row.id}_${index}` : `generated_${index}_${Date.now()}`, // 🔥 ENSURE UNIQUE ID
                db_id: row.id, // 🔥 STORE REAL DATABASE ID separately for updates
                is_grouped_row: false, // This is original data, not consolidated
                draw_number: row.draw_number || '',
                scope_detail: row.scope_detail || '',
                description: isBlankRow ? '-' : (row.scope_detail || row.description || 'General Work'),
                contractor_name: contractorName,
                company: contractorName,
                // Mark total/subtotal rows from backend so we can exclude from sums
                is_total_row: (row.is_total_row === true) || (!!row.dda_term && ((row.dda_term.is_total === true) || (row.dda_term.is_total_row === true))) || (typeof row.total_type === 'string' && row.total_type.toLowerCase().includes('total')),
                original_budget: (row.original_budget || 0) / viewModeDivisor.value,
                total_adjustments: (parseFloat(row.current_budget || 0) - parseFloat(row.original_budget || 0)) / viewModeDivisor.value,
                // ✅ FIX: Use API values for prior/current adjustments instead of recalculating
                previous_adjustments: (row.previous_adjustments || 0) / viewModeDivisor.value,
                current_adjustments: (row.current_adjustments || 0) / viewModeDivisor.value,
                current_budget: (row.current_budget || 0) / viewModeDivisor.value,
                remaining_budget: (row.balance_to_complete || 0) / viewModeDivisor.value,
                variance: (row.original_budget && row.original_budget > 0) ? ((row.current_budget - row.original_budget) / row.original_budget) : 0,
                current_request: (row.current_request || 0) / viewModeDivisor.value,
                draw_summary: row.current_request || 0,
                // FIXED BILLING LOGIC: Use simple budget data calculations
                // Total Billing to Date = current_budget - balance_to_complete
                total_billing_to_date: ((row.current_budget || 0) - (row.balance_to_complete || 0)) / viewModeDivisor.value,
                percent_complete: (row.current_budget && row.current_budget > 0) ? (((row.current_budget || 0) - (row.balance_to_complete || 0)) / row.current_budget) : 0,
                previously_funded: (row.previously_funded || 0) / viewModeDivisor.value,

                // 🎯 FIXED: If no spent_to_date column mapped, calculate from current_budget - remaining_budget
                total_completed_to_date: ((row.current_budget || 0) - (row.balance_to_complete || 0)) / viewModeDivisor.value,
                
                // Tree structure for AG-Grid - PRESERVE ORIGINAL HIERARCHY
                itemHierarchy: hierarchyPath,
                
                // Exact column-based hierarchy metadata from Excel
                main_category: row.main_category || 'General',
                sub_category_1: row.sub_category_1 || '',
                sub_category_2: row.sub_category_2 || '',
                sub_category_3: row.sub_category_3 || '',
                hierarchy_level: row.hierarchy_level || 1,
                is_parent: false,
                is_indented: false,
                
                // Data source tracking
                data_source: 'excel_template',
                is_contractor_data: false,
                dda_term_id: row.dda_term_id || null,
                dda_term: row.dda_term || null,
                dda_term_name: row.dda_term_name || null,
                category_override: row.category_override || null, // 🔥 CATEGORY OVERRIDE
                
                // CRITICAL: Preserve source document reference and order
                document_id: row.document_id,
                document_filepath: row.document_filepath,
                source_row_position: row.source_row_position // PRESERVE EXCEL SOURCE ORDER
              };
              
              // 🔥 FIX: Replace "Unknown" with "-" for rows with all $0 values
              if ((aRow.description === 'Unknown' || aRow.description === '') && 
                  aRow.original_budget === 0 && 
                  aRow.current_budget === 0 && 
                  aRow.total_billing_to_date === 0 && 
                  aRow.remaining_budget === 0) {
                aRow.description = '-';
                aRow.dda_term_id = null;
                aRow.dda_term = null;
                aRow.dda_term_name = '-';
              }
              
              // 🔥 NEW: Exclude computed totals from grid (they're only for charts)
              if (!row.is_computed_total) {
                drawDetailRowData.value.push(aRow);
              }
            });
          
          // CRITICAL: Sort final results by source_row_position to preserve Excel order
          drawDetailRowData.value.sort((a, b) => {
            // First priority: source_row_position (Excel order)
            if (a.source_row_position != null && b.source_row_position != null) {
              return a.source_row_position - b.source_row_position;
            }
            
            // If only one has source_row_position, prioritize it
            if (a.source_row_position != null && b.source_row_position == null) return -1;
            if (a.source_row_position == null && b.source_row_position != null) return 1;
            
            // Fallback: sort by category hierarchy
            const aCategory = a.main_category || '';
            const bCategory = b.main_category || '';
            if (aCategory !== bCategory) {
              return aCategory.localeCompare(bCategory);
            }
            
            // Then by description
            const aDescription = (a.scope_detail || a.description || '').toLowerCase();
            const bDescription = (b.scope_detail || b.description || '').toLowerCase();
            return aDescription.localeCompare(bDescription);
          });
          
          } // Close the grouped data processing
          
          // 🔄 LOAD DDA TERMS: Ensure DDA terms are loaded for proper highlighting
          if (!ddaTerms.value || ddaTerms.value.length === 0) {
            await loadDDATermsForCharts();
          }
          
          // 🔄 POPULATE DDA TERM OBJECTS: Fix highlighting for existing data
          drawDetailRowData.value.forEach(row => {
            if (row.dda_term_id && (!row.dda_term || typeof row.dda_term === 'string')) {
              const fullDDAterm = ddaTerms.value?.find(t => t.id === row.dda_term_id);
              if (fullDDAterm) {
                row.dda_term = fullDDAterm;
              }
            }
          });
          
          // 🔄 LOAD TEMPLATE DDA MAPPINGS: Apply saved QA selections before auto-matching
          // Check if we have a template ID from URL parameters (from recent template completion)
          const templateId = route.query.template_id || route.query.templateId;
          await loadAndApplyTemplateDDAMappings(templateId);
          
          // ➕ Check if Project Total exists (DDA term ID 11642)
          hasProjectTotal.value = drawDetailRowData.value.some(row => row.dda_term_id === 11642);
          console.log(`📊 Project Total check: ${hasProjectTotal.value} (found in ${drawDetailRowData.value.length} rows)`);
          
          // 🔄 AUTO-MATCH DDA TERMS: Check for missing DDA terms and auto-assign
          // Uses debounce to prevent spam when rapidly switching draws
          if (drawDetailRowData.value.length > 0) {
            // 🔥 DEBOUNCE: Cancel any pending auto-match when draw changes
            if (autoMatchDebounceTimer) {
              clearTimeout(autoMatchDebounceTimer);
            }
            autoMatchDebounceTimer = setTimeout(() => {
              // 🔥 Refresh auth token before auto-matching to prevent 401 errors
              authToken.value = authStore.getAuthTokenHeader;
              autoMatchMissingDDATerms();
            }, 500); // 500ms debounce - responsive but prevents rapid-fire
          }
          
          drawDetailDataCtr.value++;
          
          // 🚀 CRITICAL FIX: Rebuild chart data AFTER fetch completes with correct divisor
          // This ensures the chart uses the latest data with the current viewModeDivisor
          if (drawDetailRowData.value && drawDetailRowData.value.length > 0 && !forCostCurve) {
            console.log(`🔄 Post-fetch: Re-building chart data with divisor=${viewModeDivisor.value}`);
            buildChartDataFromDDATermTotals(drawDetailRowData.value);
          }
      }
      },
      errorStore
    );
  } else {
    isNoDataFetch.value = false;
  }
  isDrawDetailGridDataLoaded.value = true;
}

function fetchG703DrawDetailGridData() {
  // Do not fetch or mutate G703 unless the G703 tab is active
  if (btSubMenuSelectedItemId.value !== 2) return;
  if (selectedId.value) {
    // Use the PROPER G703 API endpoint - fetch all G703 data (no draw filter for now)
    // Use the UI-selected G703 draw filter, not the upload modal value
    const drawParam = (g703DrawNumber && g703DrawNumber.value && g703DrawNumber.value !== 'all')
      ? `&draw_number=${g703DrawNumber.value}`
      : '';
    const endpoint = `${apiBase}projects/fetch_project_g703?project_ids=${selectedId.value}${drawParam}`;
    console.log(`🔥 G703 FETCH: Calling API endpoint: ${endpoint}`);
    axiosQuery(
      endpoint,
      authToken,
      async (data) => {
        // 🛡️ GUARD: Don't update if component was unmounted during async operation
        if (!isComponentMounted) {
          console.log('🛡️ fetchG703DrawDetailGridData: Component unmounted, skipping update');
          return;
        }
        
        // Handle the enhanced response structure from backend
        const g703Records = data.g703_data || data || [];
        
        // 🔍 DEBUG: Log what we got from API
        console.log(`🔍 G703 API Response: ${g703Records.length} total records received`);
        if (g703Records.length > 0) {
          console.log(`🔍 First G703 record:`, {
            description: g703Records[0].description,
            original_budget: g703Records[0].original_budget,
            current_budget: g703Records[0].current_budget,
            draw_number: g703Records[0].draw_number
          });
        }
        
        // 🔗 NEW: Store template information for links
        g703TemplateInfo.value = data.document_template_info || [];
        console.log(`🔗 G703 Template Info: Found ${g703TemplateInfo.value.length} documents with template information`);
        
        // 📋 Extract application number and period through from first G703 record
        if (g703Records && g703Records.length > 0) {
          g703ApplicationNumber.value = g703Records[0].application_number || null;
          g703PeriodThrough.value = g703Records[0].period_through || null;
          console.log(`📋 G703 Chart Data: App #${g703ApplicationNumber.value}, Period: ${g703PeriodThrough.value}`);
          
          // 💰 CRITICAL FIX: Use "Project Total" row (DDA Term 11642) instead of summing all records to avoid double counting
          const PROJECT_TOTAL_DDA_TERM_ID = 11642;
          const projectTotalRow = g703Records.find(record => record.dda_term_id === PROJECT_TOTAL_DDA_TERM_ID);
          
          if (projectTotalRow) {
            console.log(`✅ Found Project Total row (DDA Term ${PROJECT_TOTAL_DDA_TERM_ID}): ${projectTotalRow.description}`);
            
            // Extract values from Project Total row
            const originalBudget = parseFloat(projectTotalRow.original_budget || 0);
            const currentBudget = parseFloat(projectTotalRow.current_budget || 0);
            const currentRequest = parseFloat(projectTotalRow.current_request || 0);
            
            // 🔄 FALLBACK: Use current_adjustments if change_orders is null (old records)
            const changeOrders = parseFloat(projectTotalRow.change_orders || projectTotalRow.current_adjustments || 0);
            
            // 🔄 FALLBACK: Use all_budget_transfers if available, otherwise calculate from budget difference
            let budgetTransfers = parseFloat(projectTotalRow.all_budget_transfers || 0);
            if (budgetTransfers === 0 && currentBudget !== originalBudget) {
              // If no explicit budget_transfers but budgets differ, it could be reallocations
              // For now, calculate as: Current Budget - Original Budget - Change Orders
              budgetTransfers = currentBudget - originalBudget - changeOrders;
            }
            
            // Reallocations = Budget Transfers (internal reallocations between line items)
            g703Reallocations.value = budgetTransfers;
            g703ReallocationsPct.value = originalBudget > 0 ? (budgetTransfers / originalBudget) * 100 : 0;
            
            // Net Change = Change Orders (external contract changes)
            g703NetChange.value = changeOrders;
            g703NetChangePct.value = originalBudget > 0 ? (changeOrders / originalBudget) * 100 : 0;
            
            // Contract Sum to Date = Current Budget (original + all changes)
            g703ContractSum.value = currentBudget;
            
            // Current Request = Current draw request
            g703CurrentRequest.value = currentRequest;
            
            console.log(`💰 G703 Payment Variances from Project Total: Reallocations=${g703Reallocations.value}, NetChange=${g703NetChange.value}, ContractSum=${g703ContractSum.value}, CurrentRequest=${g703CurrentRequest.value}`);
          } else {
            console.warn(`⚠️ No Project Total row found with DDA Term ${PROJECT_TOTAL_DDA_TERM_ID}, chart will show $0`);
            g703Reallocations.value = 0;
            g703ReallocationsPct.value = 0;
            g703NetChange.value = 0;
            g703NetChangePct.value = 0;
            g703ContractSum.value = 0;
            g703CurrentRequest.value = 0;
          }
        }
        
        if (!Array.isArray(g703Records) || g703Records.length === 0) {
          console.log(`🔥 G703 NO DATA: Setting isNoDataFetch = true`);
          isNoDataFetch.value = true;
          g703RowData.value = [];
        } else {
          g703RowData.value = [];
          let skippedCount = 0;
          
          // Process G703 data - we need to extract or create proper trade codes
          g703Records.forEach((g703Record, index) => {
            // ONLY skip rows with header text (NOT blank descriptions - those are valid!)
            if (g703Record.description && (
                g703Record.description.toLowerCase().trim() === 'description of work' ||
                g703Record.description.toLowerCase().trim() === 'trade code' ||
                g703Record.description.toLowerCase().trim() === 'contractor name' ||
                g703Record.description.toLowerCase().trim() === 'scheduled value')) {
              console.log(`Skipping G703 header row: "${g703Record.description}"`);
              skippedCount++;
              return; // Skip this iteration
            }
            
            // DISPLAY EXACTLY AS EXTRACTED FROM DATABASE - NO MODIFICATIONS
            
            const aRow = {
              id: g703Record.id || `g703_${index}`,
              // Use EXACT data from database with NO modifications
              trade_code: g703Record.trade_code,
              description: g703Record.description,
              contractor_names: g703Record.contractor_names,
              contractor_name: g703Record.contractor_names, // Legacy field
              // DDA Term fields
              dda_term_id: g703Record.dda_term_id,
              dda_term_name: g703Record.dda_term_name,
              dda_term: g703Record.dda_term,
              original_budget: parseFloat(g703Record.original_budget || 0),
              all_budget_transfers: parseFloat(g703Record.all_budget_transfers || 0),
              owner_previous_approved: parseFloat(g703Record.owner_previous_approved || 0),
              owner_approved_completed: parseFloat(g703Record.owner_approved_completed || 0),
              current_budget: parseFloat(g703Record.current_budget || 0),
              subcontractor_completed: parseFloat(g703Record.subcontractor_completed || 0),
              previously_funded: parseFloat(g703Record.previously_funded || 0),
              current_request: parseFloat(g703Record.current_request || 0),
              materials_presently_stored: parseFloat(g703Record.materials_presently_stored || 0),
              total_completed_to_date: parseFloat(g703Record.total_completed_to_date || 0),
              total_completed_to_date_pct: parseFloat(g703Record.total_completed_to_date_pct || 0),
              balance_to_complete: parseFloat(g703Record.balance_to_complete || 0),
              retainage_percent: parseFloat(g703Record.retainage_percent || 0),
              current_retainage_held: parseFloat(g703Record.current_retainage_held || 0),
              total_retainage_held: parseFloat(g703Record.total_retainage_held || 0),
              draw_number: g703Record.draw_number,
              application_number: g703Record.application_number,
              period_through: g703Record.period_through,
              document_id: g703Record.document_id,
              document_filepath: g703Record.document_filepath,
              source_row_position: index,
              // 🔥 FIX: Add total_adjustments for budget movements chart
              total_adjustments: parseFloat(g703Record.current_budget || 0) - parseFloat(g703Record.original_budget || 0),
              current_adjustments: parseFloat(g703Record.current_budget || 0) - parseFloat(g703Record.original_budget || 0)
            };

            g703RowData.value.push(aRow);


          });
          
          console.log(`📊 G703 Processing Complete: ${g703RowData.value.length} rows added, ${skippedCount} header rows skipped (from ${g703Records.length} API records)`);
          
          // Skip auto-matching in UI to avoid churn and API noise; handled elsewhere when needed
          
          g703DataCtr.value++;
          
          // 🔥 NEW: Auto-match DDA terms for G703 construction terms
          // Uses debounce to prevent spam when rapidly switching draws
          if (g703RowData.value.length > 0) {
            // 🔥 DEBOUNCE: Cancel any pending auto-match when draw changes
            if (autoMatchDebounceTimer) {
              clearTimeout(autoMatchDebounceTimer);
            }
            autoMatchDebounceTimer = setTimeout(() => {
              // 🔥 Refresh auth token before auto-matching to prevent 401 errors
              authToken.value = authStore.getAuthTokenHeader;
              autoMatchMissingG703DDATerms();
            }, 500); // 500ms debounce - responsive but prevents rapid-fire
          }
        }
      },
      errorStore
    );
  } else {
    isNoDataFetch.value = false;
  }
  isG703GridDataLoaded.value = true;
}

// Upload Draw Modal
let showUploadDrawModal = ref(false);

// NEW: Template Queue Management
const showNewUploadModal = ref(false);
const templateQueue = ref([]);
const isProcessingQueue = ref(false);
let uploadModalKey = ref(0); // Force re-render key
let uploadedDocumentId = ref(null);
let selectedFile = ref(null);
let selectedTemplate = ref('');
let selectedDrawNumber = ref('');
let useCustomDrawNumber = ref(false);
let availableTemplates = ref([]);
let appendToCurrent = ref(false);

// G703 Template Modal
let showG703TemplateModal = ref(false);
let g703TemplateId = ref(null);
let g703SampleFileName = ref('');
let isDragOver = ref(false);
let isUploading = ref(false);
let uploadProgress = ref(0);
let uploadStatus = ref('');
let ocrProgress = ref(0);
let ocrStatus = ref('');
let showOCRProgress = ref(false);
let showExtractionResults = ref(false);
let extractionResults = ref(null);
let existingDraws = ref([]);
let suggestedDrawNumber = ref('');

// Computed helpers for append option visibility and template type
const selectedTemplateDataComputed = computed(() => {
  return availableTemplates.value.find(t => t.id === selectedTemplate.value || t.value === selectedTemplate.value);
});

const isExcelSelected = computed(() => {
  const t = selectedTemplateDataComputed.value;
  if (!selectedTemplate.value && !t) return false;
  return selectedTemplate.value === 'excel_draw_tracker' ||
         t?.template_type === 'excel_draw_tracker' ||
         (t?.name && t.name.toLowerCase().includes('excel'));
});

const isExistingSelectedDraw = computed(() => {
  return !!selectedDrawNumber.value && existingDraws.value.some(d => d.draw_number == selectedDrawNumber.value);
});

// Fetch existing draws when modal opens
async function fetchProjectDraws() {
  try {
    console.log('🔄 Fetching project draws for project:', selectedId.value);
    
    // QUICK FIX: Use the working drawNumberList that gets populated on component mount
    if (drawNumberList.value && drawNumberList.value.length > 0) {
      console.log('📊 Using existing drawNumberList instead of broken endpoint');
      console.log('📋 Available draws from drawNumberList:', drawNumberList.value);
      
      // drawNumberList is already sorted in descending order, let's sort ascending for display
      const sortedDrawNumbers = [...drawNumberList.value].sort((a, b) => {
        const aNum = parseInt(a.toString().match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.toString().match(/\d+/)?.[0] || '0');
        return aNum - bNum; // Ascending order
      });
      
      console.log('📋 Sorted draws for dropdown:', sortedDrawNumbers);
      
      // Convert to the format the dropdown expects
      existingDraws.value = sortedDrawNumbers.map((drawNum, index) => ({
        id: `draw_${index}`,
        draw_number: drawNum,
        period_through: null,
        status: 'existing'
      }));
      
      // Calculate suggested next draw (highest + 1)
      if (sortedDrawNumbers.length > 0) {
        const lastDrawNum = Math.max(...sortedDrawNumbers.map(d => parseInt(d.toString().match(/\d+/)?.[0] || '0')));
        suggestedDrawNumber.value = String(lastDrawNum + 1);
        console.log('💡 Suggested next draw:', suggestedDrawNumber.value);
      } else {
        suggestedDrawNumber.value = '1';
      }
      
      // If no draws exist, pre-fill with suggested draw
      if (existingDraws.value.length === 0 && !selectedDrawNumber.value) {
        selectedDrawNumber.value = suggestedDrawNumber.value;
      }
      
      console.log('✅ Successfully populated draw dropdown:', existingDraws.value.map(d => d.draw_number));
      return;
    }
    
    // Fallback: Try extracting from drawDetailRowData if drawNumberList is empty
    if (drawDetailRowData.value && drawDetailRowData.value.length > 0) {
      console.log('📊 Fallback: Using existing draw detail data instead of broken endpoint');
      
      // Extract unique draw numbers from existing data
      const uniqueDrawNumbers = [...new Set(
        drawDetailRowData.value
          .map(row => row.draw_number)
          .filter(num => num != null && num !== undefined)
          .sort((a, b) => a - b)
      )];
      
      console.log('📋 Found existing draws from drawDetailRowData:', uniqueDrawNumbers);
      
      // Convert to the format the dropdown expects
      existingDraws.value = uniqueDrawNumbers.map((drawNum, index) => ({
        id: `draw_${index}`,
        draw_number: drawNum,
        period_through: null,
        status: 'existing'
      }));
      
      // Calculate suggested next draw (highest + 1)
      if (uniqueDrawNumbers.length > 0) {
        const lastDraw = Math.max(...uniqueDrawNumbers);
        suggestedDrawNumber.value = String(lastDraw + 1);
        console.log('💡 Suggested next draw:', suggestedDrawNumber.value);
      } else {
        suggestedDrawNumber.value = '1';
      }
      
      // If no draws exist, pre-fill with suggested draw
      if (existingDraws.value.length === 0 && !selectedDrawNumber.value) {
        selectedDrawNumber.value = suggestedDrawNumber.value;
      }
      
      console.log('✅ Successfully populated draw dropdown from fallback:', existingDraws.value.map(d => d.draw_number));
      return;
    }
    
    // Fallback: Try the original endpoint (will fail but we handle it gracefully)
    const response = await axios.get(
      `${apiBase}projects/fetch_project_dd_draw_numbers?project_ids=${selectedId.value}`, 
      { headers: authToken.value }
    );
    
    if (response.data) {
      const drawNumbers = response.data || [];
      existingDraws.value = drawNumbers.map((drawNum, index) => ({
        id: `draw_${index}`,
        draw_number: drawNum,
        period_through: null,
        status: 'existing'
      }));
      
      if (drawNumbers.length > 0) {
        const lastDraw = drawNumbers[drawNumbers.length - 1];
        if (lastDraw && lastDraw.match(/APP-(\d+)/)) {
          const num = parseInt(lastDraw.match(/APP-(\d+)/)[1]);
          suggestedDrawNumber.value = `APP-${num + 1}`;
        } else if (lastDraw && lastDraw.match(/\d+/)) {
          const num = parseInt(lastDraw.match(/\d+/)[0]);
          suggestedDrawNumber.value = String(num + 1);
        }
      } else {
        suggestedDrawNumber.value = '1';
      }
      
      if (existingDraws.value.length === 0 && !selectedDrawNumber.value) {
        selectedDrawNumber.value = suggestedDrawNumber.value;
      }
    }
  } catch (error) {
    console.error('Error fetching project draws:', error);
    // Default to suggesting "1" if fetch fails
    suggestedDrawNumber.value = '1';
    existingDraws.value = []; // Make sure it's empty array, not undefined
  }
}

function handleDrawInput(event) {
  // Validation will happen on submit
  // This allows users to enter any draw number they want
}

function formatDate(dateString) {
  if (!dateString) return 'No date';
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

function formatDateOnly(dateString) {
  if (!dateString) return null;
  // Handle ISO dates with timezone (e.g., "2017-07-31T00:00:00.000Z")
  // Extract just the date portion in MM-DD-YY format
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}-${day}-${year}`;
}

async function openUploadModal() {
  // Reset ALL modal state before opening
  closeUploadModal(); // This ensures clean state
  
  // Force re-render of modal to clear any leftover DOM state
  uploadModalKey.value++;
  
  // Open the NEW redesigned modal
  showNewUploadModal.value = true;
  
  // Fetch both templates and existing draws
  await Promise.all([
    fetchAvailableTemplates(),
    fetchProjectDraws()
  ]);
  
  console.log('✅ Upload modal opened with clean state, key:', uploadModalKey.value);
  
  // Track in Datadog
  if (datadogRum) {
    datadogRum.addAction('draw_upload_modal_opened', {
      project_id: selectedId.value,
      project_name: projectName.value
    });
  }
}

// Fetch available templates when component mounts
async function fetchAvailableTemplates() {
  try {
    // Include project_id to get both base templates AND project-specific templates
    const projectId = selectedId.value || route.params.id;
    const apiUrl = projectId ? `${apiBase}draw_templates?project_id=${projectId}` : `${apiBase}draw_templates`;
    const response = await axios.get(apiUrl, { headers: authToken.value });
    
    // SIMPLIFIED: Only PDF and XLS options - document types handled in Step 1 of new PDF uploader
    const defaultTemplates = [
      { id: 'pdf_multi_doc', name: 'PDF Document (G702/G703/Lien Waiver)', value: 'pdf_multi_doc', template_type: 'pdf_multi_doc' },
      { id: 'pdf_draw_tracker', name: 'PDF Draw Tracker', value: 'pdf_draw_tracker', template_type: 'pdf_draw_tracker' },
      { id: 'excel_draw_tracker', name: 'Excel/XLS Document', value: 'excel_draw_tracker', template_type: 'excel_draw_tracker' }
    ];
    
    if (response.data && response.data.length > 0) {
      // Filter for only the new simplified template types + existing project-specific templates
      availableTemplates.value = response.data
        .filter(template => {
          // Exclude deprecated "Project Template" entries (legacy naming pattern)
          if (template.name && template.name.includes('Project Template')) {
            return false;
          }
          
          // Keep ONLY current project-specific templates (PDF draw tracker, Excel draw tracker)
          // Exclude deprecated Excel project templates
          if (template.project_specific) {
            return template.template_type === 'pdf_draw_tracker' || 
                   template.template_type === 'excel_draw_tracker';
          }
          
          // Keep only PDF multi-doc, PDF draw tracker, and Excel templates from base templates
          return template.template_type === 'pdf_multi_doc' || 
                 template.template_type === 'pdf_draw_tracker' ||
                 template.template_type === 'excel_draw_tracker' ||
                 template.value === 'pdf_multi_doc' ||
                 template.value === 'pdf_draw_tracker' ||
                 template.value === 'excel_draw_tracker';
        })
        .map(template => ({
          id: template.id || template.value || 'unknown',
          name: template.name || 'Unknown Template',
          value: template.id ? template.id.toString() : (template.value || 'unknown'),
          template_type: template.template_type,
          project_specific: template.project_specific,
          is_default: template.is_default
        }));
      
      // Always ensure the THREE base templates are available
      const hasPdfMultiDoc = availableTemplates.value.some(t => 
        t.value === 'pdf_multi_doc' && !t.project_specific
      );
      
      if (!hasPdfMultiDoc) {
        availableTemplates.value.unshift({
          id: 'pdf_multi_doc',
          name: 'PDF Document (G702/G703/Lien Waiver)',
          value: 'pdf_multi_doc',
          template_type: 'pdf_multi_doc',
          project_specific: false
        });
      }
      
      const hasPdfDrawTracker = availableTemplates.value.some(t => 
        t.value === 'pdf_draw_tracker' && !t.project_specific
      );
      
      if (!hasPdfDrawTracker) {
        availableTemplates.value.splice(1, 0, {
          id: 'pdf_draw_tracker',
          name: 'PDF Draw Tracker',
          value: 'pdf_draw_tracker',
          template_type: 'pdf_draw_tracker',
          project_specific: false
        });
      }
      
      const hasExcelTracker = availableTemplates.value.some(t => 
        t.value === 'excel_draw_tracker' && !t.project_specific
      );
      
      if (!hasExcelTracker) {
        availableTemplates.value.splice(2, 0, {
          id: 'excel_draw_tracker',
          name: 'Excel/XLS Document',
          value: 'excel_draw_tracker',
          template_type: 'excel_draw_tracker',
          project_specific: false
        });
      }
      
      console.log('📋 Available templates loaded:', availableTemplates.value.length);
      console.log('🎯 Template types:', availableTemplates.value.map(t => t.template_type));
      
    } else {
      // Fallback to simplified default templates
      availableTemplates.value = defaultTemplates;
    }
  } catch (error) {
    console.error('Error fetching templates:', error);
    // Fallback to simplified templates on error
    availableTemplates.value = [
      { id: 'pdf_multi_doc', name: 'PDF Document (G702/G703/Lien Waiver)', value: 'pdf_multi_doc', template_type: 'pdf_multi_doc' },
      { id: 'pdf_draw_tracker', name: 'PDF Draw Tracker', value: 'pdf_draw_tracker', template_type: 'pdf_draw_tracker' },
      { id: 'excel_draw_tracker', name: 'Excel/XLS Document', value: 'excel_draw_tracker', template_type: 'excel_draw_tracker' }
    ];
  }
}

function handleFileDrop(event) {
  event.preventDefault();
  isDragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
}

function triggerFileInput() {
  document.querySelector('input[type="file"]').click();
}

function handleFileSelect(event) {
  selectedFile.value = event.target.files[0];
}

function removeFile() {
  selectedFile.value = null;
}

// NEW: Open redesigned upload modal
function openNewUploadModal() {
  showNewUploadModal.value = true;
}

// NEW: Close redesigned upload modal
function closeNewUploadModal() {
  showNewUploadModal.value = false;
}

// NEW: Handle upload queue from modal
async function handleUploadQueue({ drawNumber, appendMode, queue }) {
  // CRITICAL: Use route.params.id directly since selectedId may not be set yet
  const currentProjectId = selectedId.value || route.params.id;
  console.log('📦 Starting upload queue:', { 
    drawNumber, 
    appendMode, 
    count: queue.length,
    projectId: currentProjectId,
    selectedIdValue: selectedId.value,
    routeParamsId: route.params.id
  });
  
  // Close modal
  closeNewUploadModal();
  
  // STEP 1: Upload ALL files first and get their template IDs
  console.log('📤 Step 1: Uploading all files...');
  const apiBase = process.env.VUE_APP_ENV_API_BASE || 'http://167.172.25.255:3000/api/v1/';
  
  for (const template of queue) {
    try {
      // 🔥 FIX: Handle both single file and multi-file structures
      const formData = new FormData();
      
      // Check if this is a multi-file upload (HC + Invoices combined)
      if (template.files && template.files.length > 0) {
        // 🆕 MULTIPLE FILES: Send ALL files as document[files][]
        console.log(`📤 Uploading ${template.displayName} with ${template.files.length} files...`);
        template.files.forEach((file, index) => {
          formData.append('document[files][]', file);
          console.log(`  📄 File ${index + 1}: ${file.name}`);
        });
      } else if (template.file) {
        // Single file upload
        console.log(`📤 Uploading ${template.displayName} (single file)...`);
        formData.append('document[file]', template.file);
      } else {
        throw new Error('No file found in template object');
      }
      
      formData.append('document[project_id]', currentProjectId);
      formData.append('document[draw_number]', drawNumber);
      formData.append('document[document_type]', template.templateType);
      
      formData.append('draw_template[name]', `${template.displayName} - Draw ${drawNumber}`);
      formData.append('draw_template[description]', `Auto-generated from upload`);
      formData.append('draw_template[template_type]', template.templateType);
      formData.append('draw_template[project_id]', currentProjectId);
      formData.append('draw_template[draw_number]', drawNumber);
      
      if (appendMode) {
        formData.append('draw_template[append_mode]', 'true');
      }
      
      // 🎯 INVOICE AUTO-ASSIGN: Pass flags for automatic document type assignment
      if (template.defaultDocType) {
        formData.append('draw_template[default_doc_type]', template.defaultDocType);
        console.log(`📋 Default doc type for all pages: ${template.defaultDocType}`);
      }
      if (template.autoAssignAllPages) {
        formData.append('draw_template[auto_assign_all_pages]', 'true');
        console.log(`⏭️ Auto-assigning all pages for: ${template.displayName}`);
      }
      const response = await fetch(`${apiBase}draw_templates`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const data = await response.json();
      if (data.success && data.template) {
        template.templateId = data.template.id;
        // Track file name(s) for display
        if (template.files && template.files.length > 0) {
          template.fileName = `${template.files.length} files`;
        } else if (template.file) {
          template.fileName = template.file.name || template.displayName;
        } else {
          template.fileName = template.displayName;
        }
        delete template.file; // Remove file object (can't be serialized)
        delete template.files; // Remove files array (can't be serialized)
        console.log(`✅ ${template.displayName} uploaded: ${template.templateId}`);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error(`❌ Failed to upload ${template.displayName}:`, error);
      template.status = 'failed';
      template.error = error.message;
    }
  }
  
  // STEP 2: Initialize queue with uploaded templates
  console.log('📋 Step 2: Initializing queue with uploaded templates...');
  templateQueueManager.initialize({
    projectId: currentProjectId,
    drawNumber: drawNumber,
    appendMode: appendMode || false,
    templates: queue,
    onComplete: handleQueueComplete
  });
  
  // STEP 3: Start processing queue (navigating to each template)
  console.log('🚀 Step 3: Starting template processing...');
  isProcessingQueue.value = true;
  await processNextInQueue();
}

// NEW: Process next template in queue (navigate to each uploaded template)
async function processNextInQueue() {
  const nextTemplate = templateQueueManager.next();
  
  if (!nextTemplate) {
    console.log('✅ No more templates in queue');
    isProcessingQueue.value = false;
    return;
  }
  
  console.log('🔄 Processing template:', nextTemplate.displayName);
  
  try {
    // All templates should have templateId at this point (uploaded in batch)
    if (!nextTemplate.templateId) {
      throw new Error('Template ID missing - upload may have failed');
    }
    
    // Build URL with query params
    let setupUrl = `/draw-template-setup/${nextTemplate.templateId}?filename=${encodeURIComponent(nextTemplate.fileName || 'document')}&return_to=upload_queue&queue_index=${nextTemplate.index}`;
    
    // 🎯 INVOICE AUTO-ASSIGN: Add params to auto-select invoice type for all pages
    if (nextTemplate.defaultDocType) {
      setupUrl += `&default_doc_type=${nextTemplate.defaultDocType}`;
      console.log(`📋 Will pre-select doc type: ${nextTemplate.defaultDocType}`);
    }
    if (nextTemplate.autoAssignAllPages) {
      setupUrl += `&auto_assign_all=true`;
      console.log(`⏭️ Will auto-assign all pages as: ${nextTemplate.defaultDocType || 'default'}`);
    }
    
    console.log('✅ Navigating to template setup:', setupUrl);
    window.location.href = setupUrl;
    
  } catch (error) {
    console.error('❌ Failed to process template:', error);
    templateQueueManager.markFailed(error.message);
    
    // Try next template
    await processNextInQueue();
  }
}

// NEW: Handle queue completion
function handleQueueComplete() {
  console.log('🎉 All templates processed successfully!');
  
  // Show toast notification
  showToastNotification('Draw is Processing', 'success', 3000);
  
  // Clear queue from localStorage
  templateQueueManager.clearLocalStorage();
  
  // Reload project data
  fetchProjectDetails();
  
  isProcessingQueue.value = false;
}

// NEW: Show toast notification
function showToastNotification(message, type = 'info', duration = 3000) {
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 16px 24px;
    background: ${type === 'success' ? '#10b981' : '#3b82f6'};
    color: white;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    font-size: 14px;
    font-weight: 600;
    z-index: 10001;
    animation: slideIn 0.3s ease;
  `;
  toast.textContent = message;
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.style.animation = 'slideOut 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

async function uploadDrawDocument() {
  console.log('Upload draw document called');
  console.log('Form values:', {
    selectedFile: selectedFile.value?.name,
    selectedId: selectedId.value,
    selectedTemplate: selectedTemplate.value,
    selectedDrawNumber: selectedDrawNumber.value
  });
  
  if (!selectedFile.value || !selectedId.value || !selectedTemplate.value || !selectedDrawNumber.value) {
    console.log('Missing required fields:', {
      file: !!selectedFile.value,
      id: !!selectedId.value,
      template: !!selectedTemplate.value,
      drawNumber: !!selectedDrawNumber.value
    });
    return;
  }
  
  console.log('Selected template:', selectedTemplate.value);
  console.log('Available templates:', availableTemplates.value);
  
  // Check if this is an Excel template (base template OR existing project Excel template)
  const selectedTemplateData = availableTemplates.value.find(t => t.id === selectedTemplate.value || t.value === selectedTemplate.value);
  const isExcelTemplate = selectedTemplate.value === 'excel_draw_tracker' || 
                         selectedTemplateData?.template_type === 'excel_draw_tracker' ||
                         selectedTemplateData?.name?.toLowerCase().includes('excel');
  
  // Check if this is a PDF multi-document template (replaces individual G702/G703 templates)
  const isPdfMultiDocTemplate = selectedTemplate.value === 'pdf_multi_doc' || 
                                selectedTemplateData?.template_type === 'pdf_multi_doc' ||
                                selectedTemplateData?.name?.toLowerCase().includes('pdf multi-doc');
  
  // Legacy G703 template check (for backward compatibility) - but NOT if it's a PDF multi-doc template
  const isG703Template = !isPdfMultiDocTemplate && (
                         selectedTemplate.value === '703' || 
                         selectedTemplate.value === 'G703' ||
                         selectedTemplate.value === 'g703' ||
                         selectedTemplateData?.template_type === 'g703' ||
                         selectedTemplateData?.name?.toLowerCase().includes('g703') ||
                         selectedTemplateData?.name?.toLowerCase().includes('continuation'));
  
  console.log('Template data:', selectedTemplateData);
  console.log('Is Excel template:', isExcelTemplate);
  console.log('Is PDF multi-doc template:', isPdfMultiDocTemplate);
  console.log('Is G703 template:', isG703Template);
  
  // NEW: Special handling for PDF Multi-Document (G702/G703/Lien Waiver)
  if (isPdfMultiDocTemplate) {
    console.log('PDF Multi-Document template selected - starting enhanced PDF processing');
    
    // Check if file is PDF format
    const isPdfFile = selectedFile.value.name.toLowerCase().endsWith('.pdf') || 
                     selectedFile.value.type.includes('pdf');
    
    if (!isPdfFile) {
      errorStore.setError('File Type Error', 'PDF Multi-Document requires a PDF file (.pdf)', 'error', false);
      return;
    }
    
    try {
      isUploading.value = true;
      uploadProgress.value = 0;
      uploadStatus.value = 'Uploading PDF for enhanced multi-document processing...';
      
      // Create draw template for PDF multi-document processing
      const formData = new FormData();
      formData.append('document[file]', selectedFile.value);
      formData.append('document[project_id]', selectedId.value);
      formData.append('document[document_type]', 'pdf_multi_doc');
      formData.append('document[draw_number]', selectedDrawNumber.value);
      formData.append('draw_template[name]', `PDF Multi-Doc Template - Draw ${selectedDrawNumber.value}`);
      formData.append('draw_template[description]', `Enhanced PDF processing for G702/G703/Lien Waiver - Draw ${selectedDrawNumber.value}`);
      formData.append('draw_template[template_type]', 'pdf_multi_doc');
      formData.append('draw_template[project_id]', selectedId.value);
      formData.append('draw_template[draw_number]', selectedDrawNumber.value);
      
      uploadProgress.value = 30;
      uploadStatus.value = 'Creating PDF multi-document template...';
      
      const response = await axios.post(`${apiBase}draw_templates`, formData, {
        headers: {
          ...authToken.value,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          uploadProgress.value = Math.min(30 + percentCompleted * 0.7, 100);
        }
      });
      
      uploadProgress.value = 100;
      uploadStatus.value = 'PDF multi-document template created successfully!';
      
      if (response.data.success && response.data.template) {
        console.log('✅ PDF Multi-Document template created:', response.data.template.id);
        
        // Close modal and redirect to enhanced PDF processing
        closeUploadModal();
        
        // Show success message
        errorStore.setError('PDF Processing', 'PDF uploaded! Starting enhanced multi-document processing with page selection.', 'success', false);
        
        // Navigate to enhanced DrawTemplateSetup with PDF page selection
        await router.push(`/draw-template-setup/${response.data.template.id}?template_type=pdf_multi_doc`);
        
      } else {
        throw new Error('PDF multi-document template creation failed');
      }
      
    } catch (error) {
      console.error('Error creating PDF multi-document template:', error);
      uploadProgress.value = 0;
      uploadStatus.value = 'PDF multi-document template creation failed';
      errorStore.setError('PDF Template Creation Error', error.response?.data?.error || 'Failed to create PDF multi-document template', 'error', false);
      isUploading.value = false;
    }
    
    return; // Exit early for PDF multi-document files
  }
  
  // 📊 PDF DRAW TRACKER: Special handling for PDF-based draw trackers
  const isPdfDrawTracker = selectedTemplate.value === 'pdf_draw_tracker' || 
                          selectedTemplateData?.template_type === 'pdf_draw_tracker';
  
  if (isPdfDrawTracker) {
    console.log('PDF Draw Tracker selected - AI-powered table extraction');
    
    // Check if file is PDF format
    const isPdfFile = selectedFile.value.name.toLowerCase().endsWith('.pdf') || 
                     selectedFile.value.type.includes('pdf');
    
    if (!isPdfFile) {
      errorStore.setError('File Type Error', 'PDF Draw Tracker requires a PDF file (.pdf)', 'error', false);
      return;
    }
    
    try {
      isUploading.value = true;
      uploadProgress.value = 0;
      
      // Check for existing PDF draw tracker templates for this project
      const existingPdfTemplates = availableTemplates.value.filter(t => 
        t.template_type === 'pdf_draw_tracker' && t.project_id === selectedId.value && t.project_specific
      );
      
      const isExistingTemplate = existingPdfTemplates.length > 0 && selectedTemplateData?.project_specific === true;
      
      if (isExistingTemplate) {
        // Using existing template - reuse column mappings
        uploadStatus.value = 'Processing PDF with saved column mappings...';
        
        const formData = new FormData();
        formData.append('document[file]', selectedFile.value);
        formData.append('document[project_id]', selectedId.value);
        formData.append('document[document_type]', 'pdf_draw_tracker');
        formData.append('document[draw_number]', selectedDrawNumber.value);
        formData.append('draw_template[name]', `PDF Draw Tracker - Draw ${selectedDrawNumber.value}`);
        formData.append('draw_template[description]', `PDF Draw Tracker using saved mappings for Draw ${selectedDrawNumber.value}`);
        formData.append('draw_template[template_type]', 'pdf_draw_tracker');
        formData.append('draw_template[project_id]', selectedId.value);
        formData.append('draw_template[draw_number]', selectedDrawNumber.value);
        formData.append('draw_template[copy_from_template_id]', selectedTemplate.value); // Reuse mappings
        
        uploadProgress.value = 30;
        
        const response = await axios.post(`${apiBase}draw_templates`, formData, {
          headers: {
            ...authToken.value,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            uploadProgress.value = Math.min(30 + percentCompleted * 0.7, 100);
          }
        });
        
        uploadProgress.value = 100;
        uploadStatus.value = 'PDF processed with saved mappings!';
        
        if (response.data.success && response.data.template) {
          console.log('✅ PDF Draw Tracker created with existing mappings:', response.data.template.id);
          
          closeUploadModal();
          errorStore.setError('Reusing Mappings', 'PDF processed! Review AI-detected current draw column...', 'success', false);
          await router.push(`/draw-template-setup/${response.data.template.id}`);
        } else {
          throw new Error('PDF draw tracker creation failed');
        }
        
      } else {
        // First PDF draw tracker - AI will detect all mappings
        uploadStatus.value = 'Processing PDF with AI mapping detection...';
        
        const formData = new FormData();
        formData.append('document[file]', selectedFile.value);
        formData.append('document[project_id]', selectedId.value);
        formData.append('document[document_type]', 'pdf_draw_tracker');
        formData.append('document[draw_number]', selectedDrawNumber.value);
        formData.append('draw_template[name]', `PDF Draw Tracker - Draw ${selectedDrawNumber.value}`);
        formData.append('draw_template[description]', `AI-detected PDF Draw Tracker for Draw ${selectedDrawNumber.value}`);
        formData.append('draw_template[template_type]', 'pdf_draw_tracker');
        formData.append('draw_template[project_id]', selectedId.value);
        formData.append('draw_template[draw_number]', selectedDrawNumber.value);
        
        uploadProgress.value = 30;
        
        const response = await axios.post(`${apiBase}draw_templates`, formData, {
          headers: {
            ...authToken.value,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            uploadProgress.value = Math.min(30 + percentCompleted * 0.7, 100);
          }
        });
        
        uploadProgress.value = 100;
        uploadStatus.value = 'PDF processed with AI detection!';
        
        if (response.data.success && response.data.template) {
          console.log('✅ PDF Draw Tracker created with AI mappings:', response.data.template.id);
          
          closeUploadModal();
          errorStore.setError('AI Mapping Complete', 'PDF processed! Review AI-suggested column mappings...', 'success', false);
          await router.push(`/draw-template-setup/${response.data.template.id}`);
        } else {
          throw new Error('PDF draw tracker creation failed');
        }
      }
      
    } catch (error) {
      console.error('Error creating PDF draw tracker:', error);
      uploadProgress.value = 0;
      uploadStatus.value = 'PDF processing failed';
      errorStore.setError('PDF Processing Error', error.response?.data?.error || 'Failed to process PDF draw tracker', 'error', false);
      isUploading.value = false;
    }
    
    return; // Exit early for PDF draw tracker files
  }
  
  // Special handling for Excel Draw Tracker (base template OR existing Excel template)
  if (isExcelTemplate) {
    // Check if file is Excel format (including .xlsm macro-enabled workbooks)
    const isExcelFile = selectedFile.value.name.toLowerCase().endsWith('.xlsx') || 
                       selectedFile.value.name.toLowerCase().endsWith('.xls') ||
                       selectedFile.value.name.toLowerCase().endsWith('.xlsm') ||
                       selectedFile.value.type.includes('spreadsheet') ||
                       selectedFile.value.type.includes('excel');
    
    if (!isExcelFile) {
      errorStore.setError('File Type Error', 'Excel Draw Tracker requires an Excel file (.xlsx, .xls, or .xlsm)', 'error', false);
      return;
    }
    
    try {
      isUploading.value = true;
      uploadProgress.value = 0;
      
      // Check if using existing template or creating new one
      const isExistingTemplate = selectedTemplateData?.project_specific === true;
      
      if (isExistingTemplate) {
        // Using existing project template - create new template that copies existing config
        uploadStatus.value = 'Using existing template configuration...';
        
        const formData = new FormData();
        formData.append('document[file]', selectedFile.value);
        formData.append('document[project_id]', selectedId.value);
        formData.append('document[document_type]', 'excel_draw_tracker');
        formData.append('document[draw_number]', selectedDrawNumber.value);
      if (appendToCurrent.value) {
        formData.append('document[append_to_current]', 'true');
      }
        formData.append('draw_template[name]', `Excel Draw Template - Draw ${selectedDrawNumber.value}`);
        formData.append('draw_template[description]', `Template using existing configuration for Draw ${selectedDrawNumber.value}`);
        formData.append('draw_template[template_type]', 'excel_draw_tracker');
        formData.append('draw_template[project_id]', selectedId.value);
        formData.append('draw_template[draw_number]', selectedDrawNumber.value);
        formData.append('draw_template[copy_from_template_id]', selectedTemplate.value); // Copy config from existing template
        
        uploadProgress.value = 30;
        uploadStatus.value = 'Uploading Excel file with existing template...';
      
        // Create new template that copies existing config
        const response = await axios.post(`${apiBase}draw_templates`, formData, {
          headers: {
            ...authToken.value,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            uploadProgress.value = Math.min(30 + percentCompleted * 0.5, 80);
          }
        });
        
        uploadProgress.value = 100;
        uploadStatus.value = 'Document uploaded with existing template!';
        
        if (response.data.success && response.data.template) {
          console.log('Document uploaded with existing template:', response.data.template.id);
          
          // Close modal and redirect to template setup (with existing mappings pre-filled)
          closeUploadModal();
          
          // Show success message
          errorStore.setError('Using Existing Template', 'Document uploaded! Only need to select draw columns...', 'success', false);
          
          // Navigate to template setup page (will auto-populate from existing template)
          router.push(`/draw-template-setup/${response.data.template.id}`);
          
        } else {
          throw new Error('Document upload with existing template failed');
        }
        
      } else {
        // Creating new template
        uploadStatus.value = 'Creating new Excel template...';
        
      const templateData = {
        name: `Excel Draw Template - Draw ${selectedDrawNumber.value}`,
        description: `Auto-generated Excel template for Draw ${selectedDrawNumber.value}`,
        draw_number: selectedDrawNumber.value,
        project_id: selectedId.value,
        template_type: 'excel_draw_tracker'
      };
      
      const formData = new FormData();
      formData.append('document[file]', selectedFile.value);
      formData.append('document[project_id]', selectedId.value);
      formData.append('document[document_type]', 'excel_draw_tracker');
      formData.append('document[draw_number]', selectedDrawNumber.value);
      if (appendToCurrent.value) {
        formData.append('document[append_to_current]', 'true');
      }
      formData.append('draw_template[name]', templateData.name);
      formData.append('draw_template[description]', templateData.description);
      formData.append('draw_template[template_type]', templateData.template_type);
      formData.append('draw_template[project_id]', selectedId.value);
      formData.append('draw_template[draw_number]', selectedDrawNumber.value);
      
      uploadProgress.value = 30;
      uploadStatus.value = 'Uploading Excel file...';
      
      // Create template with document
      const response = await axios.post(`${apiBase}draw_templates`, formData, {
        headers: {
          ...authToken.value,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          uploadProgress.value = Math.min(30 + percentCompleted * 0.5, 80);
        }
      });
      
      uploadProgress.value = 100;
      uploadStatus.value = 'Template created successfully!';
      
      if (response.data.success && response.data.template) {
        console.log('Template created successfully:', response.data.template.id);
        
        // Close modal and redirect to template setup
        closeUploadModal();
        
        // Show success message
        errorStore.setError('Template Created', 'Excel template created! Setting up column mapping...', 'success', false);
        
        // Navigate to template setup page
        router.push(`/draw-template-setup/${response.data.template.id}`);
        
      } else {
        throw new Error('Template creation failed - no template returned');
        }
      }
      
    } catch (error) {
      console.error('Error creating Excel template:', error);
      uploadProgress.value = 0;
      uploadStatus.value = 'Template creation failed';
      errorStore.setError('Template Creation Error', error.response?.data?.error || 'Failed to create Excel template', 'error', false);
      isUploading.value = false;
    }
    
    return; // Exit early for Excel files
  }
  
  // Special handling for G703 documents - DIRECT PROCESSING (skip templates)
  if (isG703Template) {
    console.log('G703 document selected - using DIRECT PROCESSING (skip template setup)');
    
    try {
      isUploading.value = true;
      uploadProgress.value = 0;
      uploadStatus.value = 'Uploading G703 document for direct processing...';
      
      // ALWAYS use direct document upload for G703 (skip template creation)
      const formData = new FormData();
      formData.append('document[file]', selectedFile.value);
      formData.append('document[project_id]', selectedId.value);
      formData.append('document[document_type]', 'g703');
      formData.append('document[draw_number]', selectedDrawNumber.value);
      formData.append('document[auto_process]', 'true'); // CRITICAL: Enable auto-processing
      
      uploadProgress.value = 30;
      uploadStatus.value = 'Processing G703 document with AWS Textract...';
    
      // Direct document upload (triggers DocumentProcessingService -> auto-creation)
      const response = await axios.post(`${apiBase}documents`, formData, {
        headers: {
          ...authToken.value,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          uploadProgress.value = Math.min(30 + percentCompleted * 0.7, 100);
        }
      });
      
      uploadProgress.value = 100;
      uploadStatus.value = 'G703 document processed successfully!';
      
      if (response.data && response.data.id) {
        console.log('✅ G703 document processed directly:', response.data.id);
        uploadedDocumentId.value = response.data.id;
        
        // Show success message
        errorStore.setError('G703 Processed', 'G703 document processed! Records auto-created from AWS Textract data.', 'success', false);
        
        // Close modal and refresh data - stay on current project
        closeUploadModal();
        await loadProjectData();
        
        // Refresh G703 data specifically
        await fetchG703DrawDetailGridData();
        
      } else {
        throw new Error('G703 document processing failed');
      }
      
    } catch (error) {
      console.error('Error processing G703 document:', error);
      uploadProgress.value = 0;
      uploadStatus.value = 'G703 processing failed';
      errorStore.setError('G703 Processing Error', error.response?.data?.error || 'Failed to process G703 document', 'error', false);
      isUploading.value = false;
    }
    
    return; // Exit early - no template setup needed
  }
  
  // G703 processing complete - direct upload bypasses template complexity
  
  // Original logic for non-Excel/non-G703 files
  try {
    console.log('Fallback section running - selectedTemplate:', selectedTemplate.value);
    
    isUploading.value = true;
    uploadProgress.value = 0;
    uploadStatus.value = 'Uploading document...';
    
    // Create form data with correct structure for backend
    const formData = new FormData();
    formData.append('document[file]', selectedFile.value);
    formData.append('document[project_id]', selectedId.value);
    formData.append('document[document_type]', 'draw_tracker');
    formData.append('document[draw_number]', selectedDrawNumber.value);
    formData.append('document[template_type]', selectedTemplate.value);
    
    uploadProgress.value = 10;
    uploadStatus.value = 'Processing upload...';
    
    // Upload the document
    const response = await axios.post(`${apiBase}documents`, formData, {
      headers: {
        ...authToken.value,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        uploadProgress.value = Math.min(percentCompleted, 100);
      }
    });
    
    uploadStatus.value = 'Document uploaded successfully! Starting OCR processing...';
    
    if (response.data && response.data.id) {
      uploadedDocumentId.value = response.data.id;
      console.log('Document uploaded successfully:', uploadedDocumentId.value);
      
      // Show success message but keep modal open
      errorStore.setError('Upload Success', 'Document uploaded and queued for OCR processing', 'success', false);
      
      // Start status tracking in the modal (don't close)
      startDocumentStatusTracking(uploadedDocumentId.value);
      
    } else {
      throw new Error('Upload failed - no document returned');
    }
    
  } catch (error) {
    console.error('Error uploading document:', error);
    uploadProgress.value = 0;
    uploadStatus.value = 'Upload failed';
    errorStore.setError('Upload Error', error.response?.data?.error || 'Failed to upload document', 'error', false);
    isUploading.value = false;
  }
}

async function startDocumentStatusTracking(documentId, completionCallback = null) {
  try {
    console.log('Starting status tracking for document:', documentId);
    
    uploadStatus.value = 'Document queued for OCR processing...';
    uploadProgress.value = 10;
    
    let pollCount = 0;
    const maxPollAttempts = 100; // 100 attempts * 3 seconds = 5 minutes max
    
    // Poll document status every 3 seconds
    const pollInterval = setInterval(async () => {
      try {
        pollCount++;
        const response = await axios.get(`${apiBase}documents/${documentId}`, { headers: authToken.value });
        const document = response.data;
        
        console.log('Document status update:', document.status);
        
        switch (document.status) {
          case 'not_started':
            uploadStatus.value = 'Document uploaded successfully, awaiting processing queue...';
            uploadProgress.value = 15;
            break;
          case 'queued':
            uploadStatus.value = 'Document queued for OCR processing...';
            uploadProgress.value = 25;
            break;
          case 'in_progress':
            uploadStatus.value = 'OCR processing in progress - analyzing document structure...';
            uploadProgress.value = Math.min(35 + (pollCount * 2), 85); // Gradually increase during processing
            break;
          case 'done':
            uploadStatus.value = 'OCR processing completed successfully!';
            uploadProgress.value = 100;
            clearInterval(pollInterval);
            isUploading.value = false;
            // Call completion callback if provided
            if (completionCallback && typeof completionCallback === 'function') {
              console.log('🎯 Document processing complete, calling completion callback');
              completionCallback();
            }
            break;
          case 'failed':
            uploadStatus.value = `Processing failed: ${document.error || 'Unknown error'}`;
            uploadProgress.value = 0;
            clearInterval(pollInterval);
            isUploading.value = false;
            errorStore.setError('Processing Failed', document.error || 'OCR processing failed', 'error', false);
            break;
          default:
            uploadStatus.value = `Document status: ${document.status}`;
            uploadProgress.value = Math.min(30 + (pollCount), 90);
        }
        
        // Frontend timeout after 5 minutes - show result buttons even if still processing
        if (pollCount >= maxPollAttempts) {
          clearInterval(pollInterval);
          isUploading.value = false;
          if (document.status === 'in_progress') {
            uploadStatus.value = 'Processing is taking longer than expected. You can check status in the document viewer.';
            uploadProgress.value = 75;
            // Show the result buttons even though processing may still be ongoing
          }
        }
        
      } catch (error) {
        console.error('Error polling document status:', error);
        // Continue polling on error unless we've reached max attempts
        if (pollCount >= maxPollAttempts) {
          clearInterval(pollInterval);
          isUploading.value = false;
          uploadStatus.value = 'Unable to track processing status. Check document list for updates.';
        }
      }
    }, 3000);
    
  } catch (error) {
    console.error('Error starting document status tracking:', error);
    isUploading.value = false;
    errorStore.setError('Status Tracking Error', 'Unable to track document processing status', 'error', false);
  }
}

async function loadExtractionPreview() {
  try {
    // Call the extraction preview endpoint
    const response = await axios.get(
      `${apiBase}documents/${uploadedDocumentId.value}/extraction_preview`, 
      { headers: authToken.value }
    );
    
    if (response.data) {
      extractionResults.value = response.data;
      console.log('Extraction results loaded:', response.data);
    }
  } catch (error) {
    console.error('Error loading extraction preview:', error);
  }
}

async function createDrawTemplateFromDocument() {
  try {
    console.log('Creating draw template for document:', uploadedDocumentId.value);
    
    const templateData = {
      name: `Draw Template - ${new Date().toLocaleDateString()}`,
      description: 'Auto-generated draw template from project budget page',
      draw_number: drawNumber.value || 1
    };
    
    const response = await axios.post(
      `${apiBase}documents/${uploadedDocumentId.value}/create_draw_template`, 
      { template: templateData }, 
      { headers: authToken.value }
    );
    
    console.log('Draw template creation response:', response.data);
    
    if (response.data.success && response.data.template) {
      // Navigate to the template setup page
      router.push(`/draw-template-setup/${response.data.template.id}`);
    } else {
      errorStore.setError('Template Creation', response.data.error || 'Failed to create template', 'error', false);
    }
    
  } catch (error) {
    console.error('Error creating draw template:', error);
    errorStore.setError('Template Creation', 'Failed to create draw template', 'error', false);
  } finally {
    closeUploadModal();
  }
}

async function saveCorrections() {
  try {
    // Save the corrected extraction data
    const response = await axios.post(
      `${apiBase}documents/${uploadedDocumentId.value}/save_corrections`,
      { 
        corrections: extractionResults.value.extracted_fields,
        template_type: selectedTemplate.value,
        draw_number: selectedDrawNumber.value
      },
      { headers: authToken.value }
    );
    
    if (response.data.success) {
      console.log('Corrections saved successfully');
      // Move to template creation
      createDrawTemplateFromDocument();
    }
  } catch (error) {
    console.error('Error saving corrections:', error);
    errorStore.setError('Save Error', 'Failed to save corrections', 'error', false);
  }
}

// Toggle custom draw number mode
function toggleCustomDrawNumber() {
  if (useCustomDrawNumber.value) {
    selectedDrawNumber.value = '';
  } else {
    selectedDrawNumber.value = suggestedDrawNumber.value;
  }
}

// Handle custom draw number input
function handleCustomDrawInput() {
  // Allow any input for custom draw numbers
  console.log('Custom draw number:', selectedDrawNumber.value);
}

function closeUploadModal() {
  // Hide modal
  showUploadDrawModal.value = false;
  
  // Reset all upload state
  selectedFile.value = null;
  selectedTemplate.value = '';
  selectedDrawNumber.value = '';
  useCustomDrawNumber.value = false;
  uploadedDocumentId.value = null;
  
  // Reset progress indicators
  isUploading.value = false;
  uploadProgress.value = 0;
  uploadStatus.value = '';
  ocrProgress.value = 0;
  ocrStatus.value = '';
  
  // Reset step visibility
  showOCRProgress.value = false;
  showExtractionResults.value = false;
  extractionResults.value = null;
  
  // Reset drag state
  isDragOver.value = false;
  
  // Clear any existing draw data
  existingDraws.value = [];
  suggestedDrawNumber.value = '';
  appendToCurrent.value = false;
  
  // Clear any error states
  // Note: Don't clear success messages as user should see completion status
  
  console.log('✅ Upload modal completely reset and closed');
}

function viewDocumentResults() {
  if (uploadedDocumentId.value) {
    // Open document results in a new tab/window
    const baseUrl = window.location.origin;
    const resultsUrl = `${baseUrl}/document-viewer/${uploadedDocumentId.value}`;
    window.open(resultsUrl, '_blank');
  }
}

function viewDocumentList() {
  // Navigate to a document management page for this project
  const router = useRouter();
  router.push(`/projects/${selectedId.value}/documents`);
  closeUploadModal();
}

// Cost Curve Chart Settings
let costCurveCategory = ref('all');


let costCurveTotals = ref({
  hardCosts: 0,
  softCosts: 0,
  acquisition: 0,
  financing: 0,
  contingencies: 0,
  total: 0
});





// AG Charts helper functions and configurations
function formatAxisValue(value) {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${(parseFloat(value) || 0).toFixed(0)}`;
  }
}

// Cost Curve variables for AG Charts
let costCurveContractor = ref('');
let availableContractors = ref([]);

// Cost Curve Contractor Autocomplete
let costCurveContractorSearch = ref('');
let costCurveContractorDropdown = ref(false);
let costCurveFilteredContractors = ref([]);
let costCurveChartReady = ref(false);
let costCurveData = ref([]); // Separate data store for cost curve that doesn't get filtered

// Budget Summary Chart Controls
let costsToDateDraw = ref(null); // Will be set to last draw when data loads
let budgetVarianceView = ref('category');
let budgetVarianceDraw = ref(null); // Will be set to last draw when data loads

// Global Controls for all charts
let globalDrawFilter = ref(null); // Will be set to last draw when data loads

// 🎯 WHEEL NAVIGATION: Functions for draw wheel
const canGoToPreviousDraw = computed(() => {
  if (!drawNumberList.value || drawNumberList.value.length === 0) return false;
  const currentIndex = drawNumberList.value.indexOf(globalDrawFilter.value);
  return currentIndex > 0;
});

const canGoToNextDraw = computed(() => {
  if (!drawNumberList.value || drawNumberList.value.length === 0) return false;
  const currentIndex = drawNumberList.value.indexOf(globalDrawFilter.value);
  return currentIndex < drawNumberList.value.length - 1;
});

function changeDraw(direction) {
  if (!drawNumberList.value || drawNumberList.value.length === 0) return;
  
  const currentIndex = drawNumberList.value.indexOf(globalDrawFilter.value);
  if (currentIndex === -1) return;
  
  const newIndex = currentIndex + direction; // 🎯 ORIGINAL LOGIC: Works with array order
  if (newIndex >= 0 && newIndex < drawNumberList.value.length) {
    globalDrawFilter.value = drawNumberList.value[newIndex];
    // 🔥 REMOVED: updateAllChartsAndGrid() - watch on globalDrawFilter already handles this
    // Calling it here causes DUPLICATE updates (immediate + watch fires 200ms later)
  }
}

// 🎯 DRAGGABLE SLIDER: Position and drag functionality
const sliderPosition = computed(() => {
  if (!drawNumberList.value || drawNumberList.value.length === 0) return 0;
  const currentDraw = parseInt(globalDrawFilter.value);
  if (!currentDraw) return 0;
  
  // Find the INDEX of the current draw in the list
  const currentIndex = drawNumberList.value.indexOf(currentDraw);
  if (currentIndex === -1) return 0;
  
  // 🐛 FIX: drawNumberList is DESCENDING (newest first), so REVERSE the position
  // Index 0 (newest/highest) should be at RIGHT (100%), last index (oldest/lowest) should be at LEFT (0%)
  const position = 1 - (currentIndex / (drawNumberList.value.length - 1));
  return position * 100;
});

const sliderTrack = ref(null);
let isDragging = false;
let drawChangeDebounceTimer = null;

function startDrag(event) {
  isDragging = true;
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  document.addEventListener('touchmove', onDrag);
  document.addEventListener('touchend', stopDrag);
  onDrag(event); // Handle initial position
}

function onDrag(event) {
  if (!isDragging || !sliderTrack.value || !drawNumberList.value || drawNumberList.value.length === 0) return;
  
  event.preventDefault();
  const rect = sliderTrack.value.getBoundingClientRect();
  const clientX = event.touches ? event.touches[0].clientX : event.clientX;
  const position = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  
  // 🐛 FIX: drawNumberList is in DESCENDING order (newest first), so REVERSE the index
  // Left side (position=0) should be OLDEST (last index), Right side (position=1) should be NEWEST (index 0)
  const drawIndex = Math.round((1 - position) * (drawNumberList.value.length - 1));
  const newDraw = drawNumberList.value[drawIndex];
  
  if (newDraw !== globalDrawFilter.value) {
    // 🎯 IMMEDIATE: Update the draw filter for visual feedback
    globalDrawFilter.value = newDraw;
    // 🔥 REMOVED: Local debounce - watch on globalDrawFilter handles debounced updates
    // Having both local debounce AND watch debounce caused duplicate API calls
  }
}

function stopDrag() {
  isDragging = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.removeEventListener('touchmove', onDrag);
  document.removeEventListener('touchend', stopDrag);
  // 🔥 REMOVED: Direct updateAllChartsAndGrid() call - watch handles it
  // The watch has 200ms debounce which is fast enough for good UX
}

// Watch statements (moved here after variable declarations to avoid undefined errors)
// Load/refresh G702 grid only when tab is active and the draw changes
watch(g702DrawNumber, (newVal, oldVal) => {
  if (btSubMenuSelectedItemId.value === 3) {
    fetchG702DrawDetailGridData();
  }
  // 🆕 ALSO REFRESH LW when draw changes
  if (btSubMenuSelectedItemId.value === 5) {
    fetchG702DrawDetailGridData(); // Will trigger refreshLWData()
  }
  // ⚠️ RETAINAGE TAB: Don't refetch here! The tab already fetches once on mount, and globalDrawFilter watch handles display updates
});

// NEW: Watch for route changes to check if we're returning from template completion
watch(() => route.fullPath, () => {
  console.log('🔄 Route changed, checking for queue continuation...');
  nextTick(() => {
    checkAndContinueQueue();
  });
});

// ⚠️ DISABLED: This watch is redundant - updateAllChartsAndGrid() (line 9810) already handles tab-specific fetches
// Leaving this enabled causes duplicate API calls because:
// 1. This watch fires → calls fetchG702DrawDetailGridData()
// 2. Watch at 9810 fires → calls updateAllChartsAndGrid() → calls fetchG702DrawDetailGridData() again
// 🚀 The deduplication flags prevent the second call, but it's cleaner to remove the duplicate watch entirely
// 
// watch(() => globalDrawFilter && globalDrawFilter.value, (newVal, oldVal) => {
//   if (newVal != null && newVal !== oldVal) {
//     // G702 tab
//     if (btSubMenuSelectedItemId.value === 3) {
//       g702DrawNumber.value = newVal;
//       fetchG702DrawDetailGridData();
//     }
//     // 🆕 LIEN WAIVER TAB: Refetch for new draw
//     if (btSubMenuSelectedItemId.value === 5) {
//       g702DrawNumber.value = newVal;
//       fetchG702DrawDetailGridData(); // Will trigger refreshLWData()
//     }
//     // 🆕 RETAINAGE TAB: Just refresh the display filter, don't refetch (already have all draws)
//     if (btSubMenuSelectedItemId.value === 7) {
//       // Don't refetch! Just refresh the displayed data for the new draw
//       refreshRetainageData();
//     }
//   }
// });

// If draw numbers list initializes while on 702 and grid is empty, fetch once
watch(drawNumberList, (newList) => {
  if (btSubMenuSelectedItemId.value === 3 && (!g702RowData.value || g702RowData.value.length === 0)) {
    fetchG702DrawDetailGridData();
  }
});

// 🎯 CRITICAL FIX: Watch globalDrawFilter and update ALL charts (not just G702)
// This ensures Total Project Costs chart ALWAYS updates when draw changes
// Use debounce to prevent API call collisions when changing draws rapidly
let watchDebounceTimer = null;
watch(() => globalDrawFilter && globalDrawFilter.value, (newVal, oldVal) => {
  if (newVal != null && newVal !== oldVal) {
    console.log(`🔄 WATCH: globalDrawFilter changed from ${oldVal} to ${newVal}, updating all charts...`);
    
    // Clear existing timer
    if (watchDebounceTimer) {
      clearTimeout(watchDebounceTimer);
    }
    
    // Debounce: wait 200ms before updating (prevents rapid-fire API calls)
    watchDebounceTimer = setTimeout(() => {
      updateAllChartsAndGrid();
      
      // 📄 If on Invoice tab, also fetch invoice data for new draw
      if (btSubMenuSelectedItemId.value == 8) {
        fetchInvoiceData();
      }
      
      // 🚀 RETAINAGE TAB: Refetch G702 data for new draw (current + previous)
      if (btSubMenuSelectedItemId.value === 7) {
        console.log(`🔄 Retainage tab: Refetching G702 for new draw ${newVal}`);
        g702DrawNumber.value = 'retainage'; // Signal to fetch current + previous
        fetchG702DrawDetailGridData();
      }
      
      watchDebounceTimer = null;
    }, 200);
  }
});

let globalViewMode = ref('total'); // 'total' or 'per_gsf'

// Budget Variance data
let contractorVarianceData = ref([]);

// 🔍 ENHANCED ZOLA with AUTO BBL LOOKUP using NYC GeoSearch API - MOVED TO TOP LEVEL
const enhancedZolaLink = ref('https://zola.planning.nyc.gov/');
const bblConversionAttempted = ref(false);

const fetchBBLFromAddress = async () => {
  if (bblConversionAttempted.value) return; // Prevent multiple calls
  
  const address = projectInformation.value?.address_line_1;
  const city = projectInformation.value?.city;
  
  console.log('🎯 ZoLa Debug - Project Info:', {
    address,
    city,
    state: projectInformation.value?.state,
    boro: projectInformation.value?.borough_code,
    block: projectInformation.value?.block,
    lot: projectInformation.value?.lot
  });
  
  // Check if we already have BBL data
  const existingBoro = projectInformation.value?.borough_code || projectInformation.value?.borough;
  const existingBlock = projectInformation.value?.block;
  const existingLot = projectInformation.value?.lot;
  
  if (existingBoro && existingBlock && existingLot) {
    console.log('✅ Using existing BBL data:', existingBoro, existingBlock, existingLot);
    enhancedZolaLink.value = `https://zola.planning.nyc.gov/lot/${existingBoro}/${existingBlock}/${existingLot}`;
    bblConversionAttempted.value = true;
    return;
  }
  
  if (!address) {
    console.log('⚠️ No address available for BBL lookup');
    return;
  }
  
  bblConversionAttempted.value = true;
  
  try {
    const fullAddress = `${address}, ${city || 'New York'}, NY`;
    console.log('🔍 Fetching BBL from NYC GeoSearch API for:', fullAddress);
    const bbl = await fetchBBLFromAddressAPI(fullAddress);
    
    if (bbl && bbl.boro && bbl.block && bbl.lot) {
      const lat = parseFloat(projectInformation.value?.latitude);
      const lng = parseFloat(projectInformation.value?.longitude);
      const zoomLevel = !Number.isNaN(lat) && !Number.isNaN(lng) ? `#19.15/${lat}/${lng}` : '';
      const url = `https://zola.planning.nyc.gov/l/lot/${bbl.boro}/${bbl.block}/${bbl.lot}?search=true${zoomLevel}`;
      console.log('🎯 Using dynamically fetched BBL:', url);
      enhancedZolaLink.value = url;
    } else {
      console.log('⚠️ Could not fetch BBL, using address search fallback');
      enhancedZolaLink.value = `https://zola.planning.nyc.gov/?search=${encodeURIComponent(fullAddress)}`;
    }
  } catch (error) {
    console.log('⚠️ BBL fetch failed:', error);
    enhancedZolaLink.value = `https://zola.planning.nyc.gov/?search=${encodeURIComponent(address)}`;
  }
};

// Build ZOLA deep link using either BBL or geocoded lat/lng when available
const zolaLink = computed(() => {
  try {
    // Check enhanced BBL lookup FIRST (from API) - this will trigger reactivity when it updates
    if (enhancedZolaLink.value && enhancedZolaLink.value !== 'https://zola.planning.nyc.gov/') {
      console.log('✅ Using enhanced BBL lookup:', enhancedZolaLink.value);
      return enhancedZolaLink.value;
    }

    // Then check if BBL is present on project info (from database)
    const boro = projectInformation.value?.borough_code || projectInformation.value?.borough || projectInformation.value?.boro;
    const block = projectInformation.value?.block;
    const lot = projectInformation.value?.lot;
    const lat = parseFloat(projectInformation.value?.latitude);
    const lng = parseFloat(projectInformation.value?.longitude);
    const zoom = 18.5;

    console.log('🔍 zolaLink computed - checking conditions:', {
      boro, block, lot, lat, lng,
      enhancedZolaLink: enhancedZolaLink.value
    });

    if (boro && block && lot && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      const url = `https://zola.planning.nyc.gov/l/lot/${encodeURIComponent(boro)}/${encodeURIComponent(block)}/${encodeURIComponent(lot)}?search=true#${zoom}/${lat}/${lng}`;
      console.log('✅ Using project BBL with coords:', url);
      return url;
    }
    if (boro && block && lot) {
      const url = `https://zola.planning.nyc.gov/l/lot/${encodeURIComponent(boro)}/${encodeURIComponent(block)}/${encodeURIComponent(lot)}?search=true`;
      console.log('✅ Using project BBL without coords:', url);
      return url;
    }
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      const url = `https://zola.planning.nyc.gov/#${zoom}/${lat}/${lng}`;
      console.log('✅ Using project lat/lng:', url);
      return url;
    }
  } catch (error) {
    console.log('⚠️ Error in zolaLink computed:', error);
  }
  // Fallback to ZOLA home
  console.log('⚠️ Using fallback ZOLA home');
  return 'https://zola.planning.nyc.gov/';
});

// Watchers for ZoLa BBL conversion
watch(() => subMenuSelectedItemId.value, (newValue) => {
  if (newValue === 4 && projectInformation.value && !bblConversionAttempted.value) {
    console.log('🎯 Zoning tab selected, fetching BBL...');
    fetchBBLFromAddress();
  }
});

// Also try when project information becomes available
watch(projectInformation, (newValue) => {
  if (newValue && subMenuSelectedItemId.value === 4 && !bblConversionAttempted.value) {
    console.log('🎯 Project info loaded while on Zoning tab, fetching BBL...');
    fetchBBLFromAddress();
  }
}, { deep: true });

// Doughnut Chart AG Charts Configuration
const doughnutAgChartOptions = computed(() => {
  const data = [
    { category: 'Acquisition Costs', value: lcTotalRevisedBudget.value, color: '#52596D' },
    { category: 'Hard Costs', value: hcTotalRevisedBudget.value, color: '#4A5FD5' },
    { category: 'Soft Costs', value: scTotalRevisedBudget.value, color: '#4A8FD0' },
    { category: 'Finance/Misc Costs', value: miscFinancingTotalRevisedBudget.value, color: '#8A92A9' },
    { category: 'Contingencies', value: contingenciesTotalRevisedBudget.value, color: '#d3d3d3' }
  ];

  return {
    data: data,
    height: 300,
    width: 280,
    series: [{
      type: 'pie',
      angleKey: 'value',
      categoryKey: 'category',
      innerRadiusRatio: 0.6,
      fills: data.map(d => d.color),
      strokes: data.map(d => d.color),
      strokeWidth: 1,
      tooltip: {
        renderer: (params) => ({
          title: params.datum.category,
          content: formatAxisValue(params.datum.value)
        })
      },
      label: {
        enabled: false
      }
    }],
    background: {
      fill: 'transparent'
    },
    legend: {
      enabled: true,
      position: 'bottom',
      spacing: 40,
      item: {
        paddingX: 16,
        paddingY: 8,
        marker: {
          shape: 'square',
          size: 12,
          strokeWidth: 0
        },
        label: {
          fontSize: 12,
          fontWeight: '500'
        }
      }
    },
    padding: {
      top: 10,
      right: 10,
      bottom: 60,
      left: 10
    }
  };
});
// Total Project Costs Stacked Bar Chart AG Charts Configuration
const costsToDateAgChartOptions = computed(() => {
  // 🎯 USE EXACT SAME DATA AS LEGEND - NO BULLSHIT
  const plannedBudget = cdTotalOriginalBudget.value || 0;
  const currentBudget = cdTotalCurrentBudget.value || 0;
  const completedToDate = cdTotalBilledToDate.value || 0;
  
  console.log('🎯 CHART USING LEGEND DATA:', {
    plannedBudget: plannedBudget,
    currentBudget: currentBudget, 
    completedToDate: completedToDate
  });
  
  // 🎯 SIMPLE: Calculate chart segments from legend values
  const completedSegment = completedToDate;
  const plannedRemainingSegment = Math.max(0, plannedBudget - completedToDate);
  const budgetVarianceSegment = Math.max(0, currentBudget - plannedBudget);
  
  console.log(`🎯 BUDGET SEGMENTS: Completed=${completedSegment}, PlannedRemaining=${plannedRemainingSegment}, Variance=${budgetVarianceSegment}`);
  
  const data = [
    { 
      category: 'Total',
      completedToDate: completedSegment,
      plannedRemaining: plannedRemainingSegment,
      budgetVariance: budgetVarianceSegment,
      // Keep original values for tooltips
      plannedBudget: plannedBudget,
      currentBudget: currentBudget,
      totalCompleted: completedToDate
    }
  ];

  console.log('📊 Total Project Costs Chart - Single Stacked Bar:', {
    plannedBudget: plannedBudget.toLocaleString(),
    currentBudget: currentBudget.toLocaleString(), 
    completedToDate: completedToDate.toLocaleString()
  });

  return {
    data: data,
    height: 250, // 🎯 FIXED HEIGHT: Prevent shifting when chart loads
    width: '100%',
    autoSize: false, // 🎯 Disable auto-sizing to prevent movement
    groupPadding: 0,
    series: [
      {
        type: 'column',
        xKey: 'category',
        yKey: 'completedToDate',
        yName: 'Complete to Date',
        fill: '#2563eb', // Dark blue - bottom segment
        stroke: '#2563eb',
        strokeWidth: 0,
        stacked: true,
        tooltip: {
          renderer: (params) => {
            const percentage = ((params.datum.totalCompleted || 0) / (params.datum.currentBudget || 1) * 100).toFixed(0);
            return {
              title: 'Complete to Date',
              content: `${formatAxisValue(params.datum.totalCompleted)} (${percentage}%)`
            };
          }
        }
      },
      {
        type: 'column',
        xKey: 'category',
        yKey: 'plannedRemaining',
        yName: 'Planned Budget',
        fill: '#93c5fd', // Light blue - matches mockup
        stroke: '#93c5fd',
        strokeWidth: 0,
        stacked: true,
        tooltip: {
          renderer: (params) => ({
            title: 'Planned Budget (Remaining)',
            content: formatAxisValue(params.datum.plannedRemaining)
          })
        }
      },
      {
        type: 'column',
        xKey: 'category',
        yKey: 'budgetVariance',
        yName: 'Current Budget (Overage)',
        fill: '#bfdbfe', // Lighter blue - matches mockup
        stroke: '#bfdbfe',
        strokeWidth: 0,
        stacked: true,
        tooltip: {
          renderer: (params) => {
            const variance = (params.datum.currentBudget || 0) - (params.datum.plannedBudget || 0);
            const varianceText = variance >= 0 ? `+${formatAxisValue(variance)}` : formatAxisValue(variance);
            const percentage = ((variance / (params.datum.plannedBudget || 1)) * 100).toFixed(0);
            return {
              title: 'Budget Variance',
              content: `${varianceText} (${percentage}%) vs Planned`
            };
          }
        }
      }
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        paddingInner: 0.85, // 🎯 MATCH MOCKUP: Very narrow bar
        paddingOuter: 0.5, // 🎯 CENTERED: Equal padding on both sides
        label: {
          enabled: false // Hide "Total" label like mockup
        }
      },
      {
        type: 'number',
        position: 'right', // 🎯 MATCH MOCKUP: Labels on right side
        label: {
          fontSize: 11,
          color: '#6b7280',
          formatter: (params) => formatAxisValue(params.value)
        },
        gridStyle: [{
          stroke: '#e5e7eb', // 🎯 MATCH MOCKUP: Horizontal marker lines
          lineDash: [0] // Solid lines
        }],
        nice: true,
        tick: {
          enabled: true,
          size: 0
        }
      }
    ],
    background: {
      fill: 'transparent'
    },
    padding: {
      top: 10,
      right: 100, // 🎯 MORE space to push bar left for centering
      bottom: 20,
      left: 100 // 🎯 MATCH right padding to center the bar
    },
    legend: {
      enabled: false
    }
  };
});

// 🎯 CACHE: Static chart data that doesn't change with draw selection
let cachedChartData = null;
let cachedChartSeries = null;

// Function to invalidate chart cache when filters change
function invalidateChartCache() {
  cachedChartData = null;
  cachedChartSeries = null;
  lastCostCurveHash = ''; // Also clear the data cache hash to force rebuild
}

// 🎯 Calculate label positions for Total Project Costs chart (match mockup)
function getLabelPosition(type) {
  const completed = cdTotalBilledToDate.value || 0;
  const planned = cdTotalOriginalBudget.value || 0;
  const current = cdTotalCurrentBudget.value || 0;
  
  if (current === 0) return 50; // Default center
  
  // Calculate percentages of each segment from the BOTTOM (how chart draws)
  const completedPct = (completed / current) * 100; // e.g., 100% if fully complete
  const plannedPct = (planned / current) * 100;
  
  // 🎯 Chart draws from BOTTOM to TOP, so positions are inverted:
  // - 0% from bottom = 100% from top (bottom of container)
  // - 100% from bottom = 0% from top (top of container)
  
  if (type === 'completed') {
    // Middle of completed segment (which starts at bottom)
    // If completed is 100%, segment goes from 0-100% from bottom
    // Middle is at 50% from bottom = 50% from top
    const bottomPosition = completedPct / 2; // Middle of segment from bottom
    return 100 - bottomPosition; // Convert to top-based percentage
  } else if (type === 'planned') {
    // Planned budget is the TOTAL bar height (since current = planned in this case)
    // Position at middle of entire bar
    const barMiddle = plannedPct / 2; // Middle of planned bar from bottom
    return 100 - barMiddle; // Convert to top-based percentage
  } else if (type === 'current') {
    // Current budget label at very top
    return 5; // Top 5% of chart area
  }
  
  return 50;
}

// Cost Curve AG Charts Configuration  
const costCurveAgChartOptions = computed(() => {
  if (!costCurveChartReady.value) return null;
  
  // 🎯 DEPENDENCY: Include costCurveCategory to trigger rebuild when filter changes
  const currentFilter = costCurveCategory.value;
  
  // 🎯 OPTIMIZATION: Only rebuild chart data if it doesn't exist, cost curve data changes, or filter changes
  if (!cachedChartData || !cachedChartSeries || cachedChartData.filterUsed !== currentFilter) {
    console.log('🎯 Building cost curve chart data (filter:', currentFilter, ')');
    cachedChartData = buildAgChartData();
    cachedChartData.filterUsed = currentFilter; // Track which filter was used
    cachedChartSeries = buildAgChartSeries();
  }
  
  // 🎯 HIGHLIGHTING: Only the draw highlighting changes, not the data
  const selectedDraw = costsToDateDraw.value;
  console.log('🎯 Chart highlighting - Selected draw:', selectedDraw);
  
  const chartData = cachedChartData;
  const chartSeries = cachedChartSeries;
  
  // Debug: Log chart data and series to verify tooltip configuration

  
  // Test tooltip data structure
  if (chartData.length > 0) {
    const sampleData = chartData[0];
    
    // Debug: Check if category values exist and are non-zero
    
    // Check a later data point to see progression
    if (chartData.length > 10) {
      const laterData = chartData[10];
    }
  }
  
  // ✅ DYNAMIC CHART SIZING: Adjust based on number of draws for better space utilization (computed once)
  const drawCount = chartData.length;
  const isVerySmallDataset = drawCount <= 2; // 🎯 NEW: Handle 1-2 draws
  const isSmallDataset = drawCount <= 5;
  const isMediumDataset = drawCount <= 15;
  

  
  return {
    data: chartData,
    autoSize: true,
    background: {
      fill: 'transparent'
    },
    padding: {
      top: 16,
      right: isSmallDataset ? 18 : 28,
      bottom: 24, // ensure x-axis labels and ticks are fully visible
      left: isSmallDataset ? 48 : 60
    },
    // 🔥 NEW: Click handler to change draw when bar is clicked
    listeners: {
      seriesNodeClick: (event) => {
        if (event.datum && event.datum.draw) {
          const clickedDraw = event.datum.draw;
          console.log('🎯 Cost curve bar clicked - Draw:', clickedDraw);
          
          // Update the draw number to load that draw's data in the grid
          drawNumber.value = clickedDraw;
          globalDrawFilter.value = clickedDraw;
          
          // Fetch the draw details for the clicked draw
          fetchDrawDetailGridData();
        }
      }
    },
    // 🎯 REMOVED: highlightStyle only works on hover, not programmatic selection
    series: chartSeries,
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          fontSize: 12,
          color: '#2c3e50',
          rotation: 0
        },
        // 🎯 MUCH SMALLER BARS: Aggressive padding to prevent huge bars with few draws
        paddingInner: isVerySmallDataset ? 0.92 : (isSmallDataset ? 0.85 : (isMediumDataset ? 0.6 : 0.4)),
        paddingOuter: isVerySmallDataset ? 0.7 : (isSmallDataset ? 0.6 : (isMediumDataset ? 0.4 : 0.2))
      },
      {
        type: 'number',
        position: 'left',
        label: {
          fontSize: 10,
          formatter: (params) => formatAxisValue(params.value)
        },
        gridStyle: [{
          stroke: '#f0f0f0',
          lineDash: [2, 4]
        }],
        nice: true,
        min: 0 // Prevent negative values from showing
      }
    ],
    legend: {
      enabled: false // Disabled to use custom legend below
    }
  };
});

// (Removed) Separate initial draw bar chart options

// Budget Variance Summary - CUSTOM TORNADO CHART DATA
const tornadoData = computed(() => {
  if (budgetVarianceView.value === 'contractor') {
    // Use real contractor data if available, otherwise mock data
    if (Array.isArray(contractorVarianceData.value) && contractorVarianceData.value.length > 0) {
      return contractorVarianceData.value.map(contractor => {
        let contractorName = contractor.name || contractor.contractor_name;
        // Truncate very long contractor names for display
        if (contractorName && contractorName.length > 25) {
          contractorName = contractorName.substring(0, 22) + '...';
        }
        return {
          category: contractorName,
          variance: contractor.variance || contractor.total_variance || 0
        };
      });
    } else {
      // NO MOCK DATA - Return empty array if no real contractor data
      console.log('🚫 No contractor data available - showing empty chart (NO MOCK DATA)');
      return [];
    }
    } else if (budgetVarianceView.value === 'category') {
    // Category view - calculate variance DIRECTLY from cost curve Step 3 total rows
    console.log('🔥 Budget Variance Summary - Using COST CURVE data directly');
    
    if (!costCurveData.value || !Array.isArray(costCurveData.value)) {
      console.warn('No cost curve data available for variance calculation');
      return [];
    }
    
    // Get ALL cost curve detail rows (NOT total rows - variance is in detail!)
    const detailRows = costCurveData.value.filter(row => row.is_total_row !== true);
    
    // Filter by selected draw
    const selectedDraw = parseInt(budgetVarianceDraw.value) || 
      (drawNumberList.value && drawNumberList.value.length > 0 ? drawNumberList.value[0] : null);
    let drawToProcess = selectedDraw ? 
      detailRows.filter(row => parseInt(row.draw_number) === selectedDraw) : 
      detailRows;
    
    // 🔥 NEW: Get previous draw data to check for new items
    const previousDrawNum = selectedDraw ? selectedDraw - 1 : null;
    const previousDrawRows = previousDrawNum && previousDrawNum > 0 ? 
      detailRows.filter(row => parseInt(row.draw_number) === previousDrawNum) : 
      [];
    
    // Create a map of previous draw items by description for quick lookup
    const previousDrawMap = {};
    previousDrawRows.forEach(row => {
      const key = `${row.description}_${row.dda_term_id}`;
      previousDrawMap[key] = {
        current_budget: parseFloat(row.current_budget || 0),
        original_budget: parseFloat(row.original_budget || 0)
      };
    });
    
    // 🎯 FIX: Sum line items by DDA categories to calculate variances
    
    // Use line items already filtered above (drawToProcess contains the right line items)
    // Calculate variance by summing line items into DDA categories
    const categoryVariances = {};
    
    drawToProcess.forEach(row => {
      const originalBudget = parseFloat(row.original_budget || 0);
      const currentBudget = parseFloat(row.current_budget || 0);
      const variance = currentBudget - originalBudget;
      
      // 🔥 NEW: Exclude new items or items that were $0 in previous draw
      const itemKey = `${row.description}_${row.dda_term_id}`;
      const prevItem = previousDrawMap[itemKey];
      
      // Skip if this is a new item (not in previous draw)
      if (!prevItem) {
        console.log(`⚠️ Skipping NEW item from variance: ${row.description}`);
        return;
      }
      
      // Skip if previous draw had $0 budget (newly funded item)
      if (prevItem.current_budget === 0 && currentBudget > 0) {
        console.log(`⚠️ Skipping newly funded item (was $0): ${row.description}`);
        return;
      }
      
      // Use DDA category mapping instead of hardcoded total_type
      const categoryKey = getDDATermChartCategory(row.dda_term_id, row.dda_term_name);
      if (!categoryKey) return; // skip unknown category
      const categoryName = {
        hardCosts: 'Hard Costs',
        softCosts: 'Soft Costs', 
        acquisition: 'Acquisition',
        financing: 'Financing',
        contingencies: 'Contingencies'
      }[categoryKey] || categoryKey;
      
      // Sum up variances for each category (multiple line items per category)
      if (!categoryVariances[categoryName]) {
        categoryVariances[categoryName] = 0;
      }
      categoryVariances[categoryName] += variance;
    });
    
    // Convert to array format and filter out zero variances
    const result = Object.keys(categoryVariances)
      .map(category => ({
        category: category,
        variance: categoryVariances[category]
      }))
      .filter(item => Math.abs(item.variance) > 100); // Show variances > $100
     
     console.log('🔥 FINAL category variances (filtered):', result.map(item => ({
       category: item.category,
       variance: `$${item.variance.toLocaleString()}`
     })));
     
     return result;
  } else if (budgetVarianceView.value === 'lineitem') {
    // 🎯 NEW: Line item view - show individual line items with variances
    console.log('🔥 BUDGET VARIANCE: Using line item view from cost curve data');
    
    if (!costCurveData.value || !Array.isArray(costCurveData.value)) {
      console.warn('No cost curve data available for line item variance calculation');
      return [];
    }
    
    // Get detail rows (NOT total rows) for line items  
    const detailRows = costCurveData.value.filter(row => row.is_total_row !== true);
    console.log(`🎯 Found ${detailRows.length} line item detail rows`);
    
    // Filter by selected draw
    const selectedDraw = parseInt(budgetVarianceDraw.value) || 
      (drawNumberList.value && drawNumberList.value.length > 0 ? drawNumberList.value[0] : null);
    let lineItemsToProcess = selectedDraw ? 
      detailRows.filter(row => parseInt(row.draw_number) === selectedDraw) : 
      detailRows;
      console.log(`🎯 Filtered to draw ${selectedDraw}: ${lineItemsToProcess.length} line items`);
    
    // 🔥 NEW: Get previous draw data to check for new items (line item view)
    const previousDrawNum = selectedDraw ? selectedDraw - 1 : null;
    const previousDrawLineItems = previousDrawNum && previousDrawNum > 0 ? 
      detailRows.filter(row => parseInt(row.draw_number) === previousDrawNum) : 
      [];
    
    // Create a map of previous draw items by description for quick lookup
    const previousLineItemMap = {};
    previousDrawLineItems.forEach(row => {
      const key = `${row.description}_${row.dda_term_id}`;
      previousLineItemMap[key] = {
        current_budget: parseFloat(row.current_budget || 0),
        original_budget: parseFloat(row.original_budget || 0)
      };
    });
    
    // Calculate variance for each line item and limit to top variances
    const lineItemVariances = lineItemsToProcess
      .map(row => {
        const originalBudget = parseFloat(row.original_budget || 0);
        const currentBudget = parseFloat(row.current_budget || 0);
        const variance = currentBudget - originalBudget;
        
        // 🔥 NEW: Check if this is a new item or was $0 in previous draw
        const itemKey = `${row.description}_${row.dda_term_id}`;
        const prevItem = previousLineItemMap[itemKey];
        
        // Mark as new item or newly funded
        const isNew = !prevItem;
        const isNewlyFunded = prevItem && prevItem.current_budget === 0 && currentBudget > 0;
        
        // Create display name from description, truncate if too long
        let displayName = row.description || 'Unknown Item';
        if (displayName.length > 30) {
          displayName = displayName.substring(0, 27) + '...';
        }
        
        return {
          category: displayName,
          variance: variance,
          fullDescription: row.description,
          contractor: row.contractor_name || 'N/A',
          isNew: isNew,
          isNewlyFunded: isNewlyFunded
        };
      })
      .filter(item => {
        // Exclude new items and newly funded items from variance report
        if (item.isNew || item.isNewlyFunded) {
          console.log(`⚠️ Excluding from variance report: ${item.fullDescription} (${item.isNew ? 'NEW' : 'NEWLY FUNDED'})`);
          return false;
        }
        return Math.abs(item.variance) > 1000; // Filter significant variances
      })
      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance)) // Sort by absolute variance
      .slice(0, 10); // Show top 10 line item variances
    
    return lineItemVariances;
  }
  
  // Default fallback - return empty array if no view matches
  return [];
});

const maxAbsVariance = computed(() => {
  const maxAbs = Math.max(...tornadoData.value.map(d => Math.abs(d.variance)));
  console.log('🔢 Tornado data variances:', tornadoData.value.map(d => ({ category: d.category, variance: d.variance })));
  console.log('🔢 Max absolute variance:', maxAbs);
  return maxAbs > 0 ? maxAbs : 100000;
});

// Top Budget Movements - Real data from grid
const topBudgetMovements = computed(() => {
  // Try multiple data sources - use G703 data when on G703 tab, else drawDetailRowData
  let sourceData = null;
  let sourceName = '';
  
  // 🔥 FIX: Check G703 tab first (btSubMenuSelectedItemId === 2)
  if (btSubMenuSelectedItemId.value === 2 && g703RowData.value && Array.isArray(g703RowData.value) && g703RowData.value.length > 0) {
    sourceData = g703RowData.value;
    sourceName = 'g703RowData';
  } else if (drawDetailRowData.value && Array.isArray(drawDetailRowData.value) && drawDetailRowData.value.length > 0) {
    sourceData = drawDetailRowData.value;
    sourceName = 'drawDetailRowData';
  } else if (leafParentData.value && Array.isArray(leafParentData.value) && leafParentData.value.length > 0) {
    sourceData = leafParentData.value;
    sourceName = 'leafParentData';
  } else {
    console.log('🚫 No data available for budget movements - checking g703RowData, drawDetailRowData and leafParentData');
    console.log('🔍 g703RowData length:', g703RowData.value?.length || 0);
    console.log('🔍 drawDetailRowData length:', drawDetailRowData.value?.length || 0);
    console.log('🔍 leafParentData length:', leafParentData.value?.length || 0);
    return [];
  }
  
  console.log(`🔍 Using data source: ${sourceName} with ${sourceData.length} rows`);
  console.log('🔍 Sample rows with budget info:', sourceData.slice(0, 5).map(r => ({
    desc: r.description?.substring(0, 30),
    orig: r.original_budget,
    curr: r.current_budget,
    calc_adj: r.current_adjustments,
    is_total: r.is_total_row
  })));
  
  // ✅ FIXED: Filter to rows with significant adjustments using calculated current_adjustments
  const budgetMovements = sourceData
    .filter(row => {
      // 🔥 For G703, recalculate adjustment as current - original to ensure we have fresh data
      const calculatedAdjustment = (parseFloat(row.current_budget) || 0) - (parseFloat(row.original_budget) || 0);
      const hasVariance = calculatedAdjustment && Math.abs(calculatedAdjustment) > 1000;
      const isNotTotal = !row.is_total_row;
      const isNotContingency = !row.description?.toLowerCase().includes('contingency');
      const description = (row.description || '').toLowerCase();
      const isNotSubtotal = !description.includes('subtotal') && !description.includes('total');
      
      // 🔥 CRITICAL FIX: Exclude new items with $0 or very small original budget (prevents +7000000% variance)
      const hasValidOriginalBudget = row.original_budget && Math.abs(row.original_budget) > 100;
      
      // Store calculated adjustment on the row for later use
      row._calculatedAdjustment = calculatedAdjustment;
      
      return isNotTotal && hasVariance && isNotContingency && isNotSubtotal && hasValidOriginalBudget;
    })
    .sort((a, b) => Math.abs(b._calculatedAdjustment || 0) - Math.abs(a._calculatedAdjustment || 0))
    .slice(0, 5);

  console.log(`🎯 Found ${budgetMovements.length} items with significant budget movements`);
  budgetMovements.forEach(r => {
    console.log(`  📊 ${r.description?.substring(0, 35)}: Orig=${r.original_budget}, Curr=${r.current_budget}, Diff=${r._calculatedAdjustment}`);
  });
  
  const result = budgetMovements.map(row => ({
    description: row.description || 'Unknown Item',
    variance: row._calculatedAdjustment || 0,
    originalBudget: row.original_budget || 0,
    isNew: false
  }));
  
  console.log(`🎯 Top Budget Movements from ${sourceName} (${result.length} items):`, result);
  return result;
});

// 🔥 FIXED: Hard Cost Contingency - Include draw number in cache key
let hardCostContingencyCache = null;
let lastDataLength = 0;
let lastDrawNumber = null;

const hardCostContingency = computed(() => {
  // Include current draw in cache key to refresh when draw changes
  const currentDraw = drawNumber.value || selectedDrawNumber.value || null;
  const currentDataLength = drawDetailRowData.value?.length || 0;
  
  if (hardCostContingencyCache && lastDataLength === currentDataLength && lastDrawNumber === currentDraw) {
    return hardCostContingencyCache;
  }
  // Try multiple data sources
  let sourceData = null;
  let sourceName = '';
  
  if (drawDetailRowData.value && Array.isArray(drawDetailRowData.value) && drawDetailRowData.value.length > 0) {
    sourceData = drawDetailRowData.value;
    sourceName = 'drawDetailRowData';
  } else if (leafParentData.value && Array.isArray(leafParentData.value) && leafParentData.value.length > 0) {
    sourceData = leafParentData.value;
    sourceName = 'leafParentData';
  } else {
    console.log('🚫 No data available for hard cost contingency search');
    return null;
  }
  
  // 🔥 SUM ALL hard cost contingency rows instead of finding first only
  const hardContingencyRows = sourceData.filter(row => {
    const categoryId = row.dda_term?.dda_term_category_id;
    const ddaTerm = row.dda_term?.term || row.dda_term_name || '';
    return categoryId === 210 && ddaTerm.toLowerCase().includes('hard');
  });
  
  if (hardContingencyRows.length === 0) {
    console.log(`🚫 No hard cost contingency found in ${sourceName}`);
    return null;
  }
  
  // ✅ FIXED: Use current_adjustments to calculate usage
  // Contingency doesn't get "spent" - it gets "used" when budget is reallocated to other line items
  // When current_adjustments is NEGATIVE, that's contingency being used (budget going down)
  
  // Sum current draw's hard cost contingency
  const hardContingency = hardContingencyRows.reduce((sum, row) => ({
    currentBudget: (sum.currentBudget || 0) + (parseFloat(row.current_budget) || 0),
    balanceOfContingency: (sum.balanceOfContingency || 0) + (parseFloat(row.remaining_budget) || 0),
    totalSpentToDate: (sum.totalSpentToDate || 0) + (parseFloat(row.total_billing_to_date) || 0),
    originalBudget: (sum.originalBudget || 0) + (parseFloat(row.original_budget) || 0),
    variance: (sum.variance || 0) + (parseFloat(row.total_adjustments) || 0),
    currentRequest: (sum.currentRequest || 0) + (parseFloat(row.current_request) || 0)
  }), {});
  
  // ✅ FIXED: Usage This Period = current billing (current_request)
  hardContingency.usage = hardContingency.currentRequest || 0;
  
  // ✅ FIXED: Calculate ONLY Hard Cost values (category 197) for Hard Cost Contingency balance
  const hardCostCurrentBudget = sourceData
    .filter(row => row.dda_term?.dda_term_category_id === 197) // ONLY Hard Costs
    .reduce((sum, row) => sum + (parseFloat(row.current_budget) || 0), 0);
  
  const hardCostSpentToDate = sourceData
    .filter(row => row.dda_term?.dda_term_category_id === 197) // ONLY Hard Costs
    .reduce((sum, row) => sum + (parseFloat(row.total_billing_to_date) || 0), 0);
  
  // ✅ FIXED: Balance on Remaining Cost calculation based on project setting
  // Check if contingencies are already excluded from HC/SC budget (like project 710)
  // Use the checkbox value directly for real-time updates
  const contingenciesExcluded = contingenciesExcludedFromBudget.value;
  
  const contingencyRemaining = (hardContingency.currentBudget || 0) - (hardContingency.totalSpentToDate || 0);
  const hardCostTotalRemainingWork = hardCostCurrentBudget - hardCostSpentToDate;
  
  let balanceOfRemainingCost = 0;
  
  if (contingenciesExcluded) {
    // Formula for projects where contingencies are already backed out (12% result)
    // Denominator INCLUDES the contingency remaining (total hard costs remaining)
    balanceOfRemainingCost = hardCostTotalRemainingWork > 0 ? (contingencyRemaining / hardCostTotalRemainingWork) * 100 : 0;
  } else {
    // Default formula for most projects (90%) - (14% result)
    // Formula: (Hard Cost Contingency Remaining) / (Hard Cost Remaining Work - Hard Cost Contingency Remaining)
    // Contingency should cover the OTHER remaining work, not itself!
    const hardCostRemainingWorkExcludingContingency = hardCostTotalRemainingWork - contingencyRemaining;
    balanceOfRemainingCost = hardCostRemainingWorkExcludingContingency > 0 ? (contingencyRemaining / hardCostRemainingWorkExcludingContingency) * 100 : 0;
  }
  
  console.log(`✅ SUMMED ${hardContingencyRows.length} hard cost contingency rows: $${hardContingency.currentBudget}`);
  console.log(`📊 Hard Cost - Balance on Remaining Cost = ($${contingencyRemaining.toFixed(0)}) / ($${hardCostTotalRemainingWork.toFixed(0)} - $${contingencyRemaining.toFixed(0)}) = ${balanceOfRemainingCost.toFixed(1)}%`);
  
  const result = {
    usage: hardContingency.usage || 0,
    remaining: balanceOfRemainingCost, // Now a percentage value
    currentBudget: hardContingency.currentBudget || 0,
    originalBudget: hardContingency.originalBudget || 0,
    variance: hardContingency.variance || 0,
    totalSpentToDate: hardContingency.totalSpentToDate || 0,
    balanceOfContingency: contingencyRemaining // Remaining contingency dollars
  };
  
  // Cache the result to prevent recalculation
  hardCostContingencyCache = result;
  lastDataLength = drawDetailRowData.value?.length || 0;
  lastDrawNumber = currentDraw;
  
  return result;
});

// Soft Cost Contingency - Real data (look for Soft Costs Contingency)
// 🔥 FIXED: Soft Cost Contingency - Include draw number in cache key
let softCostContingencyCache = null;
let lastSoftDataLength = 0;
let lastSoftDrawNumber = null;

const softCostContingency = computed(() => {
  // Include current draw in cache key to refresh when draw changes
  const currentDraw = drawNumber.value || selectedDrawNumber.value || null;
  const currentDataLength = drawDetailRowData.value?.length || 0;
  
  if (softCostContingencyCache && lastSoftDataLength === currentDataLength && lastSoftDrawNumber === currentDraw) {
    return softCostContingencyCache;
  }
  // Try multiple data sources
  let sourceData = null;
  let sourceName = '';
  
  if (drawDetailRowData.value && Array.isArray(drawDetailRowData.value) && drawDetailRowData.value.length > 0) {
    sourceData = drawDetailRowData.value;
    sourceName = 'drawDetailRowData';
  } else if (leafParentData.value && Array.isArray(leafParentData.value) && leafParentData.value.length > 0) {
    sourceData = leafParentData.value;
    sourceName = 'leafParentData';
  } else {
    console.log('🚫 No data available for soft cost contingency search');
    return null;
  }
  
  // 🔥 SUM ALL soft cost contingency rows instead of finding first only
  const softContingencyRows = sourceData.filter(row => {
    const categoryId = row.dda_term?.dda_term_category_id;
    const ddaTerm = row.dda_term?.term || row.dda_term_name || '';
    return categoryId === 210 && ddaTerm.toLowerCase().includes('soft');
  });
  
  if (softContingencyRows.length === 0) {
    console.log(`🚫 No soft cost contingency found in ${sourceName}`);
    return null;
  }
  
  // ✅ FIXED: Use current_adjustments to calculate usage
  // Contingency doesn't get "spent" - it gets "used" when budget is reallocated to other line items
  // When current_adjustments is NEGATIVE, that's contingency being used (budget going down)
  
  // Sum current draw's soft cost contingency
  const softContingency = softContingencyRows.reduce((sum, row) => ({
    currentBudget: (sum.currentBudget || 0) + (parseFloat(row.current_budget) || 0),
    balanceOfContingency: (sum.balanceOfContingency || 0) + (parseFloat(row.remaining_budget) || 0),
    totalSpentToDate: (sum.totalSpentToDate || 0) + (parseFloat(row.total_billing_to_date) || 0),
    originalBudget: (sum.originalBudget || 0) + (parseFloat(row.original_budget) || 0),
    variance: (sum.variance || 0) + (parseFloat(row.total_adjustments) || 0),
    currentRequest: (sum.currentRequest || 0) + (parseFloat(row.current_request) || 0)
  }), {});
  
  // ✅ FIXED: Usage This Period = current billing (current_request)
  softContingency.usage = softContingency.currentRequest || 0;
  
  // ✅ FIXED: Calculate ONLY Soft Cost values (category 198) for Soft Cost Contingency balance
  const softCostCurrentBudget = sourceData
    .filter(row => row.dda_term?.dda_term_category_id === 198) // ONLY Soft Costs
    .reduce((sum, row) => sum + (parseFloat(row.current_budget) || 0), 0);
  
  const softCostSpentToDate = sourceData
    .filter(row => row.dda_term?.dda_term_category_id === 198) // ONLY Soft Costs
    .reduce((sum, row) => sum + (parseFloat(row.total_billing_to_date) || 0), 0);
  
  // ✅ FIXED: Balance on Remaining Cost calculation based on project setting
  // Check if contingencies are already excluded from HC/SC budget (like project 710)
  // Use the checkbox value directly for real-time updates
  const contingenciesExcluded = contingenciesExcludedFromBudget.value;
  
  const contingencyRemaining = (softContingency.currentBudget || 0) - (softContingency.totalSpentToDate || 0);
  const softCostTotalRemainingWork = softCostCurrentBudget - softCostSpentToDate;
  
  let balanceOfRemainingCost = 0;
  
  if (contingenciesExcluded) {
    // Formula for projects where contingencies are already backed out (12% result)
    // Denominator INCLUDES the contingency remaining (total soft costs remaining)
    balanceOfRemainingCost = softCostTotalRemainingWork > 0 ? (contingencyRemaining / softCostTotalRemainingWork) * 100 : 0;
  } else {
    // Default formula for most projects (90%) - (14% result)
    // Formula: (Soft Cost Contingency Remaining) / (Soft Cost Remaining Work - Soft Cost Contingency Remaining)
    // Contingency should cover the OTHER remaining work, not itself!
    const softCostRemainingWorkExcludingContingency = softCostTotalRemainingWork - contingencyRemaining;
    balanceOfRemainingCost = softCostRemainingWorkExcludingContingency > 0 ? (contingencyRemaining / softCostRemainingWorkExcludingContingency) * 100 : 0;
  }
  
  console.log(`✅ SUMMED ${softContingencyRows.length} soft cost contingency rows: $${softContingency.currentBudget}`);
  console.log(`📊 Soft Cost - Balance on Remaining Cost = ($${contingencyRemaining.toFixed(0)}) / ($${softCostTotalRemainingWork.toFixed(0)} - $${contingencyRemaining.toFixed(0)}) = ${balanceOfRemainingCost.toFixed(1)}%`);
  
  const result = {
    usage: softContingency.usage || 0,
    remaining: balanceOfRemainingCost, // Now a percentage value
    currentBudget: softContingency.currentBudget || 0,
    originalBudget: softContingency.originalBudget || 0,
    variance: softContingency.variance || 0,
    totalSpentToDate: softContingency.totalSpentToDate || 0,
    balanceOfContingency: contingencyRemaining // Remaining contingency dollars
  };
  
  // Cache the result to prevent recalculation
  softCostContingencyCache = result;
  lastSoftDataLength = drawDetailRowData.value?.length || 0;
  lastSoftDrawNumber = currentDraw;
  
  return result;
});

// 🆕 Development Fee % Utilized
const developmentFeeUtilized = computed(() => {
  // Try multiple data sources
  let sourceData = null;
  
  if (drawDetailRowData.value && Array.isArray(drawDetailRowData.value) && drawDetailRowData.value.length > 0) {
    sourceData = drawDetailRowData.value;
  } else if (leafParentData.value && Array.isArray(leafParentData.value) && leafParentData.value.length > 0) {
    sourceData = leafParentData.value;
  } else {
    return null;
  }
  
  // Find Development Fee row (look for "Development Fee" or "Developer Fee" in description)
  const devFeeRow = sourceData.find(row => {
    const description = (row.description || '').toLowerCase();
    const ddaTerm = (row.dda_term?.term || row.dda_term_name || '').toLowerCase();
    return description.includes('development fee') || 
           description.includes('developer fee') ||
           ddaTerm.includes('development fee') ||
           ddaTerm.includes('developer fee');
  });
  
  if (!devFeeRow) {
    console.log('🚫 No Development Fee row found');
    return null;
  }
  
  const currentBudget = parseFloat(devFeeRow.current_budget) || 0;
  const totalSpentToDate = parseFloat(devFeeRow.total_billing_to_date) || 0;
  
  if (currentBudget === 0) {
    return 0;
  }
  
  const percentUtilized = (totalSpentToDate / currentBudget) * 100;
  
  // Calculate project completion % for color coding
  const projectCompleted = cdTotalBilledToDate.value || 0;
  const projectBudget = cdTotalCurrentBudget.value || 1;
  const projectCompletionPct = projectBudget > 0 ? (projectCompleted / projectBudget) * 100 : 0;
  
  console.log(`📊 Development Fee % Utilized: ${totalSpentToDate} / ${currentBudget} = ${percentUtilized.toFixed(1)}%`);
  console.log(`📊 Project Completion %: ${projectCompleted.toLocaleString()} / ${projectBudget.toLocaleString()} = ${projectCompletionPct.toFixed(1)}%`);
  console.log(`📊 Color: ${percentUtilized > projectCompletionPct ? 'RED (dev fee ahead)' : 'GREEN (dev fee on track)'}`);
  
  return percentUtilized;
});

// 🆕 Development Fee CSS Class (for color coding)
const developmentFeeColorClass = computed(() => {
  if (developmentFeeUtilized.value === null) return '';
  
  const projectCompleted = cdTotalBilledToDate.value || 0;
  const projectBudget = cdTotalCurrentBudget.value || 1;
  const projectCompletionPct = projectBudget > 0 ? (projectCompleted / projectBudget) * 100 : 0;
  
  return developmentFeeUtilized.value > projectCompletionPct ? 'red-text' : 'green-text';
});

// 🚀 SMALL CHANGE #1: Add prefunded data as ref and function
let prefundedCircleData = ref([]);
let showPrefundedTooltip = ref(false);

function getPrefundedAmountForCategory(totalType) {
  // Helper function to get prefunded amount for a specific category
  const categoryName = {
    hard_costs: 'Hard Costs',
    soft_costs: 'Soft Costs', 
    acquisition: 'Acquisition',
    financing: 'Financing',
    contingencies: 'Contingencies'
  }[totalType] || totalType;
  
  const prefundedItem = prefundedCircleData.value?.find(item => item.category === categoryName);
  const amount = prefundedItem ? prefundedItem.amount : 0;
  
  console.log(`💰 Prefunded amount for ${totalType}: $${amount.toLocaleString()}`);
  return amount;
}

function calculatePrefundedData() {
  if (!costCurveData.value || !Array.isArray(costCurveData.value) || costCurveData.value.length === 0) {
    prefundedCircleData.value = [];
    return;
  }

  // Find FIRST available draw dynamically (could be Draw 59, not Draw 1!)
  const lineItems = costCurveData.value.filter(row => row.is_total_row !== true);
  
  const allDraws = [...new Set(lineItems.map(row => parseInt(row.draw_number) || 0))].sort((a, b) => a - b);
  const firstDrawNumber = allDraws[0];
  
  if (!firstDrawNumber) {
    prefundedCircleData.value = [];
    return;
  }

  console.log(`💰 PREFUNDED DATA: Using Draw ${firstDrawNumber} as first available draw`);

  // Get first draw line items only and sum by DDA categories
  const firstDrawLineItems = lineItems.filter(row => {
    const recordDraw = parseInt(row.draw_number) || 0;
    return recordDraw === firstDrawNumber;
  });

  // Sum line items by DDA category for prefunded data
  const categoryTotals = {};
  firstDrawLineItems.forEach(row => {
    const categoryKey = getDDATermChartCategory(row.dda_term_id, row.dda_term_name);
    const categoryName = {
      hardCosts: 'Hard Costs',
      softCosts: 'Soft Costs',
      acquisition: 'Acquisition', 
      financing: 'Financing',
      contingencies: 'Contingencies'
    }[categoryKey] || categoryKey;
    
    if (!categoryTotals[categoryName]) {
      categoryTotals[categoryName] = { fundedAmount: 0, totalType: categoryKey };
    }

  });

  const prefundedData = [];
  Object.keys(categoryTotals).forEach(categoryName => {
    const data = categoryTotals[categoryName];
    const fundedAmount = data.fundedAmount / viewModeDivisor.value;
    
    if (fundedAmount > 0) {
      prefundedData.push({
        category: categoryName,
        amount: fundedAmount
      });
      
      console.log(`💰 PREFUNDED ${categoryName}: ${fundedAmount.toLocaleString()}`);
    }
  });


  prefundedCircleData.value = prefundedData;
}

// 🚀 SMALL CHANGE #2: Add circle chart options (no UI yet)
const prefundedCircleOptions = computed(() => {
  if (!prefundedCircleData.value || prefundedCircleData.value.length === 0) {
    return null;
  }

  const categoryColors = {
    'Hard Costs': '#2E4BC6',
    'Soft Costs': '#7FB3D3', 
    'Acquisition': '#B8C5E0',
    'Financing': '#9B59B6',
    'Contingencies': '#E67E22'
  };

  console.log(`🎨 PREFUNDED CHART: Building options for ${prefundedCircleData.value.length} categories`);

  return {
    data: prefundedCircleData.value,
    series: [{
      type: 'pie',
      angleKey: 'amount',
      labelKey: 'category',
      fills: prefundedCircleData.value.map(d => categoryColors[d.category] || '#95A5A6'),
      innerRadiusRatio: 0.4, // Donut chart
      label: {
        enabled: true,
        fontSize: 10
      },
      tooltip: {
        renderer: ({ datum }) => ({
          content: `${datum.category}: ${kMillionsWithDecimalsFormat(datum.amount)}`
        })
      }
    }],
    background: {
      fill: 'transparent'
    },
    title: {
      enabled: false // Will add title in HTML overlay
    },
    legend: {
      enabled: false // Will show total below chart
    },
    width: 200,
    height: 200
  };
});

function buildAgChartData() {
  const startTime = performance.now();
  // console.log(`📊 SIMPLIFIED: Building cost curve from DDA term totals only...`);
  
  // Use the separate cost curve data instead of grid data
  if (!costCurveData.value || !Array.isArray(costCurveData.value)) {
    console.warn('No cost curve data available');
    return [];
  }

  // Helper to derive chart category from a row's DDA info
  const deriveCategory = (row) => {
    const ddaTermId = row?.dda_term_id;
    const isComputed = row?.is_computed_total === true;
    
    // 🎯 PRIORITY 1: REAL DDA TERM TOTALS (always use these if present)
    if (ddaTermId === 11635) return { category: 'hardCosts', isReal: true };     // Hard Costs Total
    if (ddaTermId === 11637) return { category: 'softCosts', isReal: true };     // Soft Costs Total  
    if (ddaTermId === 11633) return { category: 'acquisition', isReal: true };   // Acquisition Costs Total
    if (ddaTermId === 11639) return { category: 'financing', isReal: true };     // Financing Costs Total
    if (ddaTermId === 11641) return { category: 'financing', isReal: true };     // Total Misc. & Financing Costs
    if (ddaTermId === 12206) return { category: 'contingencies', isReal: true }; // Contingency Reserve (Contingencies Total)
    if (ddaTermId === 11619) return { category: 'contingencies', isReal: true }; // Hard Costs Contingency
    if (ddaTermId === 11620) return { category: 'contingencies', isReal: true }; // Soft Costs Contingency
    
    // 🎯 PRIORITY 2: COMPUTED TOTALS (only use if no real DDA term exists)
    if (isComputed) {
      const totalType = row?.total_type;
      if (totalType === 'hard_costs') return { category: 'hardCosts', isReal: false };
      if (totalType === 'soft_costs') return { category: 'softCosts', isReal: false };
      if (totalType === 'acquisition') return { category: 'acquisition', isReal: false };
      if (totalType === 'financing') return { category: 'financing', isReal: false };
      if (totalType === 'contingencies') return { category: 'contingencies', isReal: false };
    }
    
    return null;
  };


  // 🎯 CORRECTED: Use category 209 (totals with is_total_row flag) AND 210 (contingencies - ALL rows)
  const totalRows = costCurveData.value.filter(row => {
    const categoryId = row?.dda_term?.dda_term_category_id;
    
    // Category 210 (contingencies): Include ALL rows regardless of is_total_row flag
    if (categoryId === 210) return true;
    
    // Category 209 (totals): Only include if is_total_row is set
    if (categoryId === 209) {
      return (row.is_total_row === true || row.is_total_row === "true");
    }
    
    return false;
  });


  // Get all draws and exclude the FIRST draw (to avoid large closing costs)
  const allDraws = [...new Set(costCurveData.value.map(row => parseInt(row.draw_number) || 0))].sort((a, b) => a - b);
  const firstDraw = allDraws.length > 0 ? allDraws[0] : null;
  const validDraws = allDraws.filter(d => d > 0 && d !== firstDraw); // 🎯 EXCLUDE FIRST DRAW (whatever it is)
  

  // Build chart data - one point per draw
  const chartData = [];

  validDraws.forEach(drawNum => {
    const drawData = {
      draw: drawNum,
      hardCosts: 0,
      softCosts: 0,
      acquisition: 0,
      financing: 0,
      contingencies: 0,
      projectTotal: 0  // 🔥 NEW: Project Total for single bar view
    };

    // 🎯 PRIORITY FIX: Separate real and computed totals, prioritize real
    const drawTotals = totalRows.filter(row => parseInt(row.draw_number) === drawNum);
    const categorizedTotals = {}; // { category: { real: row, computed: row } }
    
    // First pass: Categorize all totals by real vs computed
    drawTotals.forEach(row => {
      const categoryInfo = deriveCategory(row);
      if (!categoryInfo) return;
      
      const { category, isReal } = categoryInfo;
      
      if (!categorizedTotals[category]) {
        categorizedTotals[category] = { real: null, computed: null };
      }
      
      if (isReal) {
        categorizedTotals[category].real = row;
      } else {
        categorizedTotals[category].computed = row;
      }
    });
    
    // Second pass: Use real totals if available, otherwise use computed
    Object.entries(categorizedTotals).forEach(([category, totals]) => {
      // 🎯 PRIORITY: Use real total if it exists, otherwise fallback to computed
      const row = totals.real || totals.computed;
      if (!row) return;
      
      const isReal = !!totals.real;
      
      // 🔥 FIX: Use current_budget for acquisition/contingencies (reserves), current_request for spending categories
      const isReserveCategory = category === 'acquisition' || category === 'contingencies';
      const value = parseFloat(isReserveCategory ? (row.current_budget || 0) : (row.current_request || 0));
      
      // 🎯 FILTER: Only include categories based on dropdown selection
      if (costCurveCategory.value !== 'all') {
        const categoryMapping = {
          'hard_costs': 'hardCosts',
          'soft_costs': 'softCosts',
          'acquisition': 'acquisition',
          'financing': 'financing',
          'contingencies': 'contingencies'
        };
        const selectedCategory = categoryMapping[costCurveCategory.value];
        if (category !== selectedCategory) return; // Skip if not the selected category
      }

      drawData[category] = value;
      // Verbose logging removed - was causing console spam
    });
    
    // Handle Project Total separately (outside category logic)
    const projectTotalRow = drawTotals.find(row => row.dda_term?.id === 11642 || row.dda_term_id === 11642);
    if (projectTotalRow) {
      const value = parseFloat(projectTotalRow.current_request || 0);
      drawData.projectTotal = value;
      // Verbose logging removed
    }

    chartData.push(drawData);
  });
  
  return chartData;
}
// 🎯 LINEAR REGRESSION TREND LINE CALCULATION
function calculateTrendLine(data, yKey) {
  const validData = data.filter(d => d[yKey] > 0);
  if (validData.length < 2) return data.map(d => ({ ...d, trend: 0 }));
  const n = validData.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  validData.forEach((d, i) => {
    const x = i;
    const y = d[yKey] || 0;
    sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x;
  });
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return data.map((d, i) => ({
    ...d,
    trend: Math.max(0, slope * i + intercept)
  }));
}

function buildAgChartSeries() {
  const series = [];
  
  // 🎯 CAPTURE SELECTED DRAW FOR HIGHLIGHTING: Will modify data instead of using itemStyler
  const selectedDrawNum = parseInt(costsToDateDraw.value) || parseInt(drawNumber.value) || 0;
  console.log('🎯 Chart highlighting - Selected draw:', selectedDrawNum);
  
  // Category colors
  const categoryColors = {
    hardCosts: '#4A5FD5',
    softCosts: '#4A8FD0', 
    acquisition: '#52596D',
    financing: '#8A92A9',
    contingencies: '#d3d3d3'
  };
  
  const categoryLabels = {
    hardCosts: 'Hard Costs',
    softCosts: 'Soft Costs',
    acquisition: 'Acquisition',
    financing: 'Financing & Misc.',
    contingencies: 'Contingencies'
  };
  
  // Add main category series based on selection
  if (costCurveCategory.value === 'all') {
    // 🔥 NEW: Show single bar with Project Total values instead of stacked categories
    // Use a consistent blue color for the project totals
    const projectTotalColor = '#2563eb'; // Blue color
    
    series.push({
      type: 'column',
      xKey: 'draw',
      yKey: 'projectTotal',
      yName: 'Project Total',
      fill: projectTotalColor,
      fillOpacity: 0.8,
      stroke: projectTotalColor,
      strokeWidth: 2,
      stacked: false, // Single bars, not stacked
      data: cachedChartData,
      marker: {
        enabled: false  
      },
      tooltip: {
        renderer: (params) => {
          const value = params.datum.projectTotal || 0;
          console.log(`🎯 TOOLTIP TRIGGERED for Project Total:`, {
            value: value,
            draw: params.datum.draw
          });
          return {
            title: `Project Total - Draw ${params.datum.draw}`,
            content: formatAxisValue(value)
          };
        }
      }
    });
    
    // 🎯 Add smooth trend line for "All Categories" view
    const dataWithTrend = calculateTrendLine(cachedChartData, 'projectTotal');
    
    series.push({
      type: 'line',
      xKey: 'draw',
      yKey: 'trend',
      yName: 'Trend',
      stroke: projectTotalColor,
      strokeWidth: 3,
      strokeOpacity: 0.6,
      marker: {
        enabled: false
      },
      data: dataWithTrend,
      tooltip: {
        enabled: false
      }
    });
  } else {
    // Show single category
    const categoryMapping = {
      'hard_costs': 'hardCosts',
      'soft_costs': 'softCosts',
      'acquisition': 'acquisition',
      'financing': 'financing',
      'contingencies': 'contingencies'
    };
    
    const category = categoryMapping[costCurveCategory.value];
    if (category && categoryColors[category]) {
      series.push({
        type: 'column', // Changed from 'area' to 'column' for bar chart
        xKey: 'draw',
        yKey: category,
        yName: categoryLabels[category],
        fill: categoryColors[category],
        fillOpacity: 0.8,
        stroke: categoryColors[category],
        strokeWidth: 2,
        // Note: Selected draw highlighting for single category view
        marker: {
          enabled: false  // Remove dots from the area
        },
        tooltip: {
          renderer: (params) => {
            const value = params.datum[category] || 0;
            return {
              title: `${categoryLabels[category]} - ${params.datum.draw}`,
              content: formatAxisValue(value)
            };
          }
        }
      });
      
      // 🎯 Add linear regression trend line for single category view
      const dataWithTrend = calculateTrendLine(cachedChartData, category);
      
      series.push({
        type: 'line',
        xKey: 'draw',
        yKey: 'trend',
        yName: 'Trend',
        stroke: categoryColors[category],
        strokeWidth: 3,
        strokeOpacity: 0.6,
        marker: {
          enabled: false
        },
        data: dataWithTrend,
        tooltip: {
          enabled: false
        }
      });
    }
  }
  
  return series;
}

let lastCostCurveHash = '';
let isCostCurveUpdating = false;
async function updateCostCurveData() {
  if (isCostCurveUpdating) return;
  console.log('🔄 Updating Cost Curve with AG Charts...');
  
  // 🚀 OPTIMIZED: Simple project-based caching including filter selection
  const projectKey = String(selectedId.value || '');
  const recordCount = drawDetailRowData.value?.length || 0;
  const filterKey = costCurveCategory.value || 'all';
  const newHash = `${projectKey}_${recordCount}_${filterKey}`; // Include filter in hash
  
  if (lastCostCurveHash === newHash && costCurveData.value && costCurveData.value.length > 0) {
    // Skip refetch/rebuild; data already represents all draws with same DDA assignments
    console.log('🎯 COST CURVE: Using cached data, skipping refetch');
  } else {
    isCostCurveUpdating = true;
    try {
  await fetchCostCurveData();
      lastCostCurveHash = newHash;
    } finally {
      isCostCurveUpdating = false;
    }
  }
  
  // Update available contractors dropdown
  updateAvailableContractors();
  
    // Calculate totals for legend from Step 3 total rows ONLY
  const totals = {
    hardCosts: 0,
    softCosts: 0,
    acquisition: 0,
    financing: 0,
    contingencies: 0,
    total: 0
  };
  
  // SIMPLE: Skip the complex calculations - just use the chart data directly
  
  // FORCE update of reactive totals so legend shows correctly
  costCurveTotals.value = { ...totals };
  
  // Mark chart as ready for AG Charts rendering (idempotent)
  if (!costCurveChartReady.value) {
  costCurveChartReady.value = true;
  }
  
  // Force Vue to re-render
  nextTick(() => {
  });
}

// 🚀 PERFORMANCE: Cache cost curve data to avoid redundant fetches
let costCurveDataCache = null;
let costCurveDataCacheProjectId = null;

async function fetchCostCurveData() {
  console.log(`📊 OPTIMIZED: Fetching and efficiently processing cost curve data`);
  console.log(`🔍 DEBUG: selectedId.value = ${selectedId.value}`);
  
  if (!selectedId.value) {
    console.log(`❌ No selectedId.value, setting costCurveData to empty array`);
    costCurveData.value = [];
    return;
  }
  
  // 🚀 CACHE HIT: Return cached data if available for same project
  if (costCurveDataCache && costCurveDataCacheProjectId === selectedId.value) {
    console.log(`✅ CACHE HIT: Using cached cost curve data (${costCurveDataCache.length} records)`);
    costCurveData.value = costCurveDataCache;
    return;
  }
  
  try {
    // 🚀 FETCH ALL DRAWS (needed for cost curve chart to show progression across all draws)
    let endpoint = `${apiBase}projects/fetch_project_draw_details?project_ids=${selectedId.value}`;
    console.log(`🔄 Fetching all draws for cost curve: ${endpoint}`);
    
    const response = await axios.get(endpoint, { headers: authToken.value });
    
    console.log(`🔍 API Response structure:`, {
      hasData: !!response.data,
      hasDrawDetails: !!(response.data && response.data.draw_details),
      isArray: Array.isArray(response.data),
      drawDetailsIsArray: Array.isArray(response.data?.draw_details),
      dataKeys: response.data ? Object.keys(response.data) : 'no data'
    });
    
    // 🎯 FIX: Handle both possible response formats
    let drawDetailsArray = null;
    if (response.data && Array.isArray(response.data)) {
      // Direct array response
      drawDetailsArray = response.data;
    } else if (response.data && response.data.draw_details && Array.isArray(response.data.draw_details)) {
      // Nested draw_details response
      drawDetailsArray = response.data.draw_details;
    }
    
    if (drawDetailsArray) {
      console.log(`📊 Processing ${drawDetailsArray.length} records...`);
      
      // 🚀 PERFORMANCE OPTIMIZATION: Use Map for O(1) duplicate detection
      const seenRecords = new Map();
      const processedData = [];
      
      // 🚀 OPTIMIZED: Single pass processing with efficient duplicate detection
      for (const row of drawDetailsArray) {
        // Simplified unique key - focus on essential uniqueness
        const recordKey = `${row.id}_${row.draw_number}`;
        
        if (seenRecords.has(recordKey)) {
          continue; // Skip duplicates
        }
        
        seenRecords.set(recordKey, true);
        
        // 🚀 OPTIMIZED: Minimize object creation and parsing
        processedData.push({
          id: row.id,
          draw_number: parseInt(row.draw_number || 0),
          current_budget: parseFloat(row.current_budget || 0),
          original_budget: parseFloat(row.original_budget || 0),
          // 🎯 FIXED: Calculate from current_budget - balance_to_complete since no spent_to_date column mapped
          total_completed_to_date: parseFloat(row.current_budget || 0) - parseFloat(row.balance_to_complete || 0),
          current_request: parseFloat(row.current_request || 0),
          dda_term_id: row.dda_term_id,
          dda_term_name: row.dda_term_name,
          dda_term: row.dda_term, // 🎯 CRITICAL FIX: Include the full dda_term object with category_id
          contractor_name: row.contractor_name || 'Unknown',
          description: row.description || row.scope_detail || 'General Work',
          balance_to_complete: parseFloat(row.balance_to_complete || 0),
          is_total_row: row.is_total_row
        });
      }
      
        costCurveData.value = processedData;
        
        // 🚀 CACHE: Store the processed data for future use
        costCurveDataCache = processedData;
        costCurveDataCacheProjectId = selectedId.value;
        
        // 🎯 CACHE INVALIDATION: Clear cached chart data when cost curve data changes
        cachedChartData = null;
        cachedChartSeries = null;
  
      console.log(`📊 Cost curve raw data: ${drawDetailsArray.length} → ${costCurveData.value.length} records (removed exact duplicates only, CACHED for future use)`);
  
  // 🚀 SMALL CHANGE #1B: Calculate prefunded data right after cost curve data loads
  calculatePrefundedData();
      
      // Log draw distribution WITH AMOUNTS
      const drawCounts = {};
      const drawAmounts = {};
      costCurveData.value.forEach(row => {
        const draw = row.draw_number || 0;
        const amount = parseFloat(row.current_request || 0);
        drawCounts[draw] = (drawCounts[draw] || 0) + 1;
        drawAmounts[draw] = (drawAmounts[draw] || 0) + amount;
      });
      

      
    } else {
      costCurveData.value = [];
      console.warn('❌ No cost curve data received from API - response format not recognized');
      console.log(`🔍 Full response keys:`, response.data ? Object.keys(response.data) : 'no data');
    }
  } catch (error) {
    console.error('Error fetching cost curve data:', error);
    costCurveData.value = [];
  }
}

async function updateCostsToDateData() {
  // 🎯 FIXED: Update Total Project Costs chart with current draw's Project Total row
  try {
    const apiUrl = `${apiBase}projects/fetch_project_draw_details?project_ids=${selectedId.value}&draw_number=${globalDrawFilter.value}`;
    console.log(`🔄 Fetching project total for draw ${globalDrawFilter.value}: ${apiUrl}`);
    
    const response = await fetch(apiUrl);
    const data = await response.json();
    
    if (data && data.draw_details && Array.isArray(data.draw_details)) {
      // Find the Project Total row
      const projectRow = data.draw_details.find(row => 
        row.is_total_row && row.dda_term_name && 
        row.dda_term_name.toLowerCase().includes('project total')
      );
      
      if (projectRow) {
        cdTotalCurrentBudget.value = parseFloat(projectRow.current_budget || 0);
        cdTotalOriginalBudget.value = parseFloat(projectRow.original_budget || 0);
        cdTotalBilledToDate.value = parseFloat(projectRow.total_completed_to_date || 0);
        console.log(`✅ Total Project Costs updated for draw ${globalDrawFilter.value}: Current=$${cdTotalCurrentBudget.value.toLocaleString()}, Original=$${cdTotalOriginalBudget.value.toLocaleString()}, Billed=$${cdTotalBilledToDate.value.toLocaleString()}`);
      } else {
        console.log(`⚠️ No Project Total row found for draw ${globalDrawFilter.value}`);
      }
    }
  } catch (error) {
    console.error('❌ Failed to update Total Project Costs chart:', error);
  }
}
async function updateBudgetVarianceData() {
  console.log('🔄 Updating Budget Variance chart for draw:', budgetVarianceDraw.value, 'view:', budgetVarianceView.value);
  
  if (budgetVarianceView.value === 'category') {
    // Use existing category-based variance data - these are already calculated from setChartData
    // These values come from the total rows and represent original vs current budget variance
    console.log('Using category view with real variance data from total rows');
    console.log('Variance values:', {
      acquisition: lcTotalVariance.value,
      hardCosts: hcTotalVariance.value, 
      softCosts: scTotalVariance.value,
      financing: miscFinancingTotalVariance.value,
      contingencies: contingenciesTotalVariance.value
    });
    // Data is already available in lcTotalVariance, hcTotalVariance, etc.
  } else {
    // Generate contractor-specific variance data from draw detail data
    console.log('Calculating contractor variance data from draw details for draw:', budgetVarianceDraw.value);
    contractorVarianceData.value = calculateContractorVariances();
  }
}

function extractContractorFromDescription(description) {
  if (!description) return null;
  
  // Clean up the description first - remove OCR artifacts
  let cleanDesc = description.replace(/\s+/g, ' ').trim();
  
  // Common contractor patterns in construction descriptions
  const contractorPatterns = [
    // Pattern: "CompanyName - Work description"
    /^([A-Za-z0-9\s&.,'-]+)\s*-\s*(.+)$/,
    // Pattern: "CompanyName: Work description"  
    /^([A-Za-z0-9\s&.,'-]+)\s*:\s*(.+)$/,
    // Pattern: "Work by CompanyName"
    /by\s+([A-Za-z0-9\s&.,'-]{3,30})$/i,
    // Pattern: "CompanyName (trade/work)"
    /^([A-Za-z0-9\s&.,'-]+)\s*\((.+)\)$/
  ];
  
  for (const pattern of contractorPatterns) {
    const match = cleanDesc.match(pattern);
    if (match && match[1]) {
      const contractor = match[1].trim();
      // Filter out common non-contractor terms
      const excludeWords = ['total', 'subtotal', 'sum', 'general', 'misc', 'miscellaneous', 'other', 'various', 'tbd', 'allowance'];
      if (contractor.length > 2 && !excludeWords.some(word => contractor.toLowerCase().includes(word))) {
        return contractor;
      }
    }
  }
  
  // Look for company suffixes (LLC, Inc, Corp, etc.)
  const companySuffixes = /\b([A-Za-z0-9\s&.,'-]+)\s+(LLC|Inc\.?|Corp\.?|Co\.?|Company|Construction|Contractors?|Builders?)\b/i;
  const companyMatch = cleanDesc.match(companySuffixes);
  if (companyMatch) {
    return companyMatch[0].trim();
  }
  
  // Extract first part if it looks like a company name (Title Case pattern)
  const titleCasePattern = /^([A-Z][a-z]+(?:\s+[A-Z][a-z]*)*(?:\s+[A-Z]+)?)/;
  const titleMatch = cleanDesc.match(titleCasePattern);
  if (titleMatch && titleMatch[1].length > 3) {
    return titleMatch[1].trim();
  }
  
  return null;
}

// Create a comprehensive contractor list from all available data
function buildContractorList() {
  const contractors = new Set();
  
  // First, get registered contractors for name normalization
  const registeredContractors = dropdownStore.generalContractors || [];
  const registeredNames = new Map(); // lowercase -> canonical name
  registeredContractors.forEach(contractor => {
    registeredNames.set(contractor.name.toLowerCase(), contractor.name);
  });
  
  // Extract from draw detail data
  drawDetailRowData.value.forEach(row => {
    if (row.contractor_name && row.contractor_name !== 'Unknown' && row.contractor_name !== 'N/A') {
      const normalizedName = matchContractorToRegistered(row.contractor_name.trim(), registeredNames);
      contractors.add(normalizedName);
    }
    
    // FIXED: Don't extract fake contractors from descriptions
    // Only use real contractor data from backend
  });
  
  // Extract from G703 data if available
  if (g703RowData.value && Array.isArray(g703RowData.value)) {
    g703RowData.value.forEach(row => {
      // FILTER OUT G703 HEADER ROWS: Skip rows that are clearly headers
      const description = (row.description || '').toLowerCase();
      if (description.includes('description of work') || 
          description.includes('trade code') || 
          description.includes('contractor name') || 
          description.includes('scheduled value') || 
          description.includes('names') || 
          description.includes('total') || 
          description.length < 3) {
        return; // Skip header rows
      }
      
      // Check both contractor_names (plural) and contractor_name (singular) for compatibility
      const contractorField = row.contractor_names || row.contractor_name;
      if (contractorField && contractorField !== 'Unknown' && contractorField !== 'N/A') {
        // G703 might have multiple contractors separated by commas
        const names = contractorField.split(/[,;&]/).map(name => name.trim());
        names.forEach(name => {
          if (name.length > 2 && !name.toLowerCase().includes('tbd')) {
            const normalizedName = matchContractorToRegistered(name, registeredNames);
            contractors.add(normalizedName);
          }
        });
      }
      
      // FIXED: Don't extract fake contractors from G703 descriptions
      // Only use real contractor data from backend
    });
  }
  
  // Convert to array and sort
  return Array.from(contractors).sort();
}

// Normalize contractor name to match registered contractors when possible
function matchContractorToRegistered(contractorName, registeredNames) {
  if (!contractorName) return contractorName;
  
  const cleanName = contractorName.trim();
  const lowerName = cleanName.toLowerCase();
  
  // Return registered name if exact match
  if (registeredNames.has(lowerName)) {
    return registeredNames.get(lowerName);
  }
  
  // Check for partial matches (for OCR variations)
  for (const [lowerRegistered, canonicalName] of registeredNames) {
    if (lowerRegistered.includes(lowerName) || lowerName.includes(lowerRegistered)) {
      // If one is contained in the other, prefer the registered name
      if (Math.abs(lowerRegistered.length - lowerName.length) <= 5) {
        return canonicalName;
      }
    }
  }
  
  // Return original if no match found
  return cleanName;
}

// Get available contractors for autocomplete
function getAvailableContractors() {
  return buildContractorList();
}

// Update available contractors dropdown for cost curve
function updateAvailableContractors() {
  if (!costCurveData.value || costCurveData.value.length === 0) {
    availableContractors.value = [];
    costCurveFilteredContractors.value = [];
    return;
  }
  
  const contractors = new Set();
  
  costCurveData.value.forEach(row => {
    // FIXED: Only use real contractor names from backend
    const contractorName = row.contractor_name;
    
    // Only add valid contractor names (no fake extraction)
    if (contractorName && contractorName !== 'Unknown' && contractorName !== 'N/A' && contractorName.length > 2) {
      contractors.add(contractorName);
    }
  });
  
  // Convert to sorted array
  availableContractors.value = Array.from(contractors).sort();
  costCurveFilteredContractors.value = [...availableContractors.value];
}

// Cost Curve Contractor Autocomplete Functions
function handleCostCurveContractorSearch(event) {
  const searchTerm = event.target.value;
  costCurveContractorSearch.value = searchTerm;
  
  if (!searchTerm || searchTerm.trim().length < 1) {
    // Show all contractors when no search term
    costCurveFilteredContractors.value = [...availableContractors.value];
  } else {
    // Improved search: match from start of words and anywhere in name
    const term = searchTerm.toLowerCase().trim();
    costCurveFilteredContractors.value = availableContractors.value.filter(contractor => {
      const contractorLower = contractor.toLowerCase();
      
      // Exact match from start
      if (contractorLower.startsWith(term)) return true;
      
      // Match from start of any word
      const words = contractorLower.split(/\s+/);
      if (words.some(word => word.startsWith(term))) return true;
      
      // Contains match as fallback
      return contractorLower.includes(term);
    }).sort((a, b) => {
      // Sort by relevance: exact start matches first, then word start matches, then contains
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      const aStartsWithTerm = aLower.startsWith(term);
      const bStartsWithTerm = bLower.startsWith(term);
      
      if (aStartsWithTerm && !bStartsWithTerm) return -1;
      if (!aStartsWithTerm && bStartsWithTerm) return 1;
      
      const aWordStartsWith = aLower.split(/\s+/).some(word => word.startsWith(term));
      const bWordStartsWith = bLower.split(/\s+/).some(word => word.startsWith(term));
      
      if (aWordStartsWith && !bWordStartsWith) return -1;
      if (!aWordStartsWith && bWordStartsWith) return 1;
      
      // Alphabetical for same relevance level
      return a.localeCompare(b);
    });
  }
  
  costCurveContractorDropdown.value = true;
}

function handleCostCurveContractorFocus() {
  // Only show dropdown if there's already a search term or no selection
  if (costCurveContractorSearch.value || !costCurveContractor.value) {
    costCurveFilteredContractors.value = [...availableContractors.value];
    costCurveContractorDropdown.value = true;
  }
}

function handleCostCurveContractorBlur() {
  // Delay hiding dropdown to allow for click selection
  setTimeout(() => {
    costCurveContractorDropdown.value = false;
  }, 150);
}

function selectCostCurveContractor(contractor) {
  costCurveContractor.value = contractor;
  costCurveContractorSearch.value = contractor;
  costCurveContractorDropdown.value = false;
  
  // Update the chart with the new contractor filter
  updateCostCurveData();
  
  console.log('🏗️ Selected contractor for cost curve:', contractor || 'All Contractors');
}

function clearCostCurveContractorSelection() {
  costCurveContractor.value = '';
  costCurveContractorSearch.value = '';
  costCurveContractorDropdown.value = false;
  
  // Update the chart to show all contractors
  updateCostCurveData();
  
  console.log('🏗️ Cleared contractor filter for cost curve');
}

// Global function to update all charts AND grid when global filters change
async function updateAllChartsAndGrid() {
  console.log('🔄 Updating all charts and grid with global filters:', {
    drawFilter: globalDrawFilter.value,
    viewMode: globalViewMode.value
  });
  
  // 🎯 Sync globalViewMode to viewByTGU for Budget Tracking grid
  if (globalViewMode.value === 'per_gsf') {
    viewByTGU.value = VIEW_MODES_TGU.VIEW_BY_GSF;
    viewModeDivisor.value = totalGsf();
    doughnutViewMode.value = 'gsf';
  } else if (globalViewMode.value === 'per_unit') {
    viewByTGU.value = VIEW_MODES_TGU.VIEW_BY_UNIT;
    viewModeDivisor.value = totalUnits() !== 0 ? totalUnits() : 1;
    doughnutViewMode.value = 'unit';
  } else {
    viewByTGU.value = VIEW_MODES_TGU.VIEW_BY_TOTAL;
    viewModeDivisor.value = 1;
    doughnutViewMode.value = 'total';
  }
  // Don't call setViewMode() here - it refetches all grid data which causes issues
  
  // 🎯 REMOVED: No more complex API calls - chart gets data directly from grid
  
  // Set individual chart filters to match global filter
  costsToDateDraw.value = globalDrawFilter.value;
  budgetVarianceDraw.value = globalDrawFilter.value;
  
  // Set grid filters to match global filter  
  // Since we removed "all draws", directly use the selected draw
  drawNumber.value = globalDrawFilter.value || 
    (drawNumberList.value && drawNumberList.value.length > 0 ? drawNumberList.value[0] : 0);
  g703DrawNumber.value = globalDrawFilter.value || 
    (g703DrawNumberList.value && g703DrawNumberList.value.length > 0 ? g703DrawNumberList.value[0] : 0);
  
  // 🔥 FIX: Only fetch data for the CURRENT tab to prevent race conditions
  // Previously, setDrawDetail() was always called which triggered autoMatchMissingDDATerms()
  // even when on G703 tab, causing the grid to reload with wrong data
  if (btSubMenuSelectedItemId.value === 1) {
    await setDrawDetail();
  } else if (btSubMenuSelectedItemId.value === 2) {
    await setG703DrawNumber();
  } else if (btSubMenuSelectedItemId.value === 3) {
    // G702 tab - update the draw number and fetch
    g702DrawNumber.value = globalDrawFilter.value;
    await fetchG702DrawDetailGridData();
  } else if (btSubMenuSelectedItemId.value === 5) {
    // 🆕 LIEN WAIVER TAB: Fetch G702 data which includes LW data
    g702DrawNumber.value = globalDrawFilter.value;
    await fetchG702DrawDetailGridData();
  }
  // 📄 Invoice tab - data is fetched when tab is clicked (btSubMenuSelectItem)
  // Do NOT fetch here to prevent infinite loops
  
  // Now update charts; cost curve shows all draws, so do NOT refresh cost curve here
  await updateCostsToDateData();
  await updateBudgetVarianceData();
  
  // 🎯 CRITICAL FIX: Re-build chart data with correct view mode divisor
  // Only rebuild if NOT on Draw Detail tab (tab 1) - Draw Detail tab handles it via setDrawDetail()
  // This prevents DUPLICATE chart builds when on Draw Detail tab
  if (btSubMenuSelectedItemId.value !== 1 && drawDetailRowData.value && drawDetailRowData.value.length > 0) {
    // Rebuild chart for other tabs that need view mode divisor applied
    buildChartDataFromDDATermTotals(drawDetailRowData.value);
  }
  
  // Verbose logging removed
}

// Function to apply per-GSF calculations to chart data
function applyGSFCalculations() {
  const projectGSF = parseFloat(projectInformation.value?.total_gsf) || 1;
  
  // Convert chart values to per-GSF
  if (projectGSF > 0) {
    cdTotalOriginalBudget.value = (cdTotalOriginalBudget.value || 0) / projectGSF;
    cdTotalCurrentBudget.value = (cdTotalCurrentBudget.value || 0) / projectGSF;
    cdTotalBilledToDate.value = (cdTotalBilledToDate.value || 0) / projectGSF;
    
    console.log('📊 Applied per-GSF calculations, Project GSF:', projectGSF);
  }
}

// Function to apply per-Unit calculations to chart data
function applyPerUnitCalculations() {
  const projectUnits = totalUnits();
  
  // Convert chart values to per-Unit
  if (projectUnits > 0) {
    cdTotalOriginalBudget.value = (cdTotalOriginalBudget.value || 0) / projectUnits;
    cdTotalCurrentBudget.value = (cdTotalCurrentBudget.value || 0) / projectUnits;
    cdTotalBilledToDate.value = (cdTotalBilledToDate.value || 0) / projectUnits;
    
    console.log('📊 Applied per-Unit calculations, Project Units:', projectUnits);
  }
}

function toggleCostCurveContractorDropdown() {
  if (costCurveContractorDropdown.value) {
    costCurveContractorDropdown.value = false;
  } else {
    costCurveFilteredContractors.value = [...availableContractors.value];
    costCurveContractorDropdown.value = true;
  }
}

function calculateContractorVariances() {
  console.log('🔄 Calculating contractor variances for draw:', budgetVarianceDraw.value);
  
  // Group data by contractor and calculate variances
  const contractorTotals = {};
  
  // Filter data by selected draw
  const selectedDraw = parseInt(budgetVarianceDraw.value) || 
    (drawNumberList.value && drawNumberList.value.length > 0 ? drawNumberList.value[0] : null);
  let dataToProcess = selectedDraw ? 
    drawDetailRowData.value.filter(row => {
      const rowDraw = parseInt(row.draw_number || 1);
      return rowDraw <= selectedDraw; // Cumulative up to selected draw
    }) : 
    drawDetailRowData.value;
  if (selectedDraw) {
    console.log(`🔄 Filtering contractor data up to draw ${selectedDraw}: ${dataToProcess.length} rows`);
  }
  
  dataToProcess.forEach(row => {
    const contractor = row.contractor_name || row.company || 'Unknown';
    
    if (!contractorTotals[contractor]) {
      contractorTotals[contractor] = {
        name: contractor,
        original_budget: 0,
        current_budget: 0,
        total_billed: 0
      };
    }
    
    contractorTotals[contractor].original_budget += parseFloat(row.original_budget || 0);
    contractorTotals[contractor].current_budget += parseFloat(row.current_budget || 0);
    contractorTotals[contractor].total_billed += parseFloat(row.total_billing_to_date || 0);
  });
  
  // Calculate variance for each contractor
  const contractorVariances = Object.values(contractorTotals)
    .filter(contractor => contractor.original_budget > 0) // Only contractors with budget
    .map(contractor => ({
      name: contractor.name,
      contractor_name: contractor.name,
      original_budget: contractor.original_budget,
      current_budget: contractor.current_budget,
      total_billed: contractor.total_billed,
      variance: contractor.current_budget - contractor.original_budget,
      total_variance: contractor.current_budget - contractor.original_budget
    }))
    .filter(contractor => Math.abs(contractor.variance) > 1000) // Only show meaningful variances
    .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance)); // Sort by absolute variance
  
  console.log('Calculated contractor variances:', contractorVariances);
  return contractorVariances;
}

function calculateVariancePercentage(variance) {
  // Calculate percentage variance (for tooltip)
  let baseAmount = 1000000; // Default fallback
  
  // Use real total budget if available
  if (cdTotalOriginalBudget.value > 0) {
    baseAmount = cdTotalOriginalBudget.value;
  } else if (cdTotalCurrentBudget.value > 0) {
    baseAmount = cdTotalCurrentBudget.value;
  }
  
  const percentage = (Math.abs(variance) / baseAmount * 100).toFixed(0);
  return variance >= 0 ? `+${percentage}` : `-${percentage}`;
}

// G703 Template Modal Functions
function closeG703TemplateModal() {
  showG703TemplateModal.value = false;
  g703TemplateId.value = null;
}

function handleG703TemplateSaved(templateData) {
  console.log('G703 template saved:', templateData);
  
  // Close the modal
  closeG703TemplateModal();
  
  // Show success message
  errorStore.setError('G703 Template Configured', 'G703 template configuration saved successfully!', 'success', false);
  
  // Refresh the available templates
  fetchAvailableTemplates();
  
  // Optionally refresh the G703 data grid
  fetchG703DrawDetailGridData();
}

// Budget Field Click Handlers
function handleEditBudgetValue(rowData, fieldName, currentValue) {
  console.log('Edit budget value clicked:', { rowData, fieldName, currentValue });
  
  // Check if this is a grouped/consolidated row
  if (rowData.is_grouped_row || rowData.is_parent) {
    errorStore.setError('Cannot Edit Grouped Row', 'This is a consolidated row that represents multiple records. To edit individual values, please view the detailed records first.', 'info', true);
    return;
  }
  
  // Check if we have a valid ID for updating
  if (!rowData.id && !rowData.draw_detail_id && btSubMenuSelectedItemId.value === 1) {
    errorStore.setError('Update Error', 'Cannot edit this item - no ID found. This may be a grouped/calculated row.', 'error', true);
    return;
  }
  
  if (!rowData.id && !rowData.g703_id && btSubMenuSelectedItemId.value === 2) {
    errorStore.setError('Update Error', 'Cannot edit this item - no ID found. This may be a grouped/calculated row.', 'error', true);
    return;
  }
  
  // Show inline edit dialog or prompt
  const newValue = prompt(`Edit ${fieldName.replace(/_/g, ' ')}:`, currentValue || 0);
  
  if (newValue !== null && !isNaN(newValue)) {
    // Here you would call an API to update the budget value
    updateBudgetValue(rowData, fieldName, parseFloat(newValue));
  }
}
async function handleViewSourceDocument(rowData, fieldName) {
  console.log('View source document clicked:', { rowData, fieldName });
  console.log('Available rowData fields:', Object.keys(rowData));
  console.log('Full rowData:', rowData);
  
  // SIMPLE APPROACH: Open document viewer for all documents in this draw
  if (rowData.draw_number && selectedId.value) {
    console.log(`📁 Opening document viewer for Draw ${rowData.draw_number} documents...`);
    openDrawDocumentViewer(selectedId.value, rowData.draw_number, fieldName);
    return;
  }
  
  console.log('⚠️ No draw number found, falling back to individual document search...');
  
  // If no direct document ID, try to find documents associated with the draw
  if (!documentId && rowData.draw_number && selectedId.value) {
    try {
      console.log(`Looking for documents for draw number: ${rowData.draw_number}`);
      
                    // Query the API to find documents associated with this draw
        const response = await axios.get(
          `${apiBase}draws/for_project/${selectedId.value}`,
          { headers: authToken.value }
        );
        
        if (response.data) {
          const drawData = response.data.draws || response.data;
          console.log('Draw API response:', drawData);
          
          if (drawData && drawData.length > 0) {
            // Find the specific draw that matches our draw number
            const draw = drawData.find(d => d.draw_number == rowData.draw_number) || drawData[0];
            console.log('Found draw data:', draw);
            
            // Check for documents in the draw - try multiple approaches
            if (draw.documents && draw.documents.length > 0) {
              // Use the first document, or look for specific types (G703, etc.)
              const relevantDoc = draw.documents.find(doc => 
                doc.filename && (
                  doc.filename.toLowerCase().includes('g703') ||
                  doc.filename.toLowerCase().includes('draw') ||
                  doc.filename.toLowerCase().includes('application')
                )
              ) || draw.documents[0];
              
              documentId = relevantDoc.id;
              openDocumentViewer(documentId, fieldName);
              return;
            } else if (draw.document_id) {
              documentId = draw.document_id;
              openDocumentViewer(documentId, fieldName);
              return;
            } else if (draw.id) {
              // Try to find documents by querying the documents endpoint for this draw
              console.log(`No documents directly on draw ${draw.id}, trying to find associated documents`);
              await findDocumentsForDraw(draw.id, fieldName);
              return;
            }
          }
          
          // No documents found in draw
          showNoDocumentError(rowData);
        } else {
          showNoDocumentError(rowData);
        }
    } catch (error) {
      console.error('Error looking up draw documents:', error);
      showNoDocumentError(rowData);
    }
  } else if (documentId) {
    // Direct document ID available
    openDocumentViewer(documentId, fieldName);
  } else {
    // No document association possible
    showNoDocumentError(rowData);
  }
}

async function findDocumentsForDraw(drawId, fieldName) {
  try {
    // Query documents endpoint to find documents associated with this draw
    const response = await axios.get(
      `${apiBase}documents?draw_id=${drawId}`,
      { headers: authToken.value }
    );
    
    if (response.data && response.data.length > 0) {
      console.log('Found documents for draw:', response.data);
      console.log('Document filenames:', response.data.map(doc => ({ id: doc.id, filename: doc.filename })));
      
      // Filter documents based on the current view context
      const isG703View = btSubMenuSelectedItemId.value === 2;
      console.log(`Current view: ${isG703View ? 'G703' : 'Draw Details'}`);
      
      let relevantDoc;
      
      if (isG703View) {
        // For G703 view, prioritize G703 documents
        relevantDoc = response.data.find(doc => 
          doc.filename && doc.filename.toLowerCase().includes('g703')
        );
        console.log('Looking for G703 documents, found:', relevantDoc);
      } else {
        // For Draw Details view, prioritize draw application documents (avoid G703)
        const drawDocs = response.data.filter(doc => 
          doc.filename && (
            doc.filename.toLowerCase().includes('draw') ||
            doc.filename.toLowerCase().includes('application') ||
            doc.filename.toLowerCase().includes('payment')
          ) && !doc.filename.toLowerCase().includes('g703')
        );
        console.log('Looking for Draw/Application documents (non-G703), found:', drawDocs);
        relevantDoc = drawDocs[0];
      }
      
      // Fallback to any document if no specific type found
      if (!relevantDoc) {
        console.log('No specific document type found, using first document');
        relevantDoc = response.data[0];
      }
      
      console.log(`Selected document for ${isG703View ? 'G703' : 'Draw Details'} view:`, { id: relevantDoc.id, filename: relevantDoc.filename });
      openDocumentViewer(relevantDoc.id, fieldName);
    } else {
      console.log('No documents found for draw ID:', drawId);
      // Try alternative approach - look for documents with matching draw number
      await findDocumentsByDrawNumber(drawId, fieldName);
    }
  } catch (error) {
    console.error('Error finding documents for draw:', error);
    showNoDocumentError({ draw_number: drawId });
  }
}

async function findDocumentsByDrawNumber(drawId, fieldName) {
  try {
    // Query documents endpoint by project and look for matching draw numbers
    const response = await axios.get(
      `${apiBase}documents?project_id=${selectedId.value}`,
      { headers: authToken.value }
    );
    
    if (response.data && response.data.length > 0) {
      console.log('Found project documents:', response.data);
      
      // Look for documents that mention the draw number
      const drawNum = drawId.toString();
      const isG703View = btSubMenuSelectedItemId.value === 2;
      let relevantDoc;
      
      if (isG703View) {
        // For G703 view, look for G703 documents with matching draw number
        relevantDoc = response.data.find(doc => 
          doc.filename && doc.filename.toLowerCase().includes('g703') && 
          (doc.filename.includes(drawNum) || doc.draw_number == drawNum)
        );
      } else {
        // For Draw Details view, look for draw application documents (avoid G703)
        relevantDoc = response.data.find(doc => 
          doc.filename && 
          !doc.filename.toLowerCase().includes('g703') &&
          (doc.filename.includes(drawNum) || doc.draw_number == drawNum) &&
          (doc.filename.toLowerCase().includes('draw') ||
           doc.filename.toLowerCase().includes('application') ||
           doc.filename.toLowerCase().includes('payment'))
        );
      }
      
      // Broader fallback if specific type not found
      if (!relevantDoc) {
        relevantDoc = response.data.find(doc => 
          doc.filename && (
            doc.filename.includes(drawNum) ||
            doc.draw_number == drawNum
          )
        );
      }
      
      if (relevantDoc) {
        console.log(`Using document for ${isG703View ? 'G703' : 'Draw Details'} view:`, relevantDoc);
        openDocumentViewer(relevantDoc.id, fieldName);
      } else {
        showNoDocumentError({ draw_number: drawNum });
      }
    } else {
      showNoDocumentError({ draw_number: drawId });
    }
  } catch (error) {
    console.error('Error finding documents by draw number:', error);
    showNoDocumentError({ draw_number: drawId });
  }
}

async function findDocumentWithField(fieldName, rowData) {
  try {
    console.log(`🔍 Searching for field "${fieldName}" across all project documents...`);
    
    // Query all documents for this project
    const response = await axios.get(
      `${apiBase}documents?project_id=${selectedId.value}`,
      { headers: authToken.value }
    );
    
    if (response.data && response.data.length > 0) {
      console.log(`📄 Searching through ${response.data.length} documents for field extraction metadata...`);
      
      // Search through each document's extraction metadata
      for (const doc of response.data) {
        if (doc.document_sections && doc.document_sections.extracted_fields) {
          // Look for the specific field in this document's extracted fields
          const fieldData = doc.document_sections.extracted_fields.find(field => 
            field.field_name === fieldName
          );
          
          if (fieldData) {
            console.log(`✅ Found field "${fieldName}" in document ${doc.id}:`, fieldData);
            return {
              id: doc.id,
              filename: doc.filename,
              bbox: fieldData.bbox,
              confidence: fieldData.confidence,
              extracted_value: fieldData.extracted_value
            };
          }
        }
        
        // Also check in table extraction data
        if (doc.document_sections && doc.document_sections.tables) {
          for (const table of doc.document_sections.tables) {
            if (table.rows) {
              for (const row of table.rows) {
                if (row.cells) {
                  for (const cell of row.cells) {
                    if (cell.field_name === fieldName) {
                      console.log(`✅ Found field "${fieldName}" in table cell of document ${doc.id}:`, cell);
                      return {
                        id: doc.id,
                        filename: doc.filename,
                        bbox: cell.bbox,
                        confidence: cell.confidence,
                        extracted_value: cell.text
                      };
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      console.log(`❌ Field "${fieldName}" not found in any document extraction metadata`);
      return null;
    }
    
    return null;
  } catch (error) {
    console.error('Error searching for field in documents:', error);
    return null;
  }
}

function openDrawDocumentViewer(projectId, drawNumber, fieldName) {
  console.log(`📁 Opening document viewer for Project ${projectId}, Draw ${drawNumber}, Type: ${fieldName}`);
  
  let allDocuments = [];
  let title = '';
  
  // Get the appropriate documents based on the tab type
  if (fieldName === 'draw_detail') {
    allDocuments = drawDetailSourceDocuments.value;
    title = `Draw ${drawNumber} - Documents`;
  } else if (fieldName === 'g703') {
    allDocuments = g703SourceDocuments.value;
    title = `Draw ${drawNumber} - G703 Documents`;
  } else if (fieldName === 'g702') {
    allDocuments = g702SourceDocuments.value;
    title = `Draw ${drawNumber} - G702 Documents`;
  }
  
  if (allDocuments.length === 0) {
    alert(`No documents found for ${title}`);
    return;
  }
  
  console.log(`📁 DEBUG: All ${allDocuments.length} documents found:`, allDocuments.map(d => ({ 
    filename: d.filename || d.filepath, 
    id: d.id, 
    created_at: d.created_at,
    file_size: d.file_size 
  })));
  
  // Filter to show only the most relevant document
  let documents = [];
  if (fieldName === 'draw_detail') {
    // First try Excel files
    const excelDocs = allDocuments.filter(doc => {
      const filename = (doc.filename || doc.filepath || '').toLowerCase();
      return filename.includes('.xls') || filename.includes('.xlsx');
    });
    console.log(`📁 Found ${excelDocs.length} Excel documents:`, excelDocs.map(d => d.filename || d.filepath));
    
    if (excelDocs.length > 0) {
      // Sort by upload date and get the latest
      documents = [excelDocs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]];
    } else {
      // Fallback: show the most recent document of any type
      console.log(`📁 No Excel files found, showing latest document`);
      documents = [allDocuments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]];
    }
  } else if (fieldName === 'g703') {
    // First try PDF files
    const pdfDocs = allDocuments.filter(doc => {
      const filename = (doc.filename || doc.filepath || '').toLowerCase();
      return filename.includes('.pdf');
    });
    console.log(`📁 Found ${pdfDocs.length} PDF documents:`, pdfDocs.map(d => d.filename || d.filepath));
    
    if (pdfDocs.length > 0) {
      // Sort by upload date and get the latest
      documents = [pdfDocs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]];
    } else {
      // Fallback: show the most recent document of any type
      console.log(`📁 No PDF files found, showing latest document`);
      documents = [allDocuments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]];
    }
  } else {
    // For G702 or other types, show all
    documents = allDocuments;
  }
  
  if (documents.length === 0) {
    alert(`No documents found for ${title}`);
    return;
  }
  
  console.log(`📁 Showing ${documents.length} document(s):`, documents.map(d => ({ 
    filename: d.filename || d.filepath, 
    id: d.id, 
    created_at: d.created_at,
    file_size: d.file_size 
  })));
  
  // Show the document viewer modal
  documentViewerVisible.value = true;
  documentViewerDocuments.value = documents;
  documentViewerTitle.value = title;
  documentViewerInitialIndex.value = 0;
}

function closeDocumentViewer() {
  documentViewerVisible.value = false;
  documentViewerDocuments.value = [];
  documentViewerTitle.value = '';
  documentViewerInitialIndex.value = 0;
}

function openDocumentViewerWithFieldHighlight(documentId, fieldName, bbox) {
  // Navigate to document viewer with field highlighting and bbox coordinates
  const documentViewerUrl = `/document-viewer/${documentId}`;
  console.log(`🎯 Opening document with field highlighting - Document: ${documentId}, Field: ${fieldName}, BBox:`, bbox);
  window.open(documentViewerUrl, '_blank');
}

function openDocumentViewer(documentId, fieldName) {
  // Navigate to document viewer (note: highlighting not yet implemented in viewer)
  const documentViewerUrl = `/document-viewer/${documentId}`;
  console.log(`Opening document viewer for field ${fieldName}:`, documentViewerUrl);
  window.open(documentViewerUrl, '_blank');
}

function showNoDocumentError(data) {
  // Handle both rowData objects and simple data objects
  const drawNumber = data.draw_number || data.id || 'unknown';
  const contractorInfo = data.contractor_name ? ` (${data.contractor_name})` : '';
  
  // Show more helpful error with available fields for debugging
  if (data.draw_number) {
    console.log('Available rowData fields:', Object.keys(data));
    console.log('Full rowData:', data);
  }
  
  // Provide actionable information
  const drawInfo = ` for Draw ${drawNumber}`;
  
  errorStore.setError(
    'No Source Document Found', 
    `No associated source document found for this budget item${drawInfo}${contractorInfo}. You may need to upload documents for this draw or establish the document association.`, 
    'info', 
    true
  );
}

async function updateBudgetValue(rowData, fieldName, newValue) {
  try {
    // Store current grid state to preserve sorting/position
    let currentSortModel = null;
    try {
      currentSortModel = btSubMenuSelectedItemId.value === 1 ? 
        (drawDetailGridApi.value && typeof drawDetailGridApi.value.getSortModel === 'function' ? drawDetailGridApi.value.getSortModel() : null) : 
        (g703GridApi.value && typeof g703GridApi.value.getSortModel === 'function' ? g703GridApi.value.getSortModel() : null);
    } catch (error) {
      console.log('Grid API not ready for sort model, continuing without it');
      currentSortModel = null;
    }
    
    // Update the local data immediately for better UX
    if (rowData[fieldName] !== undefined) {
      rowData[fieldName] = newValue;
    }
    
    let apiEndpoint;
    let requestData = {};
    let recordId;
    
    // Determine the correct API endpoint and record ID based on the current view
    if (btSubMenuSelectedItemId.value === 1) {
      // Draw Details - try multiple ID fields
      recordId = rowData.id || rowData.draw_detail_id || rowData.detailed_id;
      if (!recordId) {
        throw new Error('No valid ID found for draw detail record');
      }
      apiEndpoint = `${apiBase}draw_details/${recordId}`;
      requestData[fieldName] = newValue;
    } else if (btSubMenuSelectedItemId.value === 2) {
      // G703 Records - try multiple ID fields
      recordId = rowData.id || rowData.g703_id || rowData.general_contractor_g703_id;
      if (!recordId) {
        throw new Error('No valid ID found for G703 record');
      }
      apiEndpoint = `${apiBase}general_contractor_g703s/${recordId}`;
      requestData[fieldName] = newValue;
    } else {
      // Default budget view - this might be mvw_most_recent_by_projects which is read-only
      errorStore.setError('Update Not Available', 'Direct editing is not available for this view. Please use Draw Details or G703 view to edit values.', 'info', true);
      return;
    }
    
    console.log('Making API call:', { apiEndpoint, requestData });
    
    // Call API to update the value using axios directly
    const response = await axios.patch(apiEndpoint, requestData, { headers: authToken.value });
    
    if (response.data) {
      errorStore.setError('Budget Updated', `${fieldName.replace(/_/g, ' ')} updated successfully!`, 'success', true);
      
      // Refresh the grid data and restore sort state
      if (btSubMenuSelectedItemId.value === 1) {
        await fetchDrawDetailGridData();
        // Restore sort model after data loads
        setTimeout(() => {
          if (currentSortModel && drawDetailGridApi.value && typeof drawDetailGridApi.value.setSortModel === 'function') {
            try {
              drawDetailGridApi.value.setSortModel(currentSortModel);
            } catch (error) {
              console.log('Could not restore sort model:', error);
            }
          }
        }, 100);
      } else if (btSubMenuSelectedItemId.value === 2) {
        await fetchG703DrawDetailGridData();
        // Restore sort model after data loads
        setTimeout(() => {
          if (currentSortModel && g703GridApi.value && typeof g703GridApi.value.setSortModel === 'function') {
            try {
              g703GridApi.value.setSortModel(currentSortModel);
            } catch (error) {
              console.log('Could not restore sort model:', error);
            }
          }
        }, 100);
      } else {
        fetchGridData();
      }
    } else {
      throw new Error('Failed to update budget value');
    }
  } catch (error) {
    console.error('Error updating budget value:', error);
    errorStore.setError('Update Failed', `Failed to update budget value: ${error.response?.data?.error || error.message}`, 'error', true);
    
    // Revert the local change by refreshing the data
    if (btSubMenuSelectedItemId.value === 1) {
      fetchDrawDetailGridData();
    } else if (btSubMenuSelectedItemId.value === 2) {
      fetchG703DrawDetailGridData();
    } else {
      fetchGridData();
    }
  }
}

// ✏️ Update G703 Description
async function updateG703Description(g703Id, newDescription) {
  try {
    console.log('✏️ Updating G703 description:', { g703Id, newDescription });
    
    const response = await axios.patch(
      `${apiBase}general_contractor_g703s/${g703Id}`,
      { description: newDescription },
      { headers: authToken.value.headers }
    );
    
    console.log('✅ G703 description updated successfully');
    
    // Refresh the G703 grid to show updated data
    await fetchG703DrawDetailGridData();
    
    return response.data;
  } catch (error) {
    console.error('❌ Error updating G703 description:', error);
    errorStore.setError('Update Failed', `Failed to update G703 description: ${error.response?.data?.error || error.message}`, 'error', true);
    
    // Revert by refreshing
    await fetchG703DrawDetailGridData();
  }
}

async function updateContractorValue(rowData, fieldName, contractorName, contractorId) {
  try {
    console.log('🔥 UPDATE CONTRACTOR - Starting with:', {
      rowData,
      fieldName,
      contractorName,
      contractorId,
      btSubMenuSelectedItemId: btSubMenuSelectedItemId.value
    });
    
    let apiEndpoint;
    let requestData = {};
    let recordId;
    
    // Determine the correct API endpoint based on the current view
    if (btSubMenuSelectedItemId.value === 1) {
      // Draw Details
      recordId = rowData.id || rowData.draw_detail_id || rowData.detailed_id;
      if (!recordId) {
        throw new Error('No valid ID found for draw detail record');
      }
      apiEndpoint = `${apiBase}draw_details/${recordId}`;
      requestData.contractor_name = contractorName;
      if (contractorId) {
        requestData.contractor_id = contractorId;
      }
    } else if (btSubMenuSelectedItemId.value === 2) {
      // G703 Records
      recordId = rowData.id || rowData.g703_id || rowData.general_contractor_g703_id;
      if (!recordId) {
        throw new Error('No valid ID found for G703 record');
      }
      apiEndpoint = `${apiBase}general_contractor_g703s/${recordId}`;
      requestData.contractor_names = contractorName; // G703 uses contractor_names field
      if (contractorId) {
        requestData.contractor_id = contractorId;
      }
    } else {
      errorStore.setError('Update Not Available', 'Contractor editing is only available in Draw Details or G703 view.', 'info', true);
      return;
    }
    
    console.log('🔥 UPDATE CONTRACTOR - API Call:', { apiEndpoint, requestData });
    
    // Update the local data immediately for better UX
    if (fieldName === 'contractor_names' || fieldName === 'contractor_name') {
      rowData[fieldName] = contractorName;
      rowData.company = contractorName; // Also update company field for display
    }
    if (contractorId) {
      rowData.contractor_id = contractorId;
    }
    
    // Call API to update the contractor
    const response = await axios.patch(apiEndpoint, requestData, { headers: authToken.value });
    
    if (response.data) {
      errorStore.setError('Contractor Updated', `Contractor updated to "${contractorName}" successfully!`, 'success', true);
      
      // Update the local data source immediately for G703 view
      if (btSubMenuSelectedItemId.value === 2 && g703RowData.value) {
        const recordIndex = g703RowData.value.findIndex(record => record.id === rowData.id);
        if (recordIndex !== -1) {
          g703RowData.value[recordIndex][fieldName] = contractorName;
          if (contractorId) {
            g703RowData.value[recordIndex].contractor_id = contractorId;
          }
        }
      }
      
      // Refresh the grid to ensure consistency
      if (btSubMenuSelectedItemId.value === 1) {
        await fetchDrawDetailGridData();
      } else if (btSubMenuSelectedItemId.value === 2) {
        // Force immediate grid update by incrementing counter
        g703DataCtr.value += 1;
        
        // Refresh data from server
        await fetchG703DrawDetailGridData();
        
        // Additional grid refresh after data load
        setTimeout(() => {
          if (g703GridApi.value) {
            g703GridApi.value.refreshCells();
          }
        }, 100);
      }
    } else {
      throw new Error('Failed to update contractor');
    }
  } catch (error) {
    console.error('Error updating contractor:', error);
    errorStore.setError('Update Failed', `Failed to update contractor: ${error.response?.data?.error || error.message}`, 'error', true);
    
    // Revert the local change by refreshing the data
    if (btSubMenuSelectedItemId.value === 1) {
      fetchDrawDetailGridData();
    } else if (btSubMenuSelectedItemId.value === 2) {
      fetchG703DrawDetailGridData();
    }
  }
}

// Contractor Modal Functions
function searchContractors(searchTerm) {
  console.log('🔍 Searching contractors with term:', searchTerm);
  
  // Get all contractors: general contractors + extracted subcontractors from project data
  const generalContractors = dropdownStore.generalContractors || [];
  const extractedSubcontractors = getAvailableContractors();
  
  // Combine and deduplicate contractors
  const allContractorNames = new Set();
  const contractors = [];
  
  // Add general contractors first (they have IDs)
  generalContractors.forEach(contractor => {
    allContractorNames.add(contractor.name.toLowerCase());
    contractors.push({
      id: contractor.id,
      name: contractor.name,
      type: 'general_contractor'
    });
  });
  
  // Add extracted subcontractors (no IDs, will need to be created as subcontractors)
  extractedSubcontractors.forEach(name => {
    if (!allContractorNames.has(name.toLowerCase()) && name !== 'Unknown' && name !== 'N/A' && name.trim().length > 2) {
      contractors.push({
        id: null,
        name: name,
        type: 'subcontractor'
      });
    }
  });
  
  console.log('📝 Available contractors:', contractors.length, 
    `(${generalContractors.length} general contractors + ${contractors.length - generalContractors.length} subcontractors)`);
  
  if (!searchTerm || searchTerm.trim().length < 1) {
    // Show all contractors when search is empty - create new array to trigger reactivity
    contractorModalData.value.searchResults = [...contractors];
    contractorModalData.value.showDropdown = true;
    console.log('📋 Showing all contractors:', contractorModalData.value.searchResults.length);
    return;
  }
  
  const filtered = contractors.filter(contractor => 
    contractor.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  console.log('🎯 Filtered results:', filtered.length, 'from', contractors.length);
  
  // Create new array to ensure Vue reactivity
  contractorModalData.value.searchResults = [...filtered];
  contractorModalData.value.showDropdown = true;
}

function selectContractor(contractor) {
  contractorModalData.value.selectedContractor = contractor;
  contractorModalData.value.searchTerm = contractor.name;
  contractorModalData.value.showDropdown = false;
}

function clearContractorSelection() {
  contractorModalData.value.selectedContractor = null;
  contractorModalData.value.searchTerm = '';
  contractorModalData.value.showDropdown = false;
}

function handleContractorInputFocus() {
  // Always show dropdown on focus, trigger search if needed
  if (!contractorModalData.value.searchResults || contractorModalData.value.searchResults.length === 0) {
    searchContractors(contractorModalData.value.searchTerm || '');
  } else {
    contractorModalData.value.showDropdown = true;
  }
}

function handleContractorInputBlur() {
  // Delay hiding dropdown to allow clicks on options
  setTimeout(() => {
    contractorModalData.value.showDropdown = false;
  }, 200);
}

async function saveContractorSelection() {
  try {
    const modalData = contractorModalData.value;
    const searchTerm = modalData.searchTerm.trim();
    
    console.log('🔥 SAVE CONTRACTOR - modalData:', modalData);
    console.log('🔥 SAVE CONTRACTOR - searchTerm:', searchTerm);
    
    if (!searchTerm) {
      errorStore.setError('Error', 'Please enter a contractor name', 'error', true);
      return;
    }
    
    let contractorToSave = searchTerm;
    let contractorId = null;
    
    // Check if an existing contractor was selected
    if (modalData.selectedContractor) {
      contractorId = modalData.selectedContractor.id;
      contractorToSave = modalData.selectedContractor.name;
    } else {
      // Check if the entered name matches an existing contractor (case-insensitive)
      // Check both general contractors and extracted subcontractors
      const generalContractors = dropdownStore.generalContractors || [];
      const extractedSubcontractors = getAvailableContractors();
      
      const existingGeneralContractor = generalContractors.find(c => 
        c.name.toLowerCase() === searchTerm.toLowerCase()
      );
      
      if (existingGeneralContractor) {
        contractorId = existingGeneralContractor.id;
        contractorToSave = existingGeneralContractor.name;
      } else {
        // Check if it's an extracted subcontractor
        const isExtractedSubcontractor = extractedSubcontractors.find(name => 
          name.toLowerCase() === searchTerm.toLowerCase()
        );
        
        if (isExtractedSubcontractor) {
          // This is an extracted subcontractor - create as subcontractor
          const createNew = confirm(`"${searchTerm}" is an extracted subcontractor from your project data. Would you like to add it to the subcontractors database?`);
          if (createNew) {
            try {
              console.log('🔨 Creating new subcontractor:', searchTerm);
              const response = await axios.post(
                `${apiBase}contractors`,
                { 
                  name: searchTerm, 
                  address_line_1: '', 
                  state: '', 
                  zipcode: '',
                  contractor_type: 'subcontractor'
                },
                { headers: authToken.value }
              );
              contractorId = response.data.id;
              contractorToSave = response.data.name;
              console.log('✅ Subcontractor created:', response.data);
              
              // Refresh the general contractors list first
              console.log('🔄 Refreshing general contractors list...');
              await dropdownStore.getGeneralContractors();
              
              // Update the search results immediately
              console.log('🔍 Updating search results...');
              searchContractors('');
              
              // Set the newly created subcontractor as selected
              contractorModalData.value.selectedContractor = {
                id: contractorId,
                name: contractorToSave,
                type: 'subcontractor'
              };
              contractorModalData.value.searchTerm = contractorToSave;
              
              errorStore.setError('Subcontractor Created', `New subcontractor "${contractorToSave}" has been added to the database!`, 'success', true);
            } catch (error) {
              console.error('❌ Error creating subcontractor:', error);
              errorStore.setError('Error', `Failed to create new subcontractor: ${error.response?.data?.error || error.message}`, 'error', true);
              return;
            }
          } else {
            return; // User cancelled
          }
        } else {
          // Completely new contractor - create as subcontractor (that's what we extract from project data)
          console.log('🔨 Creating new subcontractor:', searchTerm);
          
                      try {
              // Always create as subcontractor
              const response = await axios.post(
                `${apiBase}contractors`,
                { 
                  name: searchTerm, 
                  address_line_1: '', 
                  state: '', 
                  zipcode: '',
                  contractor_type: 'subcontractor'
                },
                { headers: authToken.value }
              );
              contractorId = response.data.id;
              contractorToSave = response.data.name;
              console.log('✅ Subcontractor created:', response.data);
              errorStore.setError('Subcontractor Created', `New subcontractor "${contractorToSave}" has been added!`, 'success', true);
            
            // Update search results
            searchContractors('');
            
            // Set as selected
            contractorModalData.value.selectedContractor = {
              id: contractorId,
              name: contractorToSave,
              type: 'subcontractor'
            };
            contractorModalData.value.searchTerm = contractorToSave;
            
          } catch (error) {
            console.error('❌ Error creating contractor:', error);
            errorStore.setError('Error', `Failed to create new contractor: ${error.response?.data?.error || error.message}`, 'error', true);
            return;
          }
        }
      }
    }
    
    // Update the record with the new contractor
    console.log('🔥 CALLING updateContractorValue with:', {
      rowData: modalData.rowData,
      fieldName: modalData.fieldName,
      contractorToSave,
      contractorId
    });
    await updateContractorValue(modalData.rowData, modalData.fieldName, contractorToSave, contractorId);
    
    // Close the modal
    closeContractorModal();
    
  } catch (error) {
    console.error('Error saving contractor:', error);
    errorStore.setError('Error', `Failed to save contractor: ${error.message}`, 'error', true);
  }
}

function closeContractorModal() {
  contractorModalData.value = {
    isOpen: false,
    rowData: null,
    fieldName: '',
    currentValue: '',
    searchTerm: '',
    searchResults: [],
    showDropdown: false,
    selectedContractor: null
  };
}

// DDA Term Editing Functions
const ddaTermModalData = ref({
  isOpen: false,
  rowData: null,
  fieldName: '',
  currentValue: '',
  searchTerm: '',
  searchResults: [],
  showDropdown: false,
  selectedDDATerm: null
});
async function handleEditDDATerm(rowData, fieldName, currentValue) {
  try {
    console.log('🔥 EDIT DDA TERM - Opening modal for:', rowData, fieldName, currentValue);
    
    // Load DDA terms using the correct API endpoint
    let allDDATerms = [];
    try {
      console.log('📥 Loading DDA terms from API...');
      const response = await axios.get(`${apiBase}dda_terms`, {
        ...authToken.value,
        params: { autocomplete: true, per_page: 1000 }
      });
      
      if (response.data && Array.isArray(response.data)) {
        allDDATerms = response.data.map(term => ({
          id: term.id,
          term: term.term || term.name,
          description: term.description || '',
          category: term.category || '',
          dda_term_category_id: term.dda_term_category_id,
          type: 'dda_term'
        }));
        console.log('✅ DDA terms loaded from API:', allDDATerms.length);
        
        // 🔥 NEW: Store DDA terms in reactive ref for chart categorization
        ddaTerms.value = allDDATerms;
      } else {
        console.warn('⚠️ API returned non-array DDA terms data:', response.data);
      }
    } catch (error) {
      console.error('❌ Error loading DDA terms from API:', error);
      errorStore.setError('Error', `Failed to load DDA terms: ${error.message}`, 'error', true);
      return;
    }
    
    console.log('✅ Available DDA terms:', allDDATerms.length);
    
    ddaTermModalData.value = {
      isOpen: true,
      rowData: rowData,
      fieldName: fieldName,
      currentValue: currentValue || '',
      searchTerm: '',
      searchResults: [],
      showDropdown: false,
      selectedDDATerm: null,
      allDDATerms: allDDATerms
    };
    
    // Try to auto-match based on description first
    const description = rowData.description || rowData.scope_detail || '';
    console.log('🎯 Auto-matching DDA term for description:', description);
    
    if (description) {
      // Use the same AI/ML logic as QA step for better matching
    const autoMatch = await findBestDDATermWithOpenAI(description, allDDATerms);
      if (autoMatch) {
        console.log('✅ Auto-matched DDA term:', autoMatch.term);
        ddaTermModalData.value.selectedDDATerm = autoMatch;
        ddaTermModalData.value.searchTerm = autoMatch.term;
      } else if (currentValue) {
        // Fallback to current value
        const exactMatch = allDDATerms.find(term =>
          term.term.toLowerCase() === currentValue.toLowerCase()
        );
        if (exactMatch) {
          ddaTermModalData.value.selectedDDATerm = exactMatch;
          ddaTermModalData.value.searchTerm = exactMatch.term;
        } else {
          ddaTermModalData.value.searchTerm = currentValue;
        }
      }
    } else if (currentValue) {
      // No description, just use current value
      const exactMatch = allDDATerms.find(term =>
        term.term.toLowerCase() === currentValue.toLowerCase()
      );
      if (exactMatch) {
        ddaTermModalData.value.selectedDDATerm = exactMatch;
        ddaTermModalData.value.searchTerm = exactMatch.term;
      } else {
        ddaTermModalData.value.searchTerm = currentValue;
      }
    }
    
    // Always show initial dropdown with filtered results
    if (ddaTermModalData.value.searchTerm) {
      searchDDATermsFromList(ddaTermModalData.value.searchTerm);
    } else {
      // Show top standard terms first initially
      const standardTermsFirst = prioritizeStandardTerms(allDDATerms);
      ddaTermModalData.value.searchResults = standardTermsFirst.slice(0, 50);
      ddaTermModalData.value.showDropdown = true;
    }
  } catch (error) {
    console.error('Error opening DDA term editor:', error);
    errorStore.setError('Error', `Failed to open DDA term editor: ${error.message}`, 'error', true);
  }
}

async function searchDDATermsFromList(searchTerm) {
  const term = searchTerm ? searchTerm.trim() : '';
  
  if (!term || term.length < 2) {
    // Show top standard terms first when no search
    const standardTermsFirst = ddaTermModalData.value.allDDATerms ? 
      prioritizeStandardTerms(ddaTermModalData.value.allDDATerms).slice(0, 50) : [];
    ddaTermModalData.value.searchResults = standardTermsFirst;
    ddaTermModalData.value.showDropdown = true;
    return;
  }
  
  try {
    console.log(`🔍 DDA MODAL SEARCH: Searching for "${term}"`);
    // Use the same API endpoint as the working template setter
    const backendBaseUrl = apiBase.replace('/api/v1/', '');
    const response = await axios.get(`${backendBaseUrl}/api/v1/dda_terms/search`, {
      params: { q: term, limit: 100 }  // Get more results to sort properly
    });
    
    // Sort results by relevance - standard terms first, then by match quality
    const sortedResults = prioritizeStandardTerms(response.data || [])
      .map(term => ({
        ...term,
        matchScore: calculateTermMatchScore(term.term, searchTerm)
      }))
      .sort((a, b) => {
        // First sort by standard vs extended terms
        const aIsStandard = !a.primary_dda_term_id;
        const bIsStandard = !b.primary_dda_term_id;
        if (aIsStandard !== bIsStandard) {
          return bIsStandard ? 1 : -1;  // Standard terms first
        }
        // Then by match score
        return b.matchScore - a.matchScore;
      })
      .slice(0, 50);
    
    ddaTermModalData.value.searchResults = sortedResults;
    ddaTermModalData.value.showDropdown = true;
    console.log(`✅ DDA MODAL SEARCH: Found ${ddaTermModalData.value.searchResults.length} results for "${term}" (${sortedResults.filter(r => !r.primary_dda_term_id).length} standard terms)`);
  } catch (error) {
    console.error('❌ DDA MODAL SEARCH ERROR:', error);
    // Fallback to local filtering with better sorting
    if (ddaTermModalData.value.allDDATerms) {
      const filtered = ddaTermModalData.value.allDDATerms
        .filter(ddaTerm => {
          const termLower = ddaTerm.term.toLowerCase();
          const descriptionLower = (ddaTerm.description || '').toLowerCase();
          return termLower.includes(term.toLowerCase()) || descriptionLower.includes(term.toLowerCase());
        })
        .map(term => ({
          ...term,
          matchScore: calculateTermMatchScore(term.term, searchTerm)
        }))
        .sort((a, b) => {
          const aIsStandard = !a.primary_dda_term_id;
          const bIsStandard = !b.primary_dda_term_id;
          if (aIsStandard !== bIsStandard) {
            return bIsStandard ? 1 : -1;
          }
          return b.matchScore - a.matchScore;
        })
        .slice(0, 50);
      
      ddaTermModalData.value.searchResults = filtered;
      ddaTermModalData.value.showDropdown = true;
    }
  }
}

function selectDDATerm(ddaTerm) {
  ddaTermModalData.value.selectedDDATerm = ddaTerm;
  ddaTermModalData.value.searchTerm = ddaTerm.term;
  ddaTermModalData.value.showDropdown = false;
}

function clearDDATermSelection() {
  ddaTermModalData.value.selectedDDATerm = null;
  ddaTermModalData.value.searchTerm = '';
  searchDDATermsFromList('');
}

function handleDDATermInputFocus() {
  // Always show dropdown on focus and trigger search
  if (!ddaTermModalData.value.searchResults || ddaTermModalData.value.searchResults.length === 0) {
    searchDDATermsFromList(ddaTermModalData.value.searchTerm || '');
  } else {
    ddaTermModalData.value.showDropdown = true;
  }
}

function handleDDATermInputBlur() {
  // Delay hiding dropdown to allow clicks on options
  setTimeout(() => {
    ddaTermModalData.value.showDropdown = false;
  }, 200);
}

function closeDDATermModal() {
  ddaTermModalData.value = {
    isOpen: false,
    rowData: null,
    fieldName: '',
    currentValue: '',
    searchTerm: '',
    searchResults: [],
    showDropdown: false,
    selectedDDATerm: null
  };
}

async function updateDDATermValue(rowData, fieldName, ddaTerm, ddaTermId, suppressUI = false) {
  try {
    // Only log for manual updates (when UI is not suppressed)
    if (!suppressUI) {
      console.log(`📝 Updating DDA term to: "${ddaTerm}"`);
    }
    
    let apiEndpoint;
    let requestData = {};
    let recordId;
    
    // 🔥 FIX: Determine endpoint from ROW DATA TYPE, not current tab
    // This prevents race conditions when user switches tabs during auto-matching
    // Priority: Draw Detail > G703 > G702 (most specific ID fields first)
    const hasDrawDetailId = rowData.draw_detail_id || rowData.detailed_id;
    const hasG703Id = rowData.g703_id || rowData.general_contractor_g703_id;
    const hasG702Id = rowData.g702_id || rowData.subcontractor_g702_id;
    
    // 🔥 FIXED: Check for specific ID fields, not just properties like contractor_name
    const isDrawDetail = hasDrawDetailId || (!hasG703Id && !hasG702Id && rowData.description && !rowData.trade_code);
    const isG703 = hasG703Id || (!hasDrawDetailId && !hasG702Id && rowData.trade_code);
    const isG702 = hasG702Id;
    
    if (isDrawDetail && !isG703 && !isG702) {
      // Draw Details - 🔥 FIX: Use db_id (real database ID stored separately from grid row id)
      recordId = rowData.db_id || rowData.draw_detail_id || rowData.detailed_id || (typeof rowData.id === 'number' ? rowData.id : null);
      if (!recordId || String(recordId).startsWith('grid_')) {
        console.error('❌ Invalid Draw Detail record ID:', rowData.id, 'db_id:', rowData.db_id, 'draw_detail_id:', rowData.draw_detail_id);
        throw new Error('No valid database ID found for draw detail record.');
      }
      apiEndpoint = `${apiBase}draw_details/${recordId}`;
      requestData.dda_term_id = ddaTermId;
      
      // 🔥 CRITICAL FIX: Preserve is_total_row flag when updating DDA term
      if (rowData.is_total_row === true || rowData.is_total_row === 'true') {
        requestData.is_total_row = true;
        console.log(`🔒 Preserving is_total_row=true for row "${rowData.description}"`);
      }
    } else if (isG703) {
      // G703 Records - 🔥 FIX: Use db_id first
      recordId = rowData.db_id || rowData.g703_id || rowData.general_contractor_g703_id || (typeof rowData.id === 'number' ? rowData.id : null);
      if (!recordId || String(recordId).startsWith('grid_')) {
        console.error('❌ Invalid G703 record ID:', rowData.id, 'db_id:', rowData.db_id, 'g703_id:', rowData.g703_id);
        throw new Error('No valid database ID found for G703 record.');
      }
      apiEndpoint = `${apiBase}general_contractor_g703s/${recordId}`;
      requestData.dda_term_id = ddaTermId;
    } else if (isG702) {
      // G702 Records - 🔥 SKIP auto-matching entirely for G702
      // Only allow manual updates when user is actually on G702 tab
      if (suppressUI) {
        // This is auto-matching - skip G702 entirely
        console.log(`🚫 Skipping G702 auto-match update for: ${rowData.contractor_name || rowData.description}`);
        return;
      }
      // 🔥 FIX: Use db_id first
      recordId = rowData.db_id || rowData.g702_id || rowData.subcontractor_g702_id || (typeof rowData.id === 'number' ? rowData.id : null);
      if (!recordId || String(recordId).startsWith('grid_')) {
        console.error('❌ Invalid G702 record ID:', rowData.id, 'db_id:', rowData.db_id, 'g702_id:', rowData.g702_id);
        throw new Error('No valid database ID found for G702 record. Row may be from extracted data without saved record.');
      }
      apiEndpoint = `${apiBase}subcontractor_g702s/${recordId}`;
      requestData.dda_term_id = ddaTermId;
    } else {
      errorStore.setError('Update Not Available', 'DDA term editing is only available in Draw Details, G703, or G702 view.', 'info', true);
      return;
    }
    
    // API call logging removed to reduce console spam
    
    // Update the local data immediately for better UX
    rowData.dda_term_id = ddaTermId;
    rowData.dda_term = { id: ddaTermId, term: ddaTerm };
    rowData.dda_term_name = ddaTerm;
    
    // Call API to update the DDA term
    const response = await axios.patch(apiEndpoint, requestData, { headers: authToken.value });
    
    if (response.data) {
      // Only log success for manual updates
      if (!suppressUI) {
        console.log(`✅ DDA term updated successfully: "${ddaTerm}"`);
        
        // CRITICAL FIX: Update local row data immediately for highlighting
        if (rowData) {
          rowData.dda_term_id = ddaTermId;
          rowData.dda_term_name = ddaTerm;
          // Create proper dda_term object for highlighting logic
          const ddaTermObj = ddaTerms.value?.find(t => t.id === ddaTermId);
          if (ddaTermObj) {
            rowData.dda_term = ddaTermObj;
          } else {
            // Fallback if term not found in loaded terms
            rowData.dda_term = { 
              id: ddaTermId, 
              term: ddaTerm,
              dda_term_category_id: null
            };
          }
        }
        
        // 🧠 LEARNING: Save this correction for future draws
        const description = rowData.description || rowData.scope_detail || '';
        if (description && selectedId.value) {
          saveDDATermCorrection(selectedId.value, description, ddaTermId, ddaTerm);
        }
      }
      // Removed success modal - user feedback via grid update is sufficient
      
      // Force immediate grid refresh for both DrawDetails and G703 (unless suppressed)
      if (!suppressUI) {
      if (btSubMenuSelectedItemId.value === 1) {
        // Force immediate grid update by incrementing counter
        drawDetailDataCtr.value += 1;
        
        // Refresh data from server (this will have the updated DDA term)
        await fetchDrawDetailGridData();
          
          // CRITICAL FIX: Update charts when DDA terms change (affects categorization)
          await updateCostCurveData();
          await updateCostsToDateData();  
          await updateBudgetVarianceData();
        
        // Additional grid refresh after data load
        setTimeout(() => {
          if (drawDetailGridApi.value) {
            drawDetailGridApi.value.refreshCells();
            drawDetailGridApi.value.redrawRows();
          }
        }, 100);
      } else if (btSubMenuSelectedItemId.value === 2) {
        // 🔥 FIX: Do NOT refetch from server - this triggers auto-matching loop!
        // We already updated the local row data above (lines 13333-13363)
        // Just refresh the grid display to show the updated DDA term
        
        // Force immediate grid update by incrementing counter
        g703DataCtr.value += 1;
        
        // Update charts with already-loaded data (no server fetch)
        await updateCostCurveData();
        await updateCostsToDateData();
        await updateBudgetVarianceData();
        
        // Refresh grid cells to show updated DDA term
        setTimeout(() => {
          if (g703GridApi.value) {
            g703GridApi.value.refreshCells({ force: true });
            g703GridApi.value.redrawRows();
          }
        }, 50);
      } else if (btSubMenuSelectedItemId.value === 3) {
        // G702 tab - force immediate grid update
        g702DataCtr.value += 1;
        
        // Refresh data from server
        await fetchG702DrawDetailGridData();
        
        // Additional grid refresh after data load
        setTimeout(() => {
          if (g702GridApi.value) {
            g702GridApi.value.refreshCells();
          }
        }, 100);
        }
      }
    } else {
      throw new Error('Failed to save DDA term assignment');
    }
  } catch (error) {
    console.error('Error updating DDA term:', error);
    errorStore.setError('Error', `Failed to update DDA term: ${error.message}`, 'error', true);
    throw error;
  }
}

async function saveDDATermAssignment() {
  try {
    const modalData = ddaTermModalData.value;
    const selectedDDATerm = modalData.selectedDDATerm;
    
    // Clean logs - removed debug logging
    
    if (!selectedDDATerm) {
      errorStore.setError('Error', 'Please select a DDA term', 'error', true);
      return;
    }
    
    let ddaTermToSave = selectedDDATerm.term;
    let ddaTermId = selectedDDATerm.id;
    
    // Update the record with the new DDA term
    await updateDDATermValue(modalData.rowData, modalData.fieldName, ddaTermToSave, ddaTermId);
    
    // Close the modal
    closeDDATermModal();
    
  } catch (error) {
    console.error('Error saving DDA term assignment:', error);
    errorStore.setError('Error', `Failed to save DDA term assignment: ${error.message}`, 'error', true);
  }
}

// Helper functions for better DDA term matching and sorting
function prioritizeStandardTerms(terms) {
  if (!terms || !Array.isArray(terms)) return [];
  
  // Standard terms have no primary_dda_term_id, extended terms do
  const standardTerms = terms.filter(term => !term.primary_dda_term_id);
  const extendedTerms = terms.filter(term => term.primary_dda_term_id);
  
  // Return standard terms first, then extended terms
  return [...standardTerms, ...extendedTerms];
}

function getCategoryNameById(categoryId) {
  const categories = {
    197: 'Hard Costs',
    198: 'Soft Costs', 
    203: 'Acquisition',
    204: 'Financing',
    210: 'Contingencies',
    209: 'Other'
  };
  return categories[Number(categoryId)] || null;
}

function calculateTermMatchScore(termName, searchTerm) {
  if (!termName || !searchTerm) return 0;
  
  const term = termName.toLowerCase().trim();
  const search = searchTerm.toLowerCase().trim();
  
  // Exact match gets highest score
  if (term === search) return 100;
  
  // Term starts with search gets very high score
  if (term.startsWith(search)) return 90;
  
  // Search starts with term gets high score  
  if (search.startsWith(term)) return 85;
  
  // Term contains search gets good score
  if (term.includes(search)) return 70;
  
  // Search contains term gets decent score
  if (search.includes(term)) return 60;
  
  // Word-by-word matching
  const termWords = term.split(/\s+/).filter(w => w.length > 2);
  const searchWords = search.split(/\s+/).filter(w => w.length > 2);
  
  if (termWords.length === 0 || searchWords.length === 0) return 0;
  
  const commonWords = termWords.filter(w => searchWords.some(sw => 
    w.includes(sw) || sw.includes(w) || w.startsWith(sw) || sw.startsWith(w)
  ));
  
  if (commonWords.length > 0) {
    return (commonWords.length / Math.max(termWords.length, searchWords.length)) * 50;
  }
  
  return 0;
}

// OpenAI-powered semantic matching for much better accuracy
async function findBestDDATermWithOpenAI(description, allDDATerms) {
  if (!description || !allDDATerms || allDDATerms.length === 0) return null;
  
  try {
    if (!ENABLE_UI_OPENAI_AUTOMATCH) return null;
    loadOpenAIMatchCache();
    // Cache to avoid duplicate API calls for the same description
    const cacheKey = normalizeDescForCache(description);
    if (openAIMatchCache[cacheKey]) {
      return openAIMatchCache[cacheKey];
    }

    // Get ALL standard terms for comprehensive matching (no limit)
    const standardTerms = prioritizeStandardTerms(allDDATerms);
    const termsList = standardTerms.map(term => {
      let termEntry = term.term;
      
      // Add category context for better matching
      const categoryName = getCategoryNameById(term.dda_term_category_id);
      if (categoryName) {
        termEntry += ` [${categoryName}]`;
      }
      
      // Add parent term context for sub-terms
      if (term.primary_dda_term_id) {
        const parentTerm = allDDATerms.find(t => t.id === term.primary_dda_term_id);
        if (parentTerm) {
          termEntry += ` (sub-term of: ${parentTerm.term})`;
        }
      }
      
      // Add any available description
      if (term.description) {
        termEntry += ` (${term.description})`;
      }
      
      return termEntry;
    }).join('\n');
    
         const prompt = `You are an expert at matching construction project line items to standardized DDA (Debt Service Coverage Analysis) terms.

Description to match: "${description}"

AVAILABLE TERMS (Standard terms listed first - ALWAYS prefer these):
${termsList}

CRITICAL MATCHING RULES:
1. EXACT MATCHES: If description exactly matches a term, use it (confidence: 0.95+)
2. PARTIAL MATCHES: If description contains key words from a term, match it (confidence: 0.70-0.85)
3. SEMANTIC MATCHES: If description means the same as a term, match it (confidence: 0.65-0.80)
4. STANDARD TERMS FIRST: Always prefer standard terms over any extended/category terms
5. SPECIFIC OVER GENERAL: Never match specific trades/professions to broad categories
6. CONSTRUCTION ACRONYMS: Match common construction abbreviations:
   - "MEP" → "Mechanical, Electrical & Plumbing" [Hard Costs]
   - "AOR" → "Architect of Record" [Soft Costs]
   - "SOE" → "Statement of Eligibility" or "Schedule of Equipment" [varies by context]
   - "TPP" → "Third Party Professional" [Soft Costs]
7. TRADE TERMS: Match construction trades to their specific terms:
   - "architect" → "architect" (NOT "total soft costs")
   - "interior design" → "interior design" (NOT "total soft costs") 
   - "electrical" → "electrical" (NOT "total construction")
   - "plumbing" → "plumbing" (NOT "total construction")
   - "expediting" → "expediting" [Soft Costs]
   - "lighting" → "electrical and low voltage" [Hard Costs]
   - "facade" → "facade" or "exterior wall systems" [Hard Costs]
   - "elevator" → "elevator" [Hard Costs]
   - "landscape" → "landscaping" [Hard Costs]
8. FINANCE TERMS: Match financial items to finance terms:
   - "mortgage" → "mortgage"
   - "title insurance" → "title insurance"
   - "legal fees" → "legal"
   - "debt service" → "debt service" 
   - "construction loan" → "construction loan"
   - "mezzanine loan" → "mezzanine loan"
8. ADMINISTRATIVE TERMS: Match administrative/professional services:
   - "project manager" → "project management"
   - "owner rep" → "owner representative"
   - "accounting" → "accounting"
   - "office expenses" → "general conditions"
   - "working capital" → "working capital"
9. PROPERTY TERMS: Match property-related costs:
   - "real estate tax" → "real estate taxes"
   - "common charges" → "common area maintenance"
   - "carrying costs" → "carrying costs"
10. CONFIDENCE: Only return matches with confidence > 0.65

STRICT FORMAT - Return ONLY this JSON:
{
  "term": "exact term name from list",
  "confidence": 0.85,
  "reasoning": "why this specific match"
}

If no confident match: {"term": null, "confidence": 0.0, "reasoning": "no suitable match"}`;

    // Respect user's model and rate limits; throttle and retry on 429
    const model = getOpenAIModel();
    const maxRetries = 3;
    let attempt = 0;
    let response;
    let lastErr;

    const apiKey = getOpenAIKey();
    if (!apiKey) {
      console.warn('OpenAI key missing; skipping OpenAI match');
      return null;
    }

    // Global cooldown / caps
    const nowTs = Date.now();
    if (nowTs < openAIGlobalCooldownUntil) {
      return null;
    }
    if (openAIRequestsThisSession >= OPENAI_PER_SESSION_CAP) {
      return null;
    }
    const drawUsageKey = `${selectedId?.value || 'proj'}_${globalDrawFilter?.value || 'all'}`;
    if (!openAIDrawUsageCount[drawUsageKey]) openAIDrawUsageCount[drawUsageKey] = 0;
    if (openAIDrawUsageCount[drawUsageKey] >= OPENAI_MAX_PER_DRAW) {
      return null;
    }
    openAIDrawUsageCount[drawUsageKey]++;

    await scheduleOpenAITask(async () => {
      while (attempt <= maxRetries) {
        attempt++;
        try {
          response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
              'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
              model,
              messages: [{ role: 'user', content: prompt }],
        temperature: 0.1,
              max_tokens: 120,
              top_p: 0
            })
          });
          if (response.status === 429) {
            const retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10);
            const backoff = Math.min(2000 * attempt + Math.random() * 500, 5000);
            openAIGlobalCooldownUntil = Date.now() + (retryAfter * 1000 || OPENAI_COOLDOWN_MS_DEFAULT);
            await sleep((retryAfter * 1000) || backoff);
            continue;
          }
          break;
        } catch (e) {
          lastErr = e;
          await sleep(500 + Math.random()*250);
        }
      }
    });

    if (!response.ok) {
      console.error('OpenAI API error:', response.status, response.statusText);
      return null;
    }

    openAIRequestsThisSession++;
    const data = await response.json();
    const aiResponse = data.choices[0]?.message?.content?.trim();
    
    if (!aiResponse) {
      console.error('No response from OpenAI');
      return null;
    }

    // Parse JSON response - strip markdown code blocks if present
    let cleanResponse = aiResponse.trim();
    if (cleanResponse.startsWith('```json')) {
      cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanResponse.startsWith('```')) {
      cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }
    
    const result = JSON.parse(cleanResponse);
    
    if (!result.term || result.confidence < 0.65) {
      console.log(`OpenAI match below threshold: ${result.term} (${result.confidence})`);
      return null;
    }

    // Find the matched term in our list with flexible matching
    let matchedTerm = allDDATerms.find(term => 
      term.term.toLowerCase().trim() === result.term.toLowerCase().trim()
    );
    
    // If exact match fails, try flexible matching
    if (!matchedTerm) {
      // Clean function to normalize terms for comparison
      const cleanTerm = (str) => str.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
      
      // Try flexible matching with cleaned terms
      matchedTerm = allDDATerms.find(term => 
        cleanTerm(term.term) === cleanTerm(result.term)
      );
      
      // Try partial matching if still no match
      if (!matchedTerm) {
        const resultWords = cleanTerm(result.term).split(' ');
        matchedTerm = allDDATerms.find(term => {
          const termWords = cleanTerm(term.term).split(' ');
          // Check if all words from result are in the term or vice versa
          return resultWords.every(word => termWords.some(tWord => tWord.includes(word) || word.includes(tWord))) ||
                 termWords.every(word => resultWords.some(rWord => rWord.includes(word) || word.includes(tWord)));
        });
      }
    }

    if (!matchedTerm) {
      console.warn(`OpenAI suggested term "${result.term}" not found in our list`);
      return null;
    }

    const resultObj = {
      ...matchedTerm,
      confidence: result.confidence,
      method: 'OpenAI',
      reasoning: result.reasoning
    };
    openAIMatchCache[cacheKey] = resultObj;
    saveOpenAIMatchCache();
    return resultObj;

  } catch (error) {
    console.error('Error with OpenAI matching:', error);
    return null;
  }
}

// DDA Term Auto-Matching Functions
async function findBestDDATermMatch(description, allDDATerms) {
  if (!description || !allDDATerms || allDDATerms.length === 0) return null;
  
  try {
    // Prefer backend ML classification first to reduce OpenAI calls
    const response = await axios.post(`${apiBase}dda_terms/classify`, {
      descriptions: [description]
    }, authToken.value);
    
    if (response.data && response.data.classifications && response.data.classifications.length > 0) {
      const classification = response.data.classifications[0];
      
      // Raise confidence threshold from 0.3 to 0.6 for better precision
      if (classification.dda_term_id && classification.confidence > 0.6) {
        // Find the term in our loaded list
        const matchedTerm = allDDATerms.find(term => term.id === classification.dda_term_id);
        
        if (matchedTerm) {
          return {
            ...matchedTerm,
            confidence: classification.confidence,
            method: 'ML'
          };
        } else if (classification.dda_term_name) {
          return {
            id: classification.dda_term_id,
            term: classification.dda_term_name,
            confidence: classification.confidence,
            method: 'ML'
          };
        }
      }
    }
    
    // Enhanced keyword fallback
    const keywordMatch = findBestKeywordMatch(description, allDDATerms);
    if (keywordMatch) return keywordMatch;
    
    // Last resort: OpenAI (subject to caps/cooldowns)
    return await findBestDDATermWithOpenAI(description, allDDATerms);
    
  } catch (error) {
    // On error, try keyword; if none, skip
    const keywordMatch = findBestKeywordMatch(description, allDDATerms);
    if (keywordMatch) return keywordMatch;
    return null;
  }
}

// 🎯 Shared rule-based priority matching — runs BEFORE AI on every row.
// Handles: total rows (always), hard/soft cost contingency (always).
// Returns { id, term } on match, null otherwise.
function ruleBasedTotalRowMatch(descLower) {
  if (!descLower) return null;
  const d = descLower.trim();

  // ─── PRIORITY 1: GRAND TOTAL ────────────────────────────────────────────────
  if (d.includes('grand total')) {
    return { id: 11644, term: 'Grand Total' };
  }

  // ─── PRIORITY 2: PROJECT TOTAL ──────────────────────────────────────────────
  // "project total", "total project costs/cost", "total project", "budget total"
  // Exclude if it also contains hard/soft cost (those are more specific)
  if (
    !d.includes('contingency') &&
    (d.includes('project total') ||
     d === 'total project costs' ||
     d === 'total project cost' ||
     d === 'total costs' ||
     d === 'total budget' ||
     d === 'budget total' ||
     (d.includes('total') && d.includes('project') &&
      !d.includes('soft') && !d.includes('hard') && !d.includes('loan')))
  ) {
    return { id: 11642, term: 'Project Total' };
  }

  // ─── PRIORITY 3: CONTINGENCIES ──────────────────────────────────────────────
  // Must be checked BEFORE the broad hard/soft cost total patterns
  if (d.includes('contingency')) {
    // Hard Costs Contingency:
    //   "contractor contingency", "building contingency", "construction contingency",
    //   "gc contingency", "hc contingency", "hard cost contingency", "trade contingency"
    if (
      d.includes('hard cost') ||
      d.includes('contractor contingency') ||
      d.includes('building contingency') ||
      d.includes('construction contingency') ||
      d.includes('gc contingency') ||
      d.includes('gmp contingency') ||
      d.includes('trade contingency') ||
      d.includes('direct work contingency') ||
      d.includes('direct cost contingency') ||
      d === 'hc contingency'
    ) {
      return { id: 224, term: 'Hard Costs Contingency' };
    }

    // Soft Costs Contingency:
    //   "project contingency", "soft cost contingency", "sc contingency",
    //   "owner contingency", "financing contingency", "project loan contingency"
    if (
      d.includes('soft cost') ||
      d.includes('project contingency') ||
      d.includes('financing contingency') ||
      d.includes('project loan contingency') ||
      d === 'sc contingency'
    ) {
      return { id: 226, term: 'Soft Costs Contingency' };
    }
  }

  // ─── PRIORITY 4: HARD COSTS TOTAL ───────────────────────────────────────────
  // "hard costs total", "total hard costs", "building costs", "trade costs", "direct costs"
  if (
    !d.includes('contingency') &&
    (d.includes('total hard cost') ||
     (d.includes('hard cost') && (d.includes('total') || d.includes('subtotal'))) ||
     d.includes('building cost') ||
     d.includes('building total') ||
     d.includes('trade cost') ||
     d.includes('trade total') ||
     (d.includes('direct cost') && d.includes('total')) ||
     (d.includes('direct total') && !d.includes('project')))
  ) {
    return { id: 11635, term: 'Hard Costs Total' };
  }

  // ─── PRIORITY 5: SOFT COSTS TOTAL ───────────────────────────────────────────
  // "soft costs total", "total soft costs", "project loan", "project costs total"
  if (
    !d.includes('contingency') &&
    (d.includes('total soft cost') ||
     (d.includes('soft cost') && (d.includes('total') || d.includes('subtotal'))) ||
     d.includes('project loan') ||
     d === 'project costs total' ||
     d === 'project cost total')
  ) {
    return { id: 11637, term: 'Soft Costs Total' };
  }

  // ─── PRIORITY 6: PLAIN SUBTOTAL ─────────────────────────────────────────────
  if (
    d === 'subtotal' || d === 'sub-total' || d === 'sub total' ||
    (d.includes('subtotal') &&
     !d.includes('project') && !d.includes('hard') &&
     !d.includes('soft') && !d.includes('building') && !d.includes('contingency'))
  ) {
    return { id: 11649, term: 'SUBTOTAL' };
  }

  // ─── PRIORITY 7: PLAIN TOTAL ────────────────────────────────────────────────
  if (d === 'total' || d === 'total:' || d === 'totals') {
    return { id: 11647, term: 'Total' };
  }

  return null;
}

function findBestKeywordMatch(description, allDDATerms) {
  if (!description || !allDDATerms) return null;
  
  const desc = description.toLowerCase().trim();
  let bestMatch = null;
  let bestScore = 0;
  
  // 🎯 CRITICAL: Detect if this is a total/subtotal row
  const isTotalRow = desc.includes('total') || desc.includes('subtotal');
  const isGrandTotal = desc.includes('grand total') || desc.includes('project total');
  
  // Prioritize standard terms in keyword matching too
  const standardTerms = prioritizeStandardTerms(allDDATerms);
  
  // First pass: Try exact and substring matches
  for (const term of standardTerms) {
    if (!term || !term.term) continue;
    
    const termLower = term.term.toLowerCase();
    let score = 0;
    
    // 🎯 CRITICAL: If this is a total/subtotal row, ONLY match to total/subtotal terms
    const termIsTotal = term.is_total === true || term.dda_term_category_id === 209 || 
                        termLower.includes('total') || termLower.includes('subtotal');
    
    // Skip non-total terms for total rows, and vice versa
    if (isTotalRow && !termIsTotal) {
      continue; // Skip regular terms when matching total rows
    }
    if (!isTotalRow && termIsTotal && !isGrandTotal) {
      continue; // Skip total terms when matching regular rows (unless it's grand total)
    }
    
    // Exact match gets highest score
    if (termLower === desc) {
      score = 100;
    }
    // Check if description is contained in term or vice versa
    else if (termLower.includes(desc) && desc.length > 3) {
      score = 90 - (termLower.length - desc.length) * 0.1; // Penalize length difference
    }
    else if (desc.includes(termLower) && termLower.length > 3) {
      score = 85 - (desc.length - termLower.length) * 0.1;
    }
    // Word-by-word matching with better logic
    else {
      const termWords = termLower.split(/\s+/).filter(w => w.length > 2);
      const descWords = desc.split(/\s+/).filter(w => w.length > 2);
      
      // Enhanced word matching - partial word matches too
      const matchedWords = termWords.filter(termWord => 
        descWords.some(descWord => 
          termWord === descWord || 
          (termWord.length >= 4 && descWord.length >= 4 && 
           (termWord.startsWith(descWord) || descWord.startsWith(termWord)))
        )
      );
      
      if (matchedWords.length > 0) {
        score = (matchedWords.length / Math.max(termWords.length, descWords.length)) * 70;
        
        // 🎯 HUGE BONUS for matching "subtotal" or "total" keywords
        if (isTotalRow && matchedWords.some(w => w === 'subtotal' || w === 'total')) {
          score += 25; // Big boost for total/subtotal keyword match
        }
        
        // Bonus for important construction/financial keywords
        const importantWords = [
          'concrete', 'electrical', 'plumbing', 'hvac', 'steel', 'roofing', 'masonry', 'carpentry',
          'mortgage', 'title', 'insurance', 'legal', 'recording', 'tax', 'closing', 'loan', 'fee',
          'hard', 'soft', 'acquisition', 'financing', 'contingency' // Add category keywords
        ];
        const hasImportant = matchedWords.some(w => importantWords.includes(w));
        if (hasImportant) score += 15;
        
        // Bonus for standard terms over extended terms
        if (!term.primary_dda_term_id) score += 10;
      }
    }
    
    // 🎯 LOWER threshold for total/subtotal rows to 40 (they're simpler to match)
    const threshold = isTotalRow ? 40 : 50;
    
    if (score > bestScore && score > threshold) {
      bestScore = score;
      bestMatch = {
        ...term,
        confidence: score / 100,
        method: 'Enhanced Keyword'
      };
    }
  }
  
  // Remove verbose logging for cleaner console
  
  return bestMatch;
}

// Grid Ready Handlers
function onDrawDetailGridReady(params) {
  drawDetailGridApi.value = params.api;
  console.log('Draw Detail Grid API ready');
  console.log('✅ Data is pre-sorted by source_row_position in JavaScript - no AG-Grid sorting needed');
}

// 🔥 AG-GRID CONTEXT MENU: Right-click to hide/unhide rows
const hiddenRows = ref(new Set());
const originalDrawDetailData = ref([]);

function getContextMenuItems(params) {
  const rowData = params.node.data;
  const rowId = rowData?.description || params.node.rowIndex;
  const isHidden = hiddenRows.value.has(rowId);
  
  return [
    {
      name: isHidden ? '👁️ Show Row' : '🫥 Hide Row',
      action: function () {
        // Store original data if not already stored
        if (originalDrawDetailData.value.length === 0) {
          originalDrawDetailData.value = [...drawDetailRowData.value];
        }
        
        if (isHidden) {
          hiddenRows.value.delete(rowId);
        } else {
          hiddenRows.value.add(rowId);
        }
        // Refresh grid to apply row visibility changes
        refreshGridWithHiddenRows();
      }
    },
    'separator',
    {
      name: '👁️ Show All Hidden Rows',
      action: function () {
        hiddenRows.value.clear();
        refreshGridWithHiddenRows();
      }
    },
    'separator',
    'copy',
    'export'
  ];
}

function refreshGridWithHiddenRows() {
  // Use original data as source, filter out hidden rows
  const sourceData = originalDrawDetailData.value.length > 0 ? originalDrawDetailData.value : drawDetailRowData.value;
  const visibleData = sourceData.filter((row, index) => {
    const rowId = row?.description || index;
    return !hiddenRows.value.has(rowId);
  });
  
  // ➕ Check if Project Total exists in SOURCE data (before filtering) - DDA term ID 11642
  hasProjectTotal.value = sourceData.some(row => row.dda_term_id === 11642);
  console.log(`📊 Project Total exists: ${hasProjectTotal.value} (checked in ${sourceData.length} source rows)`);
  
  // Update reactive data to trigger grid refresh
  drawDetailRowData.value = visibleData;
  
  // Force grid refresh by incrementing counter
  drawDetailDataCtr.value++;
}

// ➕ Add Project Total Row
async function addProjectTotal() {
  if (!drawNumber.value) {
    errorStore.setError('No Draw Selected', 'Please select a draw first', 'error', false);
    return;
  }
  
  isAddingProjectTotal.value = true;
  
  try {
    console.log(`➕ Adding Project Total for project ${selectedId.value}, draw ${drawNumber.value}...`);
    
    const response = await axios.post(
      `${apiBase}draws/add_project_total/${drawNumber.value}`,
      { project_id: selectedId.value },  // 🔥 FIX: Pass project_id to avoid wrong project's draw
      authStore.getAuthTokenHeader
    );
    
    console.log('✅ Project Total added successfully:', response.data);
    errorStore.setError('Success', 'Project Total row created!', 'success', false);
    
    // Refresh the grid to show the new row
    await fetchDrawDetailGridData();
    
  } catch (error) {
    console.error('❌ Error adding Project Total:', error);
    const errorMsg = error.response?.data?.error || error.message || 'Failed to add Project Total';
    errorStore.setError('Error', errorMsg, 'error', false);
  } finally {
    isAddingProjectTotal.value = false;
  }
}

// 🎯 COMPUTE CATEGORY TOTALS: Fill in missing category total rows
async function computeCategoryTotals() {
  console.log('🧮 COMPUTE CATEGORY TOTALS: Starting computation...');
  
  if (!costCurveData.value || !Array.isArray(costCurveData.value)) {
    console.warn('No cost curve data available');
    return;
  }
  
  // Category mappings
  const categoryMap = {
    197: 'Hard Costs',
    198: 'Soft Costs',
    203: 'Acquisition',
    204: 'Financing',
    210: 'Contingencies'
  };
  
  // DDA Term IDs for existing total rows (don't overwrite these!)
  const totalRowTermIds = {
    11633: 'Acquisition',      // Acquisition Costs Total
    11635: 'Hard Costs',        // Hard Costs Total
    11637: 'Soft Costs',        // Soft Costs Total
    11639: 'Financing',         // Financing Costs Total
    11641: 'Financing',         // Total Misc. & Financing Costs
    11642: 'All Categories'     // Project Total (never touch this!)
  };
  
  // Get all unique draws
  const allDraws = [...new Set(costCurveData.value.map(row => parseInt(row.draw_number) || 0))].sort((a, b) => a - b);
  console.log(`🧮 Processing ${allDraws.length} draws...`);
  
  let computedTotalsCount = 0;
  
  // Process each draw
  allDraws.forEach(drawNum => {
    const drawRows = costCurveData.value.filter(row => parseInt(row.draw_number) === drawNum);
    
    // Check which category totals exist for this draw
    const existingTotals = {};
    drawRows.forEach(row => {
      if (row.dda_term_id && totalRowTermIds[row.dda_term_id]) {
        const category = totalRowTermIds[row.dda_term_id];
        if (category !== 'All Categories') { // Don't track Project Total
          existingTotals[category] = true;
        }
      }
    });
    
    console.log(`📊 Draw ${drawNum}: Existing totals:`, Object.keys(existingTotals));
    
    // For each category, check if total exists, if not compute it
    Object.entries(categoryMap).forEach(([categoryId, categoryName]) => {
      if (existingTotals[categoryName]) {
        console.log(`✅ Draw ${drawNum} - ${categoryName}: Total row exists, skipping`);
        return; // Skip if total already exists
      }
      
      // Compute total by summing all rows with this category
      const categoryRows = drawRows.filter(row => {
        const rowCategoryId = row.dda_term?.dda_term_category_id || row.dda_term_category_id;
        return rowCategoryId == categoryId && !row.is_total_row && !row.is_computed_total;
      });
      
      if (categoryRows.length === 0) {
        console.log(`⏭️  Draw ${drawNum} - ${categoryName}: No rows in this category, skipping`);
        return; // No rows in this category
      }
      
      // Sum up the values
      const computedTotal = {
        draw_number: drawNum,
        description: `${categoryName} Total (Computed)`,
        dda_term_name: `${categoryName} Total`,
        dda_term_category_id: parseInt(categoryId),
        is_total_row: true,
        is_computed_total: true, // Mark as computed
        total_type: categoryName.toLowerCase().replace(/ /g, '_'),
        original_budget: categoryRows.reduce((sum, row) => sum + (parseFloat(row.original_budget) || 0), 0),
        current_budget: categoryRows.reduce((sum, row) => sum + (parseFloat(row.current_budget) || 0), 0),
        current_request: categoryRows.reduce((sum, row) => sum + (parseFloat(row.current_request) || 0), 0), // 🔥 CRITICAL: Chart uses current_request!
        total_billing_to_date: categoryRows.reduce((sum, row) => sum + (parseFloat(row.total_billing_to_date) || 0), 0),
        total_completed_to_date: categoryRows.reduce((sum, row) => sum + (parseFloat(row.total_completed_to_date) || 0), 0),
        balance_to_complete: categoryRows.reduce((sum, row) => sum + (parseFloat(row.balance_to_complete) || 0), 0),
        retainage: categoryRows.reduce((sum, row) => sum + (parseFloat(row.retainage) || 0), 0)
      };
      
      // Add to cost curve data
      costCurveData.value.push(computedTotal);
      computedTotalsCount++;
      
      console.log(`✨ Draw ${drawNum} - ${categoryName}: Computed total current_request = $${computedTotal.current_request.toLocaleString()} (from ${categoryRows.length} rows)`);
    });
  });
  
  console.log(`🎉 COMPUTE CATEGORY TOTALS: Created ${computedTotalsCount} computed totals`);
  
  // 🔥 SAVE TO DATABASE: Send computed totals to backend to persist
  try {
    const computedTotals = costCurveData.value.filter(row => row.is_computed_total);
    console.log(`💾 SAVING ${computedTotals.length} computed totals to DATABASE...`);
    
    const response = await axios.post(
      `${apiBase}projects/${selectedId.value}/save_computed_totals`,
      { computed_totals: computedTotals },
      { headers: authToken.value }
    );
    
    console.log(`✅ SAVED to database:`, response.data);
    
    // Clear the cached chart data to force rebuild
    cachedChartData = null;
    
    // Show success message
    alert(`✅ Successfully computed and saved ${computedTotalsCount} missing category totals to the database!\n\nThe cost curve charts will now work for all categories.`);
    
    // Refresh the data to get the saved totals from DB
    await fetchCostCurveData();
    
  } catch (error) {
    console.error('❌ Failed to save computed totals:', error);
    alert(`⚠️ Computed ${computedTotalsCount} totals but failed to save to database.\n\nError: ${error.message}`);
  }
}

function onG703GridReady(params) {
  g703GridApi.value = params.api;
  console.log('G703 Grid API ready');
}

// 🔧 Column Remap Modal Functions
function openColumnRemapModal() {
  console.log('Opening column remap modal');
  showColumnRemapModal.value = true;
}

function closeColumnRemapModal() {
  showColumnRemapModal.value = false;
}

async function applyColumnRemappings() {
  console.log('Applying column remappings:', columnMappings.value);
  
  // Build mapping object
  const mappings = {};
  columnMappings.value.forEach(m => {
    if (m.sourceField) {
      mappings[m.gridColumn] = m.sourceField;
    }
  });
  
  console.log('Built mappings:', mappings);
  
  // TODO: Call backend API to remap the G703 data using field_sources
  // For now, show a notification that the feature is being implemented
  try {
    // Get the current draw info
    const projectId = selectedId.value;
    const drawNumber = g703DrawNumber.value;
    
    if (!projectId || !drawNumber) {
      alert('Please select a project and draw first.');
      closeColumnRemapModal();
      return;
    }
    
    // Call the backend API to apply remappings
    const response = await axiosQuery(
      'POST',
      `projects/${projectId}/remap_g703_columns`,
      authToken.value,
      { 
        draw_number: drawNumber,
        mappings: mappings 
      }
    );
    
    if (response.success) {
      console.log('Remappings applied successfully');
      // Refresh the G703 data
      await fetchG703DrawDetailGridData();
      alert('Column mappings applied successfully!');
    } else {
      console.error('Failed to apply remappings:', response.message);
      alert('Failed to apply mappings: ' + (response.message || 'Unknown error'));
    }
  } catch (error) {
    console.error('Error applying column remappings:', error);
    // For now, just show the mappings were recorded
    alert('Column remappings recorded. Backend integration pending.\n\nMappings:\n' + 
          JSON.stringify(mappings, null, 2));
  }
  
  closeColumnRemapModal();
}

// Toggle DDA Terms Column Visibility
function toggleDDATermsColumn() {
  showDDATermsColumn.value = !showDDATermsColumn.value;
  
  if (drawDetailGridApi.value) {
    // Try different possible DDA column names
    const ddaColumnIds = ['dda_term_name', 'dda_term', 'DDA Term', 'ddaTerm'];
    let columnFound = false;
    
    ddaColumnIds.forEach(colId => {
      // Use the correct AG-Grid API method
      try {
        const columnState = drawDetailGridApi.value.getColumnState();
        const column = columnState.find(col => col.colId === colId);
        
        if (column) {
          // Update column visibility using setColumnVisible
          drawDetailGridApi.value.setColumnsVisible([colId], showDDATermsColumn.value);
          columnFound = true;
          console.log(`${showDDATermsColumn.value ? 'Showed' : 'Hid'} DDA Terms column: ${colId}`);
        }
      } catch (error) {
        // Fallback method for older AG-Grid versions
        try {
          drawDetailGridApi.value.setColumnVisible(colId, showDDATermsColumn.value);
          columnFound = true;
          console.log(`${showDDATermsColumn.value ? 'Showed' : 'Hid'} DDA Terms column: ${colId} (fallback)`);
        } catch (fallbackError) {
          // Column doesn't exist, continue to next
          console.log(`Column ${colId} not found during fallback attempt`);
        }
      }
    });
    
    if (!columnFound) {
      console.warn('DDA Terms column not found in grid');
    }
  } else {
    console.warn('Draw Detail Grid API not available');
  }
}

// Toggle Category Column Visibility
function toggleCategoryColumn() {
  showCategoryColumn.value = !showCategoryColumn.value;
  
  if (drawDetailGridApi.value) {
    const categoryColumnIds = ['category', 'Category'];
    let columnFound = false;
    
    categoryColumnIds.forEach(colId => {
      try {
        const columnState = drawDetailGridApi.value.getColumnState();
        const column = columnState.find(col => col.colId === colId);
        
        if (column) {
          drawDetailGridApi.value.setColumnsVisible([colId], showCategoryColumn.value);
          columnFound = true;
          console.log(`${showCategoryColumn.value ? 'Showed' : 'Hid'} Category column: ${colId}`);
        }
      } catch (error) {
        try {
          drawDetailGridApi.value.setColumnVisible(colId, showCategoryColumn.value);
          columnFound = true;
          console.log(`${showCategoryColumn.value ? 'Showed' : 'Hid'} Category column: ${colId} (fallback)`);
        } catch (fallbackError) {
          console.log(`Column ${colId} not found during fallback attempt`);
        }
      }
    });
    
    if (!columnFound) {
      console.warn('Category column not found in grid');
    }
  } else {
    console.warn('Draw Detail Grid API not available');
  }
}

// Category override modal data
const categoryModalData = ref({
  isOpen: false,
  rowData: null,
  currentCategory: null
});

// Handle category override editing
async function handleEditCategory(rowData, currentCategory) {
  categoryModalData.value = {
    isOpen: true,
    rowData: rowData,
    currentCategory: currentCategory || 'Uncategorized'
  };
}

// Save category override
async function saveCategoryOverride(selectedCategory) {
  try {
    const rowData = categoryModalData.value.rowData;
    const dbId = rowData?.db_id || rowData?.id;
    
    if (!rowData || !dbId) {
      console.error('No row data or database ID available for category override');
      return;
    }

    // Update in backend using the real database ID
    const response = await axios.put(
      `${apiBase}draw_details/${dbId}`,
      {
        draw_detail: {
          category_override: selectedCategory
        }
      },
      authToken.value
    );

    if (response.data) {
      // Update local data in the row
      rowData.category_override = selectedCategory;
      
      // Update the grid without refetching - just update the transaction
      if (drawDetailGridApi.value) {
        // Use applyTransaction to update the row without reloading everything
        drawDetailGridApi.value.applyTransaction({ update: [rowData] });
      }
      
      console.log(`✅ Category override saved: ${selectedCategory} for database ID ${dbId}`);
    }
    
    // Close modal
    categoryModalData.value.isOpen = false;
    
  } catch (error) {
    console.error('Error saving category override:', error);
    alert('Failed to save category override: ' + (error.response?.data?.error || error.message));
  }
}

// Contractor editing functionality
async function handleEditContractor(rowData, fieldName, currentValue) {
  try {
    // Get contractors from multiple sources
    let allContractors = [];
    
    // 1. Fetch registered contractors from database
    if (!dropdownStore.generalContractors || dropdownStore.generalContractors.length === 0) {
      console.log('📥 Loading registered contractors...');
      await dropdownStore.getGeneralContractors();
      console.log('✅ Registered contractors loaded:', dropdownStore.generalContractors?.length || 0);
    }
    
    // Add registered contractors
    if (dropdownStore.generalContractors) {
      allContractors.push(...dropdownStore.generalContractors.map(c => ({
        id: c.id,
        name: c.name,
        address: c.address_line_1 || '',
        type: 'registered'
      })));
    }
    
    // 2. Add contractors extracted from project data
    const extractedContractors = getAvailableContractors();
    extractedContractors.forEach(name => {
      // Only add if not already in registered contractors
      const exists = allContractors.some(c => c.name.toLowerCase() === name.toLowerCase());
      if (!exists) {
        allContractors.push({
          id: null,
          name: name,
          address: 'From project data',
          type: 'extracted'
        });
      }
    });
    
    console.log('✅ Combined contractor list:', allContractors.length, 'contractors');
    
    // Open the contractor modal with combined data
    contractorModalData.value = {
      isOpen: true,
      rowData: rowData,
      fieldName: fieldName,
      currentValue: currentValue || '',
      searchTerm: '',
      searchResults: allContractors, // Use combined list
      showDropdown: false,
      selectedContractor: null,
      allContractors: allContractors // Store for search
    };
    
    // Set initial search term and find matching contractor
    if (currentValue) {
      contractorModalData.value.searchTerm = currentValue;
      const matchingContractor = allContractors.find(c => 
        c.name.toLowerCase() === currentValue.toLowerCase()
      );
      if (matchingContractor) {
        contractorModalData.value.selectedContractor = matchingContractor;
      }
      searchContractorsFromList(currentValue);
    } else {
      // Show all contractors when opening modal
      searchContractorsFromList('');
    }
  } catch (error) {
    console.error('Error opening contractor editor:', error);
    errorStore.setError('Error', `Failed to open contractor editor: ${error.message}`, 'error', true);
  }
}
// Enhanced contractor search using combined list
function searchContractorsFromList(searchTerm) {
  const allContractors = contractorModalData.value.allContractors || [];
  
  if (!searchTerm || searchTerm.trim().length < 1) {
    contractorModalData.value.searchResults = [...allContractors];
    contractorModalData.value.showDropdown = true;
    return;
  }
  
  const filtered = allContractors.filter(contractor => 
    contractor.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  contractorModalData.value.searchResults = [...filtered];
  contractorModalData.value.showDropdown = true;
}
// Project Edit Functions
function editProjectDetails() {
  isMenuVisible.value = false;
  editProjectData.value = {
    // Basic Information
    name: projectInformation.value.name || '',
    entity_name: projectInformation.value.entity_name || '',
    status: projectInformation.value.status || '',
    
    // Address Information
    address_line_1: projectInformation.value.address_line_1 || '',
    address_line_2: projectInformation.value.address_line_2 || '',
    city: projectInformation.value.city || '',
    state: projectInformation.value.state || '',
    zipcode: projectInformation.value.zipcode || '',
    latitude: projectInformation.value.latitude || '',
    longitude: projectInformation.value.longitude || '',
    
    // Building Specifications
    num_floors: projectInformation.value.num_floors || '',
    total_height: projectInformation.value.total_height || '',
    initial_gsf: projectInformation.value.initial_gsf || '',
    total_gsf: projectInformation.value.total_gsf || '',
    total_num_units: projectInformation.value.total_num_units || '',
    subtotal_building_gsf: projectInformation.value.subtotal_building_gsf || '',
    subtotal_num_units: projectInformation.value.subtotal_num_units || '',
    subtotal_parking_gsf: projectInformation.value.subtotal_parking_gsf || '',
    subtotal_num_spaces: projectInformation.value.subtotal_num_spaces || '',
    
    // Project Dates
    planned_start_date: projectInformation.value.planned_start_date || '',
    planned_end_date: projectInformation.value.planned_end_date || '',
    actual_start_date: projectInformation.value.actual_start_date || '',
    actual_end_date: projectInformation.value.actual_end_date || '',
    project_length_months: projectInformation.value.project_length_months || '',
    
    // Financial Information
    total_original_budget: projectInformation.value.total_original_budget || '',
    
    // Project Characteristics
    is_historic_has_landmark: projectInformation.value.is_historic_has_landmark || false,
    is_modular: projectInformation.value.is_modular || false,
    is_released: projectInformation.value.is_released || false,
    
    // Foreign Key References
    finish_level_id: projectInformation.value.finish_level_id || '',
    sustainability_type_id: projectInformation.value.sustainability_type_id || '',
    asset_category_id: projectInformation.value.asset_category_id || '',
    contractor_id: projectInformation.value.contractor_id || '',
    construction_contract_type_id: projectInformation.value.construction_contract_type_id || '',
    
    // Enhanced Fields - Multi-select and complex types
    construction_types: projectInformation.value.project_construction_types_s || [],
    amenities: projectInformation.value.amenities_s || [],
    building_use_types: projectInformation.value.building_use_types || [],
    building_use_gsf: {}, // Initialize empty GSF tracker for building use types
    parking_types: projectInformation.value.parking_types || [],
    brand_types: projectInformation.value.brand_types || [],
    
    // Notes
    notes: projectInformation.value.notes || ''
  };
  
  // Initialize building use GSF values from existing data
  console.log('🔍 Loading existing building uses:', projectInformation.value.project_building_uses);
  
  // Reset hierarchical data structures
  hierarchicalBuildingUseData.value = {
    parentCategories: {},
    subCategories: {},
    subCategoriesGSF: {},
    subCategoriesUnits: {},
    affordableGSF: {},
    affordableUnits: {},
    brandTypes: {}
  };
  
  hierarchicalParkingData.value = {
    parkingTypes: {},
    parkingGSF: {},
    parkingSpaces: {}
  };
  
  if (projectInformation.value.project_building_uses && Array.isArray(projectInformation.value.project_building_uses)) {
    console.log('🔍 Loading existing building uses:', projectInformation.value.project_building_uses);
    
    // ✅ VALIDATION: Filter out invalid/stale IDs before processing
    // Valid Building Use Type IDs: 199, 200, 202, 216-231, 320-350, 365
    // Valid Parking Type IDs: 211-215, 396
    const validBuildingUseIds = [199, 200, 202, 216, 217, 218, 219, 220, 221, 222, 223, 224, 225, 226, 227, 228, 229, 230, 231, 320, 321, 322, 323, 324, 325, 326, 327, 328, 329, 330, 331, 332, 333, 334, 335, 336, 337, 338, 339, 340, 341, 342, 343, 344, 345, 346, 347, 348, 349, 350, 365];
    const validParkingIds = [211, 212, 213, 214, 215, 396];
    
    projectInformation.value.project_building_uses.forEach(use => {
      // Each use has: codeable_id, codeable_type (BuildingUseType or ParkingType), total_gsf, num_units, num_spaces, etc.
      if (use.codeable_id) {
        // ✅ CRITICAL: Validate ID before processing to prevent 422 errors from stale/invalid IDs
        if (use.codeable_type === 'ParkingType' && !validParkingIds.includes(use.codeable_id)) {
          console.warn(`⚠️ Skipping invalid parking type ID: ${use.codeable_id}`);
          return; // Skip this invalid parking type
        }
        if (use.codeable_type === 'BuildingUseType' && !validBuildingUseIds.includes(use.codeable_id)) {
          console.warn(`⚠️ Skipping invalid building use type ID: ${use.codeable_id}`);
          return; // Skip this invalid building use type
        }
        
        // Check if this is a ParkingType or BuildingUseType
        if (use.codeable_type === 'ParkingType') {
          // PARKING TYPE - handle separately
          console.log('🅿️ Found parking type:', use);
          
          // OLD FORMAT: Add to parking_types array
          if (!editProjectData.value.parking_types.includes(use.codeable_id)) {
            editProjectData.value.parking_types.push(use.codeable_id);
          }
          
          // NEW HIERARCHICAL FORMAT: Populate hierarchical parking structures
          hierarchicalParkingData.value.parkingTypes[use.codeable_id] = true;
          
          if (use.total_gsf) {
            hierarchicalParkingData.value.parkingGSF[use.codeable_id] = use.total_gsf.toString();
            console.log(`🅿️ Set parking GSF for ${use.codeable_id}:`, use.total_gsf);
          }
          if (use.num_spaces) {
            hierarchicalParkingData.value.parkingSpaces[use.codeable_id] = use.num_spaces.toString();
            console.log(`🅿️ Set parking spaces for ${use.codeable_id}:`, use.num_spaces);
          }
        } else {
          // BUILDING USE TYPE
          // OLD FORMAT SUPPORT: Make sure this use type is in the selected array
          if (!editProjectData.value.building_use_types.includes(use.codeable_id)) {
            editProjectData.value.building_use_types.push(use.codeable_id);
          }
          // Store the GSF value (old format)
          if (use.total_gsf) {
            editProjectData.value.building_use_gsf[use.codeable_id] = use.total_gsf;
          }
          
          // NEW HIERARCHICAL FORMAT: Populate hierarchical structures
          // Find the building use type in our dropdown data to determine if it's a parent or sub-category
          const buildingUseType = editModalDropdownData.value.buildingUseTypes?.find(t => t.id === use.codeable_id);
        
        if (buildingUseType) {
          // Find the parent category by matching the building use type's code to our category mappings
          // ✅ VALIDATED AGAINST DATABASE - All 51 building use types from codes table (2025-10-06)
          const categoryMappings = {
            'Residential': ['Rental Apartment', 'Condominium', 'Co-Living', 'Student Housing', 'Senior Housing', 'Single Family Residence', 'Manufactured Home', 'Modular Home', 'Micro Housing'],
            'Commercial': ['Office', 'Co-Working', 'Health Care / Medical Office', 'Life Science', 'Creative Office', 'Community Facility', 'Film Studio', 'Sound Stage'],
            'Industrial': ['Warehouse', 'Self-Storage', 'Cold Storage', 'Data Center', 'Manufacturing'],
            'Hospitality': ['Upscale / Luxury Hotel', 'Economy / Midscale Hotel', 'Extended Stay Hotel', 'Micro Hotel', 'Boutique Hotel', 'Resort', 'Timeshare', 'Golf Course', 'Gaming', "Member's Club"],
            'Retail': ['Integrated Ground Retail', 'Mall', 'Strip Center', 'Market Place', 'Restaurant', 'Theater', 'Event Space', 'Art Gallery'],
            'Civic & Cultural': ['Education', 'Hospital', 'Government', 'Religious', 'Stadium', 'Museum', 'Cultural Center', 'Convention Center', 'Music Venue', 'Public Park', 'Concert Hall']
          };
          
          // Find which category this building use type belongs to
          const parentCategoryCode = Object.keys(categoryMappings).find(catCode => 
            categoryMappings[catCode].includes(buildingUseType.code)
          );
          
          if (parentCategoryCode) {
            const parentCategory = editModalDropdownData.value.assetCategories?.find(c => c.code === parentCategoryCode);
            if (parentCategory) {
              // Mark parent category as checked
              hierarchicalBuildingUseData.value.parentCategories[parentCategory.id] = true;
            }
          }
          
          // Mark sub-category as checked
          hierarchicalBuildingUseData.value.subCategories[use.codeable_id] = true;
          
          // Populate GSF and Units
          if (use.total_gsf) {
            hierarchicalBuildingUseData.value.subCategoriesGSF[use.codeable_id] = use.total_gsf.toString();
          }
          if (use.num_units) {
            hierarchicalBuildingUseData.value.subCategoriesUnits[use.codeable_id] = use.num_units.toString();
          }
          
          // Handle special fields (affordable component, brand types)
          if (use.affordable_comp_gsf) {
            hierarchicalBuildingUseData.value.affordableGSF[use.codeable_id] = use.affordable_comp_gsf.toString();
          }
          if (use.affordable_comp_num_units) {
            hierarchicalBuildingUseData.value.affordableUnits[use.codeable_id] = use.affordable_comp_num_units.toString();
          }
          if (use.brand_type) {
            hierarchicalBuildingUseData.value.brandTypes[use.codeable_id] = use.brand_type;
          }
        }
        } // end else (BuildingUseType)
      }
    });
  }
  
  // Initialize parking types from existing data
  if (projectInformation.value.project_parking_uses && Array.isArray(projectInformation.value.project_parking_uses)) {
    console.log('🅿️ Loading parking uses from API:', projectInformation.value.project_parking_uses);
    projectInformation.value.project_parking_uses.forEach(parking => {
      console.log('🅿️ Processing parking:', parking);
      if (parking.codeable_id) {
        // OLD FORMAT: Add to parking_types array
        if (!editProjectData.value.parking_types.includes(parking.codeable_id)) {
          editProjectData.value.parking_types.push(parking.codeable_id);
        }
        
        // NEW HIERARCHICAL FORMAT: Populate hierarchical structures
        hierarchicalParkingData.value.parkingTypes[parking.codeable_id] = true;
        
        if (parking.total_gsf) {
          hierarchicalParkingData.value.parkingGSF[parking.codeable_id] = parking.total_gsf.toString();
          console.log(`🅿️ Set parking GSF for ${parking.codeable_id}:`, parking.total_gsf);
        }
        if (parking.num_spaces) {
          hierarchicalParkingData.value.parkingSpaces[parking.codeable_id] = parking.num_spaces.toString();
          console.log(`🅿️ Set parking spaces for ${parking.codeable_id}:`, parking.num_spaces);
        }
      }
    });
    console.log('🅿️ Final hierarchicalParkingData:', hierarchicalParkingData.value);
  }
  
  console.log('✅ Initialized building use types (old format):', editProjectData.value.building_use_types);
  console.log('✅ Initialized building use GSF (old format):', editProjectData.value.building_use_gsf);
  console.log('✅ Initialized hierarchical building use data:', hierarchicalBuildingUseData.value);
  console.log('✅ Initialized hierarchical parking data:', hierarchicalParkingData.value);
  
  console.log('🔍 editModalDropdownData when opening modal:', {
    assetCategoriesCount: editModalDropdownData.value.assetCategories?.length || 0,
    buildingUseTypesCount: editModalDropdownData.value.buildingUseTypes?.length || 0,
    assetCategories: editModalDropdownData.value.assetCategories
  });
  
  console.log('🔍 hierarchicalBuildingUseData state:', {
    parentCategories: hierarchicalBuildingUseData.value.parentCategories,
    subCategories: hierarchicalBuildingUseData.value.subCategories,
    subCategoriesGSF: hierarchicalBuildingUseData.value.subCategoriesGSF,
    subCategoriesUnits: hierarchicalBuildingUseData.value.subCategoriesUnits
  });
  
  calculateTotalBuildingUseGSF();
  showEditProjectModal.value = true;
}

function closeEditProjectModal() {
  showEditProjectModal.value = false;
  editProjectData.value = {};
}

async function saveProjectDetails() {
  try {
    console.log('💾 Saving project details:', editProjectData.value);
    
    // Prepare the data in the format the backend expects
    const projectPayload = {
      project: {
        // Basic fields
        name: editProjectData.value.name,
        entity_name: editProjectData.value.entity_name,
        status: editProjectData.value.status,
        
        // Address fields
        address_line_1: editProjectData.value.address_line_1,
        address_line_2: editProjectData.value.address_line_2,
        city: editProjectData.value.city,
        state: editProjectData.value.state,
        zipcode: editProjectData.value.zipcode,
        
        // Building specs
        num_floors: editProjectData.value.num_floors,
        total_height: editProjectData.value.total_height,
        total_gsf: totalBuildingGSF.value + totalParkingGSF.value, // Calculate from building use + parking
        total_num_units: editProjectData.value.total_num_units,
        
        // Dates
        planned_start_date: editProjectData.value.planned_start_date,
        planned_end_date: editProjectData.value.planned_end_date,
        actual_start_date: editProjectData.value.actual_start_date,
        actual_end_date: editProjectData.value.actual_end_date,
        project_length_months: editProjectData.value.project_length_months,
        
        // Financial
        total_original_budget: editProjectData.value.total_original_budget,
        
        // Foreign keys
        construction_contract_type_id: editProjectData.value.construction_contract_type_id,
        sustainability_type_id: editProjectData.value.sustainability_type_id,
        asset_category_id: editProjectData.value.asset_category_id,
        finish_level_id: editProjectData.value.finish_level_id,
        
        // Arrays - use the proper backend field names
        construction_types: editProjectData.value.construction_types,
        amenities: editProjectData.value.amenities,
        
        // Building use types - collect from hierarchical structure
        building_use_types: (() => {
          const buildingUses = [];
          
          // Iterate through selected sub-categories
          Object.keys(hierarchicalBuildingUseData.value.subCategories).forEach(subCatId => {
            if (hierarchicalBuildingUseData.value.subCategories[subCatId]) {
              const gsf = parseInt(hierarchicalBuildingUseData.value.subCategoriesGSF[subCatId]) || 0;
              const units = parseInt(hierarchicalBuildingUseData.value.subCategoriesUnits[subCatId]) || 0;
              const affordableGsf = parseInt(hierarchicalBuildingUseData.value.affordableGSF[subCatId]) || 0;
              const affordableUnits = parseInt(hierarchicalBuildingUseData.value.affordableUnits[subCatId]) || 0;
              
              // ✅ CRITICAL FIX: Skip building use types with 0 GSF AND 0 units AND no affordable components
              // This prevents sending stale/unchecked IDs that don't exist in the database
              if (gsf === 0 && units === 0 && affordableGsf === 0 && affordableUnits === 0) {
                return; // Skip this building use type
              }
              
              const useData = {
                id: parseInt(subCatId),
                total_gsf: gsf,
                num_units: units
              };
              
              // Add affordable component if exists (backend expects affordable_comp_gsf and affordable_comp_num_units)
              if (affordableGsf > 0) {
                useData.affordable_comp_gsf = affordableGsf;
              }
              if (affordableUnits > 0) {
                useData.affordable_comp_num_units = affordableUnits;
              }
              
              // Add brand types if exists
              if (hierarchicalBuildingUseData.value.brandTypes[subCatId]) {
                useData.brand_type = hierarchicalBuildingUseData.value.brandTypes[subCatId];
              }
              if (hierarchicalBuildingUseData.value.brandTypes['branded_' + subCatId]) {
                useData.branded_residence = hierarchicalBuildingUseData.value.brandTypes['branded_' + subCatId];
              }
              
              buildingUses.push(useData);
            }
          });
          
          return buildingUses;
        })(),
        
        // Parking types - collect from hierarchical structure
        parking_types: (() => {
          const parkingUses = [];
          
          Object.keys(hierarchicalParkingData.value.parkingTypes).forEach(parkingId => {
            if (hierarchicalParkingData.value.parkingTypes[parkingId]) {
              const gsf = parseInt(hierarchicalParkingData.value.parkingGSF[parkingId]) || 0;
              const spaces = parseInt(hierarchicalParkingData.value.parkingSpaces[parkingId]) || 0;
              
              // ✅ CRITICAL FIX: Skip parking types with 0 GSF AND 0 spaces
              // This prevents sending stale/unchecked IDs that don't exist in the database
              if (gsf === 0 && spaces === 0) {
                return; // Skip this parking type
              }
              
              parkingUses.push({
                id: parseInt(parkingId),
                total_gsf: gsf,
                num_spaces: spaces
              });
            }
          });
          
          return parkingUses;
        })(),
        
        // Update subtotals from computed values
        subtotal_building_gsf: totalBuildingGSF.value,
        subtotal_num_units: totalBuildingUnits.value,
        subtotal_parking_gsf: totalParkingGSF.value,
        subtotal_num_spaces: totalParkingSpaces.value,
        
        // Characteristics
        is_historic_has_landmark: editProjectData.value.is_historic_has_landmark,
        is_modular: editProjectData.value.is_modular,
        is_released: editProjectData.value.is_released,
        
        // Notes
        notes: editProjectData.value.notes
      }
    };
    
    console.log('📤 Sending payload:', projectPayload);
    
    const response = await axios.put(
      `${apiBase}projects/${selectedId.value}`,
      projectPayload,
      { headers: authToken.value }
    );
    
    console.log('📥 Server response:', response.data);
    
    if (response.data) {
      // Close modal first
      closeEditProjectModal();
      
      // Refresh ALL project data to reflect changes
      console.log('🔄 Refreshing project data...');
      await fetchProjectInformation();
      
      console.log('✅ Project updated successfully');
      alert('Project details saved successfully!');
    }
  } catch (error) {
    console.error('❌ Error updating project:', error);
    console.error('❌ Error details:', error.response?.data);
    alert('Error updating project: ' + (error.response?.data?.message || error.message));
  }
}

function editProjectImage() {
  openEditImagesModal();
}

// Track processed draws to prevent re-processing
let processedDraws = new Set();

// Throttled OpenAI matching helpers to prevent 429s
const OPENAI_MAX_CONCURRENCY = 1; // single flight to minimize burst rate
const OPENAI_COOLDOWN_MS_DEFAULT = 60000; // global cooldown after 429
const OPENAI_PER_SESSION_CAP = 60; // hard cap per session
const OPENAI_MAX_PER_DRAW = 20; // per-draw cap
const MAX_DDA_AUTOMATCH_ROWS_PER_RUN = 200; // process at most N rows per run - increased for large templates
const AUTO_MATCH_MIN_INTERVAL_MS = 120000; // don't re-run auto-match for same draw within 2 minutes
const ENABLE_UI_OPENAI_AUTOMATCH = false; // gate UI-driven OpenAI calls to avoid 429s
let autoMatchDebounceTimer = null; // 🔥 Debounce timer to prevent auto-match on rapid draw switching
const HARDCODED_OPENAI_KEY = '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';
let openAIActive = 0;
const openAIQueue = [];
const openAIMatchCache = {};
let openAICacheLoaded = false;
let openAIRequestsThisSession = 0;
let openAIGlobalCooldownUntil = 0;
const openAIDrawUsageCount = {};
const lastAutoMatchAt = {};

function getOpenAIKey() {
  try {
    const viteKey = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_OPENAI_API_KEY) ? import.meta.env.VITE_OPENAI_API_KEY : '';
    return ((window && window.ENV && window.ENV.OPENAI_API_KEY) || viteKey || localStorage.getItem('OPENAI_API_KEY') || HARDCODED_OPENAI_KEY || '');
  } catch (e) {
    return HARDCODED_OPENAI_KEY || '';
  }
}

function getOpenAIModel() {
  try {
    return (window && window.ENV && window.ENV.OPENAI_MODEL) || 'gpt-4o-mini';
  } catch (e) {
    return 'gpt-4o-mini';
  }
}

function runOpenAIQueue() {
  while (openAIActive < OPENAI_MAX_CONCURRENCY && openAIQueue.length > 0) {
    const { exec, resolve, reject } = openAIQueue.shift();
    openAIActive++;
    exec()
      .then(resolve)
      .catch(reject)
      .finally(() => {
        openAIActive--;
        // longer delay between tasks to avoid bursts
        setTimeout(runOpenAIQueue, 800);
      });
  }
}

function scheduleOpenAITask(executor) {
  return new Promise((resolve, reject) => {
    openAIQueue.push({ exec: executor, resolve, reject });
    runOpenAIQueue();
  });
}

function normalizeDescForCache(desc) {
  return (desc || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

const OPENAI_CACHE_KEY = 'dda_openai_match_cache_v2';
function loadOpenAIMatchCache() {
  if (openAICacheLoaded) return;
  try {
    const raw = localStorage.getItem(OPENAI_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        Object.assign(openAIMatchCache, parsed);
      }
    }
  } catch (_) {
    // Ignore localStorage errors
  }
  openAICacheLoaded = true;
}
function saveOpenAIMatchCache() {
  try {
    localStorage.setItem(OPENAI_CACHE_KEY, JSON.stringify(openAIMatchCache));
  } catch (_) {
    // Ignore localStorage errors
  }
}

// 🧠 DDA TERM LEARNING SYSTEM
// One-time cleanup: Remove any blank/short key corrections from localStorage
(function cleanupBlankCorrections() {
  if (typeof window === 'undefined') return;
  if (window._ddaCleanupRan) return;
  window._ddaCleanupRan = true;
  
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('dda_term_corrections_')) {
        const corrections = JSON.parse(localStorage.getItem(key) || '{}');
        let cleaned = false;
        // Remove blank or very short description keys
        Object.keys(corrections).forEach(descKey => {
          if (!descKey || descKey.length < 3) {
            delete corrections[descKey];
            cleaned = true;
            console.log(`🧹 Cleaned up blank/short correction key from ${key}`);
          }
        });
        if (cleaned) {
          localStorage.setItem(key, JSON.stringify(corrections));
        }
      }
    });
  } catch (e) {
    // Ignore localStorage errors
  }
})();

// Store and retrieve learned DDA term corrections per project
function saveDDATermCorrection(projectId, description, ddaTermId, ddaTermName) {
  // 🔥 CRITICAL FIX: Never save corrections for blank/empty descriptions
  // This prevents blank row corrections from spreading to ALL blank rows
  const normalizedDesc = (description || '').toLowerCase().trim();
  if (!normalizedDesc || normalizedDesc.length < 3) {
    console.log(`🧠 Skipping save for blank/short description: "${description}"`);
    return; // Don't save corrections for blank or very short descriptions
  }
  
  const key = `dda_term_corrections_${projectId}`;
  const corrections = JSON.parse(localStorage.getItem(key) || '{}');
  
  corrections[normalizedDesc] = {
    dda_term_id: ddaTermId,
    dda_term_name: ddaTermName,
    learned_at: new Date().toISOString(),
    description: description // Keep original for reference
  };
  
  localStorage.setItem(key, JSON.stringify(corrections));
  console.log(`🧠 Learned correction: "${description}" → "${ddaTermName}"`);
}

function getLearnedDDATermCorrection(projectId, description) {
  // 🔥 CRITICAL FIX: Never look up corrections for blank/empty descriptions
  // This prevents ALL blank rows from getting the same DDA term
  const normalizedDesc = (description || '').toLowerCase().trim();
  if (!normalizedDesc || normalizedDesc.length < 3) {
    return null; // Don't look up corrections for blank or very short descriptions
  }
  
  const key = `dda_term_corrections_${projectId}`;
  const corrections = JSON.parse(localStorage.getItem(key) || '{}');
  
  return corrections[normalizedDesc] || null;
}

// AUTO-MATCH DDA TERMS FUNCTION
async function autoMatchMissingDDATerms() {
  // ✅ Auto-match for Draw Details (1) and G703 (2) tabs only
  // 🔥 DISABLED for G702 tab (3) - causes 401 errors and not needed
  if (![1, 2].includes(btSubMenuSelectedItemId.value)) {
    return;
  }

  // Determine which grid to use based on active tab
  let currentRowData;
  if (btSubMenuSelectedItemId.value === 1) {
    currentRowData = drawDetailRowData.value;
  } else if (btSubMenuSelectedItemId.value === 2) {
    currentRowData = g703RowData.value;
  }

  if (!currentRowData || currentRowData.length === 0) {
    return;
  }

  // 🎯 SIMPLE FIX: Only run auto-matching if there are actually missing terms
  // Don't run on draw changes - only run on initial data load when terms are truly missing
  // 🔥 ALSO skip zero-value rows - rows with all zeros are placeholder/empty rows
  const missingDDARows = currentRowData.filter(row => {
    // Skip if already has DDA term
    if (row.dda_term_id || row.dda_term_name || row.dda_term || row._skip_auto_match || row.is_total_row) {
      return false;
    }
    
    // 🔥 SKIP ZERO-VALUE ROWS - rows with all zero financial values are placeholder/empty rows
    const hasAnyValue = (
      parseFloat(row.original_budget || 0) !== 0 ||
      parseFloat(row.current_budget || 0) !== 0 ||
      parseFloat(row.previously_funded || 0) !== 0 ||
      parseFloat(row.current_request || 0) !== 0 ||
      parseFloat(row.total_completed_to_date || row.total_billing_to_date || 0) !== 0 ||
      parseFloat(row.balance_to_complete || 0) !== 0
    );
    
    if (!hasAnyValue) {
      return false; // Skip zero-value rows silently
    }
    
    return true;
  });

  if (missingDDARows.length === 0) {
    return; // Silent return - no spam when all terms are assigned
  }

  // 🎯 PREVENT SPAM: Use DRAW-SPECIFIC tracking so it runs for each new draw
  const currentDraw = globalDrawFilter.value || drawNumber.value;
  const projectKey = `auto_match_${selectedId.value}_tab_${btSubMenuSelectedItemId.value}_draw_${currentDraw}`;
  if (window[projectKey]) {
    console.log(`🚫 Auto-match already ran for draw ${currentDraw}, skipping...`);
    return; // Already ran auto-matching for this specific draw
  }
  window[projectKey] = true;

  console.log(`🔄 Auto-matching ${missingDDARows.length} missing DDA terms for tab ${btSubMenuSelectedItemId.value}...`);

    // Load DDA terms if not already loaded (no OpenAI here)
    if (!ddaTerms.value || ddaTerms.value.length === 0) {
      await loadDDATermsForCharts();
    }

  let matchedCount = 0;
  const matchPromises = [];

  // Limit rows per run to avoid API spikes
  const rowsToProcess = missingDDARows.slice(0, MAX_DDA_AUTOMATCH_ROWS_PER_RUN);
  for (const row of rowsToProcess) {
    const description = row.description || row.scope_detail || '';
    // 🔥 SKIP BLANK/SHORT DESCRIPTIONS - consistent with learned correction system (min 3 chars)
    if (!description.trim() || description.trim().length < 3) continue;
    
    // 🎯 RULE-BASED TOTAL ROW MATCHING - apply before AI matching
    const descLower = description.toLowerCase().trim();
    const totalMatch = ruleBasedTotalRowMatch(descLower);
    if (totalMatch) {
      const matchPromise = (async () => {
        try {
          row.dda_term_id = totalMatch.id;
          row.dda_term_name = totalMatch.term;
          row.dda_term = { id: totalMatch.id, term: totalMatch.term };
          row.is_total_row = true;
          await updateDDATermValue(row, 'dda_term_id', totalMatch.term, totalMatch.id, true);
          matchedCount++;
        } catch (error) {
          console.error(`Error assigning ${totalMatch.term} to "${description}":`, error);
        }
      })();
      matchPromises.push(matchPromise);
      continue;
    }

    // Skip rows with allocation (not meaningful for general AI matching)
    if (descLower.includes('allocation') || row.is_total_row === true) {
      continue;
    }

    // Try to find best match
    const matchPromise = (async () => {
      try {
        let bestMatch = null;
        
        // 1️⃣ Learned correction takes priority
        const learnedCorrection = getLearnedDDATermCorrection(selectedId.value, description);
        if (learnedCorrection) {
          console.log(`🧠 Using learned correction: "${description}" → "${learnedCorrection.dda_term_name}"`);
          bestMatch = {
            id: learnedCorrection.dda_term_id,
            term: learnedCorrection.dda_term_name,
            confidence: 1.0,
            source: 'learned'
          };
        } else {
          // 2️⃣ Internal ML then keyword
          bestMatch = await findBestDDATermMatch(description, ddaTerms.value);
          if (!bestMatch) {
            bestMatch = findBestKeywordMatch(description, ddaTerms.value);
          }

          // 3️⃣ Claude Sonnet fallback — fires whenever ML+keyword confidence < 85%
          if (!bestMatch || bestMatch.confidence < 0.85) {
            try {
              const termsListForClaude = ddaTerms.value.map(t => `${t.id}: ${t.term}`).join('\n');
              const claudeRes = await axios.post(
                `${apiBase}projects/${selectedId.value}/claude_match_dda_term`,
                { description, terms_list: termsListForClaude },
                { headers: authToken.value }
              );
              if (claudeRes.data?.success && claudeRes.data.result?.term_id && claudeRes.data.result.confidence >= 0.85) {
                const termId = claudeRes.data.result.term_id;
                const term = ddaTerms.value.find(t => t.id === termId);
                if (term) {
                  bestMatch = { id: termId, term: term.term, confidence: claudeRes.data.result.confidence, source: 'claude' };
                  console.log(`🤖 Claude Sonnet matched "${description}" → "${term.term}" (${Math.round(claudeRes.data.result.confidence * 100)}%)`);
                }
              }
            } catch (claudeErr) {
              // Silent — Claude fallback unavailable, skip
            }
          }
        }

        if (bestMatch && bestMatch.confidence >= 0.85) {
          const isCategoryTotal = detectCategoryTotal(description, bestMatch.term);
          row.dda_term_id = bestMatch.id;
          row.dda_term_name = bestMatch.term;
          row.dda_term = { id: bestMatch.id, term: bestMatch.term };
          if (isCategoryTotal.isTotal) {
            row.is_category_total = true;
            row.category_total_type = isCategoryTotal.category;
          }
          await updateDDATermValue(row, 'dda_term_id', bestMatch.term, bestMatch.id, true);
          matchedCount++;
        }
      } catch (error) {
        if (error?.response?.status === 401 || error?.response?.status === 500) return;
        console.error(`Error auto-matching DDA term for "${description}":`, error);
      }
    })();
    
    matchPromises.push(matchPromise);
  }

  // Wait for all matches to complete
  await Promise.all(matchPromises);

  // Draw already marked as processed at the beginning to prevent duplicates

  if (matchedCount > 0) {
    console.log(`✅ Auto-matched ${matchedCount} DDA terms`);
    // Refresh the grid to show the updates
    drawDetailDataCtr.value++;
  }
}

// AUTO-MATCH DDA TERMS FOR G703 TRADES (specialized for contractor/trade matching)
async function autoMatchMissingG703DDATerms() {
  // Prevent re-processing the same G703 draw (now includes draw number)
  const currentG703Draw = g703DrawNumber.value || globalDrawFilter.value;
  const drawKey = `g703_${selectedId.value}_draw_${currentG703Draw}`;
  
  console.log(`🔍 G703 Auto-match check: drawKey="${drawKey}"`);
  
  if (processedDraws.has(drawKey)) {
    console.log(`🚫 G703 Auto-matching already processed for draw ${currentG703Draw}, skipping...`);
    return;
  }
  
  // IMMEDIATE: Add to processed set to prevent multiple simultaneous calls
  processedDraws.add(drawKey);
  console.log(`🔒 Marked G703 ${drawKey} as processing to prevent duplicates`);

  if (!g703RowData.value || g703RowData.value.length === 0) {
    console.log('🔍 No G703 data to process for DDA auto-matching');
    return;
  }

  // Helper: Detect if a description is a subtotal/total row
  const isSubtotalRow = (description) => {
    if (!description) return false;
    const desc = description.toLowerCase();
    return desc.includes('subtotal') || 
           desc.includes('sub-total') || 
           desc.includes('sub total') ||
           desc.match(/^total[:\s]/i) ||
           desc.match(/total$/i) ||
           desc === 'total';
  };

  // Find rows missing DDA terms (SKIP SUBTOTALS and ZERO-VALUE rows - they shouldn't be matched)
  const missingDDARows = g703RowData.value.filter(row => {
    // Skip if already has DDA term
    if (row.dda_term_id || row.dda_term_name || row.dda_term) return false;
    
    // 🔥 SKIP SUBTOTALS - they should NOT be auto-matched to DDA terms
    if (isSubtotalRow(row.description)) {
      console.log(`🚫 Skipping subtotal row from auto-matching: "${row.description}"`);
      return false;
    }
    
    // 🔥 SKIP ZERO-VALUE ROWS - rows with all zero financial values are placeholder/empty rows
    const hasAnyValue = (
      parseFloat(row.original_budget || 0) !== 0 ||
      parseFloat(row.current_budget || 0) !== 0 ||
      parseFloat(row.previously_funded || 0) !== 0 ||
      parseFloat(row.current_request || 0) !== 0 ||
      parseFloat(row.total_completed_to_date || 0) !== 0 ||
      parseFloat(row.balance_to_complete || 0) !== 0
    );
    
    if (!hasAnyValue) {
      console.log(`🚫 Skipping zero-value row from auto-matching: "${row.description || '(blank)'}"`);
      return false;
    }
    
    return true;
  });

  if (missingDDARows.length === 0) {
    console.log('✅ All G703 rows already have DDA terms assigned (or are subtotals)');
    return;
  }

  console.log(`🔄 Auto-matching G703 DDA terms for ${missingDDARows.length} trade rows (subtotals excluded)...`);

  // Load DDA terms if not already loaded
  if (!ddaTerms.value || ddaTerms.value.length === 0) {
    await loadDDATermsForCharts();
  }

  let matchedCount = 0;
  const matchPromises = [];

  for (const row of missingDDARows) {
    // For G703, use both description and contractor name for better matching
    const description = row.description || '';
    const contractorName = row.contractor_names || row.contractor_name || '';
    
    // 🔥 SKIP BLANK/SHORT DESCRIPTIONS - consistent with learned correction system (min 3 chars)
    if (!description.trim() || description.trim().length < 3) continue;

    // 🔥 RULE-BASED TOTAL ROW DETECTION - Check FIRST before any AI matching
    const descLower = description.toLowerCase().trim();
    
    const totalRowMatch = ruleBasedTotalRowMatch(descLower);

    if (totalRowMatch) {
      console.log(`🎯 RULE-BASED MATCH: "${description}" → "${totalRowMatch.term}" (ID: ${totalRowMatch.id})`);
      const matchPromise = (async () => {
        try {
          row.dda_term_id = totalRowMatch.id;
          row.dda_term_name = totalRowMatch.term;
          row.dda_term = { id: totalRowMatch.id, term: totalRowMatch.term };
          row.is_total_row = true;
          await updateG703DDATermValue(row, 'dda_term_id', totalRowMatch.term, totalRowMatch.id, true);
          matchedCount++;
        } catch (error) {
          console.error(`Error assigning ${totalRowMatch.term} to "${description}":`, error);
        }
      })();
      matchPromises.push(matchPromise);
      continue; // Skip AI matching for total rows
    }

    // Create enhanced description for trade matching
    let enhancedDescription = description;
    if (contractorName && contractorName !== 'N/A') {
      // Extract trade from contractor name (e.g., "Brock Masonry" -> "masonry")
      const tradeFromContractor = extractTradeFromContractor(contractorName);
      if (tradeFromContractor) {
        enhancedDescription = `${tradeFromContractor} ${description}`.trim();
      }
    }
    
    if (!enhancedDescription.trim()) continue;

    // Try to find best match
    const matchPromise = (async () => {
      try {
        let bestMatch = null;
        
        // 1️⃣ Learned correction takes priority
        const learnedCorrection = getLearnedDDATermCorrection(selectedId.value, description);
        if (learnedCorrection) {
          console.log(`🧠 Using learned correction for G703: "${description}" → "${learnedCorrection.dda_term_name}"`);
          bestMatch = {
            id: learnedCorrection.dda_term_id,
            term: learnedCorrection.dda_term_name,
            confidence: 1.0,
            source: 'learned'
          };
        } else {
          // 2️⃣ Internal ML then keyword (using enhanced description with trade context)
          bestMatch = await findBestDDATermMatch(enhancedDescription, ddaTerms.value);
          if (!bestMatch) {
            bestMatch = findBestKeywordMatch(enhancedDescription, ddaTerms.value);
          }

          // 3️⃣ Claude Sonnet fallback — fires whenever ML+keyword confidence < 85%
          if (!bestMatch || bestMatch.confidence < 0.85) {
            try {
              const termsListForClaude = ddaTerms.value.map(t => `${t.id}: ${t.term}`).join('\n');
              const claudeRes = await axios.post(
                `${apiBase}projects/${selectedId.value}/claude_match_dda_term`,
                { description: enhancedDescription, terms_list: termsListForClaude },
                { headers: authToken.value }
              );
              if (claudeRes.data?.success && claudeRes.data.result?.term_id && claudeRes.data.result.confidence >= 0.85) {
                const termId = claudeRes.data.result.term_id;
                const term = ddaTerms.value.find(t => t.id === termId);
                if (term) {
                  bestMatch = { id: termId, term: term.term, confidence: claudeRes.data.result.confidence, source: 'claude' };
                  console.log(`🤖 Claude Sonnet matched G703 "${description}" → "${term.term}" (${Math.round(claudeRes.data.result.confidence * 100)}%)`);
                }
              }
            } catch (claudeErr) {
              // Silent — Claude fallback unavailable, skip
            }
          }
        }

        if (bestMatch && bestMatch.confidence >= 0.85) {
          row.dda_term_id = bestMatch.id;
          row.dda_term_name = bestMatch.term;
          row.dda_term = { id: bestMatch.id, term: bestMatch.term };
          await updateG703DDATermValue(row, 'dda_term_id', bestMatch.term, bestMatch.id, true);
          matchedCount++;
        }
      } catch (error) {
        if (error?.response?.status === 401 || error?.response?.status === 500) return;
        console.error(`Error auto-matching G703 DDA term for "${description}":`, error);
      }
    })();
    
    matchPromises.push(matchPromise);
  }

  // Wait for all matches to complete
  await Promise.all(matchPromises);

  if (matchedCount > 0) {
    console.log(`🎯 G703: Auto-matched ${matchedCount}/${missingDDARows.length} trade DDA terms`);
    // Refresh the grid to show the updates
    g703DataCtr.value++;
  }
}

// Load and apply DDA mappings from completed templates (QA step selections)
async function loadAndApplyTemplateDDAMappings(specificTemplateId = null) {
  if (!selectedId.value) {
    return;
  }

  // 🎯 PREVENT SPAM: Use project-level tracking to only run once per project session
  const templateKey = `template_loaded_${selectedId.value}`;
  if (!specificTemplateId && window[templateKey]) {
    return; // Already loaded template for this project this session
  }
  
  if (!specificTemplateId) {
    window[templateKey] = true;
  }

  console.log(`🎯 Loading template DDA mappings for project: ${selectedId.value}`);
  
  try {
    let templateData;
    
    // If we have a specific template ID (from URL or recent completion), use it directly
    if (specificTemplateId) {
      console.log(`🎯 Using specific template ID: ${specificTemplateId}`);
      const templateResponse = await axios.get(`${apiBase}draw_templates/${specificTemplateId}`, authToken.value);
      templateData = templateResponse.data;
    } else {
      // Fallback: Search for the latest completed template
      console.log('🔍 No specific template ID provided, searching for latest completed template');
      
      const templatesResponse = await axios.get(`${apiBase}draw_templates?project_id=${selectedId.value}&include_all=true`, authToken.value);
      const templates = Array.isArray(templatesResponse.data) ? templatesResponse.data : Object.values(templatesResponse.data || {});
      
      console.log(`🔍 Found ${templates.length} templates:`, templates.map(t => ({ id: t.id, status: t.status, name: t.name })));
      
      const completedTemplates = templates.filter(t => t.status === 'completed');
      console.log(`🔍 Found ${completedTemplates.length} completed templates out of ${templates.length} total`);
      
      const latestTemplate = completedTemplates
        .sort((a, b) => parseInt(b.id) - parseInt(a.id))[0];
      
      if (!latestTemplate) {
        console.log('🔍 No completed templates found');
        return;
      }
      
      console.log(`🎯 Loading DDA mappings from latest template ID: ${latestTemplate.id}`);
      const templateResponse = await axios.get(`${apiBase}draw_templates/${latestTemplate.id}`, authToken.value);
      templateData = templateResponse.data;
    }
    
    // CRITICAL FIX: DDA mappings are stored in template_config, not at root level
    const ddaTermMappings = templateData.template_config?.dda_term_mappings || templateData.dda_term_mappings;
    
    if (!ddaTermMappings || !Array.isArray(ddaTermMappings)) {
      console.log('🔍 Template has no DDA mappings');
      return;
    }
    
    console.log(`🔍 Found ${ddaTermMappings.length} DDA mappings in template config`);
    
    // Extract DDA mappings from the specific template
    let ddaMappings = [];
    ddaTermMappings.forEach(mapping => {
      // Handle both field name formats: qaPreviewRows uses 'description', grid data uses 'scope_detail'
      const description = mapping.scope_detail || mapping.description;
      if (description) {  // CRITICAL FIX: Only require description, not dda_term_id
        ddaMappings.push({
          scope_detail: description,  // Normalize to scope_detail for consistency
          dda_term_id: mapping.dda_term_id || null,  // Allow null to prevent auto-matching
          dda_term: mapping.dda_term || mapping.ddaTerm || null
        });
      }
    });
    
    console.log(`🔍 Extracted ${ddaMappings.length} valid DDA mappings from template:`, ddaMappings.slice(0, 3));
    
    console.log(`🎯 Applying ${ddaMappings.length} DDA mappings to grid data`);
    
    if (ddaMappings.length === 0) {
      return;
    }
    
    // Apply mappings to leafRowData
    if (leafRowData.value && Array.isArray(leafRowData.value)) {
      leafRowData.value.forEach(row => {
        if (row.scope_detail && !row.dda_term_id) {
          const mapping = ddaMappings.find(m => 
            m.scope_detail === row.scope_detail
          );
          if (mapping) {
            // CRITICAL FIX: Apply even null mappings to prevent auto-matching
            row.dda_term_id = mapping.dda_term_id;  // Can be null
            row.dda_term = mapping.dda_term;
            // Mark as processed to prevent auto-matching even if dda_term_id is null
            if (!mapping.dda_term_id) {
              row._skip_auto_match = true;
            }
          }
        }
      });
    }
    
    // Apply mappings to leafParentData  
    if (leafParentData.value && Array.isArray(leafParentData.value)) {
      leafParentData.value.forEach(row => {
        if (row.scope_detail && !row.dda_term_id) {
          const mapping = ddaMappings.find(m => 
            m.scope_detail === row.scope_detail
          );
          if (mapping) {
            // CRITICAL FIX: Apply even null mappings to prevent auto-matching
            row.dda_term_id = mapping.dda_term_id;  // Can be null
            row.dda_term = mapping.dda_term;
            // Mark as processed to prevent auto-matching even if dda_term_id is null
            if (!mapping.dda_term_id) {
              row._skip_auto_match = true;
            }
          }
        }
      });
    }
    
    // CRITICAL FIX: Apply mappings to drawDetailRowData (which auto-matching checks)
    if (drawDetailRowData.value && Array.isArray(drawDetailRowData.value)) {
      drawDetailRowData.value.forEach(row => {
        if (row.scope_detail && !row.dda_term_id) {
          const mapping = ddaMappings.find(m => 
            m.scope_detail === row.scope_detail
          );
          if (mapping) {
            // Apply even null mappings to prevent auto-matching
            row.dda_term_id = mapping.dda_term_id;  // Can be null
            row.dda_term = mapping.dda_term;
            row.dda_term_name = mapping.dda_term; // For consistency
            // Mark as processed to prevent auto-matching even if dda_term_id is null
            if (!mapping.dda_term_id) {
              row._skip_auto_match = true;
            }
          }
        }
      });
    }
    
    console.log(`✅ Applied ${ddaMappings.length} DDA mappings from completed templates`);
    
  } catch (error) {
    console.error('❌ Error loading template DDA mappings:', error);
  }
}

// Helper function to extract trade from contractor name
function extractTradeFromContractor(contractorName) {
  if (!contractorName) return '';
  
  const name = contractorName.toLowerCase();
  
  // Common trade patterns
  const tradePatterns = {
    'masonry': ['masonry', 'mason', 'brick', 'stone'],
    'electrical': ['electrical', 'electric', 'electric', 'electr'],
    'plumbing': ['plumbing', 'plumber', 'pipe', 'hvac'],
    'hvac': ['hvac', 'heating', 'cooling', 'air conditioning', 'mechanical'],
    'flooring': ['flooring', 'floor', 'carpet', 'tile', 'hardwood'],
    'painting': ['painting', 'paint', 'painter'],
    'roofing': ['roofing', 'roof', 'roofer'],
    'concrete': ['concrete', 'cement'],
    'steel': ['steel', 'structural', 'iron'],
    'drywall': ['drywall', 'sheetrock', 'gypsum'],
    'insulation': ['insulation', 'insul'],
    'windows': ['window', 'glazing', 'glass'],
    'doors': ['door', 'hardware'],
    'excavation': ['excavation', 'excavat', 'earth', 'grading'],
    'landscaping': ['landscape', 'landscap']
  };
  
  for (const [trade, patterns] of Object.entries(tradePatterns)) {
    if (patterns.some(pattern => name.includes(pattern))) {
      return trade;
    }
  }
  
  return '';
}

// Enhanced G703-specific OpenAI matching function
async function findBestG703DDATermWithOpenAI(description, contractorName, allDDATerms) {
  // Disabled in UI to prevent 401/429/CORS; keep signature for compatibility
  return null;
}

// Legacy OpenAI implementation (kept for reference but not invoked)
async function _findBestG703DDATermWithOpenAI_impl(description, contractorName, allDDATerms) {
  if (!description || !allDDATerms || allDDATerms.length === 0) return null;
  try {
    // 🔥 FIXED: Use ALL matchable terms, not just first 50 (same fix as Draw Details)
    const standardTerms = prioritizeStandardTerms(allDDATerms); // NO .slice(0, 50)!
    const termsList = standardTerms.map(term => `${term.term}${term.description ? ` (${term.description})` : ''}`).join('\n');
    
    const prompt = `You are an expert at matching construction trade contractors to standardized DDA (Debt Service Coverage Analysis) terms.

Trade Description: "${description}"
Contractor Name: "${contractorName}"

AVAILABLE TERMS (Standard terms listed first - ALWAYS prefer these):
${termsList}

CRITICAL G703 TRADE MATCHING RULES:
1. CONTRACTOR NAME CLUES: Extract trade from contractor name
   - "Brock Masonry" → "masonry" 
   - "S&I Electrical" → "electrical"
   - "ABC Plumbing" → "plumbing"
   - "XYZ HVAC" → "hvac"
2. GENERALIZED MATCHING: Look for broad trade categories, not specific sub-trades
   - "electrical rough-in" → "electrical" (NOT "electrical rough-in")
   - "masonry restoration" → "masonry" (NOT "restoration")
   - "plumbing fixtures" → "plumbing" (NOT "fixtures")
3. STANDARD TERMS FIRST: Always prefer standard terms over any extended/category terms
4. TRADE OVER DESCRIPTION: Prioritize contractor name trade clues over work description
5. CONFIDENCE: Only return matches with confidence > 0.65

STRICT FORMAT - Return ONLY this JSON:
{
  "term": "exact term name from list",
  "confidence": 0.85,
  "reasoning": "why this specific trade match"
}

If no confident match: {"term": null, "confidence": 0.0, "reasoning": "no suitable trade match"}`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer 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`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.1,
        max_tokens: 200
      })
    });

    if (!response.ok) {
      console.error('OpenAI API error:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    const aiResponse = data.choices[0]?.message?.content?.trim();
    
    if (!aiResponse) {
      console.error('No response from OpenAI');
      return null;
    }

    // Parse JSON response - strip markdown code blocks if present
    let cleanResponse = aiResponse.trim();
    if (cleanResponse.startsWith('```json')) {
      cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanResponse.startsWith('```')) {
      cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }
    
    const result = JSON.parse(cleanResponse);
    
    if (!result.term || result.confidence < 0.65) {
      return null;
    }

    // Find the matched term in our list with flexible matching
    let matchedTerm = allDDATerms.find(term => 
      term.term.toLowerCase().trim() === result.term.toLowerCase().trim()
    );
    
    // If exact match fails, try flexible matching
    if (!matchedTerm) {
      // Clean function to normalize terms for comparison
      const cleanTerm = (str) => str.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
      
      // Try flexible matching with cleaned terms
      matchedTerm = allDDATerms.find(term => 
        cleanTerm(term.term) === cleanTerm(result.term)
      );
      
      // Try partial matching if still no match
      if (!matchedTerm) {
        const resultWords = cleanTerm(result.term).split(' ');
        matchedTerm = allDDATerms.find(term => {
          const termWords = cleanTerm(term.term).split(' ');
          // Check if all words from result are in the term or vice versa
          return resultWords.every(word => termWords.some(tWord => tWord.includes(word) || word.includes(tWord))) ||
                 termWords.every(word => resultWords.some(rWord => rWord.includes(word) || word.includes(rWord)));
        });
      }
    }

    if (!matchedTerm) {
      return null;
    }

    return {
      ...matchedTerm,
      confidence: result.confidence,
      method: 'G703-OpenAI',
      reasoning: result.reasoning
    };

  } catch (error) {
    console.error('Error with G703 OpenAI matching:', error);
    return null;
  }
}

// G703-specific DDA term update function (forces G703 API logic regardless of current tab)
async function updateG703DDATermValue(rowData, fieldName, ddaTerm, ddaTermId, suppressUI = false) {
  try {
    // Force G703 API logic - don't rely on btSubMenuSelectedItemId
    const recordId = rowData.id || rowData.g703_id || rowData.general_contractor_g703_id;
    if (!recordId) {
      throw new Error('No valid ID found for G703 record');
    }
    
    const apiEndpoint = `${apiBase}general_contractor_g703s/${recordId}`;
    const requestData = { dda_term_id: ddaTermId };
    
    // Update the local data immediately for better UX
    rowData.dda_term_id = ddaTermId;
    rowData.dda_term = { id: ddaTermId, term: ddaTerm };
    rowData.dda_term_name = ddaTerm;
    
    // Call API to update the DDA term
    const response = await axios.patch(apiEndpoint, requestData, { headers: authToken.value });
    
    if (response.data) {
      if (!suppressUI) {
        // 🧠 LEARNING: Save this correction for future draws
        const description = rowData.description || rowData.scope_detail || '';
        if (description && selectedId.value) {
          saveDDATermCorrection(selectedId.value, description, ddaTermId, ddaTerm);
        }
        
        errorStore.setError('G703 DDA Term Updated', `DDA term updated to "${ddaTerm}" successfully!`, 'success', true);
        // 🔥 NO GRID REFRESH NEEDED: Data already updated locally (lines 15353-15355), grid will update automatically via reactivity
      }
    }
    
    return response;
  } catch (error) {
    console.error('Error updating G703 DDA term:', error);
    if (!suppressUI) {
      errorStore.setError('Update Failed', 'Failed to update DDA term. Please try again.', 'error', true);
    }
    throw error;
  }
}

// Helper function for category total detection
function detectCategoryTotal(description, ddaTerm) {
  const descLower = description.toLowerCase();
  const termLower = ddaTerm.toLowerCase();
  
  // Common total indicators
  const totalIndicators = ['total', 'subtotal', 'sub-total', 'sum', 'aggregate'];
  const hasTotal = totalIndicators.some(indicator => 
    descLower.includes(indicator) || termLower.includes(indicator)
  );
  
  if (hasTotal) {
    // Try to determine category
    if (descLower.includes('hard') || termLower.includes('hard')) {
      return { isTotal: true, category: 'Hard Costs' };
    } else if (descLower.includes('soft') || termLower.includes('soft')) {
      return { isTotal: true, category: 'Soft Costs' };
    } else if (descLower.includes('financing') || termLower.includes('financing')) {
      return { isTotal: true, category: 'Financing' };
    } else if (descLower.includes('acquisition') || termLower.includes('acquisition')) {
      return { isTotal: true, category: 'Acquisition' };
    }
    
    return { isTotal: true, category: 'General' };
  }
  
  return { isTotal: false, category: null };
}

// Map DDA term to chart category based on hierarchy
function getDDATermChartCategory(ddaTermId, ddaTermName) {
  if (!ddaTermId && !ddaTermName) return null; // do not fallback
  
  // Find the term in loaded DDA terms  
  const term = ddaTerms.value?.find(t => t.id === ddaTermId || t.term === ddaTermName);
  if (!term) return null; // do not fallback
  
  // Coerce category id to number to avoid string/number mismatch
  const categoryId = Number(term.dda_term_category_id);
  
  switch (categoryId) {
    case 197: return 'hardCosts';
    case 198: return 'softCosts';
    case 203: return 'acquisition';
    case 204: return 'financing';
    case 210: return 'contingencies';
    default:  return null; // unknown category -> skip
  }
}

// 📊 CATEGORIZED BUDGET EXPORT FUNCTIONALITY

// Export draw details in categorized budget format (like your Excel screenshot)
const exportCategorizedBudget = async (tabType = 'draw_details') => {
  try {
    console.log(`📊 Exporting categorized budget for ${tabType}...`);
    
    // Only support draw details tab (which has the proper budget structure)
    if (tabType !== 'draw_details') {
      alert('Export is only available for Draw Details tab');
      return;
    }
    
    const exportData = drawDetailRowData.value || [];
    const drawNum = drawNumber.value;
    
    if (!exportData || exportData.length === 0) {
      alert('No draw details data available to export');
      return;
    }
    
    console.log(`📊 Processing ${exportData.length} rows for categorized export`);
    
    // Create categorized budget structure
    const categorizedBudget = organizeBudgetByCategories(exportData);
    
    // Generate export file (Excel or CSV)
    const exportResult = generateCategorizedExcel(categorizedBudget, tabType, drawNum);
    
    // Download the file
    let fileName;
    if (exportResult.type === 'xlsx') {
      fileName = `${getProjectName()}_Draw_${drawNum}_Categorized_Budget_${tabType.toUpperCase()}.xlsx`;
      exportResult.workbook.writeFile(fileName);
    } else if (exportResult.type === 'csv') {
      fileName = `${getProjectName()}_Draw_${drawNum}_Categorized_Budget_${tabType.toUpperCase()}.csv`;
      downloadCSV(exportResult.content, fileName);
    }
    
    console.log(`✅ Exported categorized budget: ${fileName}`);
    
  } catch (error) {
    console.error('❌ Failed to export categorized budget:', error);
    alert('Failed to export budget. Please try again.');
  }
};

// Organize budget data by DDA categories (Hard Costs, Soft Costs, etc.)
const organizeBudgetByCategories = (rawData) => {
  const categories = {
    'Hard Costs': {
      items: [],
      subtotal: { original: 0, current: 0, billed: 0, remaining: 0, total_adjustments: 0, previous_adjustments: 0, current_adjustments: 0 }
    },
    'Soft Costs': {
      items: [],
      subtotal: { original: 0, current: 0, billed: 0, remaining: 0, total_adjustments: 0, previous_adjustments: 0, current_adjustments: 0 }
    },
    'Acquisition Costs': {
      items: [],
      subtotal: { original: 0, current: 0, billed: 0, remaining: 0, total_adjustments: 0, previous_adjustments: 0, current_adjustments: 0 }
    },
    'Financing Costs': {
      items: [],
      subtotal: { original: 0, current: 0, billed: 0, remaining: 0, total_adjustments: 0, previous_adjustments: 0, current_adjustments: 0 }
    },
    'Contingencies': {
      items: [],
      subtotal: { original: 0, current: 0, billed: 0, remaining: 0, total_adjustments: 0, previous_adjustments: 0, current_adjustments: 0 }
    },
    'Uncategorized': {
      items: [],
      subtotal: { original: 0, current: 0, billed: 0, remaining: 0, total_adjustments: 0, previous_adjustments: 0, current_adjustments: 0 }
    }
  };
  
  // Process each row and categorize
  rawData.forEach(row => {
    let categoryName = 'Uncategorized';
    
    // Determine category based on DDA term
    if (row.dda_term_category_id) {
      const categoryMap = getDDACategoryNameById(row.dda_term_category_id);
      switch (categoryMap) {
        case 'hardCosts':
          categoryName = 'Hard Costs';
          break;
        case 'softCosts':
          categoryName = 'Soft Costs';
          break;
        case 'acquisition':
          categoryName = 'Acquisition Costs';
          break;
        case 'financing':
          categoryName = 'Financing Costs';
          break;
        case 'contingencies':
          categoryName = 'Contingencies';
          break;
      }
    } else if (row.description) {
      // Fallback classification based on description
      const desc = row.description.toLowerCase();
      if (desc.includes('electrical') || desc.includes('plumbing') || desc.includes('hvac') || 
          desc.includes('mechanical') || desc.includes('construction') || desc.includes('trade')) {
        categoryName = 'Hard Costs';
      } else if (desc.includes('architect') || desc.includes('design') || desc.includes('engineering') ||
                 desc.includes('consultant') || desc.includes('legal') || desc.includes('permit')) {
        categoryName = 'Soft Costs';
      } else if (desc.includes('acquisition') || desc.includes('closing') || desc.includes('property')) {
        categoryName = 'Acquisition Costs';
      } else if (desc.includes('financing') || desc.includes('loan') || desc.includes('interest')) {
        categoryName = 'Financing Costs';
      } else if (desc.includes('contingency') || desc.includes('contingencies')) {
        categoryName = 'Contingencies';
      }
    }
    
    // Add item to category
    const budgetItem = {
      description: row.description || 'No Description',
      original_budget: parseFloat(row.original_budget || 0),
      current_budget: parseFloat(row.current_budget || 0),
      billed_to_date: parseFloat(row.total_completed_to_date || 0),
      remaining: parseFloat(row.remaining_budget || (row.current_budget || 0) - (row.total_completed_to_date || 0)),
      percentage: calculatePercentage(row.total_completed_to_date || 0, row.current_budget || 0),
      total_adjustments: parseFloat(row.total_adjustments || 0),
      previous_adjustments: parseFloat(row.previous_adjustments || 0), // Reallocation Last Period
      current_adjustments: parseFloat(row.current_adjustments || 0)    // Reallocations This Period
    };
    
    categories[categoryName].items.push(budgetItem);
    
    // Update category totals
    categories[categoryName].subtotal.original += budgetItem.original_budget;
    categories[categoryName].subtotal.current += budgetItem.current_budget;
    categories[categoryName].subtotal.billed += budgetItem.billed_to_date;
    categories[categoryName].subtotal.remaining += budgetItem.remaining;
    categories[categoryName].subtotal.total_adjustments += budgetItem.total_adjustments;
    categories[categoryName].subtotal.previous_adjustments += budgetItem.previous_adjustments;
    categories[categoryName].subtotal.current_adjustments += budgetItem.current_adjustments;
  });
  
  return categories;
};

// Generate Excel workbook in categorized format
const generateCategorizedExcel = (categorizedData, tabType, drawNum) => {
  // Check if XLSX library is available
  if (typeof window.XLSX === 'undefined') {
    console.warn('⚠️ XLSX library not found, generating CSV instead');
    return generateCategorizedCSV(categorizedData, tabType, drawNum);
  }
  
  const XLSX = window.XLSX;
  const workbook = XLSX.utils.book_new();
  const worksheet = [];
  
  // Header
  worksheet.push([]);
  worksheet.push([`Project: ${getProjectName()}`]);
  worksheet.push([`Draw ${drawNum} - ${tabType.toUpperCase()} Categorized Budget`]);
  worksheet.push([`Export Date: ${new Date().toLocaleDateString()}`]);
  worksheet.push([]);
  
  // Column headers
  worksheet.push([
    'DESCRIPTION',
    'ORIGINAL',
    'Total Reallocations',
    'CURRENT',
    'BILLED TO DATE',
    'Remaining',
    '%',
    'Reallocation Last Period',
    'Reallocations This Period'
  ]);
  
  let totalOriginal = 0;
  let totalCurrent = 0;
  let totalBilled = 0;
  let totalRemaining = 0;
  
  // Process each category
  Object.entries(categorizedData).forEach(([categoryName, categoryData]) => {
    if (categoryData.items.length === 0) return; // Skip empty categories
    
    // Category header
    worksheet.push([]);
    worksheet.push([categoryName]);
    
    // Category items
    categoryData.items.forEach(item => {
      worksheet.push([
        item.description,
        item.original_budget,
        item.total_adjustments || '', // Total Reallocations (current_budget - original_budget)
        item.current_budget,
        item.billed_to_date,
        item.remaining,
        `${item.percentage}%`,
        item.previous_adjustments || 0, // Reallocation Last Period
        item.current_adjustments || 0   // Reallocations This Period
      ]);
    });
    
    // Category subtotal
    worksheet.push([
      `${categoryName} Subtotal`,
      categoryData.subtotal.original,
      categoryData.subtotal.total_adjustments || '',
      categoryData.subtotal.current,
      categoryData.subtotal.billed,
      categoryData.subtotal.remaining,
      `${calculatePercentage(categoryData.subtotal.billed, categoryData.subtotal.current)}%`,
      categoryData.subtotal.previous_adjustments || 0,
      categoryData.subtotal.current_adjustments || 0
    ]);
    
    // Add to totals
    totalOriginal += categoryData.subtotal.original;
    totalCurrent += categoryData.subtotal.current;
    totalBilled += categoryData.subtotal.billed;
    totalRemaining += categoryData.subtotal.remaining;
  });
  
  // Project total
  worksheet.push([]);
  worksheet.push([
    'TOTAL PROJECT COSTS',
    totalOriginal,
    totalCurrent - totalOriginal, // Total Reallocations
    totalCurrent,
    totalBilled,
    totalRemaining,
    `${calculatePercentage(totalBilled, totalCurrent)}%`,
    '', // Previous adjustments (could be calculated if needed)
    ''  // Current adjustments (could be calculated if needed)
  ]);
  
  // Convert to Excel
  const ws = XLSX.utils.aoa_to_sheet(worksheet);
  XLSX.utils.book_append_sheet(workbook, ws, 'Categorized Budget');
  
  return { type: 'xlsx', workbook: workbook };
};

// Helper function to calculate percentage
const calculatePercentage = (billed, current) => {
  if (!current || current === 0) return 0;
  return Math.round((billed / current) * 100);
};

// Helper function to format currency values to 2 decimal places
const formatCurrency = (value) => {
  const num = parseFloat(value || 0);
  return num.toFixed(2);
};

// Generate CSV fallback when XLSX is not available
const generateCategorizedCSV = (categorizedData, tabType, drawNum) => {
  let csvContent = '';
  
  // Header
  csvContent += `Project: ${getProjectName()}\n`;
  csvContent += `Draw ${drawNum} - ${tabType.toUpperCase()} Categorized Budget\n`;
  csvContent += `Export Date: ${new Date().toLocaleDateString()}\n\n`;
  
  // Column headers
  csvContent += 'DESCRIPTION,ORIGINAL,Total Reallocations,CURRENT,BILLED TO DATE,Remaining,%,Reallocation Last Period,Reallocations This Period\n';
  
  let totalOriginal = 0;
  let totalCurrent = 0;
  let totalBilled = 0;
  let totalRemaining = 0;
  
  // Process each category
  Object.entries(categorizedData).forEach(([categoryName, categoryData]) => {
    if (categoryData.items.length === 0) return; // Skip empty categories
    
    // Category header
    csvContent += `\n${categoryName}\n`;
    
    // Category items
    categoryData.items.forEach(item => {
      csvContent += `"${item.description}",${formatCurrency(item.original_budget)},${formatCurrency(item.total_adjustments)},${formatCurrency(item.current_budget)},${formatCurrency(item.billed_to_date)},${formatCurrency(item.remaining)},${item.percentage}%,${formatCurrency(item.previous_adjustments)},${formatCurrency(item.current_adjustments)}\n`;
    });
    
    // Category subtotal
    csvContent += `"${categoryName} Subtotal",${formatCurrency(categoryData.subtotal.original)},${formatCurrency(categoryData.subtotal.total_adjustments)},${formatCurrency(categoryData.subtotal.current)},${formatCurrency(categoryData.subtotal.billed)},${formatCurrency(categoryData.subtotal.remaining)},${calculatePercentage(categoryData.subtotal.billed, categoryData.subtotal.current)}%,${formatCurrency(categoryData.subtotal.previous_adjustments)},${formatCurrency(categoryData.subtotal.current_adjustments)}\n`;
    
    // Add to totals
    totalOriginal += categoryData.subtotal.original;
    totalCurrent += categoryData.subtotal.current;
    totalBilled += categoryData.subtotal.billed;
    totalRemaining += categoryData.subtotal.remaining;
  });
  
  // Project total
  csvContent += `\n"TOTAL PROJECT COSTS",${formatCurrency(totalOriginal)},${formatCurrency(totalCurrent - totalOriginal)},${formatCurrency(totalCurrent)},${formatCurrency(totalBilled)},${formatCurrency(totalRemaining)},${calculatePercentage(totalBilled, totalCurrent)}%,0.00,0.00\n`;
  
  return { type: 'csv', content: csvContent };
};

// Download CSV file
const downloadCSV = (content, fileName) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper function to get project name
const getProjectName = () => {
  return projectInformation.value?.name || `Project ${selectedId.value}` || 'Unknown Project';
};

// 🤖 DRAW INSIGHTS: Open DiDiAI panel and send insights
async function openDrawInsightsModal() {
  console.log('🤖 Opening Draw Insights in DiDiAI...');
  
  isLoadingInsights.value = true;
  
  try {
    const projectId = selectedId.value;
    const drawNumber = globalDrawFilter.value;
    
    console.log(`🤖 Fetching insights for Project ${projectId}, Draw ${drawNumber}`);
    
    // Open DiDiAI panel first
    didaiStore.openPanel();
    
    // Add a loading message to DiDiAI
    didaiStore.addMessage({
      type: 'ai',
      text: `🔍 Analyzing Draw #${drawNumber} for ${projectName.value}...`,
      timestamp: new Date().toLocaleTimeString()
    });
    
    const response = await axios.get(`${apiBase}projects/${projectId}/draw_insights`, {
      params: { draw_number: drawNumber },
      headers: authToken.value
    });
    
    console.log('✅ Draw insights received:', response.data);
    
    const insights = response.data.insights || 'No insights generated.';
    const dataSummary = response.data.data_summary || {};
    
    // Format the insights message with data summary
    let formattedInsights = `## 📊 Draw #${drawNumber} Insights\n\n`;
    
    if (dataSummary.draw_details_count || dataSummary.g702_count || dataSummary.g703_count) {
      formattedInsights += `**Data Summary:**\n`;
      if (dataSummary.draw_details_count) formattedInsights += `- Draw Details: ${dataSummary.draw_details_count} line items\n`;
      if (dataSummary.g702_count) formattedInsights += `- G702 Records: ${dataSummary.g702_count}\n`;
      if (dataSummary.g703_count) formattedInsights += `- G703 Records: ${dataSummary.g703_count}\n`;
      if (dataSummary.lien_waivers_count) formattedInsights += `- Lien Waivers: ${dataSummary.lien_waivers_count}\n`;
      if (dataSummary.invoices_count) formattedInsights += `- Invoices: ${dataSummary.invoices_count}\n`;
      formattedInsights += `\n`;
    }
    
    formattedInsights += insights;
    
    // Add the insights to DiDiAI
    didaiStore.addMessage({
      type: 'ai',
      text: formattedInsights,
      timestamp: new Date().toLocaleTimeString()
    });
    
  } catch (error) {
    console.error('❌ Failed to fetch draw insights:', error);
    
    // Show error in DiDiAI
    didaiStore.addMessage({
      type: 'ai',
      text: `❌ Failed to generate insights: ${error.response?.data?.error || 'Please try again.'}`,
      timestamp: new Date().toLocaleTimeString()
    });
  } finally {
    isLoadingInsights.value = false;
  }
}

async function autoMatchAllDDATerms() {
  isAutoMatchingDDA.value = true;
  try {
    const projectId = selectedId.value;
    const drawNumber = globalDrawFilter.value;
    
    if (!projectId || !drawNumber) {
      errorStore.setError('Selection Error', 'Please select a project and draw first.', 'error', true);
      return;
    }
    
    let allDDATerms = [];
    try {
      const response = await axios.get(`${apiBase}dda_terms`, {
        ...authToken.value,
        params: { autocomplete: true, per_page: 1000 }
      });
      
      if (response.data && Array.isArray(response.data)) {
        allDDATerms = response.data.map(term => ({
          id: term.id,
          term: term.term || term.name,
          dda_term_category_id: term.dda_term_category_id,
          is_standard: term.is_standard,
          primary_dda_term_id: term.primary_dda_term_id
        }));
      }
    } catch (error) {
      errorStore.setError('Error', `Failed to load DDA terms: ${error.message}`, 'error', true);
      return;
    }
    
    if (allDDATerms.length === 0) {
      errorStore.setError('No DDA Terms', 'DDA terms not loaded.', 'error', true);
      return;
    }
    
    let rowsToProcess = [];
    let apiEndpointBase = '';
    
    if (btSubMenuSelectedItemId.value === 1) {
      rowsToProcess = drawDetailRowData.value;
      apiEndpointBase = `${apiBase}draw_details`;
    } else if (btSubMenuSelectedItemId.value === 2) {
      rowsToProcess = g703RowData.value;
      apiEndpointBase = `${apiBase}general_contractor_g703s`;
    } else {
      errorStore.setError('Unsupported Tab', 'DDA term auto-matching is only available in Draw Details or G703 tabs.', 'info', true);
      return;
    }
    
    // Helper: Detect if a description is a subtotal/total row
    const isSubtotalRow = (description) => {
      if (!description) return false;
      const desc = description.toLowerCase();
      return desc.includes('subtotal') || 
             desc.includes('sub-total') || 
             desc.includes('sub total') ||
             desc.match(/^total[:\s]/i) ||
             desc.match(/total$/i) ||
             desc.match(/grand[\s-]?total/i) ||
             desc === 'total';
    };

    const filteredRows = rowsToProcess.filter(row => {
      // Skip if no description
      if (!row.description || row.description.trim().length === 0) return false;
      return true;
    });
    
    // Build terms list ONCE and reuse for all rows - ALL standard terms (OpenAI has 200M TPM!)
    const standardTerms = allDDATerms; // Include both standard + curated extended terms (Electrical, Plumbing, HVAC, etc.)
    const termsListForClaude = standardTerms.map(term => {
      let termEntry = `${term.id}: ${term.term}`;
      const categoryName = ddaTermCategories.value.find(cat => cat.id === term.dda_term_category_id)?.name;
      if (categoryName) termEntry += ` [${categoryName}]`;
      return termEntry;
    }).join('\n');
    
    console.log(`📋 Built terms list with ${standardTerms.length} standard + curated extended terms (reusing for all rows)`);
    
    let matchedCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < filteredRows.length; i += 10) {
      const batch = filteredRows.slice(i, i + 10);
      console.log(`🎯 Processing batch ${Math.ceil((i + 1) / 10)}/${Math.ceil(filteredRows.length / 10)}`);
      
      await Promise.all(batch.map(async (row) => {
        try {
          const descLower = (row.description || '').toLowerCase().trim();

          // 🎯 Rule-based total row matching — always wins over AI
          const totalMatch = ruleBasedTotalRowMatch(descLower);
          if (totalMatch) {
            console.log(`🎯 RULE-BASED MATCH: "${row.description}" → "${totalMatch.term}"`);
            await axios.patch(`${apiEndpointBase}/${row.id}`, {
              dda_term_id: totalMatch.id
            }, { headers: authToken.value });
            matchedCount++;
            return;
          }

          console.log(`🔍 Matching "${row.description}"...`);
          const response = await axios.post(`${apiBase}projects/${projectId}/claude_match_dda_term`, {
            description: row.description,
            terms_list: termsListForClaude
          }, { headers: authToken.value });
          
          console.log(`✅ Claude response for "${row.description}":`, response.data);
          console.log(`   → term_id: ${response.data?.result?.term_id}, confidence: ${response.data?.result?.confidence}`);
          
          if (response.data?.success && response.data.result?.term_id && response.data.result.confidence >= 0.60) {
            console.log(`✅ Matched "${row.description}" to term ID ${response.data.result.term_id} (confidence: ${response.data.result.confidence})`);
            await axios.patch(`${apiEndpointBase}/${row.id}`, {
              dda_term_id: response.data.result.term_id
            }, { headers: authToken.value });
            matchedCount++;
          } else {
            console.log(`⚠️ No confident match for "${row.description}" (confidence: ${response.data?.result?.confidence || 'N/A'})`);
            errorCount++;
          }
        } catch (matchError) {
          console.error(`❌ Error matching "${row.description}":`, matchError);
          errorCount++;
        }
      }));
      
      if (i + 10 < filteredRows.length) await sleep(500);
    }
    
    errorStore.setError('Matching Complete', `Matched: ${matchedCount}, Errors/No Match: ${errorCount}`, 'success', true);
    
    if (btSubMenuSelectedItemId.value === 1) {
      await fetchDrawDetailGridData();
    } else if (btSubMenuSelectedItemId.value === 2) {
      await fetchG703DrawDetailGridData();
    }
    
  } catch (error) {
    errorStore.setError('Auto-Match Failed', `Failed: ${error.message}`, 'error', true);
    if (datadogRum) {
      datadogRum.addError(error, { context: 'auto_match_dda_terms_failed' });
    }
  } finally {
    isAutoMatchingDDA.value = false;
    // Track in Datadog
    if (datadogRum) {
      datadogRum.addAction('dda_terms_auto_matched', {
        project_id: selectedId.value,
        draw_number: globalDrawFilter.value
      });
    }
  }
}

// 📊 SPD REPORT: Generate comprehensive SPD report
async function generateSPDReport() {
  console.log('📊 Generating SPD Report...');
  
  isGeneratingSPDReport.value = true;
  spdReportError.value = '';
  
  try {
    const projectId = selectedId.value;
    const drawNumber = globalDrawFilter.value;
    
    if (!projectId || !drawNumber) {
      errorStore.setError('Selection Error', 'Please select a project and draw first.', 'error', true);
      return;
    }
    
    console.log(`📊 Fetching SPD report for Project ${projectId}, Draw ${drawNumber}`);
    
    const response = await axios.get(`${apiBase}projects/${projectId}/generate_spd_report`, {
      params: { draw_number: drawNumber },
      headers: authToken.value
    });
    
    console.log('✅ SPD Report received:', response.data);
    console.log('📊 Report has narrative:', !!response.data.narrative);
    console.log('📊 Report has metadata:', !!response.data.report_metadata);
    
    // Check if we have the required data (narrative and metadata)
    if (response.data.narrative && response.data.report_metadata) {
      spdReportData.value = response.data;
      showSPDReportModal.value = true;
      console.log('✅ Opening SPD Report modal');
      
      // Track in Datadog
      if (datadogRum) {
        datadogRum.addAction('spd_report_generated', {
          project_id: projectId,
          draw_number: drawNumber,
          project_name: projectName.value
        });
      }
    } else if (response.data.error) {
      spdReportError.value = response.data.error;
      errorStore.setError('Report Generation Failed', spdReportError.value, 'error', true);
    } else {
      console.error('❌ Invalid response format:', response.data);
      spdReportError.value = 'Invalid response format from server';
      errorStore.setError('Report Generation Failed', spdReportError.value, 'error', true);
    }
    
  } catch (error) {
    console.error('❌ Failed to generate SPD report:', error);
    spdReportError.value = error.response?.data?.error || 'Failed to generate SPD report. Please try again.';
    errorStore.setError('Report Generation Failed', spdReportError.value, 'error', true);
  } finally {
    isGeneratingSPDReport.value = false;
  }
}

// 📊 SPD REPORT: Close modal
function closeSPDReportModal() {
  showSPDReportModal.value = false;
  spdReportData.value = null;
  spdReportError.value = '';
}

// 📊 CONTINGENCY SETTING: Update project setting
async function updateContingencySetting() {
  try {
    const projectId = selectedId.value;
    if (!projectId) return;
    
    console.log(`📊 Updating contingency setting for project ${projectId}: ${contingenciesExcludedFromBudget.value}`);
    
    await axios.put(`${apiBase}projects/${projectId}`, {
      project: {
        contingencies_excluded_from_budget: contingenciesExcludedFromBudget.value
      }
    }, {
      headers: authToken.value
    });
    
    // Update the local project data
    if (projectInformation.value) {
      projectInformation.value.contingencies_excluded_from_budget = contingenciesExcludedFromBudget.value;
    }
    
    console.log('✅ Contingency setting updated successfully');
    
  } catch (error) {
    console.error('❌ Failed to update contingency setting:', error);
    errorStore.setError('Update Failed', 'Failed to update contingency setting', 'error', true);
  }
}

// 📊 SPD REPORT: Copy report to clipboard
async function copySPDReportToClipboard() {
  if (!spdReportData.value) return;
  
  try {
    const reportText = formatSPDReportForClipboard(spdReportData.value);
    await navigator.clipboard.writeText(reportText);
    // Silently copied - no popup confirmation
  } catch (error) {
    console.error('❌ Failed to copy to clipboard:', error);
    errorStore.setError('Copy Failed', 'Failed to copy report to clipboard', 'error', true);
  }
}

// 📊 SPD REPORT: Export to Word
async function exportSPDReportToWord() {
  if (!spdReportData.value) return;
  
  try {
    console.log('📄 Exporting SPD Report to Word (backend generation)...');
    
    const projectId = selectedId.value;
    const drawNumber = globalDrawFilter.value;
    
    const response = await axios.get(
      `${apiBase}projects/${projectId}/export_spd_report_docx`,
      {
        params: { draw_number: drawNumber },
        headers: authToken.value,
        responseType: 'blob'
      }
    );
    
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from backend response headers (Content-Disposition)
    const contentDisposition = response.headers['content-disposition'];
    let filename = `SPD_Report_Draw_${drawNumber}.docx`; // fallback
    if (contentDisposition) {
      const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    }
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ SPD Report exported successfully');
  } catch (error) {
    console.error('❌ Failed to export to Word:', error);
    errorStore.setError('Export Failed', 'Failed to export report to Word: ' + error.message, 'error', true);
  }
}

// 📊 SPD REPORT: Format report for clipboard
function formatSPDReportForClipboard(reportData) {
  const meta = reportData.report_metadata;
  const narrative = reportData.narrative;
  const budget = reportData.budget_table;
  const g702 = reportData.payment_application.g702;
  
  let text = `SPD ADVISORY GROUP REPORT\n`;
  text += `${'='.repeat(80)}\n\n`;
  text += `PROJECT: ${meta.project_name}\n`;
  text += `ADDRESS: ${meta.project_address}\n`;
  text += `DRAW NUMBER: ${meta.draw_number}\n`;
  text += `PERIOD THROUGH: ${meta.period_through}\n`;
  text += `REPORT DATE: ${meta.report_date}\n\n`;
  text += `${'='.repeat(80)}\n\n`;
  text += narrative;
  text += `\n\n${'='.repeat(80)}\n\n`;
  text += `PAYMENT APPLICATION SUMMARY\n\n`;
  text += `Contractor: ${g702.contractor_name || 'N/A'}\n`;
  text += `Current Payment Due: $${(g702.current_payment_due || 0).toLocaleString()}\n`;
  text += `Total Completed & Stored: $${(g702.total_completed_stored || 0).toLocaleString()}\n`;
  text += `Total Retainage: $${(g702.total_retainage || 0).toLocaleString()}\n`;
  text += `Percent Complete: ${g702.percent_complete || 0}%\n`;
  
  return text;
}

// 📊 SPD REPORT: Format narrative with HTML
function formatNarrative(narrative) {
  if (!narrative) return '';
  
  // Convert markdown-style formatting to HTML
  let html = narrative
    // Headers
    .replace(/^## (.*?)$/gm, '<h4>$1</h4>')
    .replace(/^# (.*?)$/gm, '<h3>$1</h3>')
    // Bold text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Bullet points
    .replace(/^- (.*?)$/gm, '<li>$1</li>')
    .replace(/^• (.*?)$/gm, '<li>$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
  
  // Wrap in paragraphs
  html = '<p>' + html + '</p>';
  
  // Wrap lists in ul tags
  html = html.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');
  
  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p><br><\/p>/g, '');
  
  return html;
}


// Functions
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

// 🔧 Column Remap Modal Styles
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.column-remap-modal {
  background: white;
  border-radius: 12px;
  width: 600px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #0A1628, #1a2d4d);
  color: white;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.modal-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
  line-height: 1.5;
}

.column-mapping-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mapping-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.grid-column-name {
  flex: 1;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.arrow {
  color: #3B5CCC;
  font-size: 18px;
  font-weight: bold;
}

.source-field-select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3B5CCC;
    box-shadow: 0 0 0 2px rgba(59, 92, 204, 0.1);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.modal-cancel-btn {
  background: white;
  color: #666;
  border: 1px solid #ccc;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background: #f0f0f0;
  }
}

.modal-apply-btn {
  background: linear-gradient(135deg, #3B5CCC, #7DAAF8);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: linear-gradient(135deg, #2a4ab8, #6999e7);
  }
}

.row-total-any { background-color: #f5f5f5 !important; }
.row-total-hard { background-color: #3B5CCC20 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-soft { background-color: #7DAAF820 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-acq { background-color: #52596D20 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-financing { background-color: #9AA1AF20 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-contingency { background-color: #d3d3d320 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-totals-hard { background-color: #3B5CCC20 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-totals-soft { background-color: #7DAAF820 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-totals-financing { background-color: #9AA1AF20 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-totals-acq { background-color: #52596D20 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }
.row-total-totals-contingency { background-color: #d3d3d320 !important; font-weight: bold; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #333; }

// 🆕 LW Grid Header Styles
:deep(.header-scopes-of-work) { background-color: #3B5CCC !important; color: white !important; font-weight: 600; }
:deep(.header-expenditure) { background-color: #52596D !important; color: white !important; font-weight: 600; }
:deep(.header-lw-tracking) { background-color: #7DAAF8 !important; color: white !important; font-weight: 600; }
:deep(.header-retainage) { background-color: #7DAAF8 !important; color: white !important; font-weight: 600; }

// 🆕 Retainage Tab Styles
.retainage-tab-container {
  width: 100%;
}

.retainage-header-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;
}

.retainage-title-left {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.retainage-title-right {
  font-weight: 600;
  font-size: 14px;
  color: #7DAAF8;
}

// 🆕 DiDi Insights Card Styles
.didi-insights-card {
  background: linear-gradient(180deg, #1e3a5f 0%, #1e3a5f 48px, #e8f0fa 48px, #dce8f5 100%) !important;
  border-radius: 12px !important;
  padding: 0 !important;
  box-shadow: 0 4px 24px rgba(30, 58, 95, 0.18) !important;
  border: none !important;
  overflow: hidden;
}

.didi-insights-card .chart-main-title {
  background: linear-gradient(135deg, #1e3a5f 0%, #2a4a6f 100%);
  padding: 14px 20px !important;
  margin: 0 !important;
  border-radius: 12px 12px 0 0;
}

.didi-insights-card .variance-display-container {
  padding: 16px 20px 20px 20px;
  background: linear-gradient(180deg, #e8f0fa 0%, #dce8f5 100%);
}

.didi-insights-card .variance-content {
  background: transparent !important;
}

.didi-insights-card .section-title {
  color: #1e3a5f !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  margin-bottom: 12px !important;
  padding-bottom: 8px !important;
  border-bottom: 1px solid rgba(30, 58, 95, 0.15) !important;
}

.didi-insights-card .subsection-title {
  color: #2a4a6f !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.didi-insights-card .item-label {
  color: #3a5a7f !important;
}

.didi-insights-card .item-amount {
  color: #1e6091 !important;
  font-weight: 600 !important;
}

.didi-insights-title {
  font-size: 22px !important;
  font-weight: 700 !important;
  font-style: italic !important;
  color: #7dd3c0 !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

// ➕ Add Project Total Button Styles
.add-project-total-container {
  margin: 10px 0;
  padding: 12px;
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-project-total-btn {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(74, 144, 226, 0.2);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #357abd 0%, #2a5f8f 100%);
    box-shadow: 0 4px 8px rgba(74, 144, 226, 0.3);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(74, 144, 226, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.total-hint {
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.individual-page-container {
  display: flex;
  margin-top: 53px;
  font-family: 'Inter';

  .project-name {
    font-size: 18px;
    color: $navyBlue;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.breadcrumbs {
  font-size: 14px;
  color: #8C8D92;
  
  .breadcrumb-link {
    cursor: pointer;
    transition: color 0.2s ease;
    
    &:hover {
      color: #4A90E2;
      text-decoration: underline;
    }
  }
}
.project-details-wrapper {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
}
.project-details {
  overflow-y: auto;
  overflow-x: hidden;
  height: 50%;
  padding-bottom: 32rem;
  padding-right: 0.75rem;
}

.project-details::-webkit-scrollbar {
  width: 5px;
  height: 8px;
  background-color: $white1;
}

.project-details::-webkit-scrollbar-thumb {
  background: $lightGray1;
}

.individual-page-sidebar {
  position: fixed;
  display: flex;
  height: 100vh;
  top: 50px;
  left: 0;
  background: $white1;
  box-shadow: 6px 0 18px rgba(0, 0, 0, 0.06);
  background-color: $white1;
  padding: 20px 20px 20px 30px;
  z-index: 9999; // Do not let ag-grid go on top if scrolling
}
.individual-page-sidebar.open {
  width: 400px;
  transition: width 0.5s ease;
}

.individual-page-sidebar.closed {
  width: 20px;
  padding: 0;
  transition: width 0.5s ease;
}

.hidden {
  display: none;
}

.individal-page-content {
  padding: 25px 30px;
  width: 100%;
}

.individal-page-content.small {
  margin-left: 10px;
}

.individal-page-content.large {
  margin-left: 430px;
}

.individual-page-title {
  font-size: 24px;
  font-weight: bold;
  color: $navyBlue;
  padding: 20px 0;
}

// Old single image display (replaced with carousel)
.project-img {
  margin: 10px 0px 10px 0px;
  border: .5px solid $lightGray1;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  width: 399px;
  height: 350px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
  }
}

// New carousel image display
.project-img-carousel {
  margin: 10px 0px 10px 0px;
  width: 399px;
}

.carousel-container {
  position: relative;
  border: .5px solid $lightGray1;
  border-radius: 2px;
  overflow: hidden;
}

.carousel-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 350px;
}

.main-image-container {
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
}

.image-background {
  width: 100%;
  height: 100%;
  background-color: rgb(8, 27, 46); // DDA exact color R8 G27 B46
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  text-align: center;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  .placeholder-icon {
    font-size: 48px;
    margin-bottom: 10px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; // Preserve aspect ratio
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
  }
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(8, 27, 46, 0.8); // DDA exact color with transparency
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(8, 27, 46, 1);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.carousel-arrow-left {
  left: 10px;
}

.carousel-arrow-right {
  right: 10px;
}

.image-counter {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(8, 27, 46, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

// Image title styles removed - no longer showing filename

.project-img-summary {
  padding-bottom: 15px;
  border-bottom: 2px solid $lightGray1;

  .project-img-summary-item {
    display: flex;
    justify-content: space-between;
    padding-bottom: 3px;
    font-size: 14px;

    span {
      color: $navyBlue;
      text-align: right;
    }
  }
}

.tile {
  background-color: $white1;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 5px 5px 5px 0;
  border-radius: 8px;
  display: inline-block;
  width: calc(33.333% - 20px);
  box-sizing: border-box;
  padding: 20px;
  
  // ✅ RESPONSIVE DESIGN: Optimize for larger screens
  @media (min-width: 1440px) {
    width: calc(33.333% - 15px); // Slightly less margin on large screens
    padding: 15px; // Reduce padding to save space
    margin: 3px 3px 3px 0; // Tighter margins
  }
  
  @media (min-width: 1920px) {
    width: calc(33.333% - 12px); // Even tighter on very large screens
    padding: 12px; // More compact padding
    margin: 2px 2px 2px 0; // Minimal margins
  }
}

// 3 Dotted Menu Style - Start
.menu-container {
  position: relative;
  display: inline-block;
}

.menu-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
}

.menu-content {
  position: absolute;
  right: 0;
  background-color: #f9f9f9;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.menu-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-content ul li {
  padding: 12px 16px;
  cursor: pointer;
}

.menu-content ul li:hover {
  background-color: #f1f1f1;
}

// 3 Dotted Menu Style - End

// Sidebar Menu Style - Start
.sidebar-menu {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid $mediumGray1;
  font-size: 12px;
  font-weight: lighter;

  div {
    cursor: pointer;
    padding: 5px 5px 5px 0px;
    margin-right: 10px;
  }
}

.sidebar-menu-selected {
  font-weight: bold;
  border-bottom: 3px solid $brightBlue1;
  color: $brightBlue1;
}

// Sidebar Menu Style - End

// Accordion Panel Style - Start
.accordion-panel-header {
  display: flex;
  align-items: center;
  position: relative;
  width: 97%;
  height: 27px;
  margin-top: 5px;
  margin-bottom: 10px;

  label {
    font-size: 16px;
  }
}

// Accordion Panel Style - End

// Custom Select Style - Start
.custom-select {
  position: relative;
  font-family: inherit;
  display: flex;
  align-items: center;
  font-size: 12px;

  select {
    border: 0;
    color: $brightBlue1;
    font-size: 1rem;
    background: none;
  }

  select:focus {
    border-color: $brightBlue1;
    outline: $brightBlue1;
  }
}

// Custom Select Style - End

// Building Details Style - Start
.building-details-container {
  padding: 15px 0;
  border-bottom: 2px solid $lightGray1;
}

.building-details-table {
  width: 100%;
  text-align: right;
  color: $navyBlue;
  font-size: 14px;

  .left-align {
    text-align: left;
  }

  .use-types {
    padding-left: 10px;
    color: #000;

  }

  .total {
    padding: 0;
    color: #000;
  }
}

.building-details-summary {
  padding: 10px 0;

  .building-details-summary-item {
    display: flex;
    justify-content: space-between;
    padding-bottom: 3px;
    font-size: 14px;

    span {
      color: $navyBlue;
    }
  }
}

.amenities-container {
  display: flex;
  flex-direction: column;
}

// Building Details Style - End

// Construction Details Style - Start
.construction-details-container {
  padding: 15px 0;
  border-bottom: 2px solid $lightGray1;
}

.construction-details-summary {
  padding: 10px 0;

  .construction-details-summary-item {
    display: grid;
    grid-template-columns: 55% 45%;
    justify-content: space-between;
    padding-bottom: 3px;
    font-size: 14px;

    span {
      color: $navyBlue;
      text-align: right;
    }
  }
}

.construction-details-summary-item-header {
  padding-bottom: 20px !important;
}

.construction-type-container {
  padding-top: 20px;
}

// Construction Details Style - End

// Individual Page Menu Style - Start
.individual-page-sub-menu {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid $mediumGray1;
  font-size: 16px;
  font-weight: lighter;

  div {
    cursor: pointer;
    padding: 10px 0px;
    margin-right: 50px;
  }
}

.individual-page-sub-menu-selected {
  font-weight: bold;
  border-bottom: 3px solid $brightBlue1;
  color: $brightBlue1;
}

// Indvidual Page Menu Style - End

// Budget Summary Style - Start
.budget-summary-header-container {
  margin-top: 30px;
  display: flex;
  justify-content: flex-start; // ✅ FIXED: Keep controls on LEFT next to title
  align-items: center;
  width: 100%;
  margin-bottom: 10px;

  .budget-summary-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
}

// Global Budget Controls
.global-budget-controls {
  display: flex;
  gap: 20px;
  align-items: center;

  .global-control-group {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
      font-weight: 600;
      color: $navyBlue;
      font-size: 14px;
    }

    .global-view-select {
      padding: 6px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      background-color: white;
      color: $navyBlue;
      min-width: 120px;
    }
  }
}

.budget-summary-content {
  display: flex;
  flex-direction: row;
  gap: 10px; // ✅ Reduced gap for tighter layout on larger screens
  width: 100%; // ✅ Ensure full width usage
  max-width: 100%; // ✅ Prevent overflow
  
  // ✅ RESPONSIVE DESIGN: Scale down on larger screens (15"+ monitors)
  @media (min-width: 1440px) {
    gap: 8px; // Even tighter spacing on large screens
    max-width: calc(100vw - 120px); // Account for sidebar and margins
    overflow: hidden; // Prevent any overflow
    
    // Ensure all child tiles stay within bounds
    .tile {
      flex-shrink: 1; // Allow tiles to shrink if needed
      min-width: 0; // Allow content to shrink below intrinsic size
    }
  }
}

.chart-main-title {
  font-size: 18px;
  font-weight: bolder;
}

.chart-title {
  margin: 15px 0;
  width: 100%;
  display: flex;
  justify-content: space-between;

  &.hc-title,
  &.sc-title {
   height: 25px;
   display: flex;
   align-items: center;
  }

  .hc-legend {
    display: flex;
    justify-content: flex-start;
    align-self: center;

    .hc-budget-legend {
      border-left: 5px solid $brightBlue1;
      padding-left: 5px;
      margin-left: 5px;
    }

    .hc-actual-legend {
      border-left: 5px solid $lightBlue1;
      padding-left: 5px;
      margin-left: 5px;
    }
  }

  .sc-legend {
    display: flex;
    justify-content: flex-start;
    align-self: center;

    .sc-budget-legend {
      border-left: 5px solid $mediumBlue1;
      padding-left: 5px;
      margin-left: 5px;
    }

    .sc-actual-legend {
      border-left: 5px solid #84C9DE;
      padding-left: 5px;
      margin-left: 5px;
    }
  }
}

// Budget Summary Style - End

// Doughnut Chart Styles -- Start
.doughnut-container {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;

  // Doughnut Legend
  .doughnut-chart-legend {
    font-size: 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-left: 5%;

    .acquisition-costs-legend {
      margin: 5px 0;

      .detail-container {
        display: flex;
        flex-direction: row;
        color: $darkGray1
      }

      .vertical-divider {
        border-left: 5px solid $darkGray1;
        margin: 0 10px;
      }
    }

    .hard-costs-legend {
      margin: 5px 0;

      .detail-container {
        display: flex;
        flex-direction: row;
        color: $brightBlue1;
      }

      .vertical-divider {
        border-left: 5px solid $brightBlue1;
        margin: 0 10px;
      }
    }

    .soft-costs-legend {
      margin: 5px 0;

      .detail-container {
        display: flex;
        flex-direction: row;
        color: $mediumBlue1;
      }

      .vertical-divider {
        border-left: 5px solid $mediumBlue1;
        margin: 0 10px;
      }
    }

    .financing-and-misc-costs-legend {
      margin: 5px 0;

      .detail-container {
        display: flex;
        flex-direction: row;
        color: $mediumGray1;
      }

      .vertical-divider {
        border-left: 5px solid $mediumGray1;
        margin: 0 10px;
      }
    }

    .contingencies-legend {
      margin: 5px 0;

      .detail-container {
        display: flex;
        flex-direction: row;
        color: $mediumGray1;
      }

      .vertical-divider {
        border-left: 5px solid #d3d3d3;
        margin: 0 10px;
      }
    }
  }
}

.doughnut-summary-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  /* Creates 2 columns */
  gap: 10px;
  /* Optional: Adds space between grid items */

  .doughnut-summary-item {
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: 20px;
  }
}

// Doughnut Chart Styles -- End

// Total Project Costs Style - Start
.total-project-costs {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
}

// Total Project Costs Style - End

// Cost to Date Style - Start
.cost-to-date-bar-chart-container {
  margin-left: 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.cost-to-date-legend {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: flex-start;
  font-size: 14px;

  .cost-to-date-current-budget-legend {
    div:nth-child(1),
    div:nth-child(3) {
      color: #4A90E2;
    }
  }

  .cost-to-date-original-budget-legend {
    div:nth-child(1),
    div:nth-child(3) {
      color: #8A92A9;
    }
  }

  .cost-to-date-billed-to-date-legend {
    div:nth-child(1),
    div:nth-child(3) {
      color: $brightBlue1;
    }
  }
}

// Cost to Date Style - End

// Budget Variance Summary Style - Start
.budget-variance-summary {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  div:first-child {
    padding-right: 1.25rem;
  }
}

.budget-variance-charts {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
}

.summary-stacked-bar {
  padding-right: 1rem;
}

.tornado-chart-container {
  width: 100%;
  height: 350px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
}

.tornado-chart {
  position: relative;
  width: 100%;
  padding: 20px 0;
  max-width: 800px;
  min-width: 600px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateX(-50%);
    z-index: 1;
  }
}

.tornado-row {
  display: flex;
  align-items: center;
  margin: 8px 0;
  height: 30px;
  position: relative;
  min-width: 600px;
}

.tornado-label {
  width: 150px;
  min-width: 120px;
  max-width: 200px;
  text-align: right;
  padding-right: 20px;
  font-size: 14px;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.tornado-bar-container {
  flex: 1;
  position: relative;
  height: 24px;
}

.variance-bar {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 20px;
  background: rgba(74, 95, 213, 0.6);
  border-radius: 2px;
  transition: all 0.2s ease;

  &.negative {
    background: rgba(74, 143, 208, 0.6);
    right: 50%;
  }

  &:not(.negative) {
    left: 50%;
  }

  &:hover {
    filter: brightness(1.1);
  }
}

.bar-value {
  position: absolute;
  font-size: 12px;
  font-weight: 500;
  color: #2c3e50;
  white-space: nowrap;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  
  &.inside {
    color: white;
    background: transparent;
    box-shadow: none;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  
  &.outside-left {
    right: calc(100% + 8px);
    top: 50%;
    transform: translateY(-50%);
  }
  
  &.outside-right {
    left: calc(100% + 8px);
    top: 50%;
    transform: translateY(-50%);
  }
}

.tornado-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin: 12px 0;
  
  &:hover {
    background-color: rgba(74, 95, 213, 0.05);
  }

  .tornado-label-container {
    width: 160px;
    min-width: 160px;
    padding-right: 24px;
    text-align: right;
    flex-shrink: 0;
  }

  .category-label {
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
    white-space: nowrap;
  }

  .tornado-bar-container {
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 32px;
  }
}
// Budget Variance Summary Style - End

// Enhanced Variance Display - TEXT ONLY (like Draw 12 mockup)
.variance-display-container {
  width: 100%;
  padding: 20px;
  background: white;
  box-sizing: border-box;
  overflow: hidden; // Prevent overflow
  
  .variance-content {
    width: 100%;
  }
  
  .variance-section {
    margin-bottom: 20px;
  }
  
  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 5px 0;
  }
  
  .section-subtitle {
    font-size: 12px;
    color: #6c757d;
    margin-bottom: 15px;
  }
  
  .subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    margin: 15px 0 8px 0;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 3px;
  }
  
  .variance-items {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 15px;
  }
  
  .variance-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    
    .item-label {
      flex: 1;
      font-size: 12px;
      color: #2c3e50;
      text-align: left;
      margin-right: 10px;
      
      &.new-item {
        color: #1e40af;
        font-weight: 600;
      }
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0; // Allow flex shrinking
    }
    
    .item-values {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: flex-end;
      min-width: 140px;
    }
    
    .item-amount {
      font-size: 12px;
      font-weight: 600;
      color: #2c3e50;
      text-align: right;
      white-space: nowrap;
      
      &.green-text {
        color: #10b981 !important;
      }
      
      &.red-text {
        color: #ef4444 !important;
      }
      
      &.yellow-text {
        color: #f59e0b !important;
      }
    }
    
    .item-percent {
      font-size: 12px;
      font-weight: 500;
      color: #28a745;
      text-align: right;
      min-width: 45px;
      white-space: nowrap;
      
      &:empty {
        min-width: 0;
      }
      
      /* 🎨 Color-code variance percentages */
      &.variance-good {
        color: #28a745 !important; /* Green for negative (under budget) */
      }
      
      &.variance-warning {
        color: #ffc107 !important; /* Yellow for 0-3% over budget */
      }
      
      &.variance-bad {
        color: #dc3545 !important; /* Red for 3%+ over budget */
      }
    }
  }
}

// Enhanced Variance Display - End
// Budget Tracking Style - Start
.budget-tracking-header-container {
  margin-top: 8px; // REDUCED: Minimal spacing between charts and Budget Tracking header
  margin-bottom: 5px; // REDUCED: Minimal space below header
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  .budget-tracking-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
}

.budget-tracking-subtitle {
  font-size: 14px;
  margin: 0px 10px;
  font-weight: bold;
}


// Budget Tracking Style - End

// Toogle Switch - Start
.toggle-container {
  width: 40px;
  height: 20px;
  background-color: #ccc;
  border-radius: 20px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;
}
.circle {
  width: 15px;
  height: 15px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 2.5px;
  left: 3px;
  transition: transform 0.3s ease;
}

.move-right {
  transform: translateX(18px);
}

.toggle-container:hover {
  background-color: #bbb;
}

/* Add a new class for the original-format state */
.toggle-container.original-format {
  background-color: #4A5FD5;
}

// Toggle Switch - End

// Budget Tracking Menu Style - Start
.budget-tracking-sub-menu {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid $mediumGray1;
  font-size: 16px;
  font-weight: lighter;
  margin-bottom: 5px; // REDUCED: Minimal space below tabs

  div {
    cursor: pointer;
    padding: 6px 0px; // REDUCED: Tighter vertical padding
    margin-right: 50px;
  }

  div:nth-last-child(-n+2) {
    margin-right: 0; // Removes margin-right for the last two children
  }
}

.budget-tracking-sub-menu-selected {
  font-weight: bold;
  border-bottom: 3px solid $brightBlue1;
  color: $brightBlue1;
}

.view-by-draw-number-container {
  margin-left: auto; // Pushes the Test div to the farthest right
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: 0;

  .custom-select {
    position: relative;
    font-family: inherit;
    display: flex;
    align-items: center;
    font-size: 12px;
    margin: 0 10px;

    select {
      border: 1px solid $lightGray1;
      color: $brightBlue1;
      font-size: 1rem;
      background: none;
      font-weight: bold;
    }

    select:focus {
      border-color: $brightBlue1;
      outline: $brightBlue1;
    }
  }

  // New draw selection controls layout
  .draw-selection-controls {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-right: 20px;

    .draw-selection-group {
      display: flex;
      align-items: center;
      gap: 8px;

      label {
        font-weight: 600;
        color: $navyBlue;
        font-size: 14px;
        white-space: nowrap;
      }
    }

    .global-draw-filter {
      display: flex;
      align-items: center;
      gap: 8px;

      label {
        font-weight: 600;
        color: $navyBlue;
        font-size: 14px;
        white-space: nowrap;
      }

      .global-draw-select {
        padding: 6px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        background-color: white;
        color: $navyBlue;
        min-width: 140px;
      }

      // 🎯 DRAGGABLE WHEEL: Styling for draggable slider
      .draw-wheel-container {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .draw-wheel-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid #4A5FD5;
          background-color: #fff;
          color: #4A5FD5;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
          
          &:hover:not(:disabled) {
            background-color: #4A5FD5;
            color: #fff;
            transform: scale(1.1);
          }
          
          &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
            border-color: #ccc;
            color: #ccc;
          }
        }
        
        .draw-slider-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 120px;
          flex-grow: 1;
          
          .draw-wheel-display {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 8px;
            
            .draw-current {
              font-size: 14px;
              font-weight: bold;
              color: #333;
            }
            
            .draw-total {
              font-size: 12px;
              color: #666;
            }
          }
          
          .draw-slider-track {
            position: relative;
            width: 100%;
            height: 6px;
            background-color: #e0e0e0;
            border-radius: 3px;
            cursor: pointer;
            user-select: none;
            overflow: hidden; // 🎯 FIX: Contain all child elements
            
            .draw-slider-progress {
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              background-color: #4A5FD5;
              border-radius: 3px;
              transition: width 0.1s ease;
              max-width: 100%; // 🎯 FIX: Prevent spillover
              overflow: hidden; // 🎯 FIX: Hide any overflow
            }
            
            .draw-slider-thumb {
              position: absolute;
              top: -6px;
              width: 18px;
              height: 18px;
              background-color: #4A5FD5;
              border: 2px solid #fff;
              border-radius: 50%;
              cursor: grab;
              transform: translateX(-50%);
              transition: transform 0.1s ease;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
              
              &:active {
                cursor: grabbing;
                transform: translateX(-50%) scale(1.2);
              }
              
              &:hover {
                transform: translateX(-50%) scale(1.1);
              }
            }
          }
        }
      }
    }
  }
}

// Budget Tracking Menu Style - End

// Compact Budget Tracking Grid - Start
.budget-tracking-grid {
  ::v-deep .ag-root-wrapper {
    font-size: 11px !important;
  }

  ::v-deep .ag-cell {
    padding: 2px 6px !important;
    line-height: 22px !important;
  }

  ::v-deep .ag-header-cell {
    padding: 2px 6px !important;
    font-size: 10px !important;
  }

  ::v-deep .ag-header-cell-text {
    font-size: 10px !important;
  }
}
// Compact Budget Tracking Grid - End

// AG Grid Styles - Start
.disabled-header {
  color: #999 !important;
  background-color: #f0f0f0 !important;
  cursor: not-allowed;
  pointer-events: none;
}

// AG Grid Styles - End

// Map Styles - Start
.map-container {
  margin: 10px 0px 10px 0px;
  border: .5px solid $lightGray1;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  width: 399px;
  height: 350px;
  overflow: hidden;

  #individualPageMap {
    width: 100%;
    height: 100%;
  }
}

// Map Styles - End

.totals {
  font-weight: bold;
  color: $navyBlue;
}

// Upload Draw Modal
.upload-draw-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 300px;
  text-align: center;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      margin: 0;
    }

    .close-button {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
    }
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    align-items: center;

    .upload-section {
      margin-bottom: 20px;

      .upload-instructions {
        margin-bottom: 10px;
      }

      .file-upload-area {
        border: 2px dashed $lightGray1;
        border-radius: 5px;
        padding: 20px;
        text-align: center;
        transition: border-color 0.3s ease;

        &.drag-over {
          border-color: $brightBlue1;
        }

        .upload-placeholder {
          opacity: 0.5;

          button {
            background: none;
            border: none;
            color: $brightBlue1;
            cursor: pointer;
            font-size: 16px;
          }
        }

        .file-selected {
          display: flex;
          align-items: center;
          margin-top: 10px;

          .file-info {
            display: flex;
            align-items: center;
            margin-right: 10px;

            .file-icon {
              margin-right: 5px;
            }

            .file-name {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .remove-file {
              background: none;
              border: none;
              color: $darkGray1;
              cursor: pointer;
              font-size: 16px;
            }
          }
        }

        input[type="file"] {
          display: none;
        }
      }
    }

    .modal-actions {
      display: flex;
      justify-content: space-between;
      margin-top: 20px;

      button {
        padding: 10px 20px;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        transition: background-color 0.3s ease;

        &.cancel-button {
          background-color: $lightGray1;

          &:hover {
            background-color: $mediumGray1;
          }
        }

        &.upload-button {
          background-color: $brightBlue1;
          color: white;

          &:hover {
            background-color: darken($brightBlue1, 10%);
          }
        }
      }
    }
  }
}

.upload-success {
  margin-top: 20px;

  .success-message {
    color: #28a745;
    margin-bottom: 10px;
  }

  .modal-actions {
    display: flex;
    justify-content: space-between;

    button {
      padding: 10px 20px;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      transition: background-color 0.3s ease;

      &.cancel-button {
        background-color: $lightGray1;

        &:hover {
          background-color: $mediumGray1;
        }
      }

      &.create-template-button {
        background-color: #28a745;
        color: white;

        &:hover {
          background-color: #218838;
        }
      }
    }
  }
}

// Upload Draw Button
.upload-draw-button-container {
  margin-left: 20px;
  
  .upload-new-draw-button {
    background: linear-gradient(135deg, #4A5FD5 0%, #2c5aa0 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);

    &:hover {
      background: linear-gradient(135deg, #3b4fb8 0%, #1e4a8c 100%);
      box-shadow: 0 4px 8px rgba(74, 95, 213, 0.3);
      transform: translateY(-1px);
    }


  }
}

// Modal backdrop
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

// Enhanced 4-level hierarchy styling
.hierarchy-level-1 {
  font-weight: 700;
  color: #1a1a1a;
  background-color: rgba(111, 66, 193, 0.1);
  border-left: 4px solid #6f42c1;
  padding-left: 8px;
}

.hierarchy-level-2 {
  font-weight: 600;
  color: #2c2c2c;
  background-color: rgba(40, 167, 69, 0.08);
  border-left: 3px solid #28a745;
  padding-left: 16px;
}

.hierarchy-level-3 {
  font-weight: 500;
  color: #3c3c3c;
  background-color: rgba(255, 193, 7, 0.08);
  border-left: 2px solid #ffc107;
  padding-left: 24px;
}

.hierarchy-level-4 {
  font-weight: 400;
  color: #4c4c4c;
  background-color: rgba(220, 53, 69, 0.06);
  border-left: 1px solid #dc3545;
  padding-left: 32px;
}

// Upload Draw Modal
.upload-draw-modal {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e0e0e0;

    h3 {
      margin: 0;
      color: #333;
      font-size: 20px;
      font-weight: 600;
    }

    .close-button {
      background: none;
      border: none;
      font-size: 28px;
      cursor: pointer;
      color: #888;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        background-color: #f0f0f0;
        color: #333;
      }
    }
  }

  .modal-body {
    .upload-section {
      margin-bottom: 24px;

      .upload-instructions {
        text-align: center;
        margin-bottom: 16px;

        p {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 16px;
        }

        small {
          color: #666;
          font-size: 13px;
        }
      }

      .file-upload-area {
        border: 2px dashed #d0d0d0;
        border-radius: 8px;
        padding: 32px 20px;
        text-align: center;
        transition: all 0.3s ease;
        background-color: #fafafa;

        &.drag-over {
          border-color: #6f42c1;
          background-color: #f8f4ff;
        }

        .upload-placeholder {
          .upload-icon-large {
            font-size: 48px;
            display: block;
            margin-bottom: 16px;
            opacity: 0.6;
          }

          p {
            margin: 0;
            color: #666;
            font-size: 16px;
          }

          .browse-button {
            background: none;
            border: none;
            color: #6f42c1;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            text-decoration: underline;

            &:hover {
              color: #5a2d91;
            }
          }
        }

        .file-selected {
          .file-info {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            background-color: #e8f4fd;
            border-radius: 6px;
            padding: 12px 16px;

            .file-icon {
              font-size: 20px;
            }

            .file-name {
              font-weight: 500;
              color: #333;
              max-width: 200px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .remove-file {
              background: none;
              border: none;
              color: #999;
              cursor: pointer;
              font-size: 18px;
              padding: 0;
              margin-left: 8px;

              &:hover {
                color: #333;
              }
            }
          }
        }
      }
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;

      button {
        padding: 10px 20px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s ease;

        &.cancel-button {
          background-color: #f0f0f0;
          color: #666;

          &:hover {
            background-color: #e0e0e0;
            color: #333;
          }
        }

        &.upload-button {
          background-color: #6f42c1;
          color: white;

          &:hover:not(:disabled) {
            background-color: #5a2d91;
          }

          &:disabled {
            background-color: #ccc;
            cursor: not-allowed;
          }
        }

        &.create-template-button {
          background-color: #28a745;
          color: white;

          &:hover {
            background-color: #218838;
          }
        }
      }
    }

    .upload-success {
      text-align: center;

      .success-message {
        margin-bottom: 24px;

        .success-icon {
          font-size: 48px;
          display: block;
          margin-bottom: 16px;
        }

        h4 {
          margin: 0 0 8px 0;
          color: #28a745;
          font-size: 18px;
        }

        p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }
      }
    }
  }

  .form-section {
    margin-bottom: 20px;

    .form-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: #333;
      font-size: 14px;
    }

    .form-select {
      width: 100%;
      padding: 12px;
      border: 1px solid #d0d0d0;
      border-radius: 6px;
      font-size: 14px;
      background-color: white;
      transition: border-color 0.2s ease;

      &:focus {
        outline: none;
        border-color: #6f42c1;
        box-shadow: 0 0 0 2px rgba(111, 66, 193, 0.1);
      }
    }
    
    .draw-input-wrapper {
      position: relative;
      
      .draw-number-input {
        width: 100%;
      }
      
      .custom-draw-toggle {
        margin: 10px 0;
        
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 0.9em;
          color: #666;
          
          input[type="checkbox"] {
            margin: 0;
          }
          
          .checkbox-text {
            user-select: none;
          }
        }
      }
      
      .custom-draw-input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        margin-top: 8px;
        
        &:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
      }
      
      .draw-hint {
        display: block;
        color: #666;
        font-size: 12px;
        margin-top: 4px;
        font-style: italic;
      }
    }
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.progress-section {
  margin-top: 10px;
  text-align: center;

  .progress-bar-container {
    display: inline-block;
    width: 80%;
    background-color: #f3f3f3;
    border-radius: 15px;
    padding: 2px;

    .progress-bar {
      width: 0;
      height: 20px;
      background-color: #4CAF50;
      border-radius: 10px;
      transition: width 0.3s ease-in-out;
    }

    .progress-fill {
      height: 100%;
      line-height: 20px;
      border-radius: 10px;
      text-align: center;
      color: white;
      transition: width 0.3s ease-in-out;
    }
  }

  .progress-text {
    margin-top: 5px;
    font-size: 14px;
    color: #333;
  }

  .progress-status {
    margin-top: 5px;
    font-size: 14px;
    color: #666;
  }
}
.ocr-processing-section {
  margin-top: 20px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 10px;

  .processing-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .processing-icon {
      font-size: 32px;
      color: #4CAF50;
      margin-right: 10px;
    }

    h4 {
      font-size: 18px;
      color: #333;
    }
  }

  .progress-section {
    .progress-bar-container {
      display: inline-block;
      width: 80%;
      background-color: #f3f3f3;
      border-radius: 15px;
      padding: 2px;

      .progress-bar {
        width: 0;
        height: 20px;
        background-color: #4CAF50;
        border-radius: 10px;
        transition: width 0.3s ease-in-out;
      }

      .progress-fill {
        height: 100%;
        line-height: 20px;
        border-radius: 10px;
        text-align: center;
        color: white;
        transition: width 0.3s ease-in-out;
      }
    }

    .progress-text {
      margin-top: 5px;
      font-size: 14px;
      color: #333;
    }

    .progress-status {
      margin-top: 5px;
      font-size: 14px;
      color: #666;
    }
  }

  .ocr-steps {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;

    .step {
      flex: 1;
      text-align: center;
      font-size: 14px;
      color: #666;

      &.active {
        font-weight: bold;
        color: #4CAF50;
      }

      &.completed {
        font-style: italic;
        color: #888;
      }

      .step-number {
        font-weight: bold;
        display: block;
        margin-bottom: 5px;
      }
    }
  }
}

.extraction-results-section {
  margin-top: 20px;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;

  .results-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .results-icon {
      font-size: 32px;
      color: #28a745;
      margin-right: 10px;
    }

    h4 {
      font-size: 18px;
      color: #333;
    }
  }

  .source-document-preview {
    margin-bottom: 20px;

    .document-viewer {
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        max-width: 100%;
        max-height: 300px;
        border: 1px solid #ccc;
        border-radius: 5px;
      }

      .placeholder-document {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #888;

        span {
          font-size: 24px;
          margin-bottom: 10px;
        }

        p {
          font-size: 14px;
          text-align: center;
        }
      }
    }
  }

  .extraction-summary {
    h5 {
      font-size: 16px;
      color: #333;
      margin-bottom: 10px;
    }

    .extracted-fields {
      .field-item {
        margin-bottom: 10px;

        label {
          font-weight: bold;
          display: block;
          margin-bottom: 5px;
        }

        .field-input {
          width: 100%;
          padding: 10px;
          margin-bottom: 5px;
          border: 1px solid #ccc;
          border-radius: 5px;

          &.low-confidence {
            border-color: #ff7b7b;
          }
        }

        .confidence-score {
          font-size: 12px;
          color: #888;

          &.low {
            color: #ff7b7b;
          }
        }
      }
    }
  }

  .modal-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;

    button {
      padding: 10px 20px;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      transition: background-color 0.3s ease;

      &.cancel-button {
        background-color: #f0f0f0;

        &:hover {
          background-color: #e0e0e0;
        }
      }

      &.corrections-button {
        background-color: #28a745;
        color: white;

        &:hover {
          background-color: #218838;
        }
      }

      &.create-template-button {
        background-color: #28a745;
        color: white;

        &:hover {
          background-color: #218838;
        }
      }
    }
  }
}

.result-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;

  button {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &.view-results-button,
    &.document-list-button,
    &.close-success-button {
      background-color: #28a745;
      color: white;

      &:hover {
        background-color: darken(#28a745, 10%);
      }
    }
  }
}

// Documents link at bottom of modal
.modal-footer-link {
  margin-top: 20px;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0fe 100%);
  border-top: 1px solid #e1e8ed;
  text-align: center;

  .documents-link {
    color: #4a5fd5;
    text-decoration: none;
    font-weight: 600;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    padding: 8px 12px;
    border-radius: 6px;

    &:hover {
      color: #3d4ed8;
      background: rgba(74, 95, 213, 0.1);
      text-decoration: none;
      transform: translateX(4px);
    }
  }
}

.cost-curve-chart {
  margin-top: 20px;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 500px;
  flex: 0 0 56%; // 🎯 ADJUST: Give 4% back to Total Project Costs
}

.budget-variance-chart {
  margin-top: 20px;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 500px;
  flex: 0 0 28%; // ✅ Budget Variance chart expanded to align with grid (28% width)
}

// Shared styles for both cost-curve-chart and budget-variance-chart
.cost-curve-chart,
.budget-variance-chart {
  .chart-main-title {
    font-size: 18px;
    font-weight: bolder;
    margin-bottom: 10px;
  }

  .chart-subtitle {
    font-size: 14px;
    color: #666;
    margin-bottom: 10px;
  }

  .cost-curve-controls {
    display: flex;
    justify-content: flex-start; // ✅ Remove excessive spacing between controls
    align-items: center;
    gap: 20px; // ✅ Add consistent spacing between controls instead of space-between
    margin-bottom: 10px;

      .cost-curve-filter {
    .custom-select {
      width: 200px;
    }
  }

  .contractor-filter {
    .contractor-autocomplete {
      position: relative;
      width: 160px; // ✅ Reduced width for more compact layout
    }

    .input-wrapper {
      position: relative;
      display: flex;
    }

    .contractor-search-input.compact {
      flex: 1;
      padding: 6px 10px 6px 10px;
      border: 1px solid #ddd;
      border-right: none;
      border-radius: 4px 0 0 4px;
      font-size: 13px;
      background: white;
      
      &:focus {
        outline: none;
        border-color: #4A5FD5;
        box-shadow: 0 0 0 2px rgba(74, 95, 213, 0.1);
      }
      
      &.has-selection {
        background-color: #f8f9ff;
        border-color: #4A5FD5;
      }
    }

    .dropdown-toggle {
      padding: 6px 8px;
      border: 1px solid #ddd;
      border-left: none;
      border-radius: 0 4px 4px 0;
      background: #f8f9fa;
      color: #666;
      cursor: pointer;
      font-size: 10px;
      transition: all 0.2s ease;
      
      &:hover {
        background: #e9ecef;
        color: #333;
      }
      
      &:focus {
        outline: none;
        border-color: #4A5FD5;
        background: #e3f2fd;
      }
    }

    .contractor-dropdown.compact {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border: 1px solid #ddd;
      border-radius: 4px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      max-height: 300px; // Increased from 200px to show more contractors
      overflow-y: auto;
      z-index: 1000;
      
      .contractor-option {
        padding: 8px 12px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        
        &:hover {
          background-color: #f5f5f5;
        }
        
        &.selected {
          background-color: #e3f2fd;
          color: #1976d2;
        }
        
        &:last-child {
          border-bottom: none;
        }
        
        .contractor-name {
          font-size: 13px;
          font-weight: 500;
        }
      }
      
      .no-results {
        padding: 8px 12px;
        color: #999;
        font-style: italic;
        font-size: 12px;
      }
    }

    .selected-contractor-display.compact {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 4px;
      padding: 4px 8px;
      background-color: #e3f2fd;
      border-radius: 3px;
      font-size: 12px;
      
      .selected-contractor-name {
        color: #1976d2;
        font-weight: 500;
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .clear-selection {
        background: none;
        border: none;
        color: #1976d2;
        cursor: pointer;
        font-size: 16px;
        padding: 0;
        margin-left: 6px;
        
        &:hover {
          color: #d32f2f;
        }
      }
    }
  }
  }

  .cost-curve-chart-container {
    height: 340px; // allow a bit more space so chart fits with bottom axis
    margin-bottom: 10px;
    flex: 1;
    position: relative; // ✅ Enable absolute positioning for overlay
    
    // 🎯 Default: Keep x-axis position and make bars taller by filling white space above
    // This applies to the Cost Curve chart (right side)
    &:not(.total-project-costs-chart) {
      padding-top: 100px; // Keep x-axis at same position
      
      // Make the chart take up more vertical space (taller bars filling the top white space)
      .ag-charts-vue {
        height: calc(100% + 60px) !important; // Extend upward into the padding area
        margin-top: -60px; // Pull chart up to fill white space at top
      }
    }
    
    // 🎯 Total Project Costs chart (left side) - needs different approach due to absolute labels
    &.total-project-costs-chart {
      // Move the chart wrapper down to match Cost Curve x-axis position exactly
      .chart-wrapper {
        padding-top: 100px; // Same as Cost Curve to align x-axes
      }
      
      // Adjust the absolute positioned labels to account for the shift
      .total-costs-labels-left,
      .total-costs-labels-right {
        top: 100px; // Shift labels down with the chart to match
      }
    }
  }

.cost-curve-charts-split { display: flex; gap: 10px; align-items: flex-start; }
.cost-curve-charts-split .main { flex: 1; }
.cost-curve-charts-split .initial { width: 260px; margin-top: -60px; }

  // Prefunded chart overlay CSS removed - prefunded info now shows in hover tooltip
  
  // Interactive hint for prefunded information positioned near chart bottom
  .prefunded-hint {
    position: relative;
    text-align: left;
    margin-top: -30px; // Move up closer to the chart bottom
    margin-left: 50px; // Position near where Draw 1 label would be
    z-index: 20;
    
    .hint-text {
      font-size: 10px;
      color: #7f8c8d;
      font-style: italic;
      padding: 3px 6px;
      background-color: rgba(79, 95, 213, 0.05);
      border: 1px solid rgba(79, 95, 213, 0.15);
      border-radius: 8px;
      display: inline-block;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: rgba(79, 95, 213, 0.1);
        border-color: rgba(79, 95, 213, 0.25);
        color: #4A5FD5;
      }
    }
    
    .prefunded-tooltip {
      position: absolute;
      bottom: 30px;
      left: 0;
      background: white;
      border: 2px solid #4A5FD5;
      border-radius: 8px;
      padding: 12px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      min-width: 200px;
      
      .tooltip-title {
        font-size: 12px;
        font-weight: bold;
        color: #4A5FD5;
        margin-bottom: 4px;
      }
      
      .tooltip-subtitle {
        font-size: 10px;
        color: #7f8c8d;
        font-style: italic;
        margin-bottom: 8px;
      }
      
      .tooltip-total {
        font-size: 11px;
        font-weight: bold;
        color: #2c3e50;
        margin-bottom: 8px;
      }
      
      .breakdown-title {
        font-size: 10px;
        font-weight: bold;
        color: #2c3e50;
        margin-bottom: 4px;
      }
      
      .breakdown-item {
        font-size: 10px;
        color: #34495e;
        margin-bottom: 2px;
      }
    }
  }

  .cost-curve-legend {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #666;
    height: 70px;
    margin-top: auto;

    .cost-curve-legend-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;

      .legend-color {
        width: 20px;
        height: 20px;
        border-radius: 2px;
        margin-bottom: 5px;
        
        &.hard-costs-color {
          background-color: #4A5FD5;
        }
        
        &.soft-costs-color {
          background-color: #4A8FD0;
        }
        
        &.acquisition-color {
          background-color: #52596D;
        }
        
        &.financing-color {
          background-color: #8A92A9;
        }
        
        &.total-color {
          background-color: #000000;
        }
      }

      .legend-value {
        font-weight: bold;
      }
    }
  }
}

.costs-to-date-chart {
  margin-top: 5px;
  padding: 15px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 500px;
  flex: 0 0 auto;
  width: 100%;
  max-width: 400px; // 🎯 MATCH MOCKUP: Constrain width for centered bar
  
  // ✅ RESPONSIVE DESIGN: Ensure variance box stays visible
  @media (max-width: 1200px) {
    max-width: 350px;
    min-height: 480px;
  }
  
  @media (min-width: 1440px) {
    max-width: 420px;
  }
  
  @media (min-width: 1920px) {
    max-width: 450px;
  }

  .chart-main-title {
    font-size: 18px;
    font-weight: bolder;
    margin-bottom: 15px;
    text-align: left;
  }

  .project-costs-summary-cards {
    display: flex;
    flex-direction: column; // 🎯 STACK VERTICALLY like mockup
    gap: 10px;
    margin-bottom: 20px;

    .summary-card {
      background-color: transparent;
      border-radius: 0;
      padding: 0;
      text-align: left;
      border: none;
      display: flex;
      justify-content: space-between;
      align-items: baseline;

      .card-label {
        font-size: 14px;
        color: #2c3e50;
        margin-bottom: 0;
        font-weight: 600;
      }

      .card-value {
        font-size: 16px;
        font-weight: bold;
        color: #2563eb; // Blue color for values

        &.positive-variance {
          color: #10b981; // Green
        }

        &.negative-variance {
          color: #ef4444; // Red
        }
      }
    }
  }

  .chart-subtitle {
    font-size: 14px;
    color: #666;
    margin-bottom: 10px;
  }

  .cost-curve-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .cost-curve-filter {
      .custom-select {
        width: 200px;
      }
    }
  }

  .cost-curve-chart-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 250px; // ✅ Reduced height to make Total Project Costs more compact
    position: relative;
    z-index: 2; // Higher z-index than costs-to-date chart to prevent tooltip conflicts
    overflow: visible;
  }

  // 🎯 SIMPLE: Position labels directly in the chart padding zones
  .total-costs-labels-left,
  .total-costs-labels-right {
    position: absolute;
    top: 10px; // 🎯 Match chart's top padding exactly
    bottom: 20px; // 🎯 Match chart's bottom padding exactly
    height: auto;
    width: 90px;
    pointer-events: none;
  }

  .total-costs-labels-left {
    left: 5px; // 🎯 Just inside left edge
    
    .label-item {
      align-items: flex-start;
      text-align: left;
      left: 0;
    }
  }

  .total-costs-labels-right {
    right: 105px; // 🎯 Inside the right padding zone (100px + 5px buffer)
    
    .label-item {
      align-items: flex-end;
      text-align: right;
      right: 0;
    }
  }

  .total-costs-labels-left .label-item,
  .total-costs-labels-right .label-item {
    position: absolute;
    display: flex;
    flex-direction: column;
    transform: translateY(-50%);
    font-size: 11px;
    line-height: 1.4;
    white-space: nowrap;

    .label-text {
      color: #6b7280;
      font-weight: 600;
      margin-bottom: 2px;
    }

    .label-value {
      font-weight: 700;
      font-size: 13px;
      color: #2563eb;
    }

    .label-percentage {
      font-weight: 700;
      font-size: 12px;
      color: #2563eb;
    }

    &.label-completed {
      .label-text,
      .label-value,
      .label-percentage {
        color: #2563eb; // Dark blue for completed
      }
    }

    &.label-planned {
      .label-text {
        color: #6b7280;
      }
      .label-value {
        color: #6b7280; // Gray text for planned
      }
    }

    &.label-current {
      .label-text {
        color: #6b7280;
      }
      .label-value,
      .label-percentage {
        color: #6b7280; // Gray text for current
      }
    }
  }

  .cost-curve-legend {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #666;
    height: 70px;
    margin-top: auto;

    .cost-curve-legend-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;

      .legend-color {
        width: 20px;
        height: 20px;
        border-radius: 2px;
        margin-bottom: 5px;
      }

      .legend-value {
        font-weight: bold;
      }
    }
  }
}
// Hover Edit Cell Styles
.hover-edit-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  .budget-value {
    flex: 1;
    text-align: right;
    padding: 4px 6px;
  }
  
  .edit-overlay {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    display: flex;
    align-items: center;
    padding-right: 4px;
    background: linear-gradient(to left, rgba(255,255,255,0.9) 50%, transparent 100%);
    
    .edit-btn {
      background: none;
      border: none;
      padding: 2px 4px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 11px;
      color: #666;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: #e3f2fd;
        color: #1976d2;
        transform: scale(1.1);
      }
    }
  }
  
  &:hover {
    background-color: rgba(33, 150, 243, 0.1);
    
    .edit-overlay {
      opacity: 1 !important;
      visibility: visible !important;
      
      .edit-btn {
        display: block !important;
      }
    }
  }
}
// Contractor Edit Cell Styles
.contractor-edit-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  .contractor-value {
    flex: 1;
    text-align: left;
    padding: 4px 6px;
  }
  
  .contractor-edit-overlay {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    display: flex;
    align-items: center;
    padding-right: 4px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    background: linear-gradient(to left, rgba(255,255,255,0.9) 50%, transparent 100%);
    
    .contractor-edit-btn {
      background: none;
      border: none;
      padding: 2px 4px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 11px;
      color: #666;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: #e3f2fd;
        color: #1976d2;
      }
    }
  }
  
  &:hover {
    background-color: rgba(33, 150, 243, 0.1);
    
    .contractor-edit-overlay {
      opacity: 1 !important;
      visibility: visible !important;
      
      .contractor-edit-btn {
        display: block !important;
      }
    }
  }
}

.view-by-draw-number-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 20px;
  
  .left-side-controls {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .right-side-controls {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.source-documents-compact {
  .source-docs-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    
    .docs-label {
      font-size: 12px;
      color: #6c757d;
      font-weight: 500;
    }
    
    .docs-list-inline {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      
      .doc-link-btn {
        background: #e2ecff;
        border: 1px solid #9bb3f0;
        border-radius: 8px;
        padding: 10px 16px;
        font-size: 13px;
        color: #2d3748;
        cursor: pointer;
        transition: all 0.3s ease;
        font-weight: 500;
        white-space: nowrap;
        
        &:hover {
          background: #c5d4f7;
          border-color: #4A5FD5;
          color: #1a202c;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);
          box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
        }
      }
    }
  }
}

.view-documents-below-tabs {
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  
  .doc-link-btn {
    background: #e2ecff;
    border: 1px solid #9bb3f0;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 13px;
    color: #2d3748;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #c5d4f7;
      border-color: #4A5FD5;
      color: #1a202c;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 1px 2px rgba(74, 95, 213, 0.2);
    }
  }
}

.draw-filter-companies {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  
  label {
    font-size: 13px;
    font-weight: 500;
    color: #2d3748;
  }
  
  .draw-select {
    padding: 8px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 13px;
    color: #2d3748;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #4A5FD5;
    }
    
    &:focus {
      outline: none;
      border-color: #4A5FD5;
      box-shadow: 0 0 0 3px rgba(74, 95, 213, 0.1);
    }
  }
}

// 🔗 NEW: Template Links Section Styles
.template-links-section {
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 8px;
  
  .template-links-header {
    margin-bottom: 12px;
    
    .template-links-title {
      font-size: 14px;
      font-weight: 600;
      color: #2d3748;
      padding: 0 8px;
    }
  }
  
  .template-links-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 8px;
  }
  
  .template-link-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    transition: all 0.3s ease;
    
    &.template-assigned {
      border-color: #22c55e;
      background: #f0fdf4;
    }
    
    &.template-suggested {
      border-color: #f59e0b;
      background: #fffbeb;
    }
    
    &:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
    
    .template-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1;
      
      .filename {
        font-weight: 600;
        color: #1e293b;
    font-size: 13px;
      }
      
      .applications {
        font-size: 11px;
        color: #64748b;
      }
    }
    
    .template-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .template-status {
        font-size: 11px;
        padding: 4px 8px;
        border-radius: 4px;
    font-weight: 500;
        
        &.assigned {
          background: #dcfce7;
          color: #166534;
        }
        
        &.suggested {
          background: #fef3c7;
          color: #92400e;
        }
      }
      
      .template-edit-btn {
        background: #3b82f6;
        border: none;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 11px;
        color: white;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
    
    &:hover {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
        }
        
        &:active {
          transform: translateY(0);
        }
      }
    }
  }
}

// 📊 Export Button Styles
.export-and-docs-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
}

.export-button-container {
  display: flex;
  align-items: center;
}

.export-budget-btn {
  background: linear-gradient(135deg, #4A5FD5 0%, #2c5aa0 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, #3b4fb8 0%, #1e4a8c 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(74, 95, 213, 0.3);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);
  }
}

// DDA Terms Toggle Button Styles - DDA Blue Theme
.dda-toggle-container {
  display: flex;
  align-items: center;
}

.dda-toggle-btn {
  background: #f0f4ff;
  border: 1px solid #c5d4f7;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: #4a5568;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e2ecff;
    border-color: #9bb3f0;
    color: #2d3748;
  }
  
  &.active {
    background: linear-gradient(135deg, #4A5FD5 0%, #2c5aa0 100%);
    border-color: #4A5FD5;
    color: white;
    box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);
    
    &:hover {
      background: linear-gradient(135deg, #3b4fb8 0%, #1e4a8c 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(74, 95, 213, 0.3);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
}

// Category Override Modal Styles
.category-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-option-btn {
  background: #f0f4ff;
  border: 2px solid #c5d4f7;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  color: #4a5568;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  
  &:hover {
    background: #e2ecff;
    border-color: #9bb3f0;
    color: #2d3748;
    transform: translateX(4px);
  }
  
  &.selected {
    background: linear-gradient(135deg, #4A5FD5 0%, #2c5aa0 100%);
    border-color: #4A5FD5;
    color: white;
    box-shadow: 0 2px 4px rgba(74, 95, 213, 0.2);
  }
  
  &.clear-btn {
    background: #fff3cd;
    border-color: #ffc107;
    color: #856404;
    
    &:hover {
      background: #ffe69c;
      border-color: #ffb300;
    }
  }
}

// Category cell edit styles
.category-edit-cell {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  .category-value {
    flex: 1;
  }
  
  .category-edit-overlay {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    transition: all 0.2s;
  }
  
  &:hover .category-edit-overlay {
    opacity: 1 !important;
    visibility: visible !important;
    
    .category-edit-btn {
      display: inline !important;
      background: #4A5FD5;
      border: none;
      border-radius: 4px;
      padding: 4px 8px;
      color: white;
      font-size: 12px;
      cursor: pointer;
    }
  }
}

// Contractor Modal Styles
.contractor-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.contractor-form {
  padding: 20px;
}

.form-section {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
}

.current-value {
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin-top: 4px;
}

.contractor-autocomplete {
  position: relative;
  width: 100%;
}

.contractor-search-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #4A5FD5;
    box-shadow: 0 0 0 3px rgba(74, 95, 213, 0.1);
  }
  
  &.has-selection {
    border-color: #4A5FD5;
    background-color: #f8f9ff;
  }
}

.contractor-search-container {
  position: relative;
}

.contractor-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 600px; // 🔥 Increased for better visibility
  overflow-y: auto;
}

.contractor-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f5f5f5;
  
  &:hover {
    background-color: #f8f9ff;
  }
  
  &.selected {
    background-color: #e3f2fd;
    border-left: 4px solid #4A5FD5;
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.contractor-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  line-height: 1.3;
}

.contractor-address {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
  font-style: italic;
}

.selected-contractor-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #e8f5e8;
  border-radius: 6px;
  margin-top: 8px;
  border: 2px solid #c8e6c9;
}

.selected-contractor-name {
  font-size: 13px;
  color: #2e7d32;
  font-weight: 500;
}

.clear-selection {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  padding: 0;
  margin-left: 8px;
  
  &:hover {
    color: #d32f2f;
  }
}

.no-results {
  padding: 16px;
  color: #666;
  font-size: 14px;
  text-align: center;
  font-style: italic;
}

.form-hint {
  margin-top: 8px;
  
  small {
    color: #666;
    font-size: 12px;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.btn-secondary {
  padding: 10px 20px;
  border: 2px solid #6c757d;
  background: white;
  color: #6c757d;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #6c757d;
    color: white;
  }
}

.btn-primary {
  padding: 10px 20px;
  border: 2px solid #4A5FD5;
  background: #4A5FD5;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #3a4fc2;
    border-color: #3a4fc2;
  }
}

// Edit Project Modal Styles
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #4A5FD5;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.checkbox-group {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

/* Modal Styling - Both Contractor and DDA Term */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.contractor-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 1000px; // 🔥 2x larger (was 500px)
  max-height: 90vh; // 🔥 Increased height (was 80vh)
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-button:hover {
  background-color: #f0f0f0;
  color: #666;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.dda-term-modal .modal-body {
  min-height: 500px; // 🔥 Ensure DDA term modal has enough height for dropdown
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background-color: #f8f9fa;
}

/* Form Styling */
.contractor-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.current-value {
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.contractor-autocomplete {
  position: relative;
}

.contractor-search-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.contractor-search-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.contractor-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 400px; // 🔥 2x larger (was 200px)
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.contractor-option {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.contractor-option:hover {
  background-color: #f8f9fa;
}

.contractor-option.selected {
  background-color: #e3f2fd;
}

.contractor-name {
  font-weight: 500;
  color: #333;
}

.contractor-address {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.selected-contractor-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  margin-top: 8px;
}

.selected-contractor-name {
  font-weight: 500;
  color: #1976d2;
}

.clear-selection {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
}

.clear-selection:hover {
  color: #333;
}

.form-hint {
  margin-top: 8px;
}

.form-hint small {
  color: #666;
  font-size: 12px;
}

.no-results {
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
  background-color: #f8f9fa;
}

/* Button Styling */
.btn-primary, .btn-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

/* Auto-match specific styling */
.auto-match-info {
  margin-bottom: 15px;
}

.match-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
}

.match-badge.ml-match {
  background-color: #e7f5e7;
  color: #2d8f2d;
  border: 1px solid #90ee90;
}

.match-badge.keyword-match {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.confidence {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  margin: 0;
}

.form-checkbox {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  accent-color: #4A5FD5;
}

.form-section {
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.form-section:first-child {
  margin-top: 0;
}

// Edit Images Modal Styles
.edit-images-modal {
  background: white;
  border-radius: 8px;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  flex-direction: column;
  
  .modal-header {
    text-align: center;
    position: relative;
    
    .close-button {
      position: absolute;
      top: 15px;
      right: 20px;
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #666;
      line-height: 1;
      
      &:hover {
        color: #333;
      }
    }
    
    h3 {
      margin: 0;
      padding: 20px;
      font-size: 20px;
      font-weight: 600;
      color: #333;
    }
  }
  
  .modal-body {
    padding: 0 30px 20px 30px;
    overflow-y: auto;
    flex: 1;
  }
}

.upload-section {
  text-align: center;
  padding: 20px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #f8f9fa;
}

.upload-button {
  display: inline-block;
  padding: 12px 24px;
  background: #4A5FD5;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #3a4fc2;
  }
}

.upload-hint {
  margin: 10px 0 0 0;
  color: #666;
  font-size: 14px;
}
.images-section {
  margin-bottom: 30px;
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 25px 0 20px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;
  }
  
  &:first-of-type h4 {
    margin-top: 15px;
  }
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.image-item {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #4A5FD5;
    box-shadow: 0 4px 12px rgba(74, 95, 213, 0.15);
  }
  
  &.primary {
    border-color: #ffd700;
    box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.3);
  }
}

.image-preview {
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  .image-item:hover & {
    opacity: 1;
  }
}

.btn-primary-image {
  padding: 6px 12px;
  border: 1px solid white;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &.active {
    background: #ffd700;
    color: #333;
    border-color: #ffd700;
  }
}

.btn-remove {
  width: 30px;
  height: 30px;
  border: 1px solid white;
  background: rgba(220, 53, 69, 0.8);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc3545;
  }
}

.image-info {
  padding: 10px;
}

.image-name-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  
  &:focus {
    border-color: #4A5FD5;
  }
}

.no-images {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
} .edit-project-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e0e0e0;
    background: #f8f9fa;
    border-radius: 8px 8px 0 0;

    h3 {
      margin: 0;
      color: #333;
      font-size: 18px;
      font-weight: 600;
    }

    .close-button {
      background: none;
      border: none;
      font-size: 24px;
      color: #666;
      cursor: pointer;
      padding: 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        background: #e0e0e0;
        color: #333;
      }
    }
  }

  .modal-body {
    padding: 24px;
  }

  .form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-group {
    flex: 1;

    &.full-width {
      flex: none;
      width: 100%;
    }

    label {
      display: block;
      font-weight: 500;
      color: #333;
      margin-bottom: 6px;
      font-size: 14px;
    }

    .form-input, .form-textarea {
      width: 100%;
      padding: 10px 12px;
      border: 2px solid #ddd;
      border-radius: 6px;
      font-size: 14px;
      outline: none;
      transition: all 0.2s ease;
      box-sizing: border-box;

      &:focus {
        border-color: #4A5FD5;
        box-shadow: 0 0 0 3px rgba(74, 95, 213, 0.1);
      }
    }

    .form-textarea {
      resize: vertical;
      min-height: 80px;
      font-family: inherit;
    }
  }

  .form-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }

    .section-title {
      color: #4A5FD5;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 20px 0;
      padding-bottom: 8px;
      border-bottom: 2px solid #e8f1ff;
    }
  }

  .checkbox-group {
    display: flex;
    align-items: center;
    
    .checkbox-label {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-weight: normal;
      margin: 0;
      
      .form-checkbox {
        width: auto;
        margin-right: 8px;
        cursor: pointer;
      }
    }
  }

  .checkbox-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px 20px;
    margin-top: 8px;

    .checkbox-inline {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-weight: normal;
      margin: 0;
      
      .form-checkbox {
        width: auto;
        margin-right: 8px;
        cursor: pointer;
        accent-color: #4A5FD5;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-enter-from, .fade-leave-to {
    opacity: 0;
    transform: translateX(-10px);
  }

  .building-use-types-table {
    margin-top: 12px;
    
    .building-use-row {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 10px 12px;
      border-bottom: 1px solid #f0f0f0;
      
      &:hover {
        background-color: #f9fafb;
      }
      
      .checkbox-label {
        min-width: 200px;
        display: flex;
        align-items: center;
        cursor: pointer;
        font-weight: normal;
        margin: 0;
        
        .form-checkbox {
          width: auto;
          margin-right: 8px;
          cursor: pointer;
        }
      }
      
      .gsf-input-container {
        display: flex;
        align-items: center;
        gap: 8px;
        
        .gsf-label {
          font-weight: 600;
          color: #555;
          font-size: 13px;
        }
        
        .gsf-input {
          width: 150px;
          padding: 6px 10px;
          border: 2px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          
          &:focus {
            border-color: #4A5FD5;
            box-shadow: 0 0 0 2px rgba(74, 95, 213, 0.1);
          }
        }
      }
    }
    
    .gsf-total-row {
      margin-top: 16px;
      padding: 12px 16px;
      background-color: #e8f1ff;
      border-radius: 6px;
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 14px;
      
      strong {
        color: #2c5aa0;
      }
      
      .gsf-total-value {
        font-weight: 600;
        color: #1a3a6b;
        font-size: 16px;
      }
      
      .gsf-comparison {
        color: #666;
        font-size: 13px;
        
        .gsf-mismatch {
          color: #d9534f;
          font-weight: 600;
          margin-left: 6px;
        }
      }
    }
  }

  // Hierarchical Table Styles (from ProjectInformation.vue)
  .project-detail-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 12px;
    border: 1px solid #e0e0e0;
    background-color: white;

    tr {
      border-bottom: 1px solid #f0f0f0;

      &:hover {
        background-color: #f9fafb;
      }
    }

    td {
      padding: 12px 16px;
      vertical-align: middle;
    }

    .project-detail-table-headers {
      background-color: #f5f7fa;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #ddd;

      .project-detail-table-header-desc {
        font-size: 14px;
        font-weight: 700;

        span {
          display: block;
          font-size: 12px;
          color: #777;
          font-weight: 400;
          margin-top: 4px;
        }
      }

      .project-detail-table-sub-header {
        text-align: center;
        font-size: 13px;
        color: #555;
      }
    }

    .category-checkbox-header {
      background-color: #f0f4f8;
      font-weight: 600;
      font-size: 14px;
      padding: 14px 16px !important;

      div {
        font-size: 12px;
        color: #666;
        font-weight: 400;
        margin-top: 6px;
      }
    }

    .sub-category-indent {
      padding-left: 48px !important;
      font-size: 13px;
    }

    .brand-type-container {
      padding-left: 72px !important;
      font-size: 13px;
      color: #666;
      font-style: italic;
    }

    .square-feet-per-use-header,
    .num-of-units-header {
      text-align: center;
      font-size: 12px;
      color: #555;
      font-weight: 600;
    }

    .custom-checkbox {
      display: flex;
      align-items: center;
      cursor: pointer;
      position: relative;
      user-select: none;

      .custom-checkbox-label {
        margin-left: 8px;
        font-size: 14px;
        color: #333;
      }

      .checkmark {
        position: relative;
        height: 20px;
        width: 20px;
        background-color: white;
        border: 2px solid #ccc;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:after {
          content: "";
          position: absolute;
          display: none;
          left: 6px;
          top: 2px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      }

      input:checked ~ .checkmark {
        background-color: #4A5FD5;
        border-color: #4A5FD5;
      }

      input:checked ~ .checkmark:after {
        display: block;
      }
    }

    .checkbox-text-input {
      width: 100%;

      input {
        width: 100%;
        padding: 8px 12px;
        border: 2px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        transition: border-color 0.2s ease;

        &:focus {
          outline: none;
          border-color: #4A5FD5;
          box-shadow: 0 0 0 3px rgba(74, 95, 213, 0.1);
        }

        &::placeholder {
          color: #999;
          font-style: italic;
        }
      }
    }

    .parking-type-checkbox-container {
      font-size: 13px;
    }

    .totals-row {
      background-color: #e8f1ff;
      font-weight: 600;
      border-top: 2px solid #4A5FD5;
      border-bottom: 2px solid #4A5FD5;

      td {
        padding: 14px 16px !important;
        color: #2c5aa0;
      }

      strong {
        font-size: 15px;
        color: #1a3a6b;
      }
    }

    .gsf-comparison-row {
      background-color: #fff9e6;
      border-top: 1px solid #ffd966;

      .gsf-comparison {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 13px;
        color: #666;

        .gsf-mismatch {
          color: #d9534f;
          font-weight: 600;
          padding: 4px 8px;
          background-color: #ffe6e6;
          border-radius: 4px;
        }
      }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #e0e0e0;
    background: #f8f9fa;

    .btn-secondary {
      padding: 10px 20px;
      border: 2px solid #ddd;
      background: white;
      color: #666;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:hover {
        border-color: #bbb;
        color: #333;
      }
    }

    .btn-primary {
      padding: 10px 20px;
      border: 2px solid #4A5FD5;
      background: #4A5FD5;
      color: white;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:hover {
        background: #3949a8;
        border-color: #3949a8;
      }
    }
  }
}

// ✅ RESPONSIVE DESIGN: Grid and layout optimizations for larger screens
@media (min-width: 1440px) {
  .grid-container {
    max-width: calc(100vw - 140px); // Ensure grids don't overflow viewport
    overflow-x: auto; // Allow horizontal scrolling if needed
  }
  
  .ag-grid {
    font-size: 13px; // Slightly smaller font for better density
  }
  
  .ag-header-cell-text {
    font-size: 12px; // Smaller header text
  }
  
  .individual-page-container {
    overflow-x: hidden; // Prevent horizontal page scroll
  }
  
  .individal-page-content {
    max-width: calc(100vw - 120px); // Ensure content area fits in viewport
    overflow-x: hidden;
  }
}

@media (min-width: 1920px) {
  .grid-container {
    max-width: calc(100vw - 160px); // Account for larger sidebar on huge screens
  }
  
  .budget-summary-content {
    max-width: calc(100vw - 160px); // Ensure charts don't overflow
  }
  
  .ag-grid {
    font-size: 12px; // Even more compact on very large screens
  }
  
  .tile {
    .chart-main-title {
      font-size: 16px; // Smaller chart titles
    }
  }
}

// ✅ CHART RESPONSIVE SIZING: Make charts scale down bar width and reduce whitespace
@media (min-width: 1440px) {
  .cost-curve-chart-container,
  .tornado-chart-container,
  .budget-variance-chart-container {
    max-width: 100%;
    overflow: hidden;
    
    // Force charts to use available space efficiently
    .ag-chart {
      max-width: 100% !important;
    }
  }
  
  // ✅ SPECIFIC FIX: Budget Variance Chart positioning and containment
  .budget-variance-chart {
    max-width: 32% !important; // Strict width limit
    overflow: hidden !important; // ✅ FORCE: Hide ALL overflow
    position: relative; // ✅ ADDED: Relative positioning for containment
    
    .tornado-chart-container {
      width: 100% !important; // ✅ FORCE: Exact width
      max-width: 100% !important;
      padding: 8px; // ✅ ADDED: Small padding inside
      margin: 0; // Remove any extra margins
      display: flex;
      flex-direction: column;
      overflow: hidden !important; // ✅ FORCE: No overflow allowed
      height: 350px; // ✅ FIXED: Set exact height
      box-sizing: border-box; // ✅ ADDED: Include padding in width
    }
    
    .tornado-chart {
      width: 100% !important; // ✅ FORCE: Full container width
      height: 100% !important; // ✅ FORCE: Full container height
      overflow-y: auto; // ✅ SCROLL: If content too tall
      overflow-x: hidden !important; // ✅ FORCE: No horizontal overflow
      box-sizing: border-box; // ✅ ADDED: Include padding in calculations
      
      .tornado-row {
        width: 100% !important; // ✅ FORCE: Full width for each row
        display: flex; // ✅ SIDE BY SIDE: Label next to bar
        align-items: center; // ✅ CENTER: Vertically align
        margin-bottom: 8px; // ✅ SPACING: Space between rows
        padding: 2px; // ✅ MINIMAL: Small padding
        box-sizing: border-box; // ✅ ADDED: Include padding
        overflow: hidden !important; // ✅ FORCE: Clip everything
      }
      
      .tornado-label {
        width: 25% !important; // ✅ SMALL: Only 25% for label to give bars more space
        font-size: 9px !important; // ✅ TINY: Very small font to fit
        flex-shrink: 0; // ✅ PREVENT: Label from shrinking
        white-space: nowrap; // ✅ PREVENT: Text wrapping
        overflow: hidden; // ✅ HIDE: Long text
        text-overflow: ellipsis; // ✅ SHOW: ... for overflow
        padding-right: 4px; // ✅ SPACING: Small gap between label and bar
        color: #555; // ✅ READABLE: Dark enough to read
      }
      
      .tornado-bar-container {
        flex: 1; // ✅ EXPAND: Take remaining 75% space
        height: 20px !important; // ✅ COMPACT: Smaller height for more rows
        overflow: hidden !important; // ✅ FORCE: Clip bar overflow
        position: relative; // ✅ RELATIVE: For bar positioning
        min-width: 0; // ✅ ALLOW: Container to shrink if needed
        
        // ✅ REMOVED: Duplicate variance-bar styles - using the main one above
      }
    }
  }
  
  // Reduce chart margins and padding
  .chart-title {
    margin: 10px 0; // Reduced from 15px
  }
  
  .chart-subtitle {
    margin-bottom: 8px; // Reduced whitespace
  }
  
  .cost-curve-controls {
    margin-bottom: 8px; // Tighter spacing
  }
}

// ✅ ADDITIONAL CONTAINMENT: Ensure all tiles respect viewport bounds
@media (min-width: 1440px) {
  .tile:nth-child(3) { // Third chart (Budget Variance)
    max-width: calc(32% - 8px) !important; // Account for gaps
    overflow: hidden;
    box-sizing: border-box;
  }
}

// ✨ LIGHTBOX MODAL STYLES
.lightbox-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.lightbox-container {
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  cursor: default;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-close {
  position: absolute;
  top: -50px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 40px;
  cursor: pointer;
  z-index: 10001;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
}

.lightbox-image-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
  max-height: 80vh;
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.lightbox-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(8, 27, 46, 0.9); // DDA exact color
  color: white;
  border: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-size: 30px;
  cursor: pointer;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(8, 27, 46, 1);
    transform: translateY(-50%) scale(1.15);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.lightbox-arrow-left {
  left: -80px;
}

.lightbox-arrow-right {
  right: -80px;
}

.lightbox-info {
  margin-top: 20px;
  text-align: center;
  color: white;
}

// Lightbox title styles removed - no longer showing filename

.lightbox-counter {
  font-size: 14px;
  opacity: 0.8;
  background: rgba(8, 27, 46, 0.8);
  padding: 6px 12px;
  border-radius: 15px;
  display: inline-block;
}

// Responsive adjustments for smaller screens
@media (max-width: 768px) {
  .lightbox-arrow {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
  
  .lightbox-arrow-left {
    left: -60px;
  }
  
  .lightbox-arrow-right {
    right: -60px;
  }
  
  .lightbox-close {
    top: -40px;
    font-size: 32px;
  }
}

@media (max-width: 480px) {
  .lightbox-arrow-left {
    left: 10px;
  }
  
  .lightbox-arrow-right {
    right: 10px;
  }
  
  .lightbox-image-container {
    max-width: 95vw;
  }
}

// AG-Grid Header Height Reduction and Responsive Text
.ag-header-row {
  height: 35px !important;
  min-height: 35px !important;
}

.ag-header-cell {
  padding: 4px 8px !important;
  font-size: clamp(11px, 1vw, 13px) !important;
}

.ag-cell {
  font-size: clamp(12px, 1vw, 14px) !important;
  padding: 4px 8px !important;
}

// Color-coded Budget Tracking Headers using header classes
.header-custom-lilac {
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%) !important;
  color: white !important;
}

.header-custom-lightblue {
  background: linear-gradient(135deg, #AB47BC 0%, #7B1FA2 100%) !important;
  color: white !important;
}

/* 🤖 DRAW INSIGHTS BUTTON STYLES */
.draw-insights-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  font-weight: 500;
  transition: all 0.2s;
}

.draw-insights-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.draw-insights-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 📊 SPD REPORT BUTTON STYLES */
.spd-report-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-weight: 500;
  transition: all 0.2s;
}

.spd-report-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.spd-report-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 📊 SPD REPORT MODAL STYLES - Uses standard modal styling */
.spd-report-modal {
  max-width: 1200px !important;
  width: 95% !important;
}

.spd-report-modal .modal-body {
  padding: 32px;
  overflow-y: auto;
  flex: 1;
}

.spd-report-modal .report-content {
  animation: fadeIn 0.5s ease-out;
}

.spd-report-modal .report-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.spd-report-modal .report-section:last-child {
  border-bottom: none;
}

.spd-report-modal .report-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #2563eb;
  margin: 0 0 16px 0;
}

.spd-report-modal .project-info {
  background: #f9fafb;
  padding: 16px;
  border-radius: 8px;
}

.spd-report-modal .info-row {
  padding: 8px 0;
  font-size: 14px;
  color: #374151;
}

.spd-report-modal .info-row strong {
  color: #111827;
  margin-right: 8px;
}

.spd-report-modal .narrative-content {
  font-size: 15px;
  line-height: 1.8;
  color: #374151;
}

.spd-report-modal .narrative-content h3,
.spd-report-modal .narrative-content h4 {
  color: #1e40af;
  margin: 20px 0 12px 0;
  font-weight: 600;
}

.spd-report-modal .narrative-content ul {
  margin: 12px 0;
  padding-left: 24px;
}

.spd-report-modal .narrative-content li {
  margin: 8px 0;
  line-height: 1.6;
}

.spd-report-modal .narrative-content strong {
  color: #1e40af;
  font-weight: 600;
}

.spd-report-modal .budget-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.spd-report-modal .summary-card {
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.spd-report-modal .card-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 8px;
}

.spd-report-modal .card-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 4px;
}

.spd-report-modal .card-subtext {
  font-size: 13px;
  color: #64748b;
}

.spd-report-modal .payment-summary {
  background: #f9fafb;
  padding: 20px;
  border-radius: 8px;
}

.spd-report-modal .summary-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;
}

.spd-report-modal .summary-row:last-child {
  border-bottom: none;
}

.spd-report-modal .summary-row .label {
  font-weight: 500;
  color: #374151;
}

.spd-report-modal .summary-row .value {
  font-weight: 600;
  color: #1e40af;
}

.spd-report-modal .compliance-summary {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.spd-report-modal .compliance-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.spd-report-modal .compliance-item .icon {
  font-size: 24px;
}

.spd-report-modal .compliance-item .text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.spd-report-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 32px;
  border-top: 2px solid #e0e0e0;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
}

.spd-report-modal .secondary-btn,
.spd-report-modal .primary-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.spd-report-modal .secondary-btn {
  background: #e5e7eb;
  color: #374151;
}

.spd-report-modal .secondary-btn:hover {
  background: #d1d5db;
}

.spd-report-modal .primary-btn {
  background: #2563eb;
  color: white;
}

.spd-report-modal .primary-btn:hover {
  background: #1e40af;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}

</style>

<script>
// Fetch BBL data from NYC GeoSearch API
async function fetchBBLFromAddressAPI(address) {
  try {
    console.log('🔍 Fetching BBL for address:', address);
    const response = await fetch(`https://geosearch.planninglabs.nyc/v2/search?text=${encodeURIComponent(address)}`);
    const data = await response.json();
    
    console.log('🌐 NYC GeoSearch API Response:', data);
    
    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      console.log('📍 First feature properties:', feature.properties);
      
      // Extract BBL from the addendum.pad.bbl field (e.g., "1010160036")
      const bblString = feature.properties.addendum?.pad?.bbl;
      
      if (bblString && bblString.length === 10) {
        // Parse BBL string: 1 digit borough + 5 digits block + 4 digits lot
        const boro = bblString.substring(0, 1);
        const block = bblString.substring(1, 6);  // 🔥 FIX: Get 5 chars for block (positions 1-6)
        const lot = bblString.substring(6, 10);   // 🔥 FIX: Get 4 chars for lot (positions 6-10)
        
        // Remove leading zeros from block and lot for URL formatting
        const bbl = {
          boro: boro,
          block: parseInt(block).toString(),
          lot: parseInt(lot).toString()
        };
        
        console.log('✅ Parsed BBL from GeoSearch:', {
          originalBBL: bblString,
          parsed: bbl,
          url: `https://zola.planning.nyc.gov/l/lot/${boro}/${parseInt(block)}/${parseInt(lot)}`
        });
        return bbl;
      } else {
        console.log('⚠️ BBL string format unexpected:', bblString);
      }
    }
  } catch (error) {
    console.log('⚠️ Failed to fetch BBL from GeoSearch:', error);
  }
  return null;
}

// Make function globally available
window.fetchBBLFromAddressAPI = fetchBBLFromAddressAPI;

// 🔗 NEW: Template Editor Function for G703 Template Links
function openTemplateEditor(templateUrl) {
  console.log(`🔧 Opening template editor: ${templateUrl}`);
  // Open template editor in new tab
  window.open(templateUrl, '_blank');
}
</script>